import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { useRoleProtection } from '../hooks/useRoleProtection';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import NavigationBar from '../components/NavigationBar';
import { OWNER_APPOINTMENT_BOOKED_EVENT } from '../components/BusinessOwnerAskAiWidget';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import ServiceCard from '../components/ServiceCard';
import RoleCard from '../components/RoleCard';
import ProfessionalCard from '../components/ProfessionalCard';
import RoleModal from '../components/RoleModal';
import GiftCardModal from '../components/GiftCardModal';
import TestimonialModal from '../components/TestimonialModal';
import StockImagesModal from '../components/StockImagesModal';
import PermissionGate from '../components/PermissionGate';
import { Service, ServiceFormData, ServiceFilters } from '../types/service';
import { RoleFormData, RoleFilters } from '../types/role';
import { Business, BusinessBranding, Invitation, UserRole, GiftCard, GiftCardFormData, Testimonial, TestimonialModerationData, OperatingHours, OperatingHoursFormData, Notification, Appointment, ServiceReview, ReviewReply, User, BusinessRating } from '../types';
import { apiService } from '../services/api';
import InvitationModal from '../components/InvitationModal';
import InvitationsList from '../components/InvitationsList';
import TimePicker from '../components/TimePicker';
import { Building2, ChevronDown, Settings, Save, Trash2, QrCode, X, Printer, Plus, LayoutGrid, Table, Calendar, Bell, AlertTriangle, Clock, Power, List, CheckCircle, XCircle, UserPlus, ArrowLeft, Eye, ChevronLeft, ChevronRight, CheckSquare, Briefcase, Check, Heart, Upload, ImagePlus, Image, HelpCircle, CalendarX, Search, Edit, RefreshCw, User as UserIcon, Pencil, Columns, Square, ListOrdered, Send, MessageSquare, Flag, CreditCard, ExternalLink, GripVertical, SlidersHorizontal, BarChart3, Copy } from 'lucide-react';
import { searchServices, fuzzyMatch } from '../utils/searchUtils';
import { QRCodeSVG } from 'qrcode.react';
import AppointmentCalendar from '../components/AppointmentCalendar';
import AppointmentDetailsModal from '../components/AppointmentDetailsModal';
import ScheduleModal from '../components/ScheduleModal';
import CancellationDialog from '../components/CancellationDialog';
import ReviewDetailsModal from '../components/ReviewDetailsModal';
import ProfessionalDisplayPriorityModal from '../components/ProfessionalDisplayPriorityModal';
import InvoicesSection from '../components/InvoicesSection';
import QuoteRequestsSection from '../components/QuoteRequestsSection';
import BusinessStatisticsSection from '../components/BusinessStatisticsSection';
import toast from 'react-hot-toast';
import { getBusinessDashboardNavigation, NavigationSection } from '../utils/navigationUtils';
import { THEME_PRESETS, getThemePreset, getThemePresetList, detectThemePreset } from '../utils/themePresets';
import { formatLocalYmd, getBrowserTimeZone } from '../utils/timezoneUtils';
import { canAccessCustomersPage, canViewFullCustomerPhone, isBusinessOwner, isBusinessOwnerForBusiness } from '../utils/roleUtils';
import { displayCustomerPhone } from '../utils/phoneFormatUtils';
import {
  SOCIAL_URL_CONFIG,
  SocialUrlField,
  buildSocialUrl,
  getSocialUrlSuffix,
} from '../utils/socialUrlUtils';
import { getAppointmentStartIso, getTotalDuration as getTotalDurationHelper } from '../utils/appointmentHelpers';
import { DEFAULT_BOOKING_SLOT_MINUTES, resolveBookingSlotMinutes } from '../utils/bookingSlotSettings';
import {
  DEFAULT_REGULAR_DEPOSIT_DUE_UNIT,
  DEFAULT_REGULAR_DEPOSIT_DUE_VALUE,
  formatDepositDueDurationLabel,
  normalizeDepositDueWithinUnit,
  type DepositDueWithinUnit,
} from '../utils/depositDueDuration';
import { GuidedSetupModal, SiteConfigTab } from '../components/guidedSetup/GuidedSetupModal';
import { GuidedSetupLauncher } from '../components/guidedSetup/GuidedSetupLauncher';
import { InfoTooltip } from '../components/InfoTooltip';
import UnassignedAssignmentsWarning from '../components/UnassignedAssignmentsWarning';

/** When false, the Help & Support banner after guided setup stays hidden (markup and localStorage logic retained). */
const SHOW_HELP_SUPPORT_POST_SETUP_BANNER = false;

const US_TIMEZONES: Array<{ value: string; label: string }> = [
  // Continental US
  { value: 'America/New_York', label: 'Eastern — America/New_York' },
  { value: 'America/Chicago', label: 'Central — America/Chicago' },
  { value: 'America/Denver', label: 'Mountain — America/Denver' },
  { value: 'America/Phoenix', label: 'Arizona (no DST) — America/Phoenix' },
  { value: 'America/Los_Angeles', label: 'Pacific — America/Los_Angeles' },

  // Alaska
  { value: 'America/Anchorage', label: 'Alaska — America/Anchorage' },
  { value: 'America/Adak', label: 'Aleutian — America/Adak' },

  // Hawaii
  { value: 'Pacific/Honolulu', label: 'Hawaii (no DST) — Pacific/Honolulu' },

  // US Territories (optional but commonly needed)
  { value: 'America/Puerto_Rico', label: 'Puerto Rico — America/Puerto_Rico' },
  { value: 'America/St_Thomas', label: 'US Virgin Islands — America/St_Thomas' },
  { value: 'Pacific/Pago_Pago', label: 'American Samoa — Pacific/Pago_Pago' },
  { value: 'Pacific/Guam', label: 'Guam — Pacific/Guam' },
  { value: 'Pacific/Saipan', label: 'Northern Mariana Islands — Pacific/Saipan' }
];

const BUSINESS_SHORT_NAME_PATTERN = /^[a-z0-9_-]+$/;
const BUSINESS_SHORT_NAME_MIN_LENGTH = 3;
const BUSINESS_SHORT_NAME_MAX_LENGTH = 25;

function getBusinessShortNameFormatError(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Public URL is required';
  if (trimmed.length < BUSINESS_SHORT_NAME_MIN_LENGTH) {
    return 'Public URL must be at least 3 characters';
  }
  if (trimmed.length > BUSINESS_SHORT_NAME_MAX_LENGTH) {
    return 'Public URL must be less than 25 characters';
  }
  if (!BUSINESS_SHORT_NAME_PATTERN.test(trimmed)) {
    return 'Public URL can only contain lowercase letters, numbers, hyphens, and underscores';
  }
  return null;
}

/** Extract gallery image id from URL (e.g. /businesses/:shortName/gallery/:id) */
function getGalleryImageId(url: string): string | null {
  const parts = url.split('/gallery/');
  return parts.length === 2 ? parts[1].split('?')[0].trim() || null : null;
}

const MAX_GALLERY_IMAGES = 6;

const SortableGalleryImage: React.FC<{
  imageUrl: string;
  index: number;
  onRemove: (index: number) => void;
  compact?: boolean;
}> = ({ imageUrl, index, onRemove, compact }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: imageUrl });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group ${compact ? '' : 'aspect-square'} ${isDragging ? 'opacity-80 z-50 shadow-lg' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        style={{ touchAction: 'none' }}
        className="absolute left-1 top-1 z-10 w-7 h-7 rounded bg-gray-800/70 text-white flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-gray-700/80"
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </div>
      <img
        src={imageUrl}
        alt={`Gallery ${index + 1}`}
        className={`w-full h-full object-cover rounded-lg ${compact ? 'h-24' : ''}`}
        draggable={false}
      />
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-bold hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Remove image"
      >
        ×
      </button>
    </div>
  );
};

type DashboardSection = 
  | 'services'
  | 'roles'
  | 'professionals'
  | 'appointments'
  | 'quote-requests'
  | 'invoices'
  | 'closures'
  | 'statistics'
  | 'my-appointments'
  | 'my-bookings'
  | 'my-gift-cards'
  | 'my-services'
  | 'my-work-schedule'
  | 'my-customer-reviews'
  | 'testimonials'
  | 'giftcards'
  | 'site-config'
  | 'settings'
  | 'branding'
  // | 'preview-website' // Removed 'preview-website'
  | 'notifications'
  | 'customers';



type AppointmentFilters = {
  status: string | undefined;
  professionalId: string;
  dateFrom: string;
  dateTo: string;
};

const policyDepositCentsToAmount = (digits: string): number => {
  const only = String(digits || '').replace(/\D/g, '');
  const cents = only ? parseInt(only, 10) : 0;
  return Number.isFinite(cents) ? cents / 100 : 0;
};

const policyDepositCentsToMoneyString = (digits: string): string => {
  const only = String(digits || '').replace(/\D/g, '');
  if (!only) return '';
  const cents = parseInt(only, 10);
  return (Number.isFinite(cents) ? cents / 100 : 0).toFixed(2);
};

const policyDepositAmountToCentsDigits = (amount: number): string => {
  const n = Number.isFinite(amount) ? amount : 0;
  const cents = Math.round(Math.max(0, n) * 100);
  return cents > 0 ? String(cents) : '';
};

const BusinessDashboard: React.FC = () => {
  const { user } = useAuth();
  const { currentBusiness, userBusinesses, setCurrentBusiness, loadUserBusinesses } = useBusiness();
  const { canPerformAction, getRoleProtectionStatus } = useRoleProtection();
  const [searchParams, setSearchParams] = useSearchParams();
  

  // Initialize activeSection based on URL parameter
  const getInitialSection = (): DashboardSection => {
    const validSections: DashboardSection[] = [
      'services',
      'roles',
      'professionals',
      'appointments',
      'quote-requests',
      'invoices',
      'closures',
      'statistics',
      'my-appointments',
      'my-bookings',
      'my-gift-cards',
      'my-services',
      'my-work-schedule',
      'my-customer-reviews',
      'testimonials',
      'giftcards',
      'site-config',
      'settings',
      'branding',
      'notifications',
      'customers'
    ];

    const businessId = currentBusiness?.id || '';
    const isOwnerForCurrentBusiness = !!(user && businessId && isBusinessOwnerForBusiness(user, businessId));
    const isOwnerAnywhere = isBusinessOwner(user || null);

    const sectionFromUrl = searchParams.get('section') as DashboardSection;
    if (sectionFromUrl && validSections.includes(sectionFromUrl)) {
      // Non-owner professionals should never land on owner-only Business Appointments
      if (sectionFromUrl === 'appointments' && user?.canPerformServices && !isOwnerForCurrentBusiness) {
        return 'my-appointments';
      }
      if (sectionFromUrl === 'statistics' && !isOwnerForCurrentBusiness) {
        return user?.canPerformServices ? 'my-appointments' : 'appointments';
      }
      if (
        sectionFromUrl === 'customers' &&
        businessId &&
        user &&
        !canAccessCustomersPage(user, businessId)
      ) {
        return user?.canPerformServices ? 'my-appointments' : 'appointments';
      }
      return sectionFromUrl;
    }

    // Default landing section:
    // - Owner: Business Appointments
    // - Professional (non-owner): My Appointments
    if (user?.canPerformServices) {
      return (isOwnerForCurrentBusiness || isOwnerAnywhere) ? 'appointments' : 'my-appointments';
    }

    return 'appointments';
  };

  const [activeSection, setActiveSection] = useState<DashboardSection>(getInitialSection());

  // Hard guard: Non-owner professionals must not access owner-only sections (even via URL).
  const hasRedirectedFromOwnerAppointmentsRef = useRef(false);
  useEffect(() => {
    if (!user || !currentBusiness?.id) return;

    const ownerForThisBusiness = isBusinessOwnerForBusiness(user, currentBusiness.id);
    if (ownerForThisBusiness) {
      hasRedirectedFromOwnerAppointmentsRef.current = false;
      return;
    }

    const sectionFromUrl = (searchParams.get('section') as DashboardSection) || null;
    const isTryingToViewOwnerOnlySection =
      activeSection === 'appointments' ||
      activeSection === 'statistics' ||
      sectionFromUrl === 'appointments' ||
      sectionFromUrl === 'statistics';
    if (!isTryingToViewOwnerOnlySection) return;

    const next = new URLSearchParams(searchParams);
    next.set('section', 'my-appointments');
    setSearchParams(next, { replace: true });
    setActiveSection('my-appointments');

    if (!hasRedirectedFromOwnerAppointmentsRef.current) {
      hasRedirectedFromOwnerAppointmentsRef.current = true;
      toast.error('This page is owner-only. Redirected to My Appointments.');
    }
  }, [user, currentBusiness?.id, activeSection, searchParams, setSearchParams]);

  // Block Customers page when personnel access is disabled (including direct URL).
  const [customersAccessDeniedShown, setCustomersAccessDeniedShown] = useState(false);
  useEffect(() => {
    if (!user || !currentBusiness?.id) return;

    const sectionFromUrl = (searchParams.get('section') as DashboardSection) || null;
    const isTryingCustomers = activeSection === 'customers' || sectionFromUrl === 'customers';
    if (!isTryingCustomers) {
      setCustomersAccessDeniedShown(false);
      return;
    }

    if (canAccessCustomersPage(user, currentBusiness.id)) {
      setCustomersAccessDeniedShown(false);
      return;
    }

    setCustomersAccessDeniedShown(true);
    if (activeSection !== 'customers') {
      setActiveSection('customers');
    }
  }, [user, currentBusiness?.id, activeSection, searchParams]);

  // Optional deep-linking into a specific Site Configuration tab via URL param.
  // Example: /dashboard/business?section=site-config&siteConfigTab=operating-hours
  useEffect(() => {
    const tab = searchParams.get('siteConfigTab');
    if (!tab) return;

    const isValidSiteConfigTab = (
      v: string
    ): v is
      | 'business-info'
      | 'services-categories'
      | 'operating-hours'
      | 'branding-theme'
      | 'cancellation-rescheduling-policy'
      | 'payments' =>
      v === 'business-info' ||
      v === 'services-categories' ||
      v === 'operating-hours' ||
      v === 'branding-theme' ||
      v === 'cancellation-rescheduling-policy' ||
      v === 'payments';

    if (!isValidSiteConfigTab(tab)) return;

    // Ensure we're on site-config and select the requested tab.
    if (activeSection !== 'site-config') {
      setActiveSection('site-config');
    }
    setSiteConfigActiveTab(tab);

    // Clean the param to prevent repeated state updates on subsequent renders.
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('siteConfigTab');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, activeSection]);

  // Force refresh business context when component mounts
  useEffect(() => {
    loadUserBusinesses();
  }, [loadUserBusinesses]);

  // Guided Setup (first login)
  const [showGuidedSetup, setShowGuidedSetup] = useState(false);
  const [showHelpSupportBanner, setShowHelpSupportBanner] = useState(false);
  const guidedSetupMarkedRef = useRef<string | null>(null);
  // In-memory only: launcher should NOT persist across logout/login.
  const [guidedSetupLauncherEnabled, setGuidedSetupLauncherEnabled] = useState<Record<string, boolean>>({});
  const [guidedSetupStepIndexByBusiness, setGuidedSetupStepIndexByBusiness] = useState<Record<string, number>>({});

  // Upcoming maintenance banner (within 1 day); dismiss stored in localStorage by maintenance id
  const [upcomingMaintenance, setUpcomingMaintenance] = useState<{
    id: string;
    message: string;
    from: string;
    to: string;
  } | null>(null);
  const DISMISSED_MAINTENANCE_PREFIX = 'dismissed_maintenance_';
  useEffect(() => {
    let cancelled = false;
    apiService.getUpcomingMaintenance().then((data) => {
      if (cancelled || !data) {
        setUpcomingMaintenance(null);
        return;
      }
      try {
        if (localStorage.getItem(DISMISSED_MAINTENANCE_PREFIX + data.id)) {
          setUpcomingMaintenance(null);
          return;
        }
        setUpcomingMaintenance({ id: data.id, message: data.message, from: data.from, to: data.to });
      } catch {
        setUpcomingMaintenance(null);
      }
    }).catch(() => setUpcomingMaintenance(null));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const businessId = currentBusiness?.id;
    if (!user || !businessId) {
      setShowGuidedSetup(false);
      return;
    }

    const isOwner = isBusinessOwnerForBusiness(user, businessId);
    const guidedSetupShown = currentBusiness.guidedSetupShown === true;

    if (isOwner && !guidedSetupShown) {
      setShowGuidedSetup(true);

      // Enable launcher for this in-memory session (per business)
      setGuidedSetupLauncherEnabled(prev => (prev[businessId] ? prev : { ...prev, [businessId]: true }));
      // Default step to 0 for a brand-new business session (but don't overwrite if already present)
      setGuidedSetupStepIndexByBusiness(prev => (prev[businessId] === undefined ? { ...prev, [businessId]: 0 } : prev));
      if (guidedSetupMarkedRef.current !== businessId) {
        guidedSetupMarkedRef.current = businessId;
        apiService
          .markGuidedSetupShown(businessId)
          .catch((err) => console.error('Failed to mark guided setup shown:', err));
      }
    } else {
      setShowGuidedSetup(false);
    }
  }, [user, currentBusiness]);

  const getHelpSupportBannerKey = (businessId: string) => `helpSupportBanner:${businessId}`; // 'open' | 'closed'
  const getHelpSupportBannerLegacyKey = (businessId: string) => `helpSupportBannerShown:${businessId}`; // legacy: 'true'

  // Keep banner scoped to the currently selected business and persist "open" until dismissed/viewed.
  useEffect(() => {
    const businessId = currentBusiness?.id;
    if (!user || !businessId) {
      setShowHelpSupportBanner(false);
      return;
    }

    try {
      const key = getHelpSupportBannerKey(businessId);
      const legacyKey = getHelpSupportBannerLegacyKey(businessId);
      const status = localStorage.getItem(key); // 'open' | 'closed' | null

      if (status === 'open') {
        setShowHelpSupportBanner(true);
        return;
      }
      if (status === 'closed') {
        setShowHelpSupportBanner(false);
        return;
      }

      // Migration path: older implementation may have set legacy key before the user saw/dismissed.
      // Treat that as "open" so the banner becomes visible and can be dismissed.
      const legacy = localStorage.getItem(legacyKey) === 'true';
      if (legacy) {
        localStorage.setItem(key, 'open');
        setShowHelpSupportBanner(true);
        return;
      }

      setShowHelpSupportBanner(false);
    } catch {
      setShowHelpSupportBanner(false);
    }
  }, [user, currentBusiness?.id]);

  // Initialize the banner when Guided Setup is triggered (first login).
  // Once initialized, it stays open until the user dismisses/views it.
  useEffect(() => {
    const businessId = currentBusiness?.id;
    if (!user || !businessId) return;
    if (!showGuidedSetup) return;
    if (!isBusinessOwnerForBusiness(user, businessId)) return;

    try {
      const key = getHelpSupportBannerKey(businessId);
      const existing = localStorage.getItem(key);
      if (existing !== 'open' && existing !== 'closed') {
        localStorage.setItem(key, 'open');
        setShowHelpSupportBanner(true);
      } else if (existing === 'open') {
        setShowHelpSupportBanner(true);
      }
    } catch {
      setShowHelpSupportBanner(true);
    }
  }, [showGuidedSetup, user, currentBusiness?.id]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] = useState(false);
  const [isMobileSettingsDropdownOpen, setIsMobileSettingsDropdownOpen] = useState(false);
  const mobileSettingsDropdownRef = useRef<HTMLDivElement>(null);
  const [isSiteConfigTabDropdownOpen, setIsSiteConfigTabDropdownOpen] = useState(false);
  const siteConfigTabDropdownRef = useRef<HTMLDivElement>(null);

  // Business expiration notice (owner only)
  const [showBusinessExpiredModal, setShowBusinessExpiredModal] = useState(false);
  const expiredModalShownForBusinessRef = useRef<string | null>(null);
  const currentBusinessValidUntil = (currentBusiness as any)?.validUntil;

  const parseUtcMs = (value: any): number | null => {
    if (!value) return null;
    const raw = String(value);
    // Tolerate common Postgres-ish formats like "2026-06-30 12:59:59+00"
    const normalized =
      /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(raw) ? raw.replace(' ', 'T') : raw;
    const d = value instanceof Date ? value : new Date(normalized);
    const ms = d.getTime();
    return Number.isNaN(ms) ? null : ms;
  };

  useEffect(() => {
    const businessId = currentBusiness?.id;
    if (!user || !businessId) {
      setShowBusinessExpiredModal(false);
      return;
    }
    if (!isBusinessOwnerForBusiness(user, businessId)) {
      setShowBusinessExpiredModal(false);
      return;
    }
    if (expiredModalShownForBusinessRef.current === businessId) return;

    const ms = parseUtcMs(currentBusinessValidUntil);
    if (ms !== null && Date.now() > ms) {
      expiredModalShownForBusinessRef.current = businessId;
      setShowBusinessExpiredModal(true);
    }
  }, [user, currentBusiness?.id, currentBusinessValidUntil]);
  
  const navigate = useNavigate();
  
  // Services Management State
  const [services, setServices] = useState<Service[]>([]);
  const [filters, setFilters] = useState<ServiceFilters>({ isActive: true });
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile settings dropdown and site config tab dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileSettingsDropdownRef.current && !mobileSettingsDropdownRef.current.contains(event.target as Node)) {
        setIsMobileSettingsDropdownOpen(false);
      }
      if (siteConfigTabDropdownRef.current && !siteConfigTabDropdownRef.current.contains(event.target as Node)) {
        setIsSiteConfigTabDropdownOpen(false);
      }
    };

    if (isMobileSettingsDropdownOpen || isSiteConfigTabDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileSettingsDropdownOpen, isSiteConfigTabDropdownOpen]);
  const [showDeleteServiceModal, setShowDeleteServiceModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);
  const [showDeactivateServiceModal, setShowDeactivateServiceModal] = useState(false);
  const [serviceToDeactivate, setServiceToDeactivate] = useState<Service | null>(null);
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  
  // Roles Management State
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roleFilters, setRoleFilters] = useState<RoleFilters>({});
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<UserRole | null>(null);
  const [showDeactivateRoleModal, setShowDeactivateRoleModal] = useState(false);
  const [roleToDeactivate, setRoleToDeactivate] = useState<{ role: UserRole; isActive: boolean } | null>(null);
  const [showDeleteRoleModal, setShowDeleteRoleModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<UserRole | null>(null);
  
  // Business Information State
  const [businessInfo, setBusinessInfo] = useState<Business | null>(null);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessSaving, setBusinessSaving] = useState(false);
  const [businessCategories, setBusinessCategories] = useState<Array<{ id: string; name: string; description?: string; subcategories?: Array<{ id: string; name: string; description?: string }> }>>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [businessFormData, setBusinessFormData] = useState({
    name: '',
    shortName: '',
    timezone: getBrowserTimeZone() || 'UTC',
    description: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    facebookUrl: '',
    instagramUrl: '',
    youtubeUrl: '',
    showContactForm: true,
    websiteRedirectEnabled: false,
    serviceLocation: 'IN_STORE' as 'IN_STORE' | 'ON_SITE' | 'BOTH',
    categories: [] as string[],
    subCategories: [] as string[]
  });
  const [shortNameValidation, setShortNameValidation] = useState<{
    available: boolean;
    checking: boolean;
    message?: string;
    formatError?: string | null;
  }>({ available: true, checking: false, formatError: null });
  const shortNameCheckSeqRef = useRef(0);
  const shortNameDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const businessFormShortNameRef = useRef('');

  const savedBusinessShortName = useMemo(
    () => (businessInfo?.shortName || currentBusiness?.shortName || '').trim().toLowerCase(),
    [businessInfo?.shortName, currentBusiness?.shortName]
  );
  const isPublicUrlChanged =
    !!businessInfo &&
    businessFormData.shortName.trim().toLowerCase() !== savedBusinessShortName;
  const isPublicUrlEmpty = !businessFormData.shortName.trim();
  const publicUrlHasValidationError =
    isPublicUrlChanged &&
    (!!shortNameValidation.formatError || (!shortNameValidation.available && !shortNameValidation.checking));

  // Operating Hours State
  const [operatingHours, setOperatingHours] = useState<OperatingHours[]>([]);
  const [operatingHoursLoading, setOperatingHoursLoading] = useState(false);
  const [operatingHoursSaving, setOperatingHoursSaving] = useState(false);
  const isSiteConfigSaveDisabled =
    businessSaving ||
    operatingHoursSaving ||
    shortNameValidation.checking ||
    isPublicUrlEmpty ||
    publicUrlHasValidationError;
  const operatingHoursLoadedForCalendarRef = useRef(false);
  
  // Branding State
  const [brandingInfo, setBrandingInfo] = useState<BusinessBranding | null>(null);
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [logoImageLoading, setLogoImageLoading] = useState(false);
  const [showStockImagesModal, setShowStockImagesModal] = useState(false);
  const [brandingFormData, setBrandingFormData] = useState<BusinessBranding>({
    logoUrl: '',
    primaryColor: '#3B82F6',
    secondaryColor: '#1E40AF',
    accentColor: '#f5c2cc',
    textColor: '#111827',
    backgroundColor: '#ffeef0',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    useCustomColors: false,
    layout: 'modern', // Kept for backward compatibility but not used
    themePreset: undefined,
    galleryImages: []
  });
  
  // Invitations State
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
  
  // Notifications State
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [userNames, setUserNames] = useState<{[userId: string]: string}>({});
  const [totalUnreadCount, setTotalUnreadCount] = useState<number>(0);
  
  // Pagination state for notifications
  const [notificationPage, setNotificationPage] = useState(1);
  const [notificationsPerPage] = useState(10);
  const [notificationPagination, setNotificationPagination] = useState<{ total: number; totalPages: number; hasNextPage: boolean; hasPrevPage: boolean } | null>(null);
  const [notificationFilter, setNotificationFilter] = useState<'all' | 'unread' | 'appointments' | 'services' | 'reviews'>('all');
  const [isNotificationFilterDropdownOpen, setIsNotificationFilterDropdownOpen] = useState(false);
  const [isMobileNotificationsTopCollapsed, setIsMobileNotificationsTopCollapsed] = useState(false);
  const [notificationSearchQuery, setNotificationSearchQuery] = useState('');
  const [allNotificationsForSearch, setAllNotificationsForSearch] = useState<Notification[] | null>(null);
  const [allNotificationsForSearchLoading, setAllNotificationsForSearchLoading] = useState(false);
  
  // Customers state
  const [customers, setCustomers] = useState<Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    createdAt: string;
    appointmentCount: number;
    lastAppointment?: {
      id: string;
      scheduledAt: string;
      status: string;
    } | null;
    nextAppointment?: {
      id: string;
      scheduledAt: string;
      status: string;
    } | null;
  }>>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState<string | null>(null);
  const [customerView, setCustomerView] = useState<'grid' | 'table'>('grid');
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSortOption, setCustomerSortOption] = useState<string>('most-recent-next');
  const [customerSortDropdownOpen, setCustomerSortDropdownOpen] = useState(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPagination, setCustomerPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null>(null);
  // Settings state
  const [settings, setSettings] = useState<{
    autoConfirmAppointments: boolean;
    emailAppointmentActivitiesToOwner: boolean;
    customerAppointmentMessageEnabled: boolean;
    customerAppointmentMessage: string;
    showProfessionalNameOnAssignment: boolean;
  }>({
    autoConfirmAppointments: false,
    emailAppointmentActivitiesToOwner: false,
    customerAppointmentMessageEnabled: false,
    customerAppointmentMessage: '',
    showProfessionalNameOnAssignment: false
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [isAppointmentSettingsModalOpen, setIsAppointmentSettingsModalOpen] = useState(false);
  const [isQuoteOfferSettingsModalOpen, setIsQuoteOfferSettingsModalOpen] = useState(false);
  const [isAppointmentSettingsDropdownOpen, setIsAppointmentSettingsDropdownOpen] = useState(false);
  const [statisticsRefreshTrigger, setStatisticsRefreshTrigger] = useState(0);
  // Draft for Appointment Settings modal; only committed on Save, reverted on Cancel
  const [appointmentSettingsDraft, setAppointmentSettingsDraft] = useState<
    Pick<typeof settings, 'autoConfirmAppointments' | 'emailAppointmentActivitiesToOwner' | 'showProfessionalNameOnAssignment'> & {
      bookingSlotMinutes: 15 | 30 | 60;
    }
  >({
    autoConfirmAppointments: false,
    emailAppointmentActivitiesToOwner: false,
    showProfessionalNameOnAssignment: false,
    bookingSlotMinutes: DEFAULT_BOOKING_SLOT_MINUTES
  });
  
  // Gift Cards State
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [giftCardsLoading, setGiftCardsLoading] = useState(false);
  const [isGiftCardModalOpen, setIsGiftCardModalOpen] = useState(false);
  const [editingGiftCard, setEditingGiftCard] = useState<GiftCard | undefined>(undefined);
  
  // Testimonials state (business_ratings table)
  const [serviceReviews, setServiceReviews] = useState<BusinessRating[]>([]);
  const [serviceReviewsLoading, setServiceReviewsLoading] = useState(false);
  const [serviceReviewSearch, setServiceReviewSearch] = useState('');
  const [debouncedServiceReviewSearch, setDebouncedServiceReviewSearch] = useState('');
  const [serviceReviewPage, setServiceReviewPage] = useState(1);
  const [serviceReviewPagination, setServiceReviewPagination] = useState<{
    total: number;
    limit: number;
    offset: number;
  }>({ total: 0, limit: 20, offset: 0 });
  const [serviceReviewStats, setServiceReviewStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    public: 0,
    featured: 0,
    averageRating: '0.0',
    totalRatings: 0
  });
  const [serviceReviewFilters, setServiceReviewFilters] = useState({
    isPublic: undefined as boolean | undefined
  });
  const [isServiceReviewModalOpen, setIsServiceReviewModalOpen] = useState(false);
  const [selectedServiceReview, setSelectedServiceReview] = useState<ServiceReview | null>(null);
  const [isReviewDetailsModalOpen, setIsReviewDetailsModalOpen] = useState(false);
  const [selectedReviewNotification, setSelectedReviewNotification] = useState<Notification | null>(null);
  
  // Service review card gear dropdown state
  const [openReviewDropdowns, setOpenReviewDropdowns] = useState<Set<string>>(new Set());
  const reviewDropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  // Close service review card dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      let clickedInsideAnyDropdown = false;
      
      Object.values(reviewDropdownRefs.current).forEach((ref) => {
        if (ref && ref.contains(target)) {
          clickedInsideAnyDropdown = true;
        }
      });
      
      if (!clickedInsideAnyDropdown && openReviewDropdowns.size > 0) {
        setOpenReviewDropdowns(new Set());
      }
    };

    if (openReviewDropdowns.size > 0) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openReviewDropdowns]);
  
  // Appointments State
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState<string | null>(null);
  // Calendar drag/drop: track in-flight reschedule so the UI can show a spinner on the moved block.
  const [movingAppointmentId, setMovingAppointmentId] = useState<string | null>(null);
  const [appointmentPagination, setAppointmentPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 12,
    hasNextPage: false,
    hasPrevPage: false
  });
  
  // Professional Appointments State (for "My Appointments" section)
  const [professionalAppointments, setProfessionalAppointments] = useState<Appointment[]>([]);
  const [professionalAppointmentsLoading, setProfessionalAppointmentsLoading] = useState(false);
  const [professionalAppointmentsError, setProfessionalAppointmentsError] = useState<string | null>(null);
  
  // My Bookings State (for "My Bookings" section - appointments where user is a customer)
  const [myBookings, setMyBookings] = useState<Appointment[]>([]);
  const [myBookingsLoading, setMyBookingsLoading] = useState(false);
  const [myBookingsError, setMyBookingsError] = useState<string | null>(null);
  
  // My Gift Cards State (for "My Gift Cards" section - gift cards owned by the user)
  const [myGiftCards, setMyGiftCards] = useState<GiftCard[]>([]);
  const [myGiftCardsLoading, setMyGiftCardsLoading] = useState(false);
  const [myGiftCardsError, setMyGiftCardsError] = useState<string | null>(null);
  
  // Professional Services State (for "My Services" section)
  const [professionalServices, setProfessionalServices] = useState<Service[]>([]);
  const [professionalServicesLoading, setProfessionalServicesLoading] = useState(false);
  const [professionalServicesError, setProfessionalServicesError] = useState<string | null>(null);
  const [myServicesViewMode, setMyServicesViewMode] = useState<'grid' | 'list'>('grid');
  const [myServicesCategoryFilter, setMyServicesCategoryFilter] = useState<string | undefined>('my-services');
  const [myServicesCategoryDropdownOpen, setMyServicesCategoryDropdownOpen] = useState(false);
  const [myServicesSearch, setMyServicesSearch] = useState('');
  const [professionalFavoriteServiceIds, setProfessionalFavoriteServiceIds] = useState<Set<string>>(new Set());
  const [loadingProfessionalFavorites, setLoadingProfessionalFavorites] = useState<boolean>(false);
  
  // Professional Schedule State (for "My Work Schedule" section)
  const [professionalSchedule, setProfessionalSchedule] = useState<any[]>([]);
  const [professionalScheduleLoading, setProfessionalScheduleLoading] = useState(false);
  const [professionalScheduleError, setProfessionalScheduleError] = useState<string | null>(null);
  
  // Professional Customer Reviews State (for "My Customer Reviews" section)
  const [professionalCustomerReviews, setProfessionalCustomerReviews] = useState<ServiceReview[]>([]);
  const [professionalCustomerReviewsLoading, setProfessionalCustomerReviewsLoading] = useState(false);
  const [professionalCustomerReviewsError, setProfessionalCustomerReviewsError] = useState<string | null>(null);
  
  // Review filters
  const [reviewRatingFilter, setReviewRatingFilter] = useState<string>('all');
  const [reviewServiceFilter, setReviewServiceFilter] = useState<string>('all');
  const [reviewDateFilter, setReviewDateFilter] = useState<string>('last-30-days');
  
  // Reply state (for professional customer reviews)
  const [replyingToReview, setReplyingToReview] = useState<string | null>(null);
  const [replyingToReply, setReplyingToReply] = useState<{ reviewId: string; replyId: string } | null>(null);
  const [editingReply, setEditingReply] = useState<{ reviewId: string; replyId: string } | null>(null);
  const [replyMessage, setReplyMessage] = useState<string>('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  
  // Reply state (for Testimonials / business ratings section) - one-level only
  const [testimonialReplyingToRating, setTestimonialReplyingToRating] = useState<string | null>(null);
  const [testimonialEditingReply, setTestimonialEditingReply] = useState<{ ratingId: string; replyId: string } | null>(null);
  const [testimonialReplyMessage, setTestimonialReplyMessage] = useState<string>('');
  const [isSubmittingTestimonialReply, setIsSubmittingTestimonialReply] = useState(false);

  // Schedule Modal State
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<any>(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<{ dayOfWeek: number; dayName: string } | null>(null);
  const [isDeletingSchedule, setIsDeletingSchedule] = useState(false);
  
  // Time Off Modal State
  const [isTimeOffModalOpen, setIsTimeOffModalOpen] = useState(false);
  const [editingTimeOff, setEditingTimeOff] = useState<any>(null);
  const [timeOffFormData, setTimeOffFormData] = useState({
    label: '',
    startDate: '',
    endDate: '',
    isAllDay: false,
    startTime: '',
    endTime: ''
  });
  const [timeOffSaving, setTimeOffSaving] = useState(false);
  
  // Time Off Records State
  const [timeOffs, setTimeOffs] = useState<any[]>([]);
  const [timeOffsLoading, setTimeOffsLoading] = useState(false);
  const [timeOffsError, setTimeOffsError] = useState<string | null>(null);
  const [timeOffToDelete, setTimeOffToDelete] = useState<{ id: string; label: string } | null>(null);
  const [isDeletingTimeOff, setIsDeletingTimeOff] = useState(false);
  
  // Time offs for selected professional in appointments page
  const [appointmentsProfessionalTimeOffs, setAppointmentsProfessionalTimeOffs] = useState<any[]>([]);
  const [appointmentsProfessionalTimeOffsLoading, setAppointmentsProfessionalTimeOffsLoading] = useState(false);
  

  const [appointmentFilters, setAppointmentFilters] = useState<AppointmentFilters>({
    status: 'CONFIRMED',
    professionalId: '',
    dateFrom: '',
    dateTo: ''
  });
  
  // Categories state
  const [categories, setCategories] = useState<{ id: string; name: string; icon?: string }[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // All Business Services State (for My Services selection)
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState<string | null>(null);
  
  // State for professionals
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [professionalsLoading, setProfessionalsLoading] = useState(false);
  const [professionalFilters, setProfessionalFilters] = useState({
    search: '',
    isActive: undefined as boolean | undefined
  });
  const [showToggleServicePermissionModal, setShowToggleServicePermissionModal] = useState(false);
  const [professionalToToggleService, setProfessionalToToggleService] = useState<{ professional: any; canPerformServices: boolean } | null>(null);
  const [showToggleProfessionalActiveModal, setShowToggleProfessionalActiveModal] = useState(false);
  const [professionalToToggleActive, setProfessionalToToggleActive] = useState<{ professional: any; isActive: boolean } | null>(null);
  const [showDeleteProfessionalModal, setShowDeleteProfessionalModal] = useState(false);
  const [professionalToDelete, setProfessionalToDelete] = useState<{ professional: any; name: string } | null>(null);
  const [showDisplayPriorityModal, setShowDisplayPriorityModal] = useState(false);

  // Site Configuration Tabs State
  const [siteConfigActiveTab, setSiteConfigActiveTab] = useState<
    | 'business-info'
    | 'services-categories'
    | 'operating-hours'
    | 'branding-theme'
    | 'cancellation-rescheduling-policy'
    | 'payments'
  >('business-info');

  const SITE_CONFIG_SECTION_TABS: {
    id:
      | 'business-info'
      | 'services-categories'
      | 'operating-hours'
      | 'branding-theme'
      | 'payments'
      | 'cancellation-rescheduling-policy';
    label: string;
    icon: string;
  }[] = [
    { id: 'business-info', label: 'Business Profile', icon: 'bi bi-building' },
    { id: 'services-categories', label: 'Services & Categories', icon: 'bi bi-grid' },
    { id: 'operating-hours', label: 'Business Hours', icon: 'bi bi-clock-history' },
    { id: 'branding-theme', label: 'Branding Studio', icon: 'bi bi-palette' },
    { id: 'payments', label: 'Online Payments', icon: 'bi bi-credit-card' },
    { id: 'cancellation-rescheduling-policy', label: 'Policies & Messages', icon: 'bi bi-calendar-check' },
  ];

  // Policies tab: cancellation text + reschedule window + booking deposit (saved via upsertBusinessPolicies on Save).
  const [cancellationPolicyText, setCancellationPolicyText] = useState<string>('');
  const [cancelRescheduleUpToValue, setCancelRescheduleUpToValue] = useState<number>(24);
  const [cancelRescheduleUpToUnit, setCancelRescheduleUpToUnit] = useState<'hours' | 'days'>('hours');
  const [depositRequired, setDepositRequired] = useState<boolean>(false);
  const [depositAmountCentsDigits, setDepositAmountCentsDigits] = useState<string>('');
  const [depositNonRefundableOnCancel, setDepositNonRefundableOnCancel] = useState<boolean>(false);
  const [depositDueWithinValue, setDepositDueWithinValue] = useState<number>(DEFAULT_REGULAR_DEPOSIT_DUE_VALUE);
  const [depositDueWithinUnit, setDepositDueWithinUnit] = useState<DepositDueWithinUnit>(DEFAULT_REGULAR_DEPOSIT_DUE_UNIT);
  const [showStandardPolicyModal, setShowStandardPolicyModal] = useState(false);
  const policiesLoadedForBusinessIdRef = useRef<string | null>(null);

  // Payments (Stripe Connect / Express) - UI-only state for Site Config Payments tab.
  const [businessStripeAccount, setBusinessStripeAccount] = useState<any | null>(null);
  const [businessStripeAccountLoading, setBusinessStripeAccountLoading] = useState(false);
  const [businessStripeAccountError, setBusinessStripeAccountError] = useState<string | null>(null);

  const loadBusinessStripeAccount = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      setBusinessStripeAccountLoading(true);
      setBusinessStripeAccountError(null);
      const data = await apiService.getBusinessStripeAccount(currentBusiness.id);
      setBusinessStripeAccount(data);
    } catch (e: any) {
      console.error('Failed to load Stripe account status', e);
      setBusinessStripeAccount(null);
      setBusinessStripeAccountError(e?.response?.data?.error || e?.message || 'Failed to load payments status');
    } finally {
      setBusinessStripeAccountLoading(false);
    }
  }, [currentBusiness?.id]);

  const openOnboardingUrl = useCallback((url: string) => {
    if (!url) return;
    // Open in a new tab for safety (avoid losing unsaved Site Config changes).
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleStartStripeOnboarding = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      setBusinessStripeAccountError(null);
      setBusinessStripeAccountLoading(true);
      const resp = await apiService.createBusinessStripeAccount(currentBusiness.id);
      setBusinessStripeAccount(resp.account ?? null);
      if (resp.onboardingUrl) openOnboardingUrl(resp.onboardingUrl);
    } catch (e: any) {
      console.error('Failed to start Stripe onboarding', e);
      toast.error(e?.response?.data?.error || e?.message || 'Failed to start Stripe onboarding');
      setBusinessStripeAccountError(e?.response?.data?.error || e?.message || 'Failed to start Stripe onboarding');
    } finally {
      setBusinessStripeAccountLoading(false);
    }
  }, [currentBusiness?.id, openOnboardingUrl]);

  const handleContinueStripeOnboarding = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      setBusinessStripeAccountError(null);
      setBusinessStripeAccountLoading(true);
      const resp = await apiService.createBusinessStripeOnboardingLink(currentBusiness.id);
      if (resp.onboardingUrl) openOnboardingUrl(resp.onboardingUrl);
    } catch (e: any) {
      console.error('Failed to create Stripe onboarding link', e);
      toast.error(e?.response?.data?.error || e?.message || 'Failed to create onboarding link');
      setBusinessStripeAccountError(e?.response?.data?.error || e?.message || 'Failed to create onboarding link');
    } finally {
      setBusinessStripeAccountLoading(false);
    }
  }, [currentBusiness?.id, openOnboardingUrl]);

  const handleOpenStripeDashboard = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      setBusinessStripeAccountError(null);
      setBusinessStripeAccountLoading(true);
      const resp = await apiService.createBusinessStripeLoginLink(currentBusiness.id);
      if (resp?.url) {
        // Stripe login links are short-lived; open immediately.
        window.open(resp.url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Could not open Stripe dashboard');
      }
    } catch (e: any) {
      console.error('Failed to open Stripe dashboard', e);
      toast.error(e?.response?.data?.error || e?.message || 'Failed to open Stripe dashboard');
      setBusinessStripeAccountError(e?.response?.data?.error || e?.message || 'Failed to open Stripe dashboard');
    } finally {
      setBusinessStripeAccountLoading(false);
    }
  }, [currentBusiness?.id]);

  const handleRefreshStripeAccount = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      setBusinessStripeAccountError(null);
      setBusinessStripeAccountLoading(true);
      const data = await apiService.refreshBusinessStripeAccount(currentBusiness.id);
      setBusinessStripeAccount(data);
      toast.success('Payments status refreshed.');
    } catch (e: any) {
      console.error('Failed to refresh Stripe account', e);
      toast.error(e?.response?.data?.error || e?.message || 'Failed to refresh payments status');
      setBusinessStripeAccountError(e?.response?.data?.error || e?.message || 'Failed to refresh payments status');
    } finally {
      setBusinessStripeAccountLoading(false);
    }
  }, [currentBusiness?.id]);

  useEffect(() => {
    if (activeSection !== 'site-config') return;
    if (siteConfigActiveTab !== 'payments' && siteConfigActiveTab !== 'cancellation-rescheduling-policy') return;
    loadBusinessStripeAccount();
  }, [activeSection, siteConfigActiveTab, loadBusinessStripeAccount]);
  const standardPolicyTimeLabel = useMemo(() => {
    const value = Math.max(0, Number.isFinite(cancelRescheduleUpToValue) ? cancelRescheduleUpToValue : 0);
    const unit = cancelRescheduleUpToUnit;
    const unitLabel = unit === 'days' ? 'days' : 'hours';
    return `${value} ${unitLabel}`;
  }, [cancelRescheduleUpToValue, cancelRescheduleUpToUnit]);
  const standardPolicyText = useMemo(
    () =>
      `• No-fee changes: Cancel or reschedule at least ${standardPolicyTimeLabel} in advance.

• Late cancellations & reschedules: Changes made within ${standardPolicyTimeLabel} of your appointment may result in a fee. If a deposit was required for this appointment, the deposit is typically forfeited for late cancellation/reschedule. Any refund or credit, if offered, is at the business's discretion.

• No-shows: If you do not arrive for your appointment, you may be charged a fee. If a deposit was required, it is typically forfeited. Any refund or credit, if offered, is at the business's discretion.

• Unexpected events: If severe weather, an emergency, or a business issue prevents service, we'll work with you to reschedule. Any fees may be waived at the business's discretion.`,
    [standardPolicyTimeLabel]
  );
  const standardPolicySummary = useMemo(() => {
    const value = Math.max(0, Number.isFinite(cancelRescheduleUpToValue) ? cancelRescheduleUpToValue : 0);
    const unit = cancelRescheduleUpToUnit;
    const unitLabel = unit === 'days' ? 'days' : 'hours';
    return `Cancel or reschedule online up to ${value} ${unitLabel} before your appointment start time.`;
  }, [cancelRescheduleUpToValue, cancelRescheduleUpToUnit]);
  const [workScheduleActiveTab, setWorkScheduleActiveTab] = useState<'weekly-hours' | 'time-off'>('weekly-hours');
  const [showAllCategoriesMobile, setShowAllCategoriesMobile] = useState(false);
  
  // Work Schedule Actions Dropdown State
  const [workScheduleActionsDropdownOpen, setWorkScheduleActionsDropdownOpen] = useState<string | null>(null);
  const workScheduleActionsDropdownRefs = useRef<{[key: string]: HTMLDivElement | null}>({});

  // Appointments State
  const [appointmentView, setAppointmentView] = useState<'grid' | 'table' | 'calendar'>('grid');
  const [professionalAppointmentView, setProfessionalAppointmentView] = useState<'grid' | 'table' | 'calendar'>('calendar');
  const APPOINTMENTS_PAGE_LIMIT = 12;
  const APPOINTMENTS_CALENDAR_LIMIT = 1000;
  // Mobile-only: collapse/expand the bulky appointments controls bar (new appt + view toggles + filters/date range)
  const [isMobileAppointmentsControlsOpen, setIsMobileAppointmentsControlsOpen] = useState(false);
  const [isMobileMyAppointmentsControlsOpen, setIsMobileMyAppointmentsControlsOpen] = useState(false);
  const [professionalDateRangeType, setProfessionalDateRangeType] = useState<'today' | '3days' | '1week'>('today');
  const [professionalDateRangeStart, setProfessionalDateRangeStart] = useState<Date>(new Date());
  const [isProfessionalDatePickerOpen, setIsProfessionalDatePickerOpen] = useState(false);
  const [professionalDatePickerMonth, setProfessionalDatePickerMonth] = useState(new Date());
  // Appointments page date range state (separate from my-appointments)

  const [appointmentsDateRangeType, setAppointmentsDateRangeType] = useState<'today' | '3days' | '1week'>('today');
  const [appointmentsDateRangeStart, setAppointmentsDateRangeStart] = useState<Date>(new Date());
  const [isAppointmentsDatePickerOpen, setIsAppointmentsDatePickerOpen] = useState(false);
  const [appointmentsDatePickerMonth, setAppointmentsDatePickerMonth] = useState(new Date());
  const [appointmentsDateRangeDropdownOpen, setAppointmentsDateRangeDropdownOpen] = useState(false);
  const [professionalDropdownOpen, setProfessionalDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [personnelStatusDropdownOpen, setPersonnelStatusDropdownOpen] = useState(false);
  const [rolesStatusDropdownOpen, setRolesStatusDropdownOpen] = useState(false);
  const [servicesCategoryDropdownOpen, setServicesCategoryDropdownOpen] = useState(false);
  const [servicesStatusDropdownOpen, setServicesStatusDropdownOpen] = useState(false);
  const [isServicesFilterSectionOpen, setIsServicesFilterSectionOpen] = useState(false);
  const [isRolesFilterSectionOpen, setIsRolesFilterSectionOpen] = useState(false);
  const [myAppointmentsStatusDropdownOpen, setMyAppointmentsStatusDropdownOpen] = useState(false);
  const [myAppointmentsDateRangeDropdownOpen, setMyAppointmentsDateRangeDropdownOpen] = useState(false);
  const [myBookingsStatusDropdownOpen, setMyBookingsStatusDropdownOpen] = useState(false);
  const [serviceReviewsVisibilityDropdownOpen, setServiceReviewsVisibilityDropdownOpen] = useState(false);
  const [serviceActionsDropdownOpen, setServiceActionsDropdownOpen] = useState<string | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  
  // Closures State
  type BusinessClosure = {
    id: string;
    businessId: string;
    startDate: string;
    endDate: string;
    closureType: 'all_day' | 'time_range';
    startTime?: string;
    endTime?: string;
    reason?: string;
    repeatYearly: boolean;
    createdAt: string;
    updatedAt: string;
  };
  const [closures, setClosures] = useState<BusinessClosure[]>([]);
  const [closuresLoading, setClosuresLoading] = useState(false);
  const [closuresError, setClosuresError] = useState<string | null>(null);
  const [editingClosure, setEditingClosure] = useState<BusinessClosure | null>(null);
  const [closureFormData, setClosureFormData] = useState<Partial<BusinessClosure>>({
    startDate: '',
    endDate: '',
    closureType: 'all_day',
    startTime: '',
    endTime: '',
    reason: '',
    repeatYearly: false
  });
  const [showClosureForm, setShowClosureForm] = useState(false);
  const [showDeleteClosureModal, setShowDeleteClosureModal] = useState(false);
  const [closuresFilter, setClosuresFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming');
  const [closuresFilterDropdownOpen, setClosuresFilterDropdownOpen] = useState(false);
  const [timeOffsFilter, setTimeOffsFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming');
  const [timeOffsFilterDropdownOpen, setTimeOffsFilterDropdownOpen] = useState(false);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.professional-dropdown-container')) {
        setProfessionalDropdownOpen(false);
      }
      if (!target.closest('.status-dropdown-container')) {
        setStatusDropdownOpen(false);
      }
      if (!target.closest('.personnel-status-dropdown-container')) {
        setPersonnelStatusDropdownOpen(false);
      }
      if (!target.closest('.roles-status-dropdown-container')) {
        setRolesStatusDropdownOpen(false);
      }
      if (!target.closest('.services-category-dropdown-container')) {
        setServicesCategoryDropdownOpen(false);
      }
      if (!target.closest('.services-status-dropdown-container')) {
        setServicesStatusDropdownOpen(false);
      }
      if (!target.closest('.my-appointments-status-dropdown-container')) {
        setMyAppointmentsStatusDropdownOpen(false);
      }
      if (!target.closest('.my-appointments-date-range-dropdown-container')) {
        setMyAppointmentsDateRangeDropdownOpen(false);
      }
      if (!target.closest('.appointments-date-range-dropdown-container')) {
        setAppointmentsDateRangeDropdownOpen(false);
      }
      if (!target.closest('.appointments-date-picker-container')) {
        setIsAppointmentsDatePickerOpen(false);
      }
      if (!target.closest('.my-bookings-status-dropdown-container')) {
        setMyBookingsStatusDropdownOpen(false);
      }
      if (!target.closest('.service-reviews-visibility-dropdown-container')) {
        setServiceReviewsVisibilityDropdownOpen(false);
      }
      if (!target.closest('.service-actions-dropdown-container')) {
        setServiceActionsDropdownOpen(null);
      }
      if (!target.closest('.appointment-settings-dropdown-container')) {
        setIsAppointmentSettingsDropdownOpen(false);
      }
      if (!target.closest('.work-schedule-actions-dropdown-container')) {
        setWorkScheduleActionsDropdownOpen(null);
      }
      if (!target.closest('.notification-filter-dropdown-container')) {
        setIsNotificationFilterDropdownOpen(false);
      }
      if (!target.closest('.my-services-category-dropdown-container')) {
        setMyServicesCategoryDropdownOpen(false);
      }
      if (!target.closest('.professional-date-picker-container')) {
        setIsProfessionalDatePickerOpen(false);
      }
      if (!target.closest('.my-appointments-date-range-dropdown-container')) {
        setMyAppointmentsDateRangeDropdownOpen(false);
      }
      if (!target.closest('.closures-filter-dropdown-container')) {
        setClosuresFilterDropdownOpen(false);
      }
      if (!target.closest('.time-offs-filter-dropdown-container')) {
        setTimeOffsFilterDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close work schedule actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is inside any work schedule dropdown container
      const clickedInside = Object.values(workScheduleActionsDropdownRefs.current).some(ref => 
        ref && ref.contains(target)
      );
      if (!clickedInside) {
        setWorkScheduleActionsDropdownOpen(null);
      }
    };

    if (workScheduleActionsDropdownOpen) {
      // Use click event with a small delay to allow button onClick handlers to fire first
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [workScheduleActionsDropdownOpen]);
  const [isAppointmentDetailsModalOpen, setIsAppointmentDetailsModalOpen] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState<'month' | 'day'>('month');
  const [appointmentsCalendarDisplayMode, setAppointmentsCalendarDisplayMode] = useState<'default' | 'professionalColumns'>('default');
  const [isCancellationDialogOpen, setIsCancellationDialogOpen] = useState(false);
  const [cancellationAppointment, setCancellationAppointment] = useState<Appointment | null>(null);
  const [isCancellingAppointment, setIsCancellingAppointment] = useState(false);
  const [isConfirmingAppointment, setIsConfirmingAppointment] = useState(false);

  const isAppointmentsTodayAllProfessionals =
    appointmentsDateRangeType === 'today' && !appointmentFilters.professionalId;

  // Auto-reset the toggle when it should not be available (to avoid impacting other views/filters)
  useEffect(() => {
    if (
      appointmentsCalendarDisplayMode !== 'default' &&
      (!isAppointmentsTodayAllProfessionals || appointmentView !== 'calendar' || activeSection !== 'appointments')
    ) {
      setAppointmentsCalendarDisplayMode('default');
    }
  }, [appointmentsCalendarDisplayMode, isAppointmentsTodayAllProfessionals, appointmentView, activeSection]);
  
  // Manual assignment state
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [assignmentAppointment, setAssignmentAppointment] = useState<Appointment | null>(null);
  const [assignmentSelections, setAssignmentSelections] = useState<{[assignmentId: string]: string}>({});
  const [isSavingAssignment, setIsSavingAssignment] = useState(false);
  const [assignmentProfessionals, setAssignmentProfessionals] = useState<User[]>([]);
  const [assignmentCapabilities, setAssignmentCapabilities] = useState<{[professionalId: string]: any[]}>({});
  const [assignmentSchedules, setAssignmentSchedules] = useState<{[professionalId: string]: any[]}>({});
  const [assignmentExistingAppointments, setAssignmentExistingAppointments] = useState<{[professionalId: string]: Appointment[]}>({});
  const [loadingAssignmentData, setLoadingAssignmentData] = useState(false);
  // Bump to refresh the unassigned-assignments warning after booking/assignment changes.
  const [unassignedAssignmentsRefreshKey, setUnassignedAssignmentsRefreshKey] = useState(0);

  // Load categories
  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      if (!currentBusiness?.id) {
        setCategories([]);
        return;
      }
      const categoriesData = await apiService.getServiceCategories(currentBusiness.id);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback to default categories if API fails
      setCategories([
        { id: '1', name: 'Hair Services' },
        { id: '2', name: 'Nail Services' },
        { id: '3', name: 'Massage Therapy' },
        { id: '4', name: 'Facial Treatments' },
        { id: '5', name: 'Consultation' }
      ]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Load all services (for Services Management page - both active and inactive)
  const loadServices = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    try {
      setServicesLoading(true);
      setServicesError(null);
      const servicesData = await apiService.getAllServices(currentBusiness.id);
      setServices(servicesData);
    } catch (error) {
      console.error('Error loading services:', error);
      setServicesError('Failed to load services');
      toast.error('Failed to load services');
    } finally {
      setServicesLoading(false);
    }
  }, [currentBusiness?.id]);

  // Load active services only (for My Services page)
  const loadActiveServices = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    try {
      setServicesLoading(true);
      setServicesError(null);
      const servicesData = await apiService.getServices(currentBusiness.id);
      setServices(servicesData);
    } catch (error) {
      console.error('Error loading active services:', error);
      setServicesError('Failed to load services');
      toast.error('Failed to load services');
    } finally {
      setServicesLoading(false);
    }
  }, [currentBusiness?.id]);

  // Load appointments
  const loadAppointments = useCallback(async (page = 1, options?: { silent?: boolean }) => {
    if (!currentBusiness?.id) return;
    
    try {
      if (!options?.silent) {
        setAppointmentsLoading(true);
        setAppointmentsError(null);
      }
      
      // For calendar view, load all appointments without pagination
      const isCalendarView = appointmentView === 'calendar';
      
      // Build filters object, excluding empty values
      const filters: any = {
        businessId: currentBusiness.id,
        ...(isCalendarView
          ? { page: 1, limit: APPOINTMENTS_CALENDAR_LIMIT }
          : { page, limit: APPOINTMENTS_PAGE_LIMIT }),
        // For grid and table views, sort by earliest (ascending)
        // Calendar view doesn't need explicit sort as it's handled differently
        ...(!isCalendarView ? { sort: 'asc' } : {}),
      };
      
      // Only include filter values that are not empty
      if (appointmentFilters.status) {
        filters.status = appointmentFilters.status;
      }
      if (appointmentFilters.professionalId) {
        filters.professionalId = appointmentFilters.professionalId;
      }
      if (appointmentFilters.dateFrom) {
        const iso = localYmdToUtcStartIso(appointmentFilters.dateFrom);
        if (iso) filters.dateFrom = iso;
      }
      if (appointmentFilters.dateTo) {
        const iso = localYmdToUtcEndExclusiveIso(appointmentFilters.dateTo);
        if (iso) filters.dateTo = iso;
      }
      
      const appointmentsData = await apiService.getAppointments(filters);
      setAppointments(appointmentsData.data);
      
      // Update pagination state only for non-calendar views
      if (!isCalendarView && appointmentsData.pagination) {
        setAppointmentPagination({
          currentPage: appointmentsData.pagination.page,
          totalPages: appointmentsData.pagination.totalPages,
          total: appointmentsData.pagination.total,
          limit: appointmentsData.pagination.limit || APPOINTMENTS_PAGE_LIMIT,
          hasNextPage: appointmentsData.pagination.hasNextPage,
          hasPrevPage: appointmentsData.pagination.hasPrevPage
        });
      } else if (isCalendarView) {
        // For calendar view, set pagination to show all appointments
        setAppointmentPagination({
          currentPage: 1,
          totalPages: 1,
          total: appointmentsData.data?.length || 0,
          limit: APPOINTMENTS_CALENDAR_LIMIT,
          hasNextPage: false,
          hasPrevPage: false
        });
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
      if (!options?.silent) {
        setAppointmentsError('Failed to load appointments. Please try again.');
        toast.error('Failed to load appointments');
      }
    } finally {
      if (!options?.silent) setAppointmentsLoading(false);
    }
  }, [currentBusiness?.id, appointmentFilters, appointmentView, APPOINTMENTS_CALENDAR_LIMIT, APPOINTMENTS_PAGE_LIMIT]);

  // Load closures
  const loadClosures = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    try {
      setClosuresLoading(true);
      setClosuresError(null);
      const closuresData = await apiService.getBusinessClosures(currentBusiness.id);
      setClosures(closuresData);
    } catch (error) {
      console.error('Error loading closures:', error);
      setClosuresError('Failed to load closures');
      toast.error('Failed to load closures');
    } finally {
      setClosuresLoading(false);
    }
  }, [currentBusiness?.id]);

  const handlePageChange = (page: number) => {
    loadAppointments(page);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setAppointmentFilters(prev => ({ ...prev, [filterType]: value }));
    // Reset pagination to page 1 - the useEffect will handle reloading with new filters
    setAppointmentPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleViewChange = (view: 'grid' | 'table' | 'calendar') => {
    setAppointmentView(view);
    // Reload appointments when view changes to handle pagination appropriately
    loadAppointments(1);
  };

  const handleProfessionalViewChange = (view: 'grid' | 'table' | 'calendar') => {
    setProfessionalAppointmentView(view);
    // Reload professional appointments when view changes
    loadProfessionalAppointments();
  };

  // Helper functions for professional date range
  const getProfessionalDateRange = (rangeType: 'today' | '3days' | '1week', startDate: Date) => {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    
    const end = new Date(startDate);
    
    switch (rangeType) {
      case 'today':
        end.setHours(23, 59, 59, 999);
        break;
      case '3days':
        end.setDate(start.getDate() + 2);
        end.setHours(23, 59, 59, 999);
        break;
      case '1week':
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
    }
    
    return { start, end };
  };

  const formatProfessionalDateRange = (rangeType: 'today' | '3days' | '1week', startDate: Date) => {
    const { start, end } = getProfessionalDateRange(rangeType, startDate);
    
    if (rangeType === 'today') {
      return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    return `${startStr} – ${endStr}`;
  };

  const handleProfessionalDateRangeChange = (rangeType: 'today' | '3days' | '1week') => {
    setProfessionalDateRangeType(rangeType);
    setProfessionalDateRangeStart(new Date());
  };

  const handleProfessionalDateRangeNavigate = (direction: 'prev' | 'next') => {
    const newStart = new Date(professionalDateRangeStart);
    const days = professionalDateRangeType === 'today' ? 1 : professionalDateRangeType === '3days' ? 3 : 7;
    
    if (direction === 'prev') {
      newStart.setDate(newStart.getDate() - days);
    } else {
      newStart.setDate(newStart.getDate() + days);
    }
    
    setProfessionalDateRangeStart(newStart);
  };

  const handleProfessionalDateSelect = (date: Date) => {
    setProfessionalDateRangeStart(date);
    setIsProfessionalDatePickerOpen(false);
  };

  // Helper functions for appointments page date range (reusing the same logic)
  const getAppointmentsDateRange = (rangeType: 'today' | '3days' | '1week', startDate: Date) => {
    return getProfessionalDateRange(rangeType, startDate);
  };

  const formatLocalDateYYYYMMDD = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // localStorage keys for appointments filters
  const APPOINTMENTS_STORAGE_KEY = 'businessAppointmentsFilters';
  const MY_APPOINTMENTS_STORAGE_KEY = 'businessMyAppointmentsFilters';
  const SERVICES_STORAGE_KEY = 'businessServicesFilters';
  const SERVICES_SEARCH_PARAM = 'servicesSearch';
  const SERVICES_CATEGORY_PARAM = 'servicesCategoryId';
  const SERVICES_STATUS_PARAM = 'servicesStatus';
  const SERVICES_VIEW_PARAM = 'servicesView';

  interface StoredAppointmentsFilters {
    dateRangeType: 'today' | '3days' | '1week';
    dateRangeStart: string; // YYYY-MM-DD format
    status?: string;
    professionalId?: string;
    view: 'grid' | 'table' | 'calendar';
    calendarDisplayMode?: 'default' | 'professionalColumns';
  }

  interface StoredMyAppointmentsFilters {
    dateRangeType: 'today' | '3days' | '1week';
    dateRangeStart: string; // YYYY-MM-DD format
    status?: string;
    view: 'grid' | 'table' | 'calendar';
  }

  interface StoredServicesFilters {
    search: string;
    category_id?: string;
    status: 'active' | 'inactive' | 'all';
    viewMode: 'grid' | 'list';
    addOnsOnly?: boolean;
  }

  const isServiceAddOn = (service: Service | Record<string, unknown>): boolean => {
    const s = service as Record<string, unknown>;
    return s.isAddOn === true || s.is_add_on === true || s.is_add_on === 1;
  };

  // Load appointments filters from localStorage
  const loadStoredAppointmentsFilters = (): StoredAppointmentsFilters | null => {
    try {
      const stored = localStorage.getItem(APPOINTMENTS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Normalize legacy/special values so the UI dropdown only ever sees known statuses.
        if (parsed?.status === 'upcoming') {
          parsed.status = '';
        }
        return parsed;
      }
    } catch (error) {
      console.error('Error loading stored appointments filters:', error);
    }
    return null;
  };

  // Load my appointments filters from localStorage
  const loadStoredMyAppointmentsFilters = (): StoredMyAppointmentsFilters | null => {
    try {
      const stored = localStorage.getItem(MY_APPOINTMENTS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.status === 'upcoming') {
          parsed.status = '';
        }
        return parsed;
      }
    } catch (error) {
      console.error('Error loading stored my appointments filters:', error);
    }
    return null;
  };

  // Save appointments filters to localStorage
  const saveAppointmentsFilters = (filters: StoredAppointmentsFilters) => {
    try {
      localStorage.setItem(APPOINTMENTS_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving appointments filters:', error);
    }
  };

  // Save my appointments filters to localStorage
  const saveMyAppointmentsFilters = (filters: StoredMyAppointmentsFilters) => {
    try {
      localStorage.setItem(MY_APPOINTMENTS_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving my appointments filters:', error);
    }
  };

  // Load services filters from localStorage
  const loadStoredServicesFilters = (): StoredServicesFilters | null => {
    try {
      const stored = localStorage.getItem(SERVICES_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const parsedViewMode = parsed?.viewMode;
        const viewMode: 'grid' | 'list' = parsedViewMode === 'list' ? 'list' : 'grid';
        const search = typeof parsed?.search === 'string' ? parsed.search : '';
        const category_id = typeof parsed?.category_id === 'string' ? parsed.category_id : undefined;
        const parsedStatus = parsed?.status;
        const status: 'active' | 'inactive' | 'all' =
          parsedStatus === 'inactive' ? 'inactive'
          : parsedStatus === 'all' ? 'all'
          : 'active';
        const addOnsOnly = Boolean(parsed?.addOnsOnly ?? parsed?.hideAddOnsOnly);
        return { search, category_id, status, viewMode, addOnsOnly };
      }
    } catch (error) {
      console.error('Error loading stored services filters:', error);
    }
    return null;
  };

  // Save services filters to localStorage
  const saveServicesFilters = (stored: StoredServicesFilters) => {
    try {
      localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(stored));
    } catch (error) {
      console.error('Error saving services filters:', error);
    }
  };

  const localDateFromYmd = (ymd: string): Date | null => {
    const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    return new Date(year, month - 1, day, 0, 0, 0, 0); // local midnight
  };

  // Convert a viewer-local YYYY-MM-DD into a UTC ISO instant that represents local midnight.
  // Backend uses: scheduled_at >= dateFrom AND scheduled_at < dateTo (exclusive).
  const localYmdToUtcStartIso = (ymd: string): string | null => {
    const d = localDateFromYmd(ymd);
    return d ? d.toISOString() : null;
  };

  const localYmdToUtcEndExclusiveIso = (ymd: string): string | null => {
    const d = localDateFromYmd(ymd);
    if (!d) return null;
    d.setDate(d.getDate() + 1);
    return d.toISOString();
  };

  const getAppointmentsDateRangeFilters = (rangeType: 'today' | '3days' | '1week', startDate: Date) => {
    const { start, end } = getAppointmentsDateRange(rangeType, startDate);
    return {
      dateFrom: formatLocalDateYYYYMMDD(start),
      // Store as an inclusive viewer-local end date; conversion to exclusive boundary happens at request time.
      dateTo: formatLocalDateYYYYMMDD(end)
    };
  };

  const getProfessionalDateRangeFilters = (rangeType: 'today' | '3days' | '1week', startDate: Date) => {
    const { start, end } = getProfessionalDateRange(rangeType, startDate);
    return {
      dateFrom: formatLocalDateYYYYMMDD(start),
      dateTo: formatLocalDateYYYYMMDD(end)
    };
  };

  const formatAppointmentsDateRange = (rangeType: 'today' | '3days' | '1week', startDate: Date) => {
    const { start, end } = getAppointmentsDateRange(rangeType, startDate);

    if (rangeType === 'today') {
      return start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }

    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${startStr} – ${endStr}`;
  };

  const handleAppointmentsDateRangeChange = (rangeType: 'today' | '3days' | '1week') => {
    setAppointmentsDateRangeType(rangeType);
    setAppointmentsDateRangeStart(new Date());
  };

  const handleAppointmentsDateRangeNavigate = (direction: 'prev' | 'next') => {
    const newStart = new Date(appointmentsDateRangeStart);
    const days = appointmentsDateRangeType === 'today' ? 1 : appointmentsDateRangeType === '3days' ? 3 : 7;
    
    if (direction === 'prev') {
      newStart.setDate(newStart.getDate() - days);
    } else {
      newStart.setDate(newStart.getDate() + days);
    }
    
    setAppointmentsDateRangeStart(newStart);
  };

  // Mobile swipe should move 1 day at a time (even in 3-day view).
  const handleAppointmentsSwipeNavigate = (direction: 'prev' | 'next') => {
    const newStart = new Date(appointmentsDateRangeStart);
    newStart.setDate(newStart.getDate() + (direction === 'prev' ? -1 : 1));
    setAppointmentsDateRangeStart(newStart);
  };

  const handleAppointmentsDateSelect = (date: Date) => {
    setAppointmentsDateRangeStart(date);
    setIsAppointmentsDatePickerOpen(false);
  };

  // Generate calendar days for date picker
  const generateProfessionalCalendarDays = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  // Load professional appointments (for "My Appointments" section)
  const loadProfessionalAppointments = useCallback(async () => {
    if (!user?.id || !currentBusiness?.id) return;
    
    try {
      setProfessionalAppointmentsLoading(true);
      setProfessionalAppointmentsError(null);
      
      // For calendar view, load all appointments without pagination
      const isCalendarView = professionalAppointmentView === 'calendar';
      
      const rangeFilters = getProfessionalDateRangeFilters(professionalDateRangeType, professionalDateRangeStart);
      const filters: any = {
        businessId: currentBusiness.id,
        ...(rangeFilters.dateFrom ? { dateFrom: localYmdToUtcStartIso(rangeFilters.dateFrom) } : {}),
        ...(rangeFilters.dateTo ? { dateTo: localYmdToUtcEndExclusiveIso(rangeFilters.dateTo) } : {}),
        ...(isCalendarView
          ? { limit: APPOINTMENTS_CALENDAR_LIMIT }
          : { limit: APPOINTMENTS_PAGE_LIMIT }),
        // For grid and table views, sort by earliest (ascending)
        // Calendar view doesn't need explicit sort as it's handled differently
        ...(!isCalendarView ? { sort: 'asc' } : {}),
      };
      if (appointmentFilters.status) {
        filters.status = appointmentFilters.status;
      }

      const appointmentsData = await apiService.getProfessionalAppointments(filters);
      setProfessionalAppointments(appointmentsData.data);
    } catch (error) {
      console.error('Error loading professional appointments:', error);
      setProfessionalAppointmentsError('Failed to load your appointments. Please try again.');
      toast.error('Failed to load your appointments');
    } finally {
      setProfessionalAppointmentsLoading(false);
    }
  }, [user?.id, currentBusiness?.id, professionalDateRangeType, professionalDateRangeStart, appointmentFilters.status, professionalAppointmentView, APPOINTMENTS_CALENDAR_LIMIT, APPOINTMENTS_PAGE_LIMIT]);

  // Load my bookings (for "My Bookings" section - appointments where user is a customer)
  const loadMyBookings = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setMyBookingsLoading(true);
      setMyBookingsError(null);
      
      const bookingsData = await apiService.getCustomerAppointments();
      setMyBookings(bookingsData.data);
    } catch (error) {
      console.error('Error loading my bookings:', error);
      setMyBookingsError('Failed to load your bookings. Please try again.');
      toast.error('Failed to load your bookings');
    } finally {
      setMyBookingsLoading(false);
    }
  }, [user?.id]);

  // Load my gift cards (for "My Gift Cards" section - gift cards owned by the user)
  const loadMyGiftCards = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setMyGiftCardsLoading(true);
      setMyGiftCardsError(null);
      
      const giftCardsData = await apiService.getUserGiftCards();
      setMyGiftCards(giftCardsData);
    } catch (error) {
      console.error('Error loading my gift cards:', error);
      setMyGiftCardsError('Failed to load your gift cards. Please try again.');
      toast.error('Failed to load your gift cards');
    } finally {
      setMyGiftCardsLoading(false);
    }
  }, [user?.id]);

  // Load professional services (for "My Services" section)
  const loadProfessionalServices = useCallback(async () => {
    if (!currentBusiness?.id || !user?.id) return;
    
    try {
      setProfessionalServicesLoading(true);
      setProfessionalServicesError(null);
      
      const servicesData = await apiService.getProfessionalServices(currentBusiness.id, user.id);
      setProfessionalServices(servicesData);
    } catch (error) {
      console.error('Error loading professional services:', error);
      setProfessionalServicesError('Failed to load your services. Please try again.');
      toast.error('Failed to load your services');
    } finally {
      setProfessionalServicesLoading(false);
    }
  }, [currentBusiness?.id, user?.id]);

  // Load professional schedule (for "My Work Schedule" section)
  const loadProfessionalSchedule = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    try {
      setProfessionalScheduleLoading(true);
      setProfessionalScheduleError(null);
      
      const scheduleData = await apiService.getProfessionalSchedule(currentBusiness.id);
      setProfessionalSchedule(scheduleData);
    } catch (error) {
      console.error('Error loading professional schedule:', error);
      setProfessionalScheduleError('Failed to load your work schedule. Please try again.');
      toast.error('Failed to load your work schedule');
    } finally {
      setProfessionalScheduleLoading(false);
    }
  }, [currentBusiness?.id]);

  // Load professional customer reviews (for "My Customer Reviews" section)
  const loadProfessionalCustomerReviews = useCallback(async () => {
    if (!currentBusiness?.id || !user?.id) return;
    
    try {
      setProfessionalCustomerReviewsLoading(true);
      setProfessionalCustomerReviewsError(null);
      
      // Fetch all reviews (both public and private) for the professional
      // Not passing isPublic filter will return all reviews
      const reviewsResponse = await apiService.getProfessionalReviews(user.id);
      setProfessionalCustomerReviews(reviewsResponse.data);
    } catch (error) {
      console.error('Error loading professional customer reviews:', error);
      setProfessionalCustomerReviewsError('Failed to load your customer reviews. Please try again.');
      toast.error('Failed to load your customer reviews');
    } finally {
      setProfessionalCustomerReviewsLoading(false);
    }
  }, [currentBusiness?.id, user?.id]);

  // Handle adding a reply
  const handleAddReply = async (reviewId: string, parentReplyId?: string) => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    try {
      setIsSubmittingReply(true);
      const updatedReview = await apiService.addReviewReply(reviewId, replyMessage.trim(), parentReplyId);
      
      // Update the review in the list
      setProfessionalCustomerReviews(prev => 
        prev.map(review => review.id === reviewId ? updatedReview : review)
      );
      
      // Reset state
      setReplyMessage('');
      setReplyingToReview(null);
      setReplyingToReply(null);
      toast.success('Reply added successfully');
    } catch (error: any) {
      console.error('Error adding reply:', error);
      toast.error(error.response?.data?.error || 'Failed to add reply');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  // Handle updating a reply
  const handleUpdateReply = async (reviewId: string, replyId: string) => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    try {
      setIsSubmittingReply(true);
      const updatedReview = await apiService.updateReviewReply(reviewId, replyId, replyMessage.trim());
      
      // Update the review in the list
      setProfessionalCustomerReviews(prev => 
        prev.map(review => review.id === reviewId ? updatedReview : review)
      );
      
      // Reset state
      setReplyMessage('');
      setEditingReply(null);
      toast.success('Reply updated successfully');
    } catch (error: any) {
      console.error('Error updating reply:', error);
      toast.error(error.response?.data?.error || 'Failed to update reply');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  // Note: Testimonials are currently sourced from business_ratings, which do not support replies.

  const handleAddTestimonialReply = async (ratingId: string) => {
    if (!testimonialReplyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    try {
      setIsSubmittingTestimonialReply(true);
      const updatedRating = await apiService.addBusinessRatingReply(ratingId, testimonialReplyMessage.trim());

      setServiceReviews(prev => prev.map(r => (r.id === ratingId ? updatedRating : r)));

      setTestimonialReplyMessage('');
      setTestimonialReplyingToRating(null);
      toast.success('Reply added successfully');
    } catch (error: any) {
      console.error('Error adding testimonial reply:', error);
      toast.error(error.response?.data?.error || 'Failed to add reply');
    } finally {
      setIsSubmittingTestimonialReply(false);
    }
  };

  const handleUpdateTestimonialReply = async (ratingId: string, replyId: string) => {
    if (!testimonialReplyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    try {
      setIsSubmittingTestimonialReply(true);
      const updatedRating = await apiService.updateBusinessRatingReply(ratingId, replyId, testimonialReplyMessage.trim());
      setServiceReviews(prev => prev.map(r => (r.id === ratingId ? updatedRating : r)));

      setTestimonialReplyMessage('');
      setTestimonialEditingReply(null);
      toast.success('Reply updated successfully');
    } catch (error: any) {
      console.error('Error updating testimonial reply:', error);
      toast.error(error.response?.data?.error || 'Failed to update reply');
    } finally {
      setIsSubmittingTestimonialReply(false);
    }
  };

  const renderTestimonialReplies = (replies: ReviewReply[] | undefined, ratingId: string) => {
    if (!replies || replies.length === 0) return null;
    return (
      <div className="mt-3">
        {replies.map((reply) => (
          <div key={reply.id} className="mb-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {reply.userId !== user?.id && (
                      <>
                        <span className="text-sm font-semibold text-gray-900">{reply.userName}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#0B4778]/20 text-[#0B4778]">
                          Business Owner
                        </span>
                      </>
                    )}
                    <span className="text-xs text-gray-500">{new Date(reply.createdAt).toLocaleDateString()}</span>
                    {reply.updatedAt !== reply.createdAt && (
                      <span className="text-xs text-gray-400">(edited)</span>
                    )}
                  </div>

                  {testimonialEditingReply?.ratingId === ratingId && testimonialEditingReply?.replyId === reply.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={testimonialReplyMessage}
                        onChange={(e) => setTestimonialReplyMessage(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-sm"
                        rows={3}
                        placeholder="Edit your reply..."
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleUpdateTestimonialReply(ratingId, reply.id)}
                          disabled={isSubmittingTestimonialReply}
                          className="aacore-btn2-primary disabled:opacity-50"
                        >
                          <i className="bi bi-check"></i>
                          {isSubmittingTestimonialReply ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => {
                            setTestimonialEditingReply(null);
                            setTestimonialReplyMessage('');
                          }}
                          disabled={isSubmittingTestimonialReply}
                          className="aacore-btn2-outline disabled:opacity-50"
                        >
                          <i className="bi bi-x"></i>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-700">{reply.message}</p>
                  )}
                </div>

                {reply.userId === user?.id && !testimonialEditingReply && (
                  <button
                    onClick={() => {
                      setTestimonialEditingReply({ ratingId, replyId: reply.id });
                      setTestimonialReplyMessage(reply.message);
                    }}
                    className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
                  >
                    <i className="bi bi-pencil"></i> Edit
                  </button>
                )}
              </div>

              {/* Nested replies intentionally not supported. */}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render replies recursively
  const renderReplies = (replies: ReviewReply[], reviewId: string, depth: number = 0) => {
    if (!replies || replies.length === 0) return null;

    // Helper function to format date
    const formatReplyDate = (dateString: string) => {
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      } catch {
        return dateString;
      }
    };

    return (
      <div className={`mt-3 ${depth > 0 ? 'ml-6 pl-4 border-l-2 border-gray-200' : ''}`}>
        {replies.map((reply) => (
          <div key={reply.id} className="mb-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {reply.userId !== user?.id && (
                      <>
                        <span className="text-sm font-semibold text-gray-900">{reply.userName}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          reply.userRole === 'owner' || reply.userRole === 'professional' 
                            ? 'bg-[#0B4778]/20 text-[#0B4778]' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {reply.userRole === 'owner' ? 'Business Owner' : reply.userRole === 'professional' ? 'Professional' : 'Customer'}
                        </span>
                      </>
                    )}
                    <span className="text-xs text-gray-500">{formatReplyDate(reply.createdAt)}</span>
                    {reply.updatedAt !== reply.createdAt && (
                      <span className="text-xs text-gray-400">(edited)</span>
                    )}
                  </div>
                  {editingReply?.reviewId === reviewId && editingReply?.replyId === reply.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-sm"
                        rows={3}
                        placeholder="Edit your reply..."
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleUpdateReply(reviewId, reply.id)}
                          disabled={isSubmittingReply}
                          className="aacore-btn2-primary disabled:opacity-50"
                        >
                          <i className="bi bi-check"></i>
                          {isSubmittingReply ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => {
                            setEditingReply(null);
                            setReplyMessage('');
                          }}
                          disabled={isSubmittingReply}
                          className="aacore-btn2-outline disabled:opacity-50"
                        >
                          <i className="bi bi-x"></i>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-700">{reply.message}</p>
                  )}
                </div>
                {reply.userId === user?.id && !editingReply && (
                  <button
                    onClick={() => {
                      setEditingReply({ reviewId, replyId: reply.id });
                      setReplyMessage(reply.message);
                    }}
                    className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
                  >
                    <i className="bi bi-pencil"></i> Edit
                  </button>
                )}
              </div>
              
              {/* Nested replies are intentionally not supported (one-level replies only). */}
            </div>
          </div>
        ))}
      </div>
    );
  };



  // Appointment Action Functions
  const handleUpdateAppointmentStatus = async (appointmentId: string, newStatus: string) => {
    try {
      await apiService.updateAppointment(appointmentId, { status: newStatus } as any);
      toast.success(`Appointment ${newStatus.toLowerCase()} successfully`);
      loadAppointments(appointmentPagination.currentPage); // Refresh the appointments list
    } catch (error) {
      console.error('Error updating appointment status:', error);
      toast.error('Failed to update appointment status');
    }
  };

  const handleCancelAppointment = async (appointment: Appointment) => {
    // Open cancellation dialog
    setCancellationAppointment(appointment);
    setIsCancellationDialogOpen(true);
  };

  const handleConfirmCancellation = async (cancellationData: { noShow: boolean; cancellationNotes: string; sendCancellationEmail?: boolean; cancelScope?: 'THIS' | 'REMAINING' }) => {
    if (!cancellationAppointment) return;

    try {
      await apiService.cancelAppointment(cancellationAppointment.id, cancellationData);
      toast.success('Appointment cancelled successfully');
      loadAppointments(appointmentPagination.currentPage); // Refresh the appointments list
      setIsCancellationDialogOpen(false);
      setCancellationAppointment(null);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Failed to cancel appointment');
    }
  };

  const handleConfirmAppointment = async (appointmentId: string) => {
    await handleUpdateAppointmentStatus(appointmentId, 'CONFIRMED');
  };

  // Fetch assignment data (capabilities, schedules, and existing appointments)
  const fetchAssignmentData = async (professionals: User[], businessId: string, appointment: Appointment) => {
    if (professionals.length === 0 || !businessId) return;

    setLoadingAssignmentData(true);
    try {
      // Fetch capabilities for all professionals
      const capabilityPromises = professionals.map(async (professional) => {
        try {
          const capabilities = await apiService.getProfessionalServices(businessId, professional.id);
          return { professionalId: professional.id, capabilities };
        } catch (error) {
          console.error(`Error fetching capabilities for ${professional.id}:`, error);
          return { professionalId: professional.id, capabilities: [] };
        }
      });

      // Fetch schedules for all professionals
      const schedulePromises = professionals.map(async (professional) => {
        try {
          const schedules = await apiService.getProfessionalAvailability(businessId, professional.id);
          return { professionalId: professional.id, schedules };
        } catch (error) {
          console.error(`Error fetching schedule for ${professional.id}:`, error);
          return { professionalId: professional.id, schedules: [] };
        }
      });

      // Fetch existing appointments for all professionals for the appointment date
      const appointmentStartIso = appointment ? getAppointmentStartIso(appointment) : null;
      const appointmentDate = appointmentStartIso ? new Date(appointmentStartIso) : null;
      let appointmentDateStr: string | null = null;
      let appointmentDateEndStr: string | null = null;
      
      if (appointmentDate) {
        const year = appointmentDate.getFullYear();
        const month = String(appointmentDate.getMonth() + 1).padStart(2, '0');
        const day = String(appointmentDate.getDate()).padStart(2, '0');
        appointmentDateStr = `${year}-${month}-${day}T00:00:00`;
        appointmentDateEndStr = `${year}-${month}-${day}T23:59:59`;
      }
      
      const appointmentPromises = professionals.map(async (professional) => {
        if (!appointmentDateStr || !appointmentDateEndStr) return { professionalId: professional.id, appointments: [] };
        try {
          const result = await apiService.getAppointments({
            businessId,
            professionalId: professional.id,
            startDate: appointmentDateStr,
            endDate: appointmentDateEndStr,
            status: 'upcoming' // This will include both PENDING and CONFIRMED
          });
          return { professionalId: professional.id, appointments: result.data || [] };
        } catch (error) {
          console.error(`Error fetching appointments for ${professional.id}:`, error);
          return { professionalId: professional.id, appointments: [] };
        }
      });

      const [capabilityResults, scheduleResults, appointmentResults] = await Promise.all([
        Promise.all(capabilityPromises),
        Promise.all(schedulePromises),
        Promise.all(appointmentPromises)
      ]);

      const capabilitiesMap: {[professionalId: string]: any[]} = {};
      capabilityResults.forEach(result => {
        capabilitiesMap[result.professionalId] = result.capabilities;
      });

      const schedulesMap: {[professionalId: string]: any[]} = {};
      scheduleResults.forEach(result => {
        schedulesMap[result.professionalId] = result.schedules;
      });

      const appointmentsMap: {[professionalId: string]: Appointment[]} = {};
      appointmentResults.forEach(result => {
        appointmentsMap[result.professionalId] = result.appointments;
      });

      setAssignmentCapabilities(capabilitiesMap);
      setAssignmentSchedules(schedulesMap);
      setAssignmentExistingAppointments(appointmentsMap);
    } catch (error) {
      console.error('Error fetching assignment data:', error);
    } finally {
      setLoadingAssignmentData(false);
    }
  };

  // Check if professional has capability for a service
  const hasServiceCapability = (professionalId: string, serviceId: string): boolean => {
    const capabilities = assignmentCapabilities[professionalId] || [];
    if (capabilities.length === 0) return false;
    return capabilities.some((capability: any) => {
      const capId = capability.id || capability.serviceId || capability.service_id;
      return String(capId) === String(serviceId);
    });
  };

  // Check if professional has existing appointment conflict
  const hasExistingAppointmentConflict = (professionalId: string, scheduledAt: string, serviceDuration: number): boolean => {
    if (!assignmentAppointment) return false;
    
    const existingAppointments = assignmentExistingAppointments[professionalId] || [];
    const appointmentDateTime = new Date(scheduledAt);
    const appointmentEndTime = new Date(appointmentDateTime.getTime() + serviceDuration * 60 * 1000);
    
    // Exclude the current appointment being edited
    const otherAppointments = existingAppointments.filter(apt => apt.id !== assignmentAppointment.id);

    for (const existingApt of otherAppointments) {
      const existingStartIso = getAppointmentStartIso(existingApt);
      if (!existingStartIso) continue;
      const existingStart = new Date(existingStartIso);
      // Get duration from assignments
      const existingDuration = existingApt.assignments?.reduce((total: number, assignment: any) => {
        return total + (assignment.durationMinutes || 0);
      }, 0) || 0;
      const existingEnd = new Date(existingStart.getTime() + existingDuration * 60 * 1000);
      
      // Check for overlap
      const overlaps = appointmentDateTime < existingEnd && appointmentEndTime > existingStart;
      
      if (overlaps) {
        return true;
      }
    }
    
    return false;
  };

  // Check if professional has schedule conflict
  const hasScheduleConflict = (professionalId: string, scheduledAt: string, serviceDuration: number): boolean => {
    const schedules = assignmentSchedules[professionalId] || [];

    if (schedules.length === 0) {
      return true; // No schedule means conflict
    }

    const appointmentDate = new Date(scheduledAt);
    const dayOfWeek = appointmentDate.getDay(); // 0 = Sunday, 6 = Saturday
    const appointmentTime = appointmentDate.toTimeString().slice(0, 5); // HH:mm format

    const daySchedule = schedules.find((schedule: any) => schedule.dayOfWeek === dayOfWeek);

    if (!daySchedule || !daySchedule.isAvailable) {
      return true; // No schedule or not available
    }

    // Check if appointment time is within schedule
    // Normalize times to 24-hour format (HH:mm) for comparison
    const normalizeTime = (timeStr: string): string => {
      if (!timeStr) return '00:00';
      // If already in HH:mm format (24-hour), return as is
      if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr;
      
      // Parse 12-hour format (e.g., "08:00 AM" or "8:00 PM")
      const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (match) {
        let hours = parseInt(match[1], 10);
        const minutes = match[2];
        const period = match[3].toUpperCase();
        
        if (period === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period === 'AM' && hours === 12) {
          hours = 0;
        }
        
        return `${hours.toString().padStart(2, '0')}:${minutes}`;
      }
      
      return timeStr; // Fallback to original if parsing fails
    };

    const startTime = normalizeTime(daySchedule.startTime || '00:00');
    const endTime = normalizeTime(daySchedule.endTime || '23:59');
    const normalizedAppointmentTime = normalizeTime(appointmentTime);

    const isBeforeStart = normalizedAppointmentTime < startTime;
    const isAfterEnd = normalizedAppointmentTime >= endTime;
    const hasConflict = isBeforeStart || isAfterEnd;

    const hasExistingConflict = hasExistingAppointmentConflict(professionalId, scheduledAt, serviceDuration);
    if (hasExistingConflict) {
      return true;
    }

    return hasConflict;
  };

  // Handle manual assignment
  const handleSaveAssignment = async () => {
    if (!assignmentAppointment || !currentBusiness?.id) return;

    setIsSavingAssignment(true);
    try {
      // Update assignments with selected professionals
      const updatedAssignments = assignmentAppointment.assignments.map((assignment: any) => ({
        id: assignment.id,
        serviceId: assignment.serviceId,
        professionalId: assignmentSelections[assignment.id] || assignment.professionalId || undefined,
        durationMinutes: assignment.durationMinutes,
        price: assignment.price,
        notes: assignment.notes
      }));

      await apiService.updateAppointment(assignmentAppointment.id, {
        assignments: updatedAssignments
      } as any);

      toast.success('Professional assignments updated successfully');
      setIsAssignmentModalOpen(false);
      setAssignmentAppointment(null);
      setAssignmentSelections({});
      setAssignmentCapabilities({});
      setAssignmentSchedules({});
      setUnassignedAssignmentsRefreshKey((k) => k + 1);
      loadAppointments(appointmentPagination.currentPage); // Refresh the appointments list
    } catch (error) {
      console.error('Error saving assignments:', error);
      toast.error('Failed to save assignments');
    } finally {
      setIsSavingAssignment(false);
    }
  };

  const [confirmConfirmationDialog, setConfirmConfirmationDialog] = useState<{
    isOpen: boolean;
    appointmentId: string | null;
  } | null>(null);

  const handleConfirmAppointmentWithConfirmation = (appointmentId: string) => {
    setConfirmConfirmationDialog({
      isOpen: true,
      appointmentId
    });
  };

  const handleConfirmConfirm = async () => {
    if (!confirmConfirmationDialog?.appointmentId) return;
    
    setConfirmConfirmationDialog(null);
    await handleConfirmAppointment(confirmConfirmationDialog.appointmentId);
  };

  const [completeConfirmationDialog, setCompleteConfirmationDialog] = useState<{
    isOpen: boolean;
    appointmentId: string | null;
  } | null>(null);

  const handleCompleteAppointment = async (appointmentId: string) => {
    await handleUpdateAppointmentStatus(appointmentId, 'COMPLETED');
  };

  const handleCompleteAppointmentWithConfirmation = (appointmentId: string) => {
    setCompleteConfirmationDialog({
      isOpen: true,
      appointmentId
    });
  };

  const handleConfirmComplete = async () => {
    if (!completeConfirmationDialog?.appointmentId) return;
    
    setCompleteConfirmationDialog(null);
    await handleCompleteAppointment(completeConfirmationDialog.appointmentId);
  };

  // Load categories on component mount and when business changes
  useEffect(() => {
    loadCategories();
  }, [currentBusiness?.id]);

  // Settings functions
  const loadSettings = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    try {
      const businessSettings = await apiService.getBusinessSettings(currentBusiness.id);
      setSettings({
        autoConfirmAppointments: businessSettings.autoConfirmAppointments ?? false,
        emailAppointmentActivitiesToOwner: businessSettings.emailAppointmentActivitiesToOwner ?? false,
        customerAppointmentMessageEnabled: businessSettings.customerAppointmentMessageEnabled ?? false,
        customerAppointmentMessage: businessSettings.customerAppointmentMessage ?? '',
        showProfessionalNameOnAssignment: businessSettings.showProfessionalNameOnAssignment ?? false
      });
    } catch (error) {
      console.error('Error loading business settings:', error);
      // If settings don't exist yet, use defaults
      setSettings({
        autoConfirmAppointments: false,
        emailAppointmentActivitiesToOwner: false,
        customerAppointmentMessageEnabled: false,
        customerAppointmentMessage: '',
        showProfessionalNameOnAssignment: false
      });
    }
  }, [currentBusiness?.id]);

  // Load services when business changes
  useEffect(() => {
    if (currentBusiness?.id) {
      loadServices();
      loadSettings();
    }
  }, [currentBusiness?.id, loadServices, loadSettings]);



  // Update active section when URL parameters change (for browser back/forward)
  useEffect(() => {
    const sectionFromUrl = searchParams.get('section') as DashboardSection;
    if (
      sectionFromUrl &&
      [
        'services',
        'roles',
        'professionals',
        'appointments',
        'closures',
        'my-appointments',
        'my-bookings',
        'my-gift-cards',
        'my-services',
        'my-work-schedule',
        'my-customer-reviews',
        'testimonials',
        'giftcards',
        'site-config',
        'settings',
        'branding',
        'notifications',
        'customers'
      ].includes(sectionFromUrl)
    ) {
      setActiveSection(sectionFromUrl);
    }
  }, [searchParams]);

  // When true, opening Appointments from sidebar link: use defaults (calendar + today + Confirmed + All Professional) and skip localStorage restore
  const useAppointmentsSidebarDefaultsRef = useRef(false);
  // Restore filter state from URL params when navigating back from create-appointment
  // Use a ref to track if we've already restored to avoid multiple restorations
  const hasRestoredFiltersRef = useRef<string>('');
  const hasRestoredMyFiltersRef = useRef<string>('');
  useEffect(() => {
    if (activeSection === 'appointments') {
      const dateRangeType = searchParams.get('dateRangeType');
      const dateRangeStart = searchParams.get('dateRangeStart');
      const status = searchParams.get('status');
      const professionalId = searchParams.get('professionalId');
      const view = searchParams.get('view');

      // Create a unique key from the params to check if we've already restored this state
      const paramsKey = `${dateRangeType || ''}_${dateRangeStart || ''}_${status || ''}_${professionalId || ''}_${view || ''}`;
      
      // Only restore if params exist and we haven't restored this exact state yet
      const hasParams = dateRangeType || dateRangeStart || status !== null || professionalId !== null || view;
      if (hasParams && hasRestoredFiltersRef.current !== paramsKey) {
        // Restore date range type if present
        if (dateRangeType && ['today', '3days', '1week'].includes(dateRangeType)) {
          setAppointmentsDateRangeType(dateRangeType as 'today' | '3days' | '1week');
        }

        // Restore date range start if present (use localDateFromYmd to handle timezone correctly)
        if (dateRangeStart) {
          const parsedDate = localDateFromYmd(dateRangeStart);
          if (parsedDate) {
            setAppointmentsDateRangeStart(parsedDate);
            setAppointmentsDatePickerMonth(parsedDate);
          }
        }

        // Restore status filter if present
        if (status !== null) {
          setAppointmentFilters(prev => ({ ...prev, status: status || '' }));
        }

        // Restore professional filter if present
        if (professionalId !== null) {
          setAppointmentFilters(prev => ({ ...prev, professionalId: professionalId || '' }));
        }

        // Restore view mode if present
        if (view && ['grid', 'table', 'calendar'].includes(view)) {
          setAppointmentView(view as 'grid' | 'table' | 'calendar');
        } else if (!view) {
          // If no view param, default to calendar view on first load
          setAppointmentView('calendar');
        }

        // Mark as restored
        hasRestoredFiltersRef.current = paramsKey;

        // Clean up URL params after restoring state
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('dateRangeType');
        newParams.delete('dateRangeStart');
        newParams.delete('status');
        newParams.delete('professionalId');
        newParams.delete('view');
        // Only update if params actually changed to avoid infinite loop
        if (newParams.toString() !== searchParams.toString()) {
          setSearchParams(newParams, { replace: true });
        }
      } else if (!hasParams) {
        // Reset the ref when there are no params to restore
        hasRestoredFiltersRef.current = '';
      }
    } else if (activeSection === 'my-appointments') {
      const myDateRangeType = searchParams.get('myDateRangeType');
      const myDateRangeStart = searchParams.get('myDateRangeStart');
      const myStatus = searchParams.get('myStatus');
      const myView = searchParams.get('myView');

      // Create a unique key from the params to check if we've already restored this state
      const paramsKey = `${myDateRangeType || ''}_${myDateRangeStart || ''}_${myStatus || ''}_${myView || ''}`;
      
      // Only restore if params exist and we haven't restored this exact state yet
      const hasParams = myDateRangeType || myDateRangeStart || myStatus !== null || myView;
      if (hasParams && hasRestoredMyFiltersRef.current !== paramsKey) {
        // Restore date range type if present
        if (myDateRangeType && ['today', '3days', '1week'].includes(myDateRangeType)) {
          setProfessionalDateRangeType(myDateRangeType as 'today' | '3days' | '1week');
        }

        // Restore date range start if present (use localDateFromYmd to handle timezone correctly)
        if (myDateRangeStart) {
          const parsedDate = localDateFromYmd(myDateRangeStart);
          if (parsedDate) {
            setProfessionalDateRangeStart(parsedDate);
            setProfessionalDatePickerMonth(parsedDate);
          }
        }

        // Restore status filter if present
        if (myStatus !== null) {
          setAppointmentFilters(prev => ({ ...prev, status: myStatus || '' }));
        }

        // Restore view mode if present
        if (myView && ['grid', 'table', 'calendar'].includes(myView)) {
          setProfessionalAppointmentView(myView as 'grid' | 'table' | 'calendar');
        }

        // Mark as restored
        hasRestoredMyFiltersRef.current = paramsKey;

        // Clean up URL params after restoring state
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('myDateRangeType');
        newParams.delete('myDateRangeStart');
        newParams.delete('myStatus');
        newParams.delete('myView');
        // Only update if params actually changed to avoid infinite loop
        if (newParams.toString() !== searchParams.toString()) {
          setSearchParams(newParams, { replace: true });
        }
      } else if (!hasParams) {
        // Reset the ref when there are no params to restore
        hasRestoredMyFiltersRef.current = '';
      }
    }
  }, [activeSection, searchParams, setSearchParams]);

  const applyAppointmentsSidebarDefaults = () => {
    // When clicking "Appointments" in sidebar, always reset to: calendar view + today + Confirmed + All Professional
    useAppointmentsSidebarDefaultsRef.current = true;
    const today = new Date();
    setAppointmentView('calendar');
    setAppointmentsDateRangeType('today');
    setAppointmentsDateRangeStart(today);
    setAppointmentsDatePickerMonth(today);
    setAppointmentFilters(prev => ({
      ...prev,
      status: 'CONFIRMED',
      professionalId: ''
    }));
  };

  const handleSectionChange = (section: DashboardSection) => {
    if (section === 'appointments') {
      applyAppointmentsSidebarDefaults();
    }
    if (section === 'services') {
      useServicesSidebarDefaultsRef.current = true;
      setFilters({ isActive: true, search: '', category_id: undefined });
      setViewMode('grid');
    }
    setActiveSection(section);
    // Update URL to reflect the current section (no filter params so sidebar click = fresh defaults)
    setSearchParams({ section });
    // Close sidebar on mobile after selection
    setIsSidebarOpen(false);
  };

  // When landing on `/dashboard/business` without an explicit section param (common after login),
  // behave like a sidebar click so old filter state isn't resurrected implicitly.
  const hasAppliedEntryDefaultsRef = useRef(false);
  useEffect(() => {
    if (hasAppliedEntryDefaultsRef.current) return;

    const sectionFromUrl = searchParams.get('section');
    if (sectionFromUrl) {
      hasAppliedEntryDefaultsRef.current = true;
      return;
    }

    if (activeSection === 'appointments') {
      hasAppliedEntryDefaultsRef.current = true;
      applyAppointmentsSidebarDefaults();
      setSearchParams({ section: 'appointments' }, { replace: true });
      return;
    }

    if (activeSection === 'my-appointments') {
      hasAppliedEntryDefaultsRef.current = true;
      setSearchParams({ section: 'my-appointments' }, { replace: true });
    }
  }, [activeSection, searchParams, setSearchParams]);

  const openSiteConfigTab = (tab: SiteConfigTab) => {
    // Always navigate to Site Configuration first, then select the appropriate tab.
    setActiveSection('site-config');
    setSearchParams({ section: 'site-config' });
    setSiteConfigActiveTab(tab);
    setIsSidebarOpen(false);
  };

  // Helper function to build create-appointment URL with preserved filter state
  const buildCreateAppointmentUrl = (additionalParams?: { date?: string; time?: string; from?: string; prefillProfessionalId?: string }): string => {
    const params = new URLSearchParams();
    
    // Add additional params (date, time, from, prefillProfessionalId)
    if (additionalParams?.date) {
      params.set('date', additionalParams.date);
    }
    if (additionalParams?.time) {
      params.set('time', additionalParams.time);
    }
    if (additionalParams?.from) {
      params.set('from', additionalParams.from);
    }
    if (additionalParams?.prefillProfessionalId && additionalParams.prefillProfessionalId !== '__unassigned__') {
      params.set('prefillProfessionalId', additionalParams.prefillProfessionalId);
    }
    
    // Preserve current filter state when navigating to create-appointment
    if (activeSection === 'appointments') {
      params.set('dateRangeType', appointmentsDateRangeType);
      params.set('dateRangeStart', formatLocalYmd(appointmentsDateRangeStart));
      if (appointmentFilters.status) {
        params.set('status', appointmentFilters.status);
      }
      if (appointmentFilters.professionalId) {
        params.set('professionalId', appointmentFilters.professionalId);
      }
      params.set('view', appointmentView);
    } else if (activeSection === 'my-appointments') {
      // Preserve my-appointments filter state with different param names to avoid conflicts
      params.set('myDateRangeType', professionalDateRangeType);
      params.set('myDateRangeStart', formatLocalYmd(professionalDateRangeStart));
      if (appointmentFilters.status) {
        params.set('myStatus', appointmentFilters.status);
      }
      params.set('myView', professionalAppointmentView);
    }
    
    const queryString = params.toString();
    return `/dashboard/business/create-appointment${queryString ? `?${queryString}` : ''}`;
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Services Management Functions
  const buildServicesUrl = (basePath: string, extraParams?: Record<string, string>) => {
    const params = new URLSearchParams();

    if (filters.search?.trim()) {
      params.set(SERVICES_SEARCH_PARAM, filters.search);
    }
    if (filters.category_id) {
      params.set(SERVICES_CATEGORY_PARAM, filters.category_id);
    }

    params.set(
      SERVICES_STATUS_PARAM,
      filters.isActive === true ? 'active' : filters.isActive === false ? 'inactive' : 'all'
    );
    params.set(SERVICES_VIEW_PARAM, viewMode);

    Object.entries(extraParams || {}).forEach(([key, value]) => {
      params.set(key, value);
    });

    const queryString = params.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  };

  const handleAddService = () => {
    localStorage.removeItem('addServiceFormData');
    navigate(buildServicesUrl('/services/add'));
  };

  const handleEditService = (service: Service) => {
    navigate(buildServicesUrl(`/services/edit/${service.id}`));
  };

  const useServicesSidebarDefaultsRef = useRef(false);
  const hasRestoredServicesParamsRef = useRef<string>('');
  useEffect(() => {
    if (activeSection !== 'services') {
      hasRestoredServicesParamsRef.current = '';
      return;
    }

    const servicesSearch = searchParams.get(SERVICES_SEARCH_PARAM);
    const servicesCategoryId = searchParams.get(SERVICES_CATEGORY_PARAM);
    const servicesStatus = searchParams.get(SERVICES_STATUS_PARAM);
    const servicesView = searchParams.get(SERVICES_VIEW_PARAM);

    const paramsKey = `${servicesSearch || ''}_${servicesCategoryId || ''}_${servicesStatus || ''}_${servicesView || ''}`;
    const hasParams =
      servicesSearch !== null ||
      servicesCategoryId !== null ||
      servicesStatus !== null ||
      servicesView !== null;

    if (hasParams && hasRestoredServicesParamsRef.current !== paramsKey) {
      setFilters(prev => ({
        ...prev,
        ...(servicesSearch !== null ? { search: servicesSearch || '' } : {}),
        ...(servicesCategoryId !== null ? { category_id: servicesCategoryId || undefined } : {}),
        ...(servicesStatus !== null
          ? {
              isActive:
                servicesStatus === 'active'
                  ? true
                  : servicesStatus === 'inactive'
                    ? false
                    : undefined
            }
          : {})
      }));

      if (servicesView && ['grid', 'list'].includes(servicesView)) {
        setViewMode(servicesView as 'grid' | 'list');
      }

      hasRestoredServicesParamsRef.current = paramsKey;

      const newParams = new URLSearchParams(searchParams);
      newParams.delete(SERVICES_SEARCH_PARAM);
      newParams.delete(SERVICES_CATEGORY_PARAM);
      newParams.delete(SERVICES_STATUS_PARAM);
      newParams.delete(SERVICES_VIEW_PARAM);
      if (newParams.toString() !== searchParams.toString()) {
        setSearchParams(newParams, { replace: true });
      }
    } else if (!hasParams) {
      hasRestoredServicesParamsRef.current = '';
    }
  }, [activeSection, searchParams, setSearchParams]);

  // Restore services filters from localStorage when entering the Services section
  const hasRestoredServicesFiltersRef = useRef(false);
  useEffect(() => {
    if (activeSection === 'services' && !hasRestoredServicesFiltersRef.current) {
      if (useServicesSidebarDefaultsRef.current) {
        useServicesSidebarDefaultsRef.current = false;
        hasRestoredServicesFiltersRef.current = true;
        return;
      }

      const stored = loadStoredServicesFilters();
      if (stored) {
        setFilters(prev => ({
          ...prev,
          search: stored.search,
          category_id: stored.category_id,
          isActive: stored.status === 'active' ? true : stored.status === 'inactive' ? false : undefined,
          addOnsOnly: stored.addOnsOnly ?? false,
        }));
        setViewMode(stored.viewMode);
      }
      // Mark as restored even if no stored data, so future changes can be saved
      hasRestoredServicesFiltersRef.current = true;
    } else if (activeSection !== 'services') {
      hasRestoredServicesFiltersRef.current = false;
    }
  }, [activeSection]);

  // Save services filters to localStorage whenever they change
  useEffect(() => {
    if (activeSection === 'services' && hasRestoredServicesFiltersRef.current) {
      saveServicesFilters({
        search: filters.search || '',
        category_id: filters.category_id,
        status: filters.isActive === true ? 'active' : filters.isActive === false ? 'inactive' : 'all',
        viewMode,
        addOnsOnly: Boolean(filters.addOnsOnly),
      });
    }
  }, [activeSection, filters.search, filters.category_id, filters.isActive, filters.addOnsOnly, viewMode]);

  // Guard against stale/invalid category filters (e.g., restored from a different business or deleted category).
  // Validate against the business category list rather than loaded services so a valid
  // category isn't cleared before services finish loading.
  useEffect(() => {
    if (activeSection !== 'services') return;
    if (categoriesLoading) return;
    const selectedCategoryId = filters.category_id;
    if (!selectedCategoryId) return;

    if (categories.length === 0) return;

    const existsInCategories = categories.some((category: any) => category?.id === selectedCategoryId);

    if (!existsInCategories) {
      setFilters(prev => (prev.category_id === selectedCategoryId ? { ...prev, category_id: undefined } : prev));
    }
  }, [activeSection, filters.category_id, categories, categoriesLoading]);

  // Refresh services when returning from edit page
  useEffect(() => {
    const handleFocus = () => {
      if (activeSection === 'services' && currentBusiness?.id) {
        loadServices();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [activeSection, currentBusiness?.id, loadServices]);

  const handleSaveService = async (serviceData: ServiceFormData) => {
    if (!currentBusiness?.id) return;
    
    try {
      const newService = await apiService.createService({
        businessId: currentBusiness.id,
        name: serviceData.name,
        description: serviceData.description,
        durationMinutes: serviceData.duration,
        price: serviceData.price,
        quotable: serviceData.quotable,
        quoteRequestInstructions: serviceData.quoteRequestInstructions,
        categoryId: serviceData.category_id,
        icon: serviceData.icon,
        isAddOn: serviceData.isAddOn || false,
        serviceLocation: serviceData.serviceLocation
      });
      setServices(prev => [...prev, newService]);
      toast.success('Service created successfully');
    } catch (error) {
      console.error('Error creating service:', error);
      toast.error('Failed to create service');
    }
  };

  const handleUpdateService = async (serviceId: string, serviceData: ServiceFormData) => {
    try {
      const updatedService = await apiService.updateService(serviceId, {
        name: serviceData.name,
        description: serviceData.description,
        durationMinutes: serviceData.duration,
        price: serviceData.price,
        quotable: serviceData.quotable,
        quoteRequestInstructions: serviceData.quoteRequestInstructions,
        categoryId: serviceData.category_id,
        icon: serviceData.icon,
        isAddOn: serviceData.isAddOn || false,
        serviceLocation: serviceData.serviceLocation
      });
      setServices(prev => prev.map(service => 
        service.id === serviceId ? updatedService : service
      ));
      toast.success('Service updated successfully');
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error('Failed to update service');
    }
  };

  const handleToggleServiceActive = (service: Service) => {
    setServiceToDeactivate(service);
    setShowDeactivateServiceModal(true);
  };

  const handleDuplicateService = (service: Service) => {
    navigate(buildServicesUrl(`/services/duplicate/${service.id}`));
  };

  const handleDeactivateServiceConfirm = async () => {
    if (!serviceToDeactivate) return;

    try {
      const updatedService = await apiService.toggleServiceActive(serviceToDeactivate.id);
      
      // Transform the API response to match our Service interface
      const transformedService: Service = {
        id: updatedService.id,
        businessId: updatedService.business_id,
        name: updatedService.name,
        description: updatedService.description,
        durationMinutes: updatedService.duration_minutes,
        price: updatedService.price,
        isActive: updatedService.is_active,
        category: updatedService.category_name ? {
          id: updatedService.category_id,
          name: updatedService.category_name
        } : undefined,
        createdAt: updatedService.created_at,
        updatedAt: updatedService.updated_at
      };
      
      setServices(prev => prev.map(service =>
        service.id === serviceToDeactivate.id ? transformedService : service
      ));
      toast.success(`Service ${transformedService.isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling service active status:', error);
      toast.error('Failed to update service status');
    } finally {
      setShowDeactivateServiceModal(false);
      setServiceToDeactivate(null);
    }
  };

  const handleDeactivateServiceCancel = () => {
    setShowDeactivateServiceModal(false);
    setServiceToDeactivate(null);
  };

  const handleDeleteService = (service: Service) => {
    setServiceToDelete(service);
    setShowDeleteServiceModal(true);
  };

  const handleDeleteServiceConfirm = async () => {
    if (!serviceToDelete) return;

    try {
      await apiService.softDeleteService(serviceToDelete.id);
      setServices(prev => prev.map(service =>
        service.id === serviceToDelete.id ? ({ ...service, deleted: true } as any) : service
      ));
      toast.success('Service deleted successfully');
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    } finally {
      setShowDeleteServiceModal(false);
      setServiceToDelete(null);
    }
  };

  const handleDeleteServiceCancel = () => {
    setShowDeleteServiceModal(false);
    setServiceToDelete(null);
  };

  // Load roles from database
  const loadRoles = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    setRolesLoading(true);
    try {
      const rolesData = await apiService.getBusinessRoles(currentBusiness.id);
      // Transform business roles to match UserRole interface for display
      const transformedRoles = rolesData.map((role: any) => ({
        id: role.id,
        userId: '', // Not applicable for business roles
        businessId: role.business_id,
        role: role.name,
        roleDescription: role.description,
        permissions: role.permissions?.map((p: any) => p.name) || [],
        isActive: role.is_active,
        createdAt: role.created_at,
        updatedAt: role.updated_at
      }));
      setRoles(transformedRoles);
    } catch (error) {
      console.error('Error loading roles:', error);
    } finally {
      setRolesLoading(false);
    }
  }, [currentBusiness?.id]);

  // Roles Management Functions
  const handleAddRole = () => {
    setEditingRole(null);
    setIsRoleModalOpen(true);
  };

  const handleEditRole = (role: UserRole) => {
    setEditingRole(role);
    setIsRoleModalOpen(true);
  };

  const handleSaveRole = async (roleData: RoleFormData) => {
    if (!currentBusiness?.id) {
      toast.error('No business selected');
      return;
    }

    try {
      if (editingRole) {
        // Update existing role
        const updatedRole = await apiService.updateRole(currentBusiness.id, editingRole.id, {
          name: roleData.name,
          description: roleData.description,
          permissions: roleData.permissions
        });
        
        toast.success('Role updated successfully!');
      } else {
        // Create new role
        const newRole = await apiService.createRole(currentBusiness.id, {
          name: roleData.name,
          description: roleData.description,
          permissions: roleData.permissions
        });
        
        toast.success('Role created successfully!');
      }
      
      // Close the modal
      setIsRoleModalOpen(false);
      setEditingRole(null);
      
      // Refresh the roles list to ensure consistency
      await loadRoles();
      
    } catch (error: any) {
      console.error('Error saving role:', error);
      toast.error(error.response?.data?.message || 'Failed to save role. Please try again.');
    }
  };

  const handleToggleRoleActive = (roleId: string, isActive: boolean) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;
    
    // Show confirmation dialog for both activate and deactivate
    setRoleToDeactivate({ role, isActive });
    setShowDeactivateRoleModal(true);
  };

  const handleToggleRoleActiveConfirm = async (roleId: string, isActive: boolean) => {
    if (!currentBusiness?.id) return;
    
    // Check role protection before proceeding
    const role = roles.find(r => r.id === roleId);
    if (role && role.role === 'Business Owner') {
      // Find the current user's role to check if they're trying to modify their own role
      const currentUserRole = user?.roles?.find(ur => 
        ur.businessId === currentBusiness.id && 
        ur.permissions?.some(permission => 
          ['manage_settings', 'manage_roles', 'manage_professionals'].includes(permission)
        )
      );
      
      if (currentUserRole && !canPerformAction('deactivate', currentUserRole.id)) {
        toast.error('Cannot deactivate Business Owner role due to protection settings');
        setShowDeactivateRoleModal(false);
        setRoleToDeactivate(null);
        return;
      }
    }
    
    try {
      const updatedRole = await apiService.toggleBusinessRoleActive(currentBusiness.id, roleId);
      // Transform the response to match UserRole interface
      const transformedRole = {
        id: updatedRole.id,
        userId: '', // Not applicable for business roles
        businessId: updatedRole.business_id,
        role: updatedRole.name,
        roleDescription: updatedRole.description,
        permissions: updatedRole.permissions?.map((p: any) => p.name) || [],
        isActive: updatedRole.is_active,
        createdAt: updatedRole.created_at,
        updatedAt: updatedRole.updated_at
      };
      setRoles(prev => prev.map(role =>
        role.id === roleId ? transformedRole : role
      ));
      toast.success(`Role ${updatedRole.is_active ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling role active status:', error);
      toast.error('Failed to update role status. Please try again.');
    } finally {
      setShowDeactivateRoleModal(false);
      setRoleToDeactivate(null);
    }
  };

  const handleDeactivateRoleCancel = () => {
    setShowDeactivateRoleModal(false);
    setRoleToDeactivate(null);
  };

  const handleDeleteRole = (roleId: string) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;
    
    // Check role protection before proceeding
    if (role && role.role === 'Business Owner') {
      // Find the current user's role to check if they're trying to modify their own role
      const currentUserRole = user?.roles?.find(ur => 
        ur.businessId === currentBusiness?.id && 
        ur.permissions?.some(permission => 
          ['manage_settings', 'manage_roles', 'manage_professionals'].includes(permission)
        )
      );
      
      if (currentUserRole && !canPerformAction('delete', currentUserRole.id)) {
        toast.error('Cannot delete Business Owner role due to protection settings');
        return;
      }
    }
    
    setRoleToDelete(role);
    setShowDeleteRoleModal(true);
  };

  const handleDeleteRoleConfirm = async () => {
    if (!currentBusiness?.id || !roleToDelete) return;
    
    try {
      await apiService.deleteBusinessRole(currentBusiness.id, roleToDelete.id);
      toast.success('Role deleted successfully!');
      // Refresh the roles list
      await loadRoles();
    } catch (error: any) {
      console.error('Error deleting role:', error);
      toast.error(error.response?.data?.message || 'Failed to delete role. Please try again.');
    } finally {
      setShowDeleteRoleModal(false);
      setRoleToDelete(null);
    }
  };

  const handleDeleteRoleCancel = () => {
    setShowDeleteRoleModal(false);
    setRoleToDelete(null);
  };

  // QR Code Handler
  const handleGenerateQRCode = () => {
    setShowQRCodeModal(true);
  };

  const handlePrintQRCode = () => {
    window.print();
  };

  // Preview Website Handler
  const handlePreviewWebsite = () => {
    // Use the current business short name
    const businessShortName = currentBusiness?.shortName || businessInfo?.shortName;
    if (businessShortName) {
      window.open(`/${businessShortName}`, '_blank');
    }
  };

  // Invitation Management Functions
  const loadInvitations = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    setInvitationsLoading(true);
    try {
      const invitationsData = await apiService.getInvitations(currentBusiness.id);
      // Filter out accepted invitations - they will be shown in notifications instead
      const pendingAndRevokedInvitations = invitationsData.filter(invitation => invitation.status !== 'ACCEPTED');
      setInvitations(pendingAndRevokedInvitations);
    } catch (error) {
      console.error('Error loading invitations:', error);
    } finally {
      setInvitationsLoading(false);
    }
  }, [currentBusiness?.id]);

  const loadNotifications = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    setNotificationsLoading(true);
    try {
      // Determine filter parameters based on selected filter
      let isRead: boolean | undefined = undefined;
      let category: 'APPOINTMENT' | 'REVIEW' | 'SERVICE' | 'ALERT' | undefined = undefined;
      
      if (notificationFilter === 'unread') {
        isRead = false;
      } else if (notificationFilter === 'appointments') {
        category = 'APPOINTMENT';
      } else if (notificationFilter === 'services') {
        category = 'SERVICE';
      } else if (notificationFilter === 'reviews') {
        category = 'REVIEW';
      }
      // 'all' means no filters (isRead and category remain undefined)
      
      // Load filtered notifications with pagination
      const result = await apiService.getNotifications(currentBusiness.id, {
        page: notificationPage,
        limit: notificationsPerPage,
        isRead: isRead,
        category: category
      });
      
      setNotifications(result.data);
      if (result.pagination) {
        setNotificationPagination({
          total: result.pagination.total,
          totalPages: result.pagination.totalPages,
          hasNextPage: result.pagination.hasNextPage,
          hasPrevPage: result.pagination.hasPrevPage
        });
      } else {
        setNotificationPagination(null);
      }
      
      // Extract unique sender_id values (user IDs) from notifications
      const uniqueUserIds = new Set<string>();
      result.data.forEach((notification: Notification) => {
        if (notification.sender_id && notification.sender_id !== 'system') {
          uniqueUserIds.add(notification.sender_id);
        }
      });
      
      // Fetch user information for sender IDs
      if (uniqueUserIds.size > 0 && currentBusiness?.id) {
        try {
          const userNameMap: {[userId: string]: string} = {};
          
          // Get all professionals for the business (they might be senders)
          try {
            const professionals = await apiService.getBusinessProfessionalsDetailed(currentBusiness.id);
            professionals.forEach((professional: any) => {
              const professionalId = professional.id;
              if (uniqueUserIds.has(professionalId)) {
                const fullName = professional.name || `${professional.firstName || ''} ${professional.lastName || ''}`.trim();
                if (fullName) {
                  userNameMap[professionalId] = fullName;
                }
              }
            });
          } catch (error) {
            console.error('Error loading professionals for notification names:', error);
          }
          
          // Get all customers for the business (they might also be senders)
          try {
            const customersResult = await apiService.getBusinessCustomers(currentBusiness.id, { limit: 1000 });
            customersResult.data.forEach((customer: { id: string; firstName: string; lastName: string }) => {
              if (uniqueUserIds.has(customer.id) && !userNameMap[customer.id]) {
                const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
                if (fullName) {
                  userNameMap[customer.id] = fullName;
                }
              }
            });
          } catch (error) {
            console.error('Error loading customers for notification names:', error);
          }
          
          setUserNames(userNameMap);
        } catch (error) {
          console.error('Error loading user names for notifications:', error);
        }
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  }, [currentBusiness?.id, notificationPage, notificationsPerPage, notificationFilter]);

  // Load all notifications (all pages) for client-side search - same filter as loadNotifications
  const loadAllNotificationsForSearch = useCallback(async () => {
    if (!currentBusiness?.id) return;
    setAllNotificationsForSearchLoading(true);
    try {
      let isRead: boolean | undefined = undefined;
      let category: 'APPOINTMENT' | 'REVIEW' | 'SERVICE' | 'ALERT' | undefined = undefined;
      if (notificationFilter === 'unread') {
        isRead = false;
      } else if (notificationFilter === 'appointments') {
        category = 'APPOINTMENT';
      } else if (notificationFilter === 'services') {
        category = 'SERVICE';
      } else if (notificationFilter === 'reviews') {
        category = 'REVIEW';
      }
      const all: Notification[] = [];
      let page = 1;
      let hasNext = true;
      while (hasNext) {
        const result = await apiService.getNotifications(currentBusiness.id, {
          page,
          limit: notificationsPerPage,
          isRead,
          category
        });
        all.push(...result.data);
        hasNext = result.pagination?.hasNextPage ?? false;
        page++;
      }
      setAllNotificationsForSearch(all);
      const uniqueUserIds = new Set<string>();
      all.forEach((n: Notification) => {
        if (n.sender_id && n.sender_id !== 'system') uniqueUserIds.add(n.sender_id);
      });
      if (uniqueUserIds.size > 0 && currentBusiness?.id) {
        try {
          const userNameMap: { [userId: string]: string } = {};
          try {
            const professionals = await apiService.getBusinessProfessionalsDetailed(currentBusiness.id);
            professionals.forEach((professional: any) => {
              const id = professional.id;
              if (uniqueUserIds.has(id)) {
                const fullName = professional.name || `${professional.firstName || ''} ${professional.lastName || ''}`.trim();
                if (fullName) userNameMap[id] = fullName;
              }
            });
          } catch {
            // ignore
          }
          try {
            const customersResult = await apiService.getBusinessCustomers(currentBusiness.id, { limit: 1000 });
            customersResult.data.forEach((c: { id: string; firstName: string; lastName: string }) => {
              if (uniqueUserIds.has(c.id) && !userNameMap[c.id]) {
                const fullName = `${c.firstName || ''} ${c.lastName || ''}`.trim();
                if (fullName) userNameMap[c.id] = fullName;
              }
            });
          } catch {
            // ignore
          }
          setUserNames(prev => ({ ...prev, ...userNameMap }));
        } catch {
          // ignore
        }
      }
    } catch (error) {
      console.error('Error loading all notifications for search:', error);
      setAllNotificationsForSearch([]);
    } finally {
      setAllNotificationsForSearchLoading(false);
    }
  }, [currentBusiness?.id, notificationFilter, notificationsPerPage]);

  // Load total unread notifications count for navbar badge
  const loadUnreadCount = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      // Make a lightweight call with limit 1 just to get the total count from pagination
      const response = await apiService.getNotifications(currentBusiness.id, { page: 1, limit: 1, isRead: false });
      // Use pagination.total for the actual count of ALL unread notifications
      setTotalUnreadCount(response.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading unread notifications count:', error);
      setTotalUnreadCount(0);
    }
  }, [currentBusiness?.id]);

  // Load unread count on mount and when business changes
  useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  const loadProfessionals = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    setProfessionalsLoading(true);
    try {
      const professionalsData = await apiService.getBusinessProfessionalsDetailed(currentBusiness.id);

      // Load priorities if user is Business Owner
      if (isBusinessOwnerForBusiness(user, currentBusiness.id)) {
        try {
          const professionalsWithPriorities = await apiService.getBusinessProfessionalsWithPriorities(currentBusiness.id);
          // Create a map of professional_id -> priority
          const priorityMap = new Map<string, number>();
          professionalsWithPriorities.forEach((prof: any) => {
            if (prof.priority !== null && prof.priority !== undefined) {
              priorityMap.set(prof.id, prof.priority);
            }
          });
          
          // Merge priorities into professionals data
          const professionalsWithPriorityData = professionalsData.map((prof: any) => ({
            ...prof,
            priority: priorityMap.get(prof.id) ?? null
          }));
          
          setProfessionals(professionalsWithPriorityData);
        } catch (priorityError) {
          console.error('Error loading priorities, using default order:', priorityError);
          setProfessionals(professionalsData);
        }
      } else {
        setProfessionals(professionalsData);
      }
    } catch (error) {
      console.error('Error loading professionals:', error);
      toast.error('Failed to load professionals');
    } finally {
      setProfessionalsLoading(false);
    }
  }, [currentBusiness?.id, user]);

  // Set default calendar view when appointments section is first loaded (if no view param exists)
  const hasSetDefaultAppointmentViewRef = useRef(false);
  useEffect(() => {
    if (activeSection === 'appointments' && !hasSetDefaultAppointmentViewRef.current) {
      // Only set to calendar if no view param exists in URL
      const viewParam = searchParams.get('view');
      if (!viewParam) {
        setAppointmentView('calendar');
        hasSetDefaultAppointmentViewRef.current = true;
      } else {
        // If view param exists, mark as set so we don't override it
        hasSetDefaultAppointmentViewRef.current = true;
      }
    } else if (activeSection !== 'appointments') {
      // Reset when leaving appointments section
      hasSetDefaultAppointmentViewRef.current = false;
    }
  }, [activeSection, searchParams]);

  // Load appointments when business changes, date range changes, view changes, or filters change
  useEffect(() => {
    if (currentBusiness?.id && activeSection === 'appointments') {
      const rangeFilters = getAppointmentsDateRangeFilters(appointmentsDateRangeType, appointmentsDateRangeStart);
      // Keep server-side filters in sync with the date-range UI so pagination totals match what we display.
      if (
        appointmentFilters.dateFrom !== rangeFilters.dateFrom ||
        appointmentFilters.dateTo !== rangeFilters.dateTo
      ) {
        setAppointmentPagination(prev => ({ ...prev, currentPage: 1 }));
        setAppointmentFilters(prev => ({
          ...prev,
          dateFrom: rangeFilters.dateFrom,
          dateTo: rangeFilters.dateTo
        }));
        loadProfessionals(); // keep dropdown fresh
        return;
      }
      loadAppointments(1);
      loadProfessionals(); // Load professionals for the filter dropdown
    }
  }, [
    currentBusiness?.id,
    appointmentFilters,
    appointmentsDateRangeType,
    appointmentsDateRangeStart,
    activeSection,
    loadAppointments,
    loadProfessionals
  ]);

  useEffect(() => {
    const onBooked = (event: Event) => {
      const bookedBusinessId = (event as CustomEvent<{ businessId?: string }>).detail?.businessId;
      if (!currentBusiness?.id || bookedBusinessId !== currentBusiness.id) return;
      if (activeSection !== 'appointments') return;
      void loadAppointments(1, { silent: true });
    };
    window.addEventListener(OWNER_APPOINTMENT_BOOKED_EVENT, onBooked);
    return () => window.removeEventListener(OWNER_APPOINTMENT_BOOKED_EVENT, onBooked);
  }, [currentBusiness?.id, activeSection, loadAppointments]);

  // Load closures when closures section is active
  useEffect(() => {
    if (currentBusiness?.id && activeSection === 'closures') {
      loadClosures();
    }
  }, [currentBusiness?.id, activeSection, loadClosures]);

  // Load my bookings when "My Bookings" section is active
  useEffect(() => {
    if (activeSection === 'my-bookings') {
      loadMyBookings();
    }
  }, [activeSection, loadMyBookings]);

  // Load my gift cards when "My Gift Cards" section is active
  useEffect(() => {
    if (activeSection === 'my-gift-cards') {
      loadMyGiftCards();
    }
  }, [activeSection, loadMyGiftCards]);

  // Load professional appointments when "My Appointments" section is active
  useEffect(() => {
    if (activeSection === 'my-appointments' && user?.canPerformServices) {
      loadProfessionalAppointments();
    }
  }, [activeSection, user?.canPerformServices, loadProfessionalAppointments]);

  // Restore appointments filters from localStorage on mount (skip when opened via sidebar link — defaults applied in handleSectionChange)
  const hasRestoredAppointmentsFiltersRef = useRef(false);
  useEffect(() => {
    if (activeSection === 'appointments' && !hasRestoredAppointmentsFiltersRef.current) {
      if (useAppointmentsSidebarDefaultsRef.current) {
        useAppointmentsSidebarDefaultsRef.current = false;
        hasRestoredAppointmentsFiltersRef.current = true;
        return;
      }
      const stored = loadStoredAppointmentsFilters();
      if (stored) {
        setAppointmentsDateRangeType(stored.dateRangeType);
        const parsedDate = localDateFromYmd(stored.dateRangeStart);
        if (parsedDate) {
          setAppointmentsDateRangeStart(parsedDate);
          setAppointmentsDatePickerMonth(parsedDate);
        }
        setAppointmentFilters(prev => ({
          ...prev,
          status: (stored.status && stored.status.trim()) ? stored.status : 'CONFIRMED',
          professionalId: stored.professionalId ?? prev.professionalId
        }));
        if (stored.view) {
          setAppointmentView(stored.view);
        }
        if (stored.calendarDisplayMode === 'professionalColumns' || stored.calendarDisplayMode === 'default') {
          setAppointmentsCalendarDisplayMode(stored.calendarDisplayMode);
        }
      }
      // Mark as restored even if no stored data, so future changes can be saved
      hasRestoredAppointmentsFiltersRef.current = true;
    } else if (activeSection !== 'appointments') {
      hasRestoredAppointmentsFiltersRef.current = false;
    }
  }, [activeSection]);

  // Restore my appointments filters from localStorage on mount
  const hasRestoredMyAppointmentsFiltersRef = useRef(false);
  useEffect(() => {
    if (activeSection === 'my-appointments' && !hasRestoredMyAppointmentsFiltersRef.current) {
      const stored = loadStoredMyAppointmentsFilters();
      if (stored) {
        setProfessionalDateRangeType(stored.dateRangeType);
        const parsedDate = localDateFromYmd(stored.dateRangeStart);
        if (parsedDate) {
          setProfessionalDateRangeStart(parsedDate);
          setProfessionalDatePickerMonth(parsedDate);
        }
        setAppointmentFilters(prev => ({
          ...prev,
          status: stored.status ?? prev.status
        }));
        if (stored.view) {
          setProfessionalAppointmentView(stored.view);
        }
      }
      // Mark as restored even if no stored data, so future changes can be saved
      hasRestoredMyAppointmentsFiltersRef.current = true;
    } else if (activeSection !== 'my-appointments') {
      hasRestoredMyAppointmentsFiltersRef.current = false;
    }
  }, [activeSection]);

  // Default status filter:
  // Keep as "All Status" (empty string) so the dropdown stays consistent and newly booked PENDING appointments show up.

  // Save appointments filters to localStorage whenever they change
  useEffect(() => {
    if (activeSection === 'appointments' && hasRestoredAppointmentsFiltersRef.current) {
      saveAppointmentsFilters({
        dateRangeType: appointmentsDateRangeType,
        dateRangeStart: formatLocalDateYYYYMMDD(appointmentsDateRangeStart),
        status: appointmentFilters.status,
        professionalId: appointmentFilters.professionalId,
        view: appointmentView,
        calendarDisplayMode: appointmentsCalendarDisplayMode
      });
    }
  }, [activeSection, appointmentsDateRangeType, appointmentsDateRangeStart, appointmentFilters.status, appointmentFilters.professionalId, appointmentView, appointmentsCalendarDisplayMode]);

  // Save my appointments filters to localStorage whenever they change
  useEffect(() => {
    if (activeSection === 'my-appointments' && hasRestoredMyAppointmentsFiltersRef.current) {
      saveMyAppointmentsFilters({
        dateRangeType: professionalDateRangeType,
        dateRangeStart: formatLocalDateYYYYMMDD(professionalDateRangeStart),
        status: appointmentFilters.status,
        view: professionalAppointmentView
      });
    }
  }, [activeSection, professionalDateRangeType, professionalDateRangeStart, appointmentFilters.status, professionalAppointmentView]);

  // Load professional services when "My Services" section is active
  useEffect(() => {
    if (activeSection === 'my-services' && user?.canPerformServices) {
      loadProfessionalServices();
    }
  }, [activeSection, user?.canPerformServices, loadProfessionalServices]);

  // Fetch professional favorite services when my-services section is active
  useEffect(() => {
    const fetchProfessionalFavorites = async () => {
      if (activeSection !== 'my-services' || !user || !currentBusiness?.id) {
        setProfessionalFavoriteServiceIds(new Set());
        return;
      }

      try {
        setLoadingProfessionalFavorites(true);
        const favorites = await apiService.getFavoriteServices('professional', currentBusiness.id);
        const favoriteIds = new Set(favorites.map((f: Service) => f.id));
        setProfessionalFavoriteServiceIds(favoriteIds);
      } catch (error) {
        console.error('Error fetching professional favorite services:', error);
        toast.error('Failed to load favorite services');
      } finally {
        setLoadingProfessionalFavorites(false);
      }
    };

    fetchProfessionalFavorites();
  }, [activeSection, user, currentBusiness?.id]);

  // Handle professional favorite toggle
  const handleProfessionalFavoriteToggle = async (e: React.MouseEvent, serviceId: string) => {
    e.stopPropagation();

    if (!user) {
      toast.error('Please sign in to save favorites');
      return;
    }

    const isCurrentlyFavorited = professionalFavoriteServiceIds.has(serviceId);
    const newFavoriteIds = new Set(professionalFavoriteServiceIds);

    // Optimistic update
    if (isCurrentlyFavorited) {
      newFavoriteIds.delete(serviceId);
    } else {
      newFavoriteIds.add(serviceId);
    }
    setProfessionalFavoriteServiceIds(newFavoriteIds);

    try {
      if (isCurrentlyFavorited) {
        await apiService.removeFavoriteService(serviceId, 'professional', currentBusiness?.id);
        toast.success('Removed from favorites');
      } else {
        await apiService.addFavoriteService(serviceId, 'professional', currentBusiness?.id);
        toast.success('Added to favorites');
      }
    } catch (error: any) {
      // Revert optimistic update on error
      setProfessionalFavoriteServiceIds(professionalFavoriteServiceIds);
      
      if (error.response?.status === 401) {
        toast.error('Please sign in to save favorites');
      } else if (error.response?.status === 409) {
        // Already favorited, just update the state
        setProfessionalFavoriteServiceIds(newFavoriteIds);
      } else {
        toast.error(isCurrentlyFavorited ? 'Failed to remove from favorites' : 'Failed to add to favorites');
      }
    }
  };

  // Auto-switch to "My Services" if "My Favorites" is selected but there are no professional favorites
  useEffect(() => {
    if (myServicesCategoryFilter === 'my-favorites' && professionalFavoriteServiceIds.size === 0 && !loadingProfessionalFavorites) {
      setMyServicesCategoryFilter('my-services');
    }
  }, [myServicesCategoryFilter, professionalFavoriteServiceIds.size, loadingProfessionalFavorites]);

  // Load professional schedule when "My Work Schedule" section is active
  useEffect(() => {
    if (activeSection === 'my-work-schedule' && user?.canPerformServices) {
      loadProfessionalSchedule();
    }
  }, [activeSection, user?.canPerformServices, loadProfessionalSchedule]);

  // Load professional time offs
  const loadProfessionalTimeOffs = useCallback(async () => {
    if (!currentBusiness?.id || !user?.id) return;
    
    try {
      setTimeOffsLoading(true);
      setTimeOffsError(null);
      const timeOffsData = await apiService.getProfessionalTimeOffs(currentBusiness.id, user.id);
      setTimeOffs(timeOffsData);
    } catch (error) {
      console.error('Error loading time offs:', error);
      setTimeOffsError('Failed to load time offs');
      toast.error('Failed to load time offs');
    } finally {
      setTimeOffsLoading(false);
    }
  }, [currentBusiness?.id, user?.id]);

  // Load time offs for selected professional in appointments page
  const loadAppointmentsProfessionalTimeOffs = useCallback(async (professionalId: string) => {
    if (!currentBusiness?.id || !professionalId) {
      setAppointmentsProfessionalTimeOffs([]);
      return;
    }
    
    try {
      setAppointmentsProfessionalTimeOffsLoading(true);
      const timeOffsData = await apiService.getProfessionalTimeOffs(currentBusiness.id, professionalId);
      // Ensure professionalId is included in each time off
      setAppointmentsProfessionalTimeOffs(timeOffsData.map((to: any) => ({ ...to, professionalId })));
    } catch (error) {
      console.error('Error loading professional time offs:', error);
      setAppointmentsProfessionalTimeOffs([]);
    } finally {
      setAppointmentsProfessionalTimeOffsLoading(false);
    }
  }, [currentBusiness?.id]);

  // Load time offs when Time Off tab is active
  useEffect(() => {
    if (activeSection === 'my-work-schedule' && workScheduleActiveTab === 'time-off' && user?.canPerformServices) {
      loadProfessionalTimeOffs();
    }
  }, [activeSection, workScheduleActiveTab, user?.canPerformServices, loadProfessionalTimeOffs]);

  // Load time offs for selected professional in appointments page, or all professionals in columns view
  useEffect(() => {
    if (activeSection === 'appointments' && appointmentView === 'calendar') {
      if (appointmentsCalendarDisplayMode === 'professionalColumns' && isAppointmentsTodayAllProfessionals && professionals.length > 0) {
        // Load time offs for all professionals in columns view
        const loadAllProfessionalTimeOffs = async () => {
          try {
            setAppointmentsProfessionalTimeOffsLoading(true);
            const professionalIds = professionals
              .filter((p: any) => {
                const isActive = (p?.isActive ?? p?.is_active);
                const canPerformServices = p?.canPerformServices;
                return (typeof isActive === 'boolean' ? isActive : true) && canPerformServices === true;
              })
              .map((p: any) => String(p?.userId || p?.id))
              .filter((id: string) => Boolean(id));

            const timeOffPromises = professionalIds.map(async (professionalId: string) => {
              try {
                const timeOffs = await apiService.getProfessionalTimeOffs(currentBusiness!.id, professionalId);
                return timeOffs.map((to: any) => ({ ...to, professionalId }));
              } catch (error) {
                console.error(`Error loading time offs for professional ${professionalId}:`, error);
                return [];
              }
            });

            const allTimeOffs = (await Promise.all(timeOffPromises)).flat();
            setAppointmentsProfessionalTimeOffs(allTimeOffs);
          } catch (error) {
            console.error('Error loading professional time offs:', error);
            setAppointmentsProfessionalTimeOffs([]);
          } finally {
            setAppointmentsProfessionalTimeOffsLoading(false);
          }
        };

        loadAllProfessionalTimeOffs();
      } else if (appointmentFilters.professionalId) {
        // Load time offs for single selected professional
        loadAppointmentsProfessionalTimeOffs(appointmentFilters.professionalId);
      } else {
        setAppointmentsProfessionalTimeOffs([]);
      }
    } else {
      setAppointmentsProfessionalTimeOffs([]);
    }
  }, [activeSection, appointmentView, appointmentFilters.professionalId, appointmentsCalendarDisplayMode, isAppointmentsTodayAllProfessionals, professionals, currentBusiness?.id, loadAppointmentsProfessionalTimeOffs]);

  // Load time offs when My Appointments section is active (for calendar view)
  useEffect(() => {
    if (activeSection === 'my-appointments' && user?.canPerformServices) {
      loadProfessionalTimeOffs();
    }
  }, [activeSection, user?.canPerformServices, loadProfessionalTimeOffs]);

  // Load professional customer reviews when "My Customer Reviews" section is active
  useEffect(() => {
    if (activeSection === 'my-customer-reviews' && user?.canPerformServices) {
      loadProfessionalCustomerReviews();
    }
  }, [activeSection, user?.canPerformServices, loadProfessionalCustomerReviews]);

  // Save professional schedule
  const saveProfessionalSchedule = async (scheduleData: {
    dayOfWeek: number;
    startTime?: string;
    endTime?: string;
    isAvailable?: boolean;
    isBreakTime?: boolean;
    notes?: string;
  }) => {
    if (!currentBusiness?.id) return;
    
    try {
      setScheduleSaving(true);
      await apiService.saveProfessionalSchedule(currentBusiness.id, scheduleData);
      await loadProfessionalSchedule(); // Reload the schedule
      toast.success('Schedule updated successfully');
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Failed to save schedule');
    } finally {
      setScheduleSaving(false);
    }
  };

  // Handle schedule change
  const handleScheduleChange = (dayOfWeek: number, field: string, value: any) => {
    const updatedSchedule = professionalSchedule.map(schedule => 
      schedule.dayOfWeek === dayOfWeek ? { ...schedule, [field]: value } : schedule
    );
    setProfessionalSchedule(updatedSchedule);
  };

  // Schedule Modal Functions
  const handleEditSchedule = (schedule: any) => {
    setEditingSchedule(schedule);
    setIsScheduleModalOpen(true);
  };

  const handleSaveSchedule = async (scheduleData: any) => {
    if (!currentBusiness?.id) return;
    
    try {
      // Check if scheduleData is an array (bulk update) or single object
      if (Array.isArray(scheduleData) && scheduleData.length > 1) {
        // Use bulk update for multiple days
        await apiService.bulkUpdateProfessionalSchedules(currentBusiness.id, scheduleData);
        toast.success(`${scheduleData.length} schedules updated successfully`);
      } else {
        // Use single update for one day
        const singleSchedule = Array.isArray(scheduleData) ? scheduleData[0] : scheduleData;
        await apiService.saveProfessionalSchedule(currentBusiness.id, singleSchedule);
        toast.success('Schedule saved successfully');
      }
      await loadProfessionalSchedule();
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Failed to save schedule');
      throw error; // Re-throw so ScheduleModal can handle it
    }
  };

  // Delete professional schedule
  const handleDeleteSchedule = async () => {
    if (!scheduleToDelete || !currentBusiness?.id) return;
    
    try {
      setIsDeletingSchedule(true);
      await apiService.deleteProfessionalSchedule(currentBusiness.id, scheduleToDelete.dayOfWeek);
      await loadProfessionalSchedule();
      setScheduleToDelete(null);
      toast.success('Schedule deleted successfully');
    } catch (error: any) {
      console.error('Error deleting schedule:', error);
      toast.error(error.response?.data?.message || 'Failed to delete schedule');
    } finally {
      setIsDeletingSchedule(false);
    }
  };

  // Time Off Delete Handler
  const handleDeleteTimeOff = async () => {
    if (!timeOffToDelete || !currentBusiness?.id || !user?.id) return;
    
    try {
      setIsDeletingTimeOff(true);
      await apiService.deleteProfessionalTimeOff(timeOffToDelete.id);
      await loadProfessionalTimeOffs();
      setTimeOffToDelete(null);
      toast.success('Time off deleted successfully');
    } catch (error: any) {
      console.error('Error deleting time off:', error);
      toast.error(error.response?.data?.message || 'Failed to delete time off');
    } finally {
      setIsDeletingTimeOff(false);
    }
  };

  // Helper function to format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (date: string | Date | null | undefined): string => {
    if (!date) return '';
    try {
      // If it's already in YYYY-MM-DD format, return as is
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      
      // If it's a string with time components, extract just the date part
      if (typeof date === 'string' && date.includes('T')) {
        const datePart = date.split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          return datePart;
        }
      }
      
      // Otherwise, parse as Date and format
      const dateObj = typeof date === 'string' ? new Date(date + 'T00:00:00') : date;
      if (isNaN(dateObj.getTime())) return '';
      
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Helper functions for time conversion (24-hour <-> 12-hour)
  const convert24To12 = (time24: string): string => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12.toString().padStart(2, '0')}:${minutes || '00'} ${ampm}`;
  };

  const convert12To24 = (time12: string): string => {
    if (!time12) return '';
    const parts = time12.trim().split(' ');
    if (parts.length < 2) return '';
    const [time, period] = parts;
    const [hours, minutes] = time.split(':');
    let hour24 = parseInt(hours, 10);
    const min = minutes || '00';
    
    if (period.toUpperCase() === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    
    return `${hour24.toString().padStart(2, '0')}:${min}`;
  };

  // Time Off Modal Functions
  const handleOpenTimeOffModal = (timeOff?: any) => {
    if (timeOff) {
      // Edit mode - pre-fill form
      setEditingTimeOff(timeOff);
      setTimeOffFormData({
        label: timeOff.label || '',
        startDate: formatDateForInput(timeOff.startDate),
        endDate: formatDateForInput(timeOff.endDate),
        isAllDay: timeOff.isAllDay ?? true,
        startTime: timeOff.startTime ? convert24To12(timeOff.startTime) : '',
        endTime: timeOff.endTime ? convert24To12(timeOff.endTime) : ''
      });
    } else {
      // Create mode - reset form
      setEditingTimeOff(null);
      setTimeOffFormData({
        label: '',
        startDate: '',
        endDate: '',
        isAllDay: false,
        startTime: '',
        endTime: ''
      });
    }
    setIsTimeOffModalOpen(true);
  };

  const handleCloseTimeOffModal = () => {
    setIsTimeOffModalOpen(false);
    setEditingTimeOff(null);
    setTimeOffFormData({
      label: '',
      startDate: '',
      endDate: '',
      isAllDay: false,
      startTime: '',
      endTime: ''
    });
  };

  const handleSaveTimeOff = async () => {
    if (!currentBusiness?.id || !user?.id) return;
    
    // Validate form
    if (!timeOffFormData.label.trim()) {
      toast.error('Please enter a label for the time off');
      return;
    }
    if (!timeOffFormData.startDate) {
      toast.error('Please select a start date');
      return;
    }
    if (!timeOffFormData.endDate) {
      toast.error('Please select an end date');
      return;
    }
    if (new Date(timeOffFormData.endDate) < new Date(timeOffFormData.startDate)) {
      toast.error('End date must be after start date');
      return;
    }
    if (!timeOffFormData.isAllDay) {
      if (!timeOffFormData.startTime) {
        toast.error('Please select a start time');
        return;
      }
      if (!timeOffFormData.endTime) {
        toast.error('Please select an end time');
        return;
      }
    }
    
    try {
      setTimeOffSaving(true);
      const timeOffData = {
        label: timeOffFormData.label.trim(),
        startDate: timeOffFormData.startDate,
        endDate: timeOffFormData.endDate,
        isAllDay: timeOffFormData.isAllDay,
        startTime: timeOffFormData.isAllDay ? undefined : convert12To24(timeOffFormData.startTime),
        endTime: timeOffFormData.isAllDay ? undefined : convert12To24(timeOffFormData.endTime)
      };

      if (editingTimeOff) {
        // Update existing time off
        await apiService.updateProfessionalTimeOff(editingTimeOff.id, timeOffData);
        toast.success('Time off updated successfully');
      } else {
        // Create new time off
        await apiService.createProfessionalTimeOff(currentBusiness.id, user.id, timeOffData);
        toast.success('Time off added successfully');
      }
      
      handleCloseTimeOffModal();
      // Reload time offs
      await loadProfessionalTimeOffs();
    } catch (error: any) {
      console.error('Error saving time off:', error);
      toast.error(error.response?.data?.message || (editingTimeOff ? 'Failed to update time off' : 'Failed to add time off'));
    } finally {
      setTimeOffSaving(false);
    }
  };

  // Handle service selection/deselection for My Services
  const handleToggleServiceSelection = async (serviceId: string) => {
    if (!currentBusiness?.id || !user?.id) return;
    
    // Preserve scroll position
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    
    const isCurrentlySelected = professionalServices.some(ps => ps.id === serviceId);
    
    try {
      if (isCurrentlySelected) {
        // Remove service from professional's capabilities
        await apiService.unassignServiceFromProfessional(currentBusiness.id, user.id, serviceId);
        toast.success('Service removed from your capabilities');
      } else {
        // Add service to professional's capabilities
        await apiService.assignServiceToProfessional(currentBusiness.id, user.id, serviceId);
        toast.success('Service added to your capabilities');
      }
      
      // Reload professional services to update the UI
      await loadProfessionalServices();
      
      // Restore scroll position after state update
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    } catch (error) {
      console.error('Error toggling service selection:', error);
      toast.error('Failed to update service selection');
      
      // Restore scroll position even on error
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    }
  };

  // Handle selecting all services in the current filtered view
  const handleSelectAllServices = async () => {
    if (!currentBusiness?.id || !user?.id) return;
    
    // Calculate filtered services using the same logic as in renderSectionContent
    let filteredServices = myServicesCategoryFilter === 'my-services'
      ? services.filter(service => professionalServices.some(ps => ps.id === service.id))
      : myServicesCategoryFilter
      ? services.filter(service => service.category?.id === myServicesCategoryFilter)
      : services;
    if (myServicesSearch.trim()) {
      filteredServices = searchServices(filteredServices, myServicesSearch);
    }
    
    // Get all unselected services from the filtered list
    const unselectedServices = filteredServices.filter(
      service => !professionalServices.some(ps => ps.id === service.id)
    );
    
    if (unselectedServices.length === 0) {
      toast('All services are already selected', { icon: 'ℹ️' });
      return;
    }
    
    // Preserve scroll position
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    
    try {
      // Select all unselected services
      for (const service of unselectedServices) {
        await apiService.assignServiceToProfessional(currentBusiness.id, user.id, service.id);
      }
      
      toast.success(`Selected ${unselectedServices.length} service${unselectedServices.length > 1 ? 's' : ''}`);
      
      // Reload professional services to update the UI
      await loadProfessionalServices();
      
      // Restore scroll position after state update
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    } catch (error) {
      console.error('Error selecting all services:', error);
      toast.error('Failed to select all services');
      
      // Restore scroll position even on error
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    }
  };

  // Handle deselecting all services in the current filtered view
  const handleDeselectAllServices = async () => {
    if (!currentBusiness?.id || !user?.id) return;
    
    // Calculate filtered services using the same logic as in renderSectionContent
    let filteredServices = myServicesCategoryFilter === 'my-services'
      ? services.filter(service => professionalServices.some(ps => ps.id === service.id))
      : myServicesCategoryFilter
      ? services.filter(service => service.category?.id === myServicesCategoryFilter)
      : services;
    if (myServicesSearch.trim()) {
      filteredServices = searchServices(filteredServices, myServicesSearch);
    }
    
    // Get all selected services from the filtered list
    const selectedServices = filteredServices.filter(
      service => professionalServices.some(ps => ps.id === service.id)
    );
    
    if (selectedServices.length === 0) {
      toast('No services are selected', { icon: 'ℹ️' });
      return;
    }
    
    // Preserve scroll position
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    
    try {
      // Deselect all selected services
      for (const service of selectedServices) {
        await apiService.unassignServiceFromProfessional(currentBusiness.id, user.id, service.id);
      }
      
      toast.success(`Deselected ${selectedServices.length} service${selectedServices.length > 1 ? 's' : ''}`);
      
      // Reload professional services to update the UI
      await loadProfessionalServices();
      
      // Restore scroll position after state update
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    } catch (error) {
      console.error('Error deselecting all services:', error);
      toast.error('Failed to deselect all services');
      
      // Restore scroll position even on error
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    }
  };

  const handleInviteProfessional = () => {
    if (!currentBusiness?.id) {
      toast.error('Business information not available');
      return;
    }
    navigate(`/personnel/invite/${currentBusiness.id}`);
  };

  const handleInvitationSent = () => {
    loadInvitations();
    loadNotifications();
    loadProfessionals();
    toast.success('Invitation sent successfully!');
  };

  // Professional Management Functions
  const handleToggleProfessionalActive = (professionalId: string, isActive: boolean) => {
    const professional = professionals.find(p => p.id === professionalId);
    if (!professional) return;
    
    // Check role protection before proceeding
    if (professional && !canPerformAction('deactivate', professional.roleId)) {
      toast.error('Cannot deactivate this role due to protection settings');
      return;
    }
    
    // Show confirmation dialog
    setProfessionalToToggleActive({ professional, isActive });
    setShowToggleProfessionalActiveModal(true);
  };

  const handleToggleProfessionalActiveConfirm = async () => {
    if (!currentBusiness?.id || !professionalToToggleActive) return;
    
    try {
      const updatedProfessional = await apiService.toggleProfessionalActive(
        currentBusiness.id, 
        professionalToToggleActive.professional.id
      );
      setProfessionals(prev => prev.map(prof =>
        prof.id === professionalToToggleActive.professional.id ? updatedProfessional : prof
      ));
      toast.success(`Professional ${professionalToToggleActive.isActive ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error toggling professional status:', error);
      toast.error('Failed to update professional status. Please try again.');
    } finally {
      setShowToggleProfessionalActiveModal(false);
      setProfessionalToToggleActive(null);
    }
  };

  const handleToggleProfessionalActiveCancel = () => {
    setShowToggleProfessionalActiveModal(false);
    setProfessionalToToggleActive(null);
  };

  const handleDeleteProfessional = (professionalId: string, professionalName: string) => {
    const professional = professionals.find(p => p.id === professionalId);
    if (!professional) return;
    
    // Check role protection before proceeding
    if (professional && !canPerformAction('delete', professional.roleId)) {
      toast.error('Cannot delete this role due to protection settings');
      return;
    }
    
    // Show confirmation dialog
    setProfessionalToDelete({ professional, name: professionalName });
    setShowDeleteProfessionalModal(true);
  };

  const handleDeleteProfessionalConfirm = async () => {
    if (!currentBusiness?.id || !professionalToDelete) return;
    
    try {
      await apiService.deleteProfessional(currentBusiness.id, professionalToDelete.professional.id);
      setProfessionals(prev => prev.filter(prof => prof.id !== professionalToDelete.professional.id));
      toast.success(`${professionalToDelete.name} has been removed from the business successfully!`);
    } catch (error) {
      console.error('Error deleting professional:', error);
      toast.error('Failed to remove professional. Please try again.');
    } finally {
      setShowDeleteProfessionalModal(false);
      setProfessionalToDelete(null);
    }
  };

  const handleDeleteProfessionalCancel = () => {
    setShowDeleteProfessionalModal(false);
    setProfessionalToDelete(null);
  };

  const handleEditProfessional = (professional: any) => {
    navigate(`/personnel/edit/${professional.id}`);
  };

  const handleToggleServicePermission = (professionalId: string, canPerformServices: boolean) => {
    const professional = professionals.find(p => p.id === professionalId);
    if (!professional) return;
    
    // Show confirmation dialog
    setProfessionalToToggleService({ professional, canPerformServices });
    setShowToggleServicePermissionModal(true);
  };

  const handleToggleServicePermissionConfirm = async () => {
    if (!currentBusiness?.id || !professionalToToggleService) return;
    
    try {
      // Update service permissions for the individual professional
      await apiService.updateUserServicePermissions(professionalToToggleService.professional.id, { 
        canPerformServices: professionalToToggleService.canPerformServices 
      });
      
      // Update local state
      setProfessionals(prev => prev.map(prof =>
        prof.id === professionalToToggleService.professional.id 
          ? { ...prof, canPerformServices: professionalToToggleService.canPerformServices } 
          : prof
      ));
      
      toast.success(`Service permissions ${professionalToToggleService.canPerformServices ? 'granted' : 'revoked'} successfully!`);
      
      // Refresh the page to update sidebar and all components
      setTimeout(() => {
        window.location.reload();
      }, 1000); // Wait 1 second for the toast to be visible
    } catch (error) {
      console.error('Error updating service permissions:', error);
      toast.error('Failed to update service permissions. Please try again.');
    } finally {
      setShowToggleServicePermissionModal(false);
      setProfessionalToToggleService(null);
    }
  };

  const handleToggleServicePermissionCancel = () => {
    setShowToggleServicePermissionModal(false);
    setProfessionalToToggleService(null);
  };

  // Gift Card Functions
  const loadGiftCards = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    setGiftCardsLoading(true);
    try {
      const giftCardsData = await apiService.getGiftCards(currentBusiness.id);
      setGiftCards(giftCardsData);
    } catch (error) {
      console.error('Error loading gift cards:', error);
    } finally {
      setGiftCardsLoading(false);
    }
  }, [currentBusiness?.id]);

  const handleAddGiftCard = () => {
    setEditingGiftCard(undefined);
    setIsGiftCardModalOpen(true);
  };

  const handleEditGiftCard = (giftCard: GiftCard) => {
    setEditingGiftCard(giftCard);
    setIsGiftCardModalOpen(true);
  };

  const handleUpdateGiftCard = async (giftCardData: GiftCardFormData) => {
    if (!editingGiftCard) return;
    try {
      await apiService.updateGiftCard(editingGiftCard.id, giftCardData);
      loadGiftCards();
    } catch (error) {
      console.error('Error updating gift card:', error);
      throw error;
    }
  };

  const handleToggleGiftCardStatus = async (giftCardId: string, status: 'ACTIVE' | 'CANCELLED') => {
    try {
      await apiService.updateGiftCardStatus(giftCardId, status);
      
      // Refresh gift cards
      loadGiftCards();
    } catch (error) {
      console.error('Error updating gift card status:', error);
    }
  };

  // Testimonials (business_ratings) Functions
  const loadServiceReviews = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    setServiceReviewsLoading(true);
    try {
      const limit = serviceReviewPagination.limit || 20;
      const offset = (serviceReviewPage - 1) * limit;

      const response = await apiService.getBusinessRatingsManage(currentBusiness.id, {
        isPublic: serviceReviewFilters.isPublic,
        search: debouncedServiceReviewSearch || undefined,
        limit,
        offset
      });

      setServiceReviews(response.data || []);
      setServiceReviewPagination({
        total: response.pagination?.total ?? 0,
        limit: response.pagination?.limit ?? limit,
        offset: response.pagination?.offset ?? offset
      });

      const total = response.stats?.total ?? (response.pagination?.total ?? 0);
      const publicReviews = response.stats?.public ?? 0;
      const featuredReviews = response.stats?.featured ?? 0;
      const averageRating = (response.stats?.averageRating ?? 0).toFixed(1);

      setServiceReviewStats({
        total,
        pending: 0,
        approved: publicReviews,
        rejected: 0,
        public: publicReviews,
        featured: featuredReviews,
        averageRating,
        totalRatings: total
      });
    } catch (error) {
      console.error('Error loading testimonials:', error);
    } finally {
      setServiceReviewsLoading(false);
    }
  }, [
    currentBusiness?.id,
    serviceReviewFilters,
    debouncedServiceReviewSearch,
    serviceReviewPage,
    serviceReviewPagination.limit
  ]);

  // Debounce service review search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedServiceReviewSearch(serviceReviewSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [serviceReviewSearch]);

  // Reset page when filters/search change
  useEffect(() => {
    setServiceReviewPage(1);
  }, [serviceReviewFilters.isPublic, debouncedServiceReviewSearch]);

  const loadServiceReviewStats = useCallback(async (reviews: any[] = []) => {
    if (!currentBusiness?.id) return;
    
    try {
      // Calculate stats from the provided reviews or current serviceReviews
      const reviewsToUse = reviews.length > 0 ? reviews : serviceReviews;
      const total = reviewsToUse.length;
      const publicReviews = reviewsToUse.filter(review => review.isPublic);
      const featuredReviews = reviewsToUse.filter(review => review.isFeatured);
      
      const totalRating = reviewsToUse.reduce((sum, review) => sum + review.rating, 0);
      const averageRating = total > 0 ? (totalRating / total).toFixed(1) : '0.0';
      
      setServiceReviewStats({
        total,
        pending: 0, // Service reviews don't have pending status
        approved: publicReviews.length,
        rejected: 0, // Service reviews don't have rejected status
        public: publicReviews.length,
        featured: featuredReviews.length,
        averageRating,
        totalRatings: total
      });
    } catch (error) {
      console.error('Error calculating service review stats:', error);
    }
  }, [currentBusiness?.id, serviceReviews]);

  const handleOpenServiceReviewModal = (serviceReview: ServiceReview) => {
    // For now, just show a toast with review details
    toast.success(`Review by ${serviceReview.customer?.firstName} ${serviceReview.customer?.lastName} - ${serviceReview.rating}/5 stars`);
  };

  // Toggle review dropdown
  const toggleReviewDropdown = (reviewId: string) => {
    setOpenReviewDropdowns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  const handleMakeReviewPublic = async (review: BusinessRating) => {
    setOpenReviewDropdowns(prev => {
      const newSet = new Set(prev);
      newSet.delete(review.id);
      return newSet;
    });
    try {
      await apiService.updateBusinessRatingVisibility(review.id, true);
      toast.success('Testimonial made public successfully');
      loadServiceReviews();
    } catch (error: any) {
      console.error('Error making review public:', error);
      toast.error(error.response?.data?.error || 'Failed to make testimonial public');
    }
  };

  const handleMakeReviewPrivate = async (review: BusinessRating) => {
    setOpenReviewDropdowns(prev => {
      const newSet = new Set(prev);
      newSet.delete(review.id);
      return newSet;
    });
    try {
      await apiService.updateBusinessRatingVisibility(review.id, false);
      toast.success('Testimonial made private successfully');
      loadServiceReviews();
    } catch (error: any) {
      console.error('Error making review private:', error);
      toast.error(error.response?.data?.error || 'Failed to make testimonial private');
    }
  };

  const handleCloseServiceReviewModal = () => {
    setSelectedServiceReview(null);
    setIsServiceReviewModalOpen(false);
  };

  const handleServiceReviewFilterChange = (filter: string, value: string | boolean | undefined) => {
    setServiceReviewFilters(prev => ({
      ...prev,
      [filter]: value
    }));
  };


  // Load business categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await apiService.getBusinessCategories();
        // Sort categories: "Others" should be last
        const sortedCategories = [...categories].sort((a, b) => {
          if (a.name === 'Others') return 1;
          if (b.name === 'Others') return -1;
          return a.name.localeCompare(b.name);
        });
        setBusinessCategories(sortedCategories);
      } catch (error) {
        console.error('Error loading business categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Business Information Handlers
  const loadBusinessPolicies = useCallback(
    async (businessId: string) => {
      try {
        const policies = await apiService.getBusinessPolicies(businessId);
        setCancelRescheduleUpToValue(policies.cancelRescheduleUpToValue ?? 24);
        setCancelRescheduleUpToUnit(policies.cancelRescheduleUpToUnit ?? 'hours');
        setDepositRequired(Boolean(policies.depositRequired));
        setDepositAmountCentsDigits(policyDepositAmountToCentsDigits(Number(policies.depositAmount ?? 0)));
        setDepositNonRefundableOnCancel(Boolean(policies.depositNonRefundableOnCancel));
        setDepositDueWithinValue(
          Number.isFinite(Number(policies.depositDueWithinValue)) && Number(policies.depositDueWithinValue) >= 1
            ? Math.round(Number(policies.depositDueWithinValue))
            : DEFAULT_REGULAR_DEPOSIT_DUE_VALUE
        );
        setDepositDueWithinUnit(normalizeDepositDueWithinUnit(policies.depositDueWithinUnit));
        setCancellationPolicyText(policies.cancellationPolicyText ?? '');
        policiesLoadedForBusinessIdRef.current = businessId;
      } catch (policyErr: any) {
        // Don't block Site Configuration if policies endpoint/table isn't available yet.
        if (policyErr?.response?.status !== 404 && policyErr?.response?.status !== 501) {
          console.warn('Error loading business policies:', policyErr);
        }
      }
    },
    []
  );

  const loadBusinessInfo = useCallback(async () => {
    if (!user || !currentBusiness) return;
    
    try {
      setBusinessLoading(true);
      const businessData = await apiService.getBusiness(currentBusiness.shortName);
      setBusinessInfo(businessData.business);
      setBusinessFormData({
        name: businessData.business.name || '',
        shortName: businessData.business.shortName || '',
        timezone: businessData.business.timezone || getBrowserTimeZone() || 'UTC',
        description: businessData.business.description || '',
        address: businessData.business.address || '',
        phone: businessData.business.phone || '',
        email: businessData.business.email || '',
        website: businessData.business.website || '',
        facebookUrl: businessData.business.facebookUrl || '',
        instagramUrl: businessData.business.instagramUrl || '',
        youtubeUrl: businessData.business.youtubeUrl || '',
        showContactForm: businessData.business.showContactForm !== undefined ? businessData.business.showContactForm : true,
        websiteRedirectEnabled: businessData.business.websiteRedirectEnabled !== undefined ? businessData.business.websiteRedirectEnabled : false,
        serviceLocation: businessData.business.serviceLocation || 'IN_STORE',
        categories: businessData.business.categories || [],
        subCategories: businessData.business.subCategories || []
      });
      businessFormShortNameRef.current = businessData.business.shortName || '';
      await loadBusinessPolicies(currentBusiness.id);
      setShortNameValidation({ available: true, checking: false, formatError: null });
    } catch (error) {
      console.error('Error loading business info:', error);
    } finally {
      setBusinessLoading(false);
    }
  }, [user, currentBusiness, loadBusinessPolicies]);

  const applyUpdatedBusinessContext = useCallback(
    (updatedBusiness: { name?: string; shortName?: string }) => {
      if (!currentBusiness) {
        return businessFormData.shortName?.trim() || '';
      }

      const effectiveShortName =
        updatedBusiness?.shortName?.trim() ||
        businessFormData.shortName?.trim() ||
        currentBusiness.shortName;

      if (
        effectiveShortName !== currentBusiness.shortName ||
        (updatedBusiness?.name && updatedBusiness.name !== currentBusiness.name)
      ) {
        setCurrentBusiness({
          ...currentBusiness,
          shortName: effectiveShortName,
          ...(updatedBusiness?.name ? { name: updatedBusiness.name } : {}),
        });
      }

      return effectiveShortName;
    },
    [currentBusiness, businessFormData.shortName, setCurrentBusiness]
  );

  const checkPublicUrlAvailability = useCallback(async (shortName: string, savedShortName: string) => {
    const normalizedShortName = shortName.trim().toLowerCase();
    const normalizedSavedShortName = savedShortName.trim().toLowerCase();

    if (!normalizedShortName || normalizedShortName === normalizedSavedShortName) {
      setShortNameValidation({ available: true, checking: false, formatError: null });
      return;
    }

    const formatError = getBusinessShortNameFormatError(normalizedShortName);
    if (formatError) {
      setShortNameValidation({ available: true, checking: false, formatError });
      return;
    }

    const requestSeq = ++shortNameCheckSeqRef.current;
    setShortNameValidation({ available: true, checking: true, formatError: null });

    try {
      const result = await apiService.checkShortNameAvailability(normalizedShortName);

      if (requestSeq !== shortNameCheckSeqRef.current) return;
      if (businessFormShortNameRef.current.trim().toLowerCase() !== normalizedShortName) {
        setShortNameValidation((prev) => ({ ...prev, checking: false }));
        return;
      }

      setShortNameValidation({
        available: result.available,
        checking: false,
        formatError: null,
        message: result.message,
      });
    } catch (error) {
      console.error('Error checking public URL availability:', error);

      if (requestSeq !== shortNameCheckSeqRef.current) return;
      if (businessFormShortNameRef.current.trim().toLowerCase() !== normalizedShortName) {
        setShortNameValidation((prev) => ({ ...prev, checking: false }));
        return;
      }

      setShortNameValidation({
        available: true,
        checking: false,
        formatError: null,
        message: 'Unable to verify availability. Please try again.',
      });
    }
  }, []);

  const debouncedPublicUrlCheck = useCallback((shortName: string, savedShortName: string) => {
    if (shortNameDebounceTimeoutRef.current) {
      clearTimeout(shortNameDebounceTimeoutRef.current);
    }
    shortNameDebounceTimeoutRef.current = setTimeout(() => {
      void checkPublicUrlAvailability(shortName, savedShortName);
    }, 400);
  }, [checkPublicUrlAvailability]);

  const handlePublicUrlChange = useCallback((rawValue: string) => {
    const value = rawValue.toLowerCase().slice(0, BUSINESS_SHORT_NAME_MAX_LENGTH);
    const normalizedSavedShortName = savedBusinessShortName.trim().toLowerCase();

    businessFormShortNameRef.current = value;
    setBusinessFormData(prev => ({
      ...prev,
      shortName: value,
    }));

    if (!value.trim() || value.trim().toLowerCase() === normalizedSavedShortName) {
      setShortNameValidation({ available: true, checking: false, formatError: null });
      return;
    }

    const formatError = getBusinessShortNameFormatError(value);
    if (formatError) {
      setShortNameValidation({ available: true, checking: false, formatError });
      return;
    }

    debouncedPublicUrlCheck(value, normalizedSavedShortName);
  }, [savedBusinessShortName, debouncedPublicUrlCheck]);

  const validatePublicUrlBeforeSave = useCallback(async (): Promise<boolean> => {
    const submittedShortName = businessFormData.shortName.trim().toLowerCase();
    const normalizedSavedShortName = savedBusinessShortName.trim().toLowerCase();

    if (!submittedShortName || submittedShortName === normalizedSavedShortName) {
      setShortNameValidation({ available: true, checking: false, formatError: null });
      return true;
    }

    const formatError = getBusinessShortNameFormatError(submittedShortName);
    if (formatError) {
      setShortNameValidation({ available: true, checking: false, formatError });
      toast.error(formatError);
      return false;
    }

    if (shortNameDebounceTimeoutRef.current) {
      clearTimeout(shortNameDebounceTimeoutRef.current);
      shortNameDebounceTimeoutRef.current = null;
    }

    ++shortNameCheckSeqRef.current;
    setShortNameValidation({ available: true, checking: true, formatError: null });

    try {
      const result = await apiService.checkShortNameAvailability(submittedShortName);

      setShortNameValidation({
        available: result.available,
        checking: false,
        formatError: null,
        message: result.message,
      });

      if (!result.available) {
        toast.error(result.message || 'This public URL is already taken. Please choose a different one.');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking public URL availability before save:', error);
      setShortNameValidation({
        available: true,
        checking: false,
        formatError: null,
        message: 'Unable to verify availability. Please try again.',
      });
      toast.error('Unable to verify public URL availability. Please try again.');
      return false;
    }
  }, [businessFormData.shortName, savedBusinessShortName]);

  const handleSaveBusinessInfo = async () => {
    if (!currentBusiness) return;

    const isPublicUrlValid = await validatePublicUrlBeforeSave();
    if (!isPublicUrlValid) return;
    
    try {
      setBusinessSaving(true);
      const updatedBusiness = await apiService.updateBusiness(currentBusiness.id, businessFormData);
      setBusinessInfo(updatedBusiness);
      applyUpdatedBusinessContext(updatedBusiness);
      
      // Show success message
      toast.success('Business information saved successfully!');
    } catch (error: any) {
      console.error('Error saving business info:', error);
      toast.error(error.response?.data?.message || 'Error saving business information. Please try again.');
    } finally {
      setBusinessSaving(false);
    }
  };

  const handleSaveAllSiteConfig = async () => {
    if (!currentBusiness) return;

    const roundedPolicyDeposit = Math.round(policyDepositCentsToAmount(depositAmountCentsDigits) * 100) / 100;
    const policiesWantBookingDeposit = depositRequired === true || roundedPolicyDeposit > 0;

    if (depositRequired === true && roundedPolicyDeposit <= 0) {
      toast.error('Set a deposit amount greater than zero when “Require a deposit” is on.');
      return;
    }

    const paymentsEnabled = Boolean(businessStripeAccount?.chargesEnabled);
    const effectiveDepositDueWithinValue = paymentsEnabled
      ? depositDueWithinValue
      : DEFAULT_REGULAR_DEPOSIT_DUE_VALUE;
    const effectiveDepositDueWithinUnit = paymentsEnabled
      ? depositDueWithinUnit
      : DEFAULT_REGULAR_DEPOSIT_DUE_UNIT;
    const roundedDepositDueWithinValue = Math.round(Number(effectiveDepositDueWithinValue));
    if (!Number.isFinite(roundedDepositDueWithinValue) || roundedDepositDueWithinValue < 1) {
      toast.error('Set a deposit payment window of at least 1.');
      return;
    }
    const depositDueWithinMinutes =
      effectiveDepositDueWithinUnit === 'days'
        ? roundedDepositDueWithinValue * 24 * 60
        : effectiveDepositDueWithinUnit === 'hours'
          ? roundedDepositDueWithinValue * 60
          : roundedDepositDueWithinValue;
    if (depositDueWithinMinutes > 30 * 24 * 60) {
      toast.error('Deposit payment window cannot exceed 30 days.');
      return;
    }

    const isPublicUrlValid = await validatePublicUrlBeforeSave();
    if (!isPublicUrlValid) return;

    if (!businessFormData.shortName.trim()) {
      toast.error('Public URL is required');
      return;
    }

    try {
      setBusinessSaving(true);
      setOperatingHoursSaving(true);
      setBrandingSaving(true);
      
      // Save business info
      const updatedBusiness = await apiService.updateBusiness(currentBusiness.id, businessFormData);
      setBusinessInfo(updatedBusiness);
      const effectiveShortName = applyUpdatedBusinessContext(updatedBusiness);
      
      // Save operating hours only if they exist
      if (operatingHours.length > 0) {
        // Validate operating hours
        const invalidHours = operatingHours.filter(hour => {
          if (hour.isClosed) return false; // Skip validation for closed days
          return (hour.openTime && !hour.closeTime) || (!hour.openTime && hour.closeTime);
        });
        
        if (invalidHours.length > 0) {
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const invalidDayNames = invalidHours.map(h => dayNames[h.dayOfWeek]).join(', ');
          toast.error(`Please set both open and close times for: ${invalidDayNames}`);
          return;
        }
        
        // Normalize time format: convert HH:MM:SS to HH:MM if needed
        const normalizeTime = (time: string): string => {
          if (!time || !time.trim()) return '';
          
          const trimmedTime = time.trim();
          
          // Always split by colon and take first two parts (handles both HH:MM and HH:MM:SS)
          if (trimmedTime.includes(':')) {
            const parts = trimmedTime.split(':');
            if (parts.length >= 2) {
              const hours = parseInt(parts[0], 10);
              const minutes = parseInt(parts[1], 10);
              
              // Validate hours and minutes
              if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
              }
            }
          }
          
          return '';
        };
        
        const operatingHoursFormData: OperatingHoursFormData[] = operatingHours.map(hour => {
          // If closed, times should be empty strings
          if (hour.isClosed) {
            return {
              dayOfWeek: hour.dayOfWeek,
              openTime: '',
              closeTime: '',
              isClosed: true
            };
          }
          
          const openTime = normalizeTime(hour.openTime || '');
          const closeTime = normalizeTime(hour.closeTime || '');
          
          return {
            dayOfWeek: hour.dayOfWeek,
            openTime,
            closeTime,
            isClosed: false
          };
        });
        await apiService.updateOperatingHours(currentBusiness.id, operatingHoursFormData);
      }
      
      // Save branding & theme information (excluding gallery images - managed separately)
      const { galleryImages: _, ...brandingDataWithoutGallery } = brandingFormData;
      await apiService.updateBusinessBranding(effectiveShortName, brandingDataWithoutGallery);
      setBrandingInfo(brandingFormData);

      let policiesSavedSuccessfully = true;

      // Save business policies (graceful if backend/table not ready yet)
      try {
        await apiService.upsertBusinessPolicies(currentBusiness.id, {
          cancelRescheduleUpToValue,
          cancelRescheduleUpToUnit,
          // Persist deposit if the checkbox is on OR the amount is > 0 (many users enter $20 without toggling the box).
          depositRequired: policiesWantBookingDeposit,
          depositAmount: policiesWantBookingDeposit ? roundedPolicyDeposit : 0,
          depositNonRefundableOnCancel: policiesWantBookingDeposit ? depositNonRefundableOnCancel : false,
          depositDueWithinValue: roundedDepositDueWithinValue,
          depositDueWithinUnit: effectiveDepositDueWithinUnit,
          cancellationPolicyText
        });
      } catch (policyErr: any) {
        policiesSavedSuccessfully = false;
        if (policyErr?.response?.status === 501) {
          console.warn('Business policies table missing:', policyErr);
          toast.error('Policies not saved yet. Run the business_policies SQL script, then try again.');
        } else if (policyErr?.response?.status === 403) {
          toast.error('Policies not saved. You need manage_settings permission.');
        } else {
          console.warn('Error saving business policies:', policyErr);
          toast.error('Policies not saved. Please try again.');
        }
        // Do not block the overall Site Configuration save.
      }

      // Save customer appointment message (business_settings) - keep non-blocking
      try {
        await apiService.updateBusinessSettings(currentBusiness.id, {
          customerAppointmentMessageEnabled: settings.customerAppointmentMessageEnabled,
          customerAppointmentMessage: settings.customerAppointmentMessage
        });
      } catch (settingsErr: any) {
        if (settingsErr?.response?.status === 501) {
          toast.error('Customer Appointment Message not saved yet. Run the business_settings SQL script, then try again.');
        } else if (settingsErr?.response?.status === 403) {
          toast.error('Customer Appointment Message not saved. You need manage_settings permission.');
        } else {
          console.warn('Error saving customer appointment message settings:', settingsErr);
          toast.error('Customer Appointment Message not saved. Please try again.');
        }
        // Do not block the overall Site Configuration save.
      }

      if (policiesSavedSuccessfully) {
        toast.success('Site configuration saved successfully!');
      }
    } catch (error: any) {
      console.error('Error saving site configuration:', error);
      toast.error(error.response?.data?.message || 'Error saving site configuration. Please try again.');
    } finally {
      setBusinessSaving(false);
      setOperatingHoursSaving(false);
      setBrandingSaving(false);
    }
  };

  const handleBusinessFormChange = (field: string, value: string | boolean | string[]) => {
    setBusinessFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSocialUrlSuffixChange = (field: SocialUrlField, suffix: string) => {
    const { displayPrefix } = SOCIAL_URL_CONFIG[field];
    handleBusinessFormChange(field, buildSocialUrl(displayPrefix, suffix));
  };

  const handleCategoryCheckboxToggle = (categoryId: string) => {
    setBusinessFormData(prev => {
      const currentCategories = prev.categories || [];
      const isSelected = currentCategories.includes(categoryId);
      
      if (isSelected) {
        // Remove category and all its subcategories
        const category = businessCategories.find(cat => cat.id === categoryId);
        const subcategoryIds = category?.subcategories?.map(sub => sub.id) || [];
        const currentSubCategories = prev.subCategories || [];
        
        return {
          ...prev,
          categories: currentCategories.filter(id => id !== categoryId),
          subCategories: currentSubCategories.filter(id => !subcategoryIds.includes(id))
        };
      } else {
        // Add category
        return {
          ...prev,
          categories: [...currentCategories, categoryId]
        };
      }
    });
  };

  const handleCategoryCardClick = (categoryId: string) => {
    // Clicking on the card itself - just show/hide subcategories
    setSelectedCategoryId(prev => prev === categoryId ? null : categoryId);
  };

  const handleSubcategoryToggle = (subcategoryId: string) => {
    setBusinessFormData(prev => {
      const currentSubCategories = prev.subCategories || [];
      const isSelected = currentSubCategories.includes(subcategoryId);
      
      if (isSelected) {
        // Remove subcategory
        return {
          ...prev,
          subCategories: currentSubCategories.filter(id => id !== subcategoryId)
        };
      } else {
        // Add subcategory
        return {
          ...prev,
          subCategories: [...currentSubCategories, subcategoryId]
        };
      }
    });
  };

  // Operating Hours Functions
  const loadOperatingHours = async (businessId: string) => {
    try {
      setOperatingHoursLoading(true);
      const hours = await apiService.getOperatingHours(businessId);
      // Normalize the data: convert null values to empty strings and normalize time format
      const normalizeTime = (time: string | null | undefined): string => {
        if (!time || !time.trim()) return '';
        
        // Handle HH:MM:SS format by taking only HH:MM
        const trimmedTime = time.trim();
        if (trimmedTime.includes(':')) {
          const parts = trimmedTime.split(':');
          if (parts.length >= 2) {
            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            
            // Validate hours and minutes
            if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
              return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            }
          }
        }
        
        return '';
      };
      
      const normalizedHours = hours.map(hour => ({
        ...hour,
        openTime: normalizeTime(hour.openTime),
        closeTime: normalizeTime(hour.closeTime)
      }));
      setOperatingHours(normalizedHours);
    } catch (error: any) {
      console.error('Error loading operating hours:', error);
      toast.error('Failed to load operating hours');
    } finally {
      setOperatingHoursLoading(false);
    }
  };

  const handleSaveOperatingHours = async () => {
    if (!currentBusiness) return;
    
    try {
      setOperatingHoursSaving(true);
      // Convert OperatingHours[] to OperatingHoursFormData[]
      // Validate and ensure times are in HH:MM format
      const operatingHoursFormData: OperatingHoursFormData[] = operatingHours.map(hour => {
        // If closed, times should be empty strings
        if (hour.isClosed) {
          return {
            dayOfWeek: hour.dayOfWeek,
            openTime: '',
            closeTime: '',
            isClosed: true
          };
        }
        
        // Normalize time format: convert HH:MM:SS to HH:MM if needed
        const normalizeTime = (time: string): string => {
          if (!time || !time.trim()) return '';
          
          const trimmedTime = time.trim();
          
          // Always split by colon and take first two parts (handles both HH:MM and HH:MM:SS)
          if (trimmedTime.includes(':')) {
            const parts = trimmedTime.split(':');
            if (parts.length >= 2) {
              const hours = parseInt(parts[0], 10);
              const minutes = parseInt(parts[1], 10);
              
              // Validate hours and minutes
              if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
              }
            }
          }
          
          return '';
        };
        
        const openTime = normalizeTime(hour.openTime || '');
        const closeTime = normalizeTime(hour.closeTime || '');
        
        // If times are missing or invalid, don't allow saving
        if (!openTime || !closeTime) {
          throw new Error(`Please set valid open and close times for ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][hour.dayOfWeek]}`);
        }
        
        return {
          dayOfWeek: hour.dayOfWeek,
          openTime,
          closeTime,
          isClosed: false
        };
      });
      
      await apiService.updateOperatingHours(currentBusiness.id, operatingHoursFormData);
      toast.success('Operating hours updated successfully');
    } catch (error: any) {
      console.error('Error saving operating hours:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to save operating hours');
    } finally {
      setOperatingHoursSaving(false);
    }
  };

  // Helper functions for time format conversion
  const formatTimeForDisplay = (time: string | null | undefined): string => {
    if (!time || typeof time !== 'string' || !time.trim()) return '';
    
    // Check if already in 12-hour format (HH:MM AM/PM)
    const time12Regex = /^([0-1]?[0-9]|1[0-2]):[0-5][0-9] (AM|PM)$/i;
    if (time12Regex.test(time.trim())) {
      return time.trim();
    }
    
    // Convert from 24-hour format (HH:MM) to 12-hour format
    const time24Regex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!time24Regex.test(time.trim())) {
      return ''; // Invalid format
    }
    
    const [hours, minutes] = time.trim().split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return '';
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const formatTimeForDatabase = (displayTime: string): string => {
    if (!displayTime || !displayTime.trim()) return '';
    
    // Handle if already in HH:MM format (24-hour)
    const time24Regex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (time24Regex.test(displayTime.trim())) {
      return displayTime.trim();
    }
    
    // Handle 12-hour format (HH:MM AM/PM)
    const parts = displayTime.trim().split(' ');
    if (parts.length < 2) return '';
    
    const [time, period] = parts;
    if (!time || !period) return '';
    
    const timeParts = time.split(':');
    if (timeParts.length !== 2) return '';
    
    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    
    if (isNaN(hours) || isNaN(minutes)) return '';
    if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return '';
    
    let dbHours = hours;
    
    if (period.toUpperCase() === 'PM' && hours !== 12) {
      dbHours = hours + 12;
    } else if (period.toUpperCase() === 'AM' && hours === 12) {
      dbHours = 0;
    }
    
    return `${dbHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleOperatingHoursChange = (dayOfWeek: number, field: keyof OperatingHoursFormData, value: any) => {
    setOperatingHours(prev => {
      const existing = prev.find(h => h.dayOfWeek === dayOfWeek);
      if (existing) {
        return prev.map(h => 
          h.dayOfWeek === dayOfWeek 
            ? { ...h, [field]: value }
            : h
        );
      } else {
        return [...prev, {
          id: '',
          businessId: currentBusiness?.id || '',
          dayOfWeek,
          openTime: '',
          closeTime: '',
          isClosed: false,
          [field]: value
        }];
      }
    });
  };

  const handleTimeChange = (dayOfWeek: number, field: 'openTime' | 'closeTime', displayTime: string) => {
    const dbTime = formatTimeForDatabase(displayTime);
    handleOperatingHoursChange(dayOfWeek, field, dbTime);
  };

  // Helper function to get full logo URL (handles both relative and absolute URLs)
  const getLogoUrl = (logoUrl: string | undefined): string => {
    if (!logoUrl) return '';
    // If it's already an absolute URL, return as is
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      return logoUrl;
    }
    // Otherwise, prepend the API base URL
    const apiBaseUrl = process.env.REACT_APP_API_URL || (process.env.REACT_APP_ENV === 'production' ? 'https://booking-i73o.onrender.com/api' : 'http://localhost:3001/api');
    return `${apiBaseUrl}${logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`}`;
  };

  // Show spinner in the logo circle while a new logo URL is fetching
  useEffect(() => {
    if (brandingFormData.logoUrl) {
      setLogoImageLoading(true);
    } else {
      setLogoImageLoading(false);
    }
  }, [brandingFormData.logoUrl]);

  // Branding Handlers
  const loadBrandingInfo = useCallback(async () => {
    if (!user || !currentBusiness) return;
    
    try {
      setBrandingLoading(true);
      const brandingData = await apiService.getBusinessBranding(currentBusiness.shortName);
      
      // Load gallery images separately
      try {
        const galleryImages = await apiService.getGalleryImages(currentBusiness.shortName);
        const apiBaseUrl = process.env.REACT_APP_API_URL || (process.env.REACT_APP_ENV === 'production' ? 'https://booking-i73o.onrender.com/api' : 'http://localhost:3001/api');
        const galleryImageUrls = galleryImages.map(img => {
          const fullUrl = img.url.startsWith('http') ? img.url : `${apiBaseUrl}${img.url}`;
          return fullUrl;
        });
        brandingData.galleryImages = galleryImageUrls;
      } catch (galleryError) {
        console.error('Error loading gallery images:', galleryError);
        brandingData.galleryImages = [];
      }
      
      // Convert relative logo URL to full URL if needed
      if (brandingData?.logoUrl && typeof brandingData.logoUrl === 'string') {
        if (!brandingData.logoUrl.startsWith('http://') && !brandingData.logoUrl.startsWith('https://')) {
          const apiBaseUrl = process.env.REACT_APP_API_URL || (process.env.REACT_APP_ENV === 'production' ? 'https://booking-i73o.onrender.com/api' : 'http://localhost:3001/api');
          brandingData.logoUrl = `${apiBaseUrl}${brandingData.logoUrl.startsWith('/') ? brandingData.logoUrl : `/${brandingData.logoUrl}`}`;
        }
      }
      
      // Detect theme preset if not already set
      if (!brandingData.themePreset && brandingData.primaryColor) {
        const detectedPreset = detectThemePreset({
          primaryColor: brandingData.primaryColor,
          secondaryColor: brandingData.secondaryColor,
          accentColor: brandingData.accentColor,
          textColor: brandingData.textColor,
          backgroundColor: brandingData.backgroundColor
        });
        if (detectedPreset) {
          brandingData.themePreset = detectedPreset;
        }
      }
      
      setBrandingInfo(brandingData);
      setBrandingFormData(brandingData);
    } catch (error) {
      console.error('Error loading branding info:', error);
    } finally {
      setBrandingLoading(false);
    }
  }, [user, currentBusiness]);

  const handleResetSiteConfig = useCallback(async () => {
    if (!currentBusiness) return;
    try {
      setBusinessLoading(true);
      await loadBusinessInfo();
      await loadOperatingHours(currentBusiness.id);
      await loadBrandingInfo();
      toast.success('Site configuration reset to last saved state.');
    } catch (e) {
      toast.error('Could not reset. Please try again.');
    } finally {
      setBusinessLoading(false);
    }
  }, [currentBusiness, loadBusinessInfo, loadOperatingHours, loadBrandingInfo]);

  const handleSaveBranding = async () => {
    if (!currentBusiness) return;
    
    try {
      setBrandingSaving(true);
      // Don't include galleryImages in branding update - they're managed separately
      const { galleryImages, ...brandingDataWithoutGallery } = brandingFormData;
      await apiService.updateBusinessBranding(currentBusiness.shortName, brandingDataWithoutGallery);
      setBrandingInfo(brandingFormData);
      toast.success('Branding settings saved successfully!');
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Error saving branding settings. Please try again.');
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleBrandingFormChange = (field: keyof BusinessBranding, value: any) => {
    setBrandingFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColorChange = (colorType: keyof BusinessBranding, color: string) => {
    setBrandingFormData(prev => ({
      ...prev,
      [colorType]: color
    }));
  };

  const handleResetToThemeDefaults = () => {
    // Reset to default unified layout colors
    setBrandingFormData(prev => ({
      ...prev,
      primaryColor: '#e11d48',
      secondaryColor: '#9f1239',
      accentColor: '#f5c2cc',
      textColor: '#111827',
      backgroundColor: '#ffeef0',
      useCustomColors: false,
      themePreset: undefined
    }));
    toast.success('Reset to default colors!');
  };

  const handleThemePresetSelect = async (presetKey: string) => {
    if (!currentBusiness) return;
    
    const preset = getThemePreset(presetKey);
    if (preset) {
      const updatedBranding = {
        ...brandingFormData,
        primaryColor: preset.primaryColor,
        secondaryColor: preset.secondaryColor,
        accentColor: preset.accentColor,
        textColor: preset.textColor,
        backgroundColor: preset.backgroundColor,
        themePreset: presetKey,
        useCustomColors: false
      };
      
      // Update local state immediately
      setBrandingFormData(updatedBranding);
      
      // Auto-save to database
      try {
        setBrandingSaving(true);
        const { galleryImages, ...brandingDataWithoutGallery } = updatedBranding;
        await apiService.updateBusinessBranding(currentBusiness.shortName, brandingDataWithoutGallery);
        setBrandingInfo(updatedBranding);
        toast.success(`${preset.name} theme applied and saved!`);
      } catch (error) {
        console.error('Error saving theme preset:', error);
        toast.error('Failed to save theme. Please try again.');
        // Revert to previous state on error
        setBrandingFormData(brandingFormData);
      } finally {
        setBrandingSaving(false);
      }
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentBusiness) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload an image file (JPEG, PNG, GIF, or WebP).');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size too large. Please upload an image smaller than 5MB.');
      return;
    }

    try {
      // Show loading state
      setBrandingSaving(true);
      
      // Upload the file to the server. Ensure a cache-busting query so the <img>
      // src changes even if the server path is unchanged (browser would otherwise
      // keep showing the previously cached logo).
      const uploadedLogoUrl = await apiService.uploadBusinessLogo(currentBusiness.shortName, file);
      const logoBase = uploadedLogoUrl.split('?')[0];
      const logoUrl = `${logoBase}?v=${Date.now()}`;
      
      // Update the form data with the uploaded logo URL
      setBrandingFormData(prev => ({
        ...prev,
        logoUrl: logoUrl
      }));
      
      // Also update the branding info immediately so it shows up right away
      setBrandingInfo(prev => {
        if (!prev) {
          // If no branding info exists, create default branding with the logo
          return {
            logoUrl: logoUrl,
            primaryColor: '#3B82F6',
            secondaryColor: '#1E40AF',
            accentColor: '#F59E0B',
            textColor: '#1F2937',
            backgroundColor: '#FFFFFF',
            fontFamily: 'Inter',
            useCustomColors: false,
            layout: 'modern',
            galleryImages: []
          };
        }
        return {
          ...prev,
          logoUrl: logoUrl
        };
      });
      
      toast.success('Logo uploaded successfully!');
      
      // Reload branding info to ensure we have the latest data from the server
      if (currentBusiness) {
        await loadBrandingInfo();
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      toast.error(error.response?.data?.message || 'Failed to upload logo. Please try again.');
    } finally {
      setBrandingSaving(false);
      // Reset the input so the same file can be selected again if needed
      event.target.value = '';
    }
  };

  const handleGalleryUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !currentBusiness) return;

    const currentImageCount = brandingFormData.galleryImages?.length || 0;
    const fileArray = Array.from(files);
    const totalAfterUpload = currentImageCount + fileArray.length;

    if (totalAfterUpload > MAX_GALLERY_IMAGES) {
      const allowedCount = MAX_GALLERY_IMAGES - currentImageCount;
      if (allowedCount <= 0) {
        toast.error(`Maximum of ${MAX_GALLERY_IMAGES} gallery images allowed. Please remove some images first.`);
        event.target.value = '';
        return;
      }
      toast.error(`You can only upload ${allowedCount} more image(s). Maximum of ${MAX_GALLERY_IMAGES} gallery images allowed.`);
      event.target.value = '';
      return;
    }

    try {
      const result = await apiService.uploadGalleryImages(currentBusiness.shortName, fileArray);
      
      // Convert URLs to full URLs with API base
      const apiBaseUrl = process.env.REACT_APP_API_URL || (process.env.REACT_APP_ENV === 'production' ? 'https://booking-i73o.onrender.com/api' : 'http://localhost:3001/api');
      const newImageUrls = result.images.map(img => {
        const fullUrl = img.url.startsWith('http') ? img.url : `${apiBaseUrl}${img.url}`;
        return fullUrl;
      });

      setBrandingFormData(prev => ({
        ...prev,
        galleryImages: [...(prev.galleryImages || []), ...newImageUrls]
      }));

      toast.success(`${fileArray.length} image(s) uploaded successfully!`);
    } catch (error: any) {
      console.error('Error uploading gallery images:', error);
      toast.error(error.response?.data?.message || 'Error uploading gallery images. Please try again.');
    } finally {
      event.target.value = '';
    }
  };

  const galleryRemainingSlots = Math.max(
    0,
    MAX_GALLERY_IMAGES - (brandingFormData.galleryImages?.length || 0)
  );

  const handleOpenStockImagesModal = () => {
    if (galleryRemainingSlots <= 0) {
      toast.error(`Maximum of ${MAX_GALLERY_IMAGES} gallery images allowed. Please remove some images first.`);
      return;
    }
    setShowStockImagesModal(true);
  };

  const handleUseStockImages = async (keys: string[]) => {
    if (!currentBusiness) return;

    const result = await apiService.importStockGalleryImages(currentBusiness.shortName, keys);
    const apiBaseUrl =
      process.env.REACT_APP_API_URL ||
      (process.env.REACT_APP_ENV === 'production'
        ? 'https://booking-i73o.onrender.com/api'
        : 'http://localhost:3001/api');
    const newImageUrls = result.images.map((img) => {
      const fullUrl = img.url.startsWith('http') ? img.url : `${apiBaseUrl}${img.url}`;
      return fullUrl;
    });

    setBrandingFormData((prev) => ({
      ...prev,
      galleryImages: [...(prev.galleryImages || []), ...newImageUrls],
    }));

    toast.success(`${keys.length} stock image(s) added to your gallery!`);
  };

  const removeGalleryImage = async (index: number) => {
    if (!currentBusiness || !brandingFormData.galleryImages) return;

    const imageUrl = brandingFormData.galleryImages[index];
    // Extract image ID from URL (format: /businesses/:shortName/gallery/:imageId)
    const urlParts = imageUrl.split('/gallery/');
    if (urlParts.length === 2) {
      const imageId = urlParts[1].split('?')[0]; // Remove query params if any
      try {
        await apiService.deleteGalleryImage(currentBusiness.shortName, imageId);
        setBrandingFormData(prev => ({
          ...prev,
          galleryImages: prev.galleryImages?.filter((_, i) => i !== index) || []
        }));
        toast.success('Gallery image deleted successfully!');
      } catch (error: any) {
        console.error('Error deleting gallery image:', error);
        toast.error(error.response?.data?.message || 'Error deleting gallery image. Please try again.');
      }
    } else {
      // Fallback: just remove from local state if URL format is unexpected
      setBrandingFormData(prev => ({
        ...prev,
        galleryImages: prev.galleryImages?.filter((_, i) => i !== index) || []
      }));
    }
  };

  const gallerySensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleGalleryDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !currentBusiness || !brandingFormData.galleryImages?.length) return;
    const urls = brandingFormData.galleryImages;
    const oldIndex = urls.indexOf(active.id as string);
    const newIndex = urls.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(urls, oldIndex, newIndex);
    setBrandingFormData(prev => ({ ...prev, galleryImages: reordered }));
    const imageIds = reordered.map(getGalleryImageId).filter((id): id is string => id != null);
    if (imageIds.length > 0) {
      try {
        await apiService.reorderGalleryImages(currentBusiness.shortName, imageIds);
        toast.success('Gallery order updated.');
      } catch (err) {
        console.error('Error reordering gallery:', err);
        toast.error('Failed to save order. Reverting.');
        setBrandingFormData(prev => ({ ...prev, galleryImages: urls }));
      }
    }
  }, [currentBusiness, brandingFormData.galleryImages]);

  useEffect(() => {
    return () => {
      if (shortNameDebounceTimeoutRef.current) {
        clearTimeout(shortNameDebounceTimeoutRef.current);
      }
    };
  }, []);

  // Load business information when component mounts or when site-config is active
  const businessInfoLoadedRef = useRef(false);
  
  useEffect(() => {
    if ((activeSection === 'site-config' || activeSection === 'roles' || activeSection === 'professionals') && !businessInfo && !businessInfoLoadedRef.current && currentBusiness) {
      loadBusinessInfo();
      businessInfoLoadedRef.current = true;
    }
  }, [activeSection, businessInfo, currentBusiness, loadBusinessInfo]);

  // Ensure Cancellation & Rescheduling Policy tab pre-populates from DB at least once per business.
  // This is intentionally scoped to the tab so we don't overwrite in-progress edits when navigating between tabs.
  useEffect(() => {
    if (activeSection !== 'site-config') return;
    if (siteConfigActiveTab !== 'cancellation-rescheduling-policy') return;
    const businessId = currentBusiness?.id;
    if (!businessId) return;
    if (policiesLoadedForBusinessIdRef.current === businessId) return;
    loadBusinessPolicies(businessId);
  }, [activeSection, siteConfigActiveTab, currentBusiness?.id, loadBusinessPolicies]);

  useEffect(() => {
    const showSaveBar = activeSection === 'site-config' && !businessLoading;
    if (showSaveBar) document.body.dataset.ownerSaveBar = 'true';
    else delete document.body.dataset.ownerSaveBar;
    return () => {
      delete document.body.dataset.ownerSaveBar;
    };
  }, [activeSection, businessLoading]);

  // Load branding information when Site Configuration page is active
  // Load it immediately when arriving at site-config, not just when visiting the branding-theme tab
  // This prevents the theme from being cleared when saving from other tabs
  const brandingInfoLoadedRef = useRef(false);
  
  useEffect(() => {
    if (activeSection === 'site-config' && !brandingInfo && !brandingInfoLoadedRef.current && currentBusiness) {
      loadBrandingInfo();
      brandingInfoLoadedRef.current = true;
    }
  }, [activeSection, brandingInfo, currentBusiness, loadBrandingInfo]);

  // Load invitations and professionals when professionals section is active
  useEffect(() => {
    if (activeSection === 'professionals' && currentBusiness?.id) {
      loadInvitations();
      loadProfessionals();
    }
  }, [activeSection, currentBusiness?.id, loadInvitations, loadProfessionals]);

  // Load gift cards when giftcards section is active
  const giftCardsLoadedRef = useRef(false);
  
  useEffect(() => {
    if (activeSection === 'giftcards' && currentBusiness?.id && !giftCardsLoadedRef.current) {
      loadGiftCards();
      giftCardsLoadedRef.current = true;
    }
  }, [activeSection, currentBusiness?.id, loadGiftCards]);

  // Load testimonials when testimonials section is active (and when filters/search/pagination change)
  useEffect(() => {
    if (activeSection === 'testimonials' && currentBusiness?.id) {
      loadServiceReviews();
    }
  }, [
    activeSection,
    currentBusiness?.id,
    loadServiceReviews,
    serviceReviewPage,
    serviceReviewFilters.isPublic,
    debouncedServiceReviewSearch
  ]);

  // Load roles when roles section is active OR when professionals section is active (needed for InvitationsList)
  const rolesLoadedRef = useRef(false);
  
  useEffect(() => {
    if ((activeSection === 'roles' || activeSection === 'professionals') && currentBusiness?.id) {
      if (activeSection === 'roles' && !rolesLoadedRef.current) {
        loadRoles();
        rolesLoadedRef.current = true;
      } else if (activeSection === 'professionals' && roles.length === 0) {
        // Always load roles for professionals section if they're not loaded yet
        loadRoles();
      }
    }
  }, [activeSection, currentBusiness?.id, loadRoles, roles.length]);

  // Load services when services section is active
  const servicesLoadedRef = useRef(false);
  
  useEffect(() => {
    if (activeSection === 'services' && currentBusiness?.id) {
      loadServices();
      servicesLoadedRef.current = true;
    }
  }, [activeSection, currentBusiness?.id, loadServices]);

  // Ensure services are available for appointments grid/table (for service location badges).
  // Use a ref to avoid re-triggering when loadServices() sets services to [] (empty), which would cause an infinite loop for new businesses.
  const appointmentsServicesLoadTriggeredRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeSection !== 'appointments') return;
    if (!currentBusiness?.id) return;
    const businessId = currentBusiness.id;
    if ((services || []).length > 0) {
      appointmentsServicesLoadTriggeredRef.current = businessId;
      return;
    }
    if (appointmentsServicesLoadTriggeredRef.current === businessId) return;
    appointmentsServicesLoadTriggeredRef.current = businessId;
    loadServices();
  }, [activeSection, currentBusiness?.id, loadServices, services]);

  // Load operating hours when site-config section is active
  const operatingHoursLoadedRef = useRef(false);
  
  useEffect(() => {
    if (activeSection === 'site-config' && currentBusiness?.id && !operatingHoursLoadedRef.current) {
      loadOperatingHours(currentBusiness.id);
      operatingHoursLoadedRef.current = true;
    }
  }, [activeSection, currentBusiness?.id]);

  // Load operating hours for calendar hour-range (appointments + my-appointments)
  useEffect(() => {
    if (
      (activeSection === 'appointments' || activeSection === 'my-appointments') &&
      currentBusiness?.id &&
      !operatingHoursLoadedForCalendarRef.current
    ) {
      loadOperatingHours(currentBusiness.id);
      operatingHoursLoadedForCalendarRef.current = true;
    }
  }, [activeSection, currentBusiness?.id]);

  // Load notifications when notifications section is active
  const notificationsLoadedRef = useRef(false);
  
  useEffect(() => {
    if (activeSection === 'notifications' && currentBusiness?.id && !notificationsLoadedRef.current) {
      loadNotifications();
      notificationsLoadedRef.current = true;
    }
  }, [activeSection, currentBusiness?.id, loadNotifications]);

  // Always collapse mobile "Search & Filters" when arriving at Notifications
  useEffect(() => {
    if (activeSection !== 'notifications') return;
    setIsMobileNotificationsTopCollapsed(true);
    setIsNotificationFilterDropdownOpen(false);
  }, [activeSection]);

  // Load notifications on component mount to show badge
  useEffect(() => {
    if (currentBusiness?.id && !notificationsLoading && !notificationsLoadedRef.current) {
      loadNotifications();
      notificationsLoadedRef.current = true;
    }
  }, [currentBusiness?.id, loadNotifications, notificationsLoading]);

  // Reload notifications when page or filter changes (for pagination) - skip when search is active (we paginate client-side)
  useEffect(() => {
    if (activeSection === 'notifications' && currentBusiness?.id && !notificationSearchQuery.trim()) {
      loadNotifications();
    }
  }, [notificationPage, notificationFilter, activeSection, currentBusiness?.id, loadNotifications, notificationSearchQuery]);

  // When search is active, load all notifications (all pages) so we can filter and paginate search results.
  // Dependencies intentionally exclude loadNotifications/loadAllNotificationsForSearch so that changing
  // notificationPage (pagination click) does not re-run this effect and reset to page 1.
  const notificationSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const q = notificationSearchQuery.trim();
    if (!currentBusiness?.id || activeSection !== 'notifications') return;
    if (!q) {
      setAllNotificationsForSearch(null);
      setNotificationPage(1);
      loadNotifications();
      return;
    }
    if (notificationSearchDebounceRef.current) clearTimeout(notificationSearchDebounceRef.current);
    notificationSearchDebounceRef.current = setTimeout(() => {
      notificationSearchDebounceRef.current = null;
      setNotificationPage(1);
      loadAllNotificationsForSearch();
    }, 400);
    return () => {
      if (notificationSearchDebounceRef.current) clearTimeout(notificationSearchDebounceRef.current);
    };
  }, [notificationSearchQuery, activeSection, currentBusiness?.id, notificationFilter]);

  // Client-side fuzzy search for notifications (by service, customer, phone, email, professional)
  const filteredNotifications = useMemo(() => {
    const q = notificationSearchQuery.trim();
    const source = allNotificationsForSearch !== null ? allNotificationsForSearch : notifications;
    if (!q) return source;
    const parts: string[] = [];
    return source.filter((n) => {
      parts.length = 0;
      if (n.title) parts.push(String(n.title));
      if (n.message) parts.push(String(n.message));
      const m = n.metadata;
      if (m?.invoiceNumber) parts.push(String(m.invoiceNumber));
      if (m?.invoice_id) parts.push(String(m.invoice_id));
      if (m?.appointmentId) parts.push(String(m.appointmentId));
      if (m?.serviceName) parts.push(String(m.serviceName));
      const first = m?.customer_first_name;
      const last = m?.customer_last_name;
      const rawName = m?.customerName;
      const customerName = (first || last)
        ? [first, last].map((v: any) => (v == null ? '' : String(v).trim())).filter(Boolean).join(' ')
        : (rawName == null ? '' : String(rawName).trim());
      if (customerName) parts.push(customerName);
      if (m?.customer_email) parts.push(String(m.customer_email));
      const canViewPhones = canViewFullCustomerPhone(user, currentBusiness?.id);
      if (m?.customer_phone) parts.push(displayCustomerPhone(String(m.customer_phone), canViewPhones));
      if (m?.customerEmail) parts.push(String(m.customerEmail));
      if (m?.customerPhone) parts.push(displayCustomerPhone(String(m.customerPhone), canViewPhones));
      if (m?.professionalName) parts.push(String(m.professionalName));
      if (m?.professional_first_name || m?.professional_last_name) {
        parts.push([m.professional_first_name, m.professional_last_name].filter(Boolean).join(' '));
      }
      if (Array.isArray(m?.assignments)) {
        m.assignments.forEach((a: any) => {
          if (a.professionalFirstName || a.professionalLastName) {
            parts.push([a.professionalFirstName, a.professionalLastName].filter(Boolean).join(' '));
          }
        });
      }
      const searchable = parts.join(' ');
      return fuzzyMatch(q, searchable) !== null;
    });
  }, [notifications, allNotificationsForSearch, notificationSearchQuery, user, currentBusiness?.id]);

  // When search is active, paginate filtered results client-side; otherwise show current API page
  const isNotificationSearchMode = notificationSearchQuery.trim() !== '' && allNotificationsForSearch !== null;
  const notificationSearchTotalPages = useMemo(() => {
    if (!notificationSearchQuery.trim() || filteredNotifications.length === 0) return 1;
    return Math.ceil(filteredNotifications.length / notificationsPerPage) || 1;
  }, [filteredNotifications.length, notificationSearchQuery, notificationsPerPage]);
  const displayedNotifications = useMemo(() => {
    if (isNotificationSearchMode) {
      const start = (notificationPage - 1) * notificationsPerPage;
      return filteredNotifications.slice(start, start + notificationsPerPage);
    }
    return filteredNotifications;
  }, [isNotificationSearchMode, notificationPage, notificationsPerPage, filteredNotifications]);

  // Debounce search query to avoid too many API calls
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(customerSearchQuery);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(customerSearchQuery);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [customerSearchQuery]);

  // Load customers when customers section is active
  const loadCustomers = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    setCustomersLoading(true);
    setCustomersError(null);
    try {
      const result = await apiService.getBusinessCustomers(currentBusiness.id, {
        page: customerPage,
        limit: 20,
        search: debouncedSearchQuery,
        sort: customerSortOption
      });
      setCustomers(result.data);
      setCustomerPagination(result.pagination);
    } catch (error) {
      console.error('Error loading customers:', error);
      setCustomersError('Failed to load customers. Please try again.');
      toast.error('Failed to load customers');
    } finally {
      setCustomersLoading(false);
    }
  }, [currentBusiness?.id, customerPage, debouncedSearchQuery, customerSortOption]);

  useEffect(() => {
    if (
      activeSection === 'customers' &&
      currentBusiness?.id &&
      user &&
      canAccessCustomersPage(user, currentBusiness.id)
    ) {
      loadCustomers();
    }
  }, [activeSection, currentBusiness?.id, loadCustomers, user]);

  // Reset to page 1 when search or sort changes
  useEffect(() => {
    if (activeSection === 'customers') {
      setCustomerPage(1);
    }
  }, [debouncedSearchQuery, customerSortOption, activeSection]);

  // Automatically switch to grid view on mobile
  useEffect(() => {
    const handleResize = () => {
      // Check if viewport is mobile (below lg breakpoint, which is 1024px in Tailwind)
      if (window.innerWidth < 1024 && customerView === 'table') {
        setCustomerView('grid');
      }
    };

    // Check on mount
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [customerView]);

  // Automatically switch to grid view on mobile for My Services
  useEffect(() => {
    const handleResize = () => {
      // Check if viewport is mobile (below md breakpoint, which is 768px in Tailwind)
      if (window.innerWidth < 768 && myServicesViewMode === 'list') {
        setMyServicesViewMode('grid');
      }
    };

    // Check on mount
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [myServicesViewMode]);

  // Load services and professional services when my-services section is active
  useEffect(() => {
    if (activeSection === 'my-services' && currentBusiness?.id && user?.canPerformServices) {
      loadActiveServices();
      loadProfessionalServices();
    }
  }, [activeSection, currentBusiness?.id, user?.canPerformServices, loadActiveServices, loadProfessionalServices]);
  
  // Reset refs when business changes
  useEffect(() => {
    rolesLoadedRef.current = false;
    businessInfoLoadedRef.current = false;
    brandingInfoLoadedRef.current = false;
    giftCardsLoadedRef.current = false;
    servicesLoadedRef.current = false;
    operatingHoursLoadedRef.current = false;
    operatingHoursLoadedForCalendarRef.current = false;
    notificationsLoadedRef.current = false;
  }, [currentBusiness?.id]);

  const calendarOperatingHourRange = useMemo(() => {
    // Use earliest open + latest close across the week (non-closed days)
    if (!operatingHours || operatingHours.length === 0) return undefined;

    const parseTimeToMinutes = (t?: string | null) => {
      if (!t) return null;
      const parts = t.split(':');
      if (parts.length < 2) return null;
      const hh = Number(parts[0]);
      const mm = Number(parts[1]);
      if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
      if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
      return hh * 60 + mm;
    };

    let minOpenMinutes: number | null = null;
    let maxCloseMinutes: number | null = null;

    for (const day of operatingHours) {
      if (day.isClosed) continue;
      const open = parseTimeToMinutes(day.openTime);
      const close = parseTimeToMinutes(day.closeTime);
      if (open == null || close == null) continue;
      // Ignore overnight ranges for now (close <= open)
      if (close <= open) continue;
      minOpenMinutes = minOpenMinutes == null ? open : Math.min(minOpenMinutes, open);
      maxCloseMinutes = maxCloseMinutes == null ? close : Math.max(maxCloseMinutes, close);
    }

    if (minOpenMinutes == null || maxCloseMinutes == null) return undefined;

    const startHour = Math.max(0, Math.min(23, Math.floor(minOpenMinutes / 60)));
    // endHour is the last visible hour label; floor(close/60) keeps the row that contains close minutes visible.
    const endHour = Math.max(startHour, Math.min(23, Math.floor(maxCloseMinutes / 60)));
    return { startHour, endHour };
  }, [operatingHours]);

  const getFilteredRoles = () => {
    let filtered = roles;

    if (roleFilters.isActive !== undefined) {
      filtered = filtered.filter(role => role.isActive === roleFilters.isActive);
    }

    if (roleFilters.search) {
      const searchLower = roleFilters.search.toLowerCase();
      filtered = filtered.filter(role =>
        role.role.toLowerCase().includes(searchLower) ||
        (role.permissions && role.permissions.join(' ').toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  };

  const getFilteredServices = () => {
    // Exclude soft-deleted services from Services Management UI.
    let filtered = services.filter((s: any) => s?.deleted !== true);

    if (filters.category_id) {
      filtered = filtered.filter(service => service.category?.id === filters.category_id);
    }

    if (filters.isActive !== undefined) {
      filtered = filtered.filter(service => service.isActive === filters.isActive);
    }

    // Apply fuzzy search if query exists
    if (filters.search && filters.search.trim()) {
      filtered = searchServices(filtered, filters.search);
    }

    if (filters.addOnsOnly) {
      filtered = filtered.filter(service => isServiceAddOn(service));
    }

    return filtered;
  };

  // Get unique categories from the services table (before category filtering)
  const getUniqueCategoriesFromServices = () => {
    const uniqueCategoryIds = new Set<string>();
    const uniqueCategories: { id: string; name: string }[] = [];

    // Get all services (before category filtering)
    let allServices = services.filter((s: any) => s?.deleted !== true);

    // Apply other filters except category_id
    if (filters.isActive !== undefined) {
      allServices = allServices.filter(service => service.isActive === filters.isActive);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      allServices = allServices.filter(service =>
        service.name.toLowerCase().includes(searchLower) ||
        (service.description && service.description.toLowerCase().includes(searchLower))
      );
    }

    if (filters.addOnsOnly) {
      allServices = allServices.filter(service => isServiceAddOn(service));
    }

    allServices.forEach(service => {
      if (service.category?.id) {
        uniqueCategoryIds.add(service.category.id);
      }
    });

    uniqueCategoryIds.forEach(categoryId => {
      const category = categories.find(cat => cat.id === categoryId);
      if (category) {
        uniqueCategories.push(category);
      }
    });

    // Sort by name for consistent ordering
    return uniqueCategories.sort((a, b) => a.name.localeCompare(b.name));
  };

  const getCategoryName = (categoryId: string) => {
    if (!categoryId) return 'Uncategorized';
    return categories.find(cat => cat.id === categoryId)?.name || 'Unknown';
  };

  const getProfessionalNames = (professionalIds?: string[]) => {
    if (!professionalIds || !Array.isArray(professionalIds)) {
      return [];
    }
    return professionalIds
      .map(id => professionals.find(prof => prof.id === id)?.name)
      .filter(Boolean) as string[];
  };

  const getFilteredProfessionals = () => {
    const filtered = professionals.filter(professional => {
      // Search filter
      if (professionalFilters.search) {
        const searchTerm = professionalFilters.search.toLowerCase();
        const nameMatch = professional.name.toLowerCase().includes(searchTerm);
        const emailMatch = professional.email.toLowerCase().includes(searchTerm);
        if (!nameMatch && !emailMatch) return false;
      }

      // Status filter
      if (professionalFilters.isActive !== undefined) {
        if (professional.isActive !== professionalFilters.isActive) return false;
      }

      return true;
    });

    // Sort by priority first (if available), then by role, then by name
    return filtered.sort((a, b) => {
      // First, sort by display priority if both have priorities
      if (a.priority !== null && a.priority !== undefined && b.priority !== null && b.priority !== undefined) {
        return a.priority - b.priority;
      }
      
      // If only one has priority, prioritize it
      if (a.priority !== null && a.priority !== undefined) {
        return -1;
      }
      if (b.priority !== null && b.priority !== undefined) {
        return 1;
      }
      
      // If neither has priority, sort by role (Owner first, then Professional), then by name
      const getRolePriority = (role?: string) => {
        if (role === 'Business Owner') return 1;
        if (role === 'Professional') return 2;
        return 3;
      };

      const roleA = getRolePriority(a.role);
      const roleB = getRolePriority(b.role);

      // Sort by role
      if (roleA !== roleB) {
        return roleA - roleB;
      }

      // Then sort by name alphabetically
      return a.name.localeCompare(b.name);
    });
  };

  const renderSectionContent = () => {
    // Helper function to get notification icon and color based on category and type
    const getNotificationIcon = (category?: string, type?: string) => {
      if (type === 'QUOTE_REQUEST_SUBMITTED' || type === 'QUOTE_OFFER_ACCEPTED') {
        return { icon: 'bi bi-file-earmark-text', color: type === 'QUOTE_OFFER_ACCEPTED' ? 'text-green-600' : 'text-indigo-600' };
      }
      if (type === 'INVOICE_PAYMENT_RECEIVED') {
        return { icon: 'bi bi-currency-dollar', color: 'text-emerald-600' };
      }
      if (type === 'APPOINTMENT_DEPOSIT_RECEIVED') {
        return { icon: 'bi bi-wallet2', color: 'text-emerald-700' };
      }
      if (type === 'APPOINTMENT_DEPOSIT_REQUESTED') {
        return { icon: 'bi bi-currency-dollar', color: 'text-amber-600' };
      }
      // Check for cancelled appointments first
      if (type === 'APPOINTMENT_CANCELLED') {
        return { icon: 'bi bi-calendar-x', color: 'text-red-600' };
      }
      
      // Check for rescheduled appointments
      if (type === 'APPOINTMENT_RESCHEDULED') {
        return { icon: 'bi bi-calendar-event', color: 'text-orange-600' };
      }
      
      switch (category) {
        case 'APPOINTMENT':
          return { icon: 'bi bi-calendar2-check', color: 'text-[#0B4778]' };
        case 'REVIEW':
          return { icon: 'bi bi-star', color: 'text-green-600' };
        case 'SERVICE':
          return { icon: 'bi bi-tools', color: 'text-teal-600' };
        case 'ALERT':
          return { icon: 'bi bi-alarm', color: 'text-pink-600' };
        default:
          return { icon: 'bi bi-bell', color: 'text-amber-500' };
      }
    };

    // Helper functions for date formatting - available to all sections
    const formatDateTime = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    const formatTime = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };

    /** Time string: single time or "Between x – y" when flex-only (no firm scheduledAt) */
    const getAppointmentTimeDisplay = (appointment: any) => {
      if (appointment?.scheduledAt) {
        return formatTime(appointment.scheduledAt);
      }
      const flex = appointment?.flexScheduledAt;
      if (flex?.earliest && flex?.latest) {
        return `Between ${formatTime(flex.earliest)} – ${formatTime(flex.latest)}`;
      }
      const iso = getAppointmentStartIso(appointment);
      return iso ? formatTime(iso) : '—';
    };

    /** One-line date/time: for flex-only shows "Date Between x – y" */
    const getAppointmentDateTimeDisplay = (appointment: any) => {
      if (appointment?.scheduledAt) {
        return formatDateTime(appointment.scheduledAt);
      }
      const flex = appointment?.flexScheduledAt;
      if (flex?.earliest && flex?.latest) {
        return `${formatDate(flex.earliest)} Between ${formatTime(flex.earliest)} – ${formatTime(flex.latest)}`;
      }
      const startIso = getAppointmentStartIso(appointment);
      if (!startIso) return 'Date TBD';
      return formatDateTime(startIso);
    };

    const getStatusColor = (status: string) => {
      switch (status) {
        case 'CONFIRMED': return 'bg-green-100 text-green-800';
        case 'PENDING': return 'bg-yellow-100 text-yellow-800';
        case 'CANCELLED': return 'bg-red-100 text-red-800';
        case 'COMPLETED': return 'bg-[#0B4778]/20 text-[#0B4778]';
        default: return 'bg-gray-100 text-gray-800';
      }
    };

    // Helper: resolved icon comes from the appointments API (service.icon -> category.icon -> null)
    const getAssignmentIcon = (assignment: any): string | null => {
      const icon = assignment?.serviceIcon;
      if (typeof icon === 'string' && icon.trim() !== '') return icon;
      return null;
    };

    // Helper function to get service names from assignments
    const getServiceNames = (appointment: any) => {
      if (!appointment || !appointment.assignments || appointment.assignments.length === 0) {
        return 'No services assigned';
      }
      return appointment.assignments.map((assignment: any) => assignment.serviceName || 'Unknown Service').join(', ');
    };

    // Helper function to get total duration from assignments (window duration when per-assignment times exist)
    const getTotalDuration = (appointment: any) => getTotalDurationHelper(appointment);

    // Helper function to get total price from assignments
    const getTotalPrice = (appointment: any) => {
      if (!appointment || !appointment.assignments || appointment.assignments.length === 0) {
        return 0;
      }
      return appointment.assignments.reduce((total: number, assignment: any) => {
        // Handle both string and number price values
        let price = 0;
        if (typeof assignment.price === 'number') {
          price = assignment.price;
        } else if (typeof assignment.price === 'string') {
          price = parseFloat(assignment.price) || 0;
        }
        return total + price;
      }, 0);
    };

    // Helper function to get professional names from assignments
    const getProfessionalNames = (appointment: any) => {
      if (!appointment || !appointment.assignments || appointment.assignments.length === 0) {
        return '';
      }
      const professionals = appointment.assignments
        .map((assignment: any) => assignment.professionalFirstName && assignment.professionalLastName 
          ? `${assignment.professionalFirstName} ${assignment.professionalLastName}` 
          : null)
        .filter(Boolean);
      return Array.from(new Set(professionals)).join(', ');
    };

    // Helper function to check if appointment needs manual assignment
    const needsManualAssignment = (appointment: Appointment): boolean => {
      if (!appointment || !appointment.assignments || appointment.assignments.length === 0) {
        return false;
      }
      // Check if any assignment is missing a professionalId
      return appointment.assignments.some((assignment: any) => !assignment.professionalId);
    };

    switch (activeSection) {
      case 'invoices':
        return <InvoicesSection />;

      case 'quote-requests':
        return (
          <QuoteRequestsSection
            offerSettingsModalOpen={isQuoteOfferSettingsModalOpen}
            onOfferSettingsModalOpenChange={setIsQuoteOfferSettingsModalOpen}
          />
        );

      case 'services':
        const filteredServices = getFilteredServices();
        const canAddService = !categoriesLoading && categories.length > 0;
        
        return (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Services Management</h2>
                <p className="text-gray-600 mt-1">Create and manage your business services and offerings</p>
              </div>
              <div className="flex gap-3 flex-nowrap w-full sm:w-auto">
                <button 
                  onClick={() => navigate(buildServicesUrl('/categories', { from: 'services' }))}
                  className="aacore-btn2-secondary flex-1 min-w-0 sm:flex-none overflow-hidden"
                >
                  <i className="bi bi-tags flex-shrink-0"></i>
                  <span className="truncate">Categories ({categories.length})</span>
                </button>
                <button 
                  onClick={handleAddService}
                  disabled={!canAddService}
                  title={!canAddService ? 'Create a category before adding a service' : undefined}
                  className="aacore-btn2-primary flex-1 min-w-0 sm:flex-none whitespace-nowrap"
                >
                  <Plus className="w-4 h-4" />
                  <span className="md:hidden">New Service</span>
                  <span className="hidden md:inline">Add New Service</span>
                </button>
              </div>
            </div>

            {/* Mobile: collapsible filters */}
            <div className="md:hidden">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    const nextOpen = !isServicesFilterSectionOpen;
                    if (!nextOpen) {
                      setServicesCategoryDropdownOpen(false);
                      setServicesStatusDropdownOpen(false);
                    }
                    setIsServicesFilterSectionOpen(nextOpen);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 bg-[#0B4778] text-white hover:bg-[#093a63] transition-colors ${
                    isServicesFilterSectionOpen ? 'rounded-t-lg' : 'rounded-lg'
                  }`}
                  aria-expanded={isServicesFilterSectionOpen}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <SlidersHorizontal className="w-4 h-4" />
                    Filters
                  </span>
                  <span className="flex items-center gap-2 text-sm font-medium">
                    {filteredServices.length} service{filteredServices.length !== 1 ? 's' : ''}
                    <ChevronDown className={`w-5 h-5 text-white/90 transition-transform ${isServicesFilterSectionOpen ? 'rotate-180' : ''}`} />
                  </span>
                </button>

                {isServicesFilterSectionOpen && (
                  <div className="p-4 space-y-3 border-t border-gray-200 rounded-b-lg">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search services..."
                        value={filters.search || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        className={`w-full pl-10 ${filters.search ? 'pr-10' : 'pr-3'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]`}
                      />
                      {filters.search && (
                        <button
                          type="button"
                          onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                          aria-label="Clear search"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="relative services-category-dropdown-container">
                      <button
                        type="button"
                        onClick={() => setServicesCategoryDropdownOpen(!servicesCategoryDropdownOpen)}
                        className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-gray-900">
                          {filters.category_id
                            ? categories.find(c => c.id === filters.category_id)?.name || 'All Categories'
                            : 'All Categories'}
                        </span>
                      </button>
                      {servicesCategoryDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setFilters(prev => ({ ...prev, category_id: undefined }));
                              setServicesCategoryDropdownOpen(false);
                            }}
                          >
                            All Categories
                          </div>
                          <div className="border-t border-gray-200 my-1"></div>
                          {getUniqueCategoriesFromServices().map(category => (
                            <div
                              key={category.id}
                              className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setFilters(prev => ({ ...prev, category_id: category.id }));
                                setServicesCategoryDropdownOpen(false);
                              }}
                            >
                              {category.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="relative services-status-dropdown-container">
                      <button
                        type="button"
                        onClick={() => setServicesStatusDropdownOpen(!servicesStatusDropdownOpen)}
                        className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-gray-900">
                          {filters.isActive === undefined
                            ? 'All Status'
                            : filters.isActive
                              ? 'Active'
                              : 'Inactive'}
                        </span>
                      </button>
                      {servicesStatusDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setFilters(prev => ({ ...prev, isActive: undefined }));
                              setServicesStatusDropdownOpen(false);
                            }}
                          >
                            All Status
                          </div>
                          <div className="border-t border-gray-200 my-1"></div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setFilters(prev => ({ ...prev, isActive: true }));
                              setServicesStatusDropdownOpen(false);
                            }}
                          >
                            Active
                          </div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setFilters(prev => ({ ...prev, isActive: false }));
                              setServicesStatusDropdownOpen(false);
                            }}
                          >
                            Inactive
                          </div>
                        </div>
                      )}
                    </div>

                    <label className="inline-flex items-center gap-2 w-full cursor-pointer select-none px-3 py-2 border border-gray-300 rounded-lg bg-white">
                      <input
                        type="checkbox"
                        checked={Boolean(filters.addOnsOnly)}
                        onChange={(e) =>
                          setFilters(prev => ({ ...prev, addOnsOnly: e.target.checked }))
                        }
                        className="rounded border-gray-300 text-[#0B4778] focus:ring-[#0B4778]"
                      />
                      <span className="text-sm text-gray-900 whitespace-nowrap">Add-ons only</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Desktop: always-visible filters */}
            <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Search and Filters */}
                <div className="flex-1 flex flex-wrap gap-3 items-center">
                  <div className="w-full md:max-w-md relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search services..."
                      value={filters.search || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      className={`w-full pl-10 ${filters.search ? 'pr-10' : 'pr-3'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]`}
                    />
                    {filters.search && (
                      <button
                        type="button"
                        onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                        aria-label="Clear search"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  <div className="w-full md:w-auto flex flex-col gap-2 md:flex-row md:flex-nowrap md:items-center md:gap-3">
                  <div className="flex gap-3 w-full md:w-auto flex-nowrap">
                    <div className="relative services-category-dropdown-container flex-1 md:flex-none md:w-[11rem]">
                      <button
                        type="button"
                        onClick={() => setServicesCategoryDropdownOpen(!servicesCategoryDropdownOpen)}
                        className="w-full md:w-[11rem] px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-gray-900 truncate">
                          {filters.category_id 
                            ? categories.find(c => c.id === filters.category_id)?.name || 'All Categories'
                            : 'All Categories'
                          }
                        </span>
                      </button>
                      
                      {servicesCategoryDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setFilters(prev => ({ ...prev, category_id: undefined }));
                              setServicesCategoryDropdownOpen(false);
                            }}
                          >
                            All Categories
                          </div>
                          <div className="border-t border-gray-200 my-1"></div>
                          {getUniqueCategoriesFromServices().map(category => (
                            <div
                              key={category.id}
                              className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setFilters(prev => ({ ...prev, category_id: category.id }));
                                setServicesCategoryDropdownOpen(false);
                              }}
                            >
                              {category.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="relative services-status-dropdown-container flex-1 md:flex-none md:w-[9rem]">
                      <button
                        type="button"
                        onClick={() => setServicesStatusDropdownOpen(!servicesStatusDropdownOpen)}
                        className="w-full md:w-[9rem] px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-gray-900 truncate">
                          {filters.isActive === undefined 
                            ? 'All Status'
                            : filters.isActive 
                              ? 'Active' 
                              : 'Inactive'
                          }
                        </span>
                      </button>
                      
                      {servicesStatusDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setFilters(prev => ({ ...prev, isActive: undefined }));
                              setServicesStatusDropdownOpen(false);
                            }}
                          >
                            All Status
                          </div>
                          <div className="border-t border-gray-200 my-1"></div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setFilters(prev => ({ ...prev, isActive: true }));
                              setServicesStatusDropdownOpen(false);
                            }}
                          >
                            Active
                          </div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setFilters(prev => ({ ...prev, isActive: false }));
                              setServicesStatusDropdownOpen(false);
                            }}
                          >
                            Inactive
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                    <label className="inline-flex items-center gap-2 w-full md:w-auto cursor-pointer select-none md:shrink-0 md:px-3 md:py-2 md:border md:border-gray-300 md:rounded-lg md:bg-white">
                      <input
                        type="checkbox"
                        checked={Boolean(filters.addOnsOnly)}
                        onChange={(e) =>
                          setFilters(prev => ({ ...prev, addOnsOnly: e.target.checked }))
                        }
                        className="rounded border-gray-300 text-[#0B4778] focus:ring-[#0B4778]"
                      />
                      <span className="text-sm text-gray-900 whitespace-nowrap">Add-ons only</span>
                    </label>
                  </div>
                </div>

                {/* View Toggle Buttons */}
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setViewMode('grid')}
                    title="Grid View"
                    className={`${viewMode === 'grid' ? 'aacore-btn2-primary' : 'aacore-btn2-outline bg-gray-100 hover:bg-gray-200'}`}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => setViewMode('list')}
                    title="List View"
                    className={`${viewMode === 'list' ? 'aacore-btn2-primary' : 'aacore-btn2-outline bg-gray-100 hover:bg-gray-200'}`}
                  >
                    <Table className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Services Content */}
            {filteredServices.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 text-center text-gray-500">
                  <span className="text-4xl mb-4 block">🛠️</span>
                  <p className="text-lg font-medium">
                    {services.length === 0 ? 'No services added yet' : 'No services match your filters'}
                  </p>
                  <p className="text-sm">
                    {services.length === 0 
                      ? (
                        categories.length === 0 ? (
                          <>
                            Create your first <strong>category</strong> (e.g., Manicure, Pedicure) to organize offerings, then add your first service to start accepting appointments.
                          </>
                        ) : (
                          'Add your first service to start accepting appointments.'
                        )
                      )
                      : 'Try adjusting your search or filter criteria.'
                    }
                  </p>
                  {filteredServices.length === 0 && (
                    <div className="flex items-center justify-center gap-3 mt-4">
                      {categories.length === 0 && (
                        <button
                          onClick={() => navigate(buildServicesUrl('/categories', { from: 'services' }))}
                          className="aacore-btn2-secondary"
                        >
                          <Plus className="w-4 h-4" />
                          <span className="md:hidden">Category</span>
                          <span className="hidden md:inline">Add First Category</span>
                        </button>
                      )}
                      <button
                        onClick={handleAddService}
                        disabled={!canAddService}
                        title={!canAddService ? 'Create a category before adding a service' : undefined}
                        className="aacore-btn2-primary"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="md:hidden">Service</span>
                        <span className="hidden md:inline">Add First Service</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              (viewMode === 'grid' || isMobile) ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredServices.map(service => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      onEdit={handleEditService}
                      onDuplicate={handleDuplicateService}
                      onToggleActive={handleToggleServiceActive}
                      onDelete={handleDeleteService}
                      categoryName={getCategoryName(service.category?.id || '')}
                      professionalNames={[]}
                    />
                  ))}
                </div>
                ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-visible w-full">
                  <div className="overflow-x-auto w-full">
                    <table className="w-full divide-y divide-gray-200 border border-gray-200 border-collapse">
                      <thead className="bg-gray-50">
                        <tr className="divide-x divide-gray-200">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Service
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Category
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Duration
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredServices.map(service => {
                          const serviceIcon = (service as any).icon || service.icon;
                          const categoryIcon = service.category?.icon;
                          const resolvedIcon = (typeof serviceIcon === 'string' && serviceIcon.trim() !== '')
                            ? serviceIcon
                            : (typeof categoryIcon === 'string' && categoryIcon.trim() !== '')
                              ? categoryIcon
                              : null;
                          
                          return (
                          <tr
                            key={service.id}
                            className="hover:bg-gray-50 relative divide-x divide-gray-200"
                            style={{ zIndex: serviceActionsDropdownOpen === service.id ? 1000 : undefined }}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                {/* Service Icon */}
                                <div className="flex-shrink-0 flex flex-col">
                                  <div 
                                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: '#3b82f615' }}
                                  >
                                    {resolvedIcon ? (
                                      <span className="text-xl" style={{ color: '#3b82f6' }}>
                                        {resolvedIcon}
                                      </span>
                                    ) : (
                                      <i 
                                        className="bi bi-star-fill"
                                        style={{ 
                                          fontSize: '20px',
                                          color: '#FBBF24'
                                        }}
                                      ></i>
                                    )}
                                  </div>
                                  {/* Add-on Badge - aligned with icon */}
                                  {((service as any).isAddOn || (service as any).is_add_on) && (
                                    <div className="mt-1">
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                        Add-on
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900">{service.name}</div>
                                  <div className="text-sm text-gray-500 truncate max-w-xs mt-1">{service.description}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0B4778]/20 text-[#0B4778]">
                                {getCategoryName(service.category?.id || '')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(service as any)?.quotable === true || service.price === null
                                ? 'Quotable'
                                : `$${typeof service.price === 'number'
                                    ? service.price.toFixed(2)
                                    : parseFloat((service as any).price || '0').toFixed(2)
                                  }`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {service.durationMinutes} min
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                service.isActive 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {service.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="relative service-actions-dropdown-container z-50">
                                <button
                                  onClick={() => setServiceActionsDropdownOpen(
                                    serviceActionsDropdownOpen === service.id ? null : service.id
                                  )}
                                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors"
                                  title="Actions"
                                >
                                  <Settings className="w-4 h-4" />
                                </button>
                                
                                {serviceActionsDropdownOpen === service.id && (
                                  <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-[99999]">
                                    <div className="py-1">
                                      <button
                                        onClick={() => {
                                          handleEditService(service);
                                          setServiceActionsDropdownOpen(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                      >
                                        <Edit className="w-4 h-4" />
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleDuplicateService(service);
                                          setServiceActionsDropdownOpen(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                      >
                                        <Copy className="w-4 h-4" />
                                        Duplicate
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleToggleServiceActive(service);
                                          setServiceActionsDropdownOpen(null);
                                        }}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                                          service.isActive
                                            ? 'text-orange-600 hover:text-orange-700'
                                            : 'text-green-600 hover:text-green-700'
                                        }`}
                                      >
                                        <Power className="w-4 h-4" />
                                        {service.isActive ? 'Deactivate' : 'Activate'}
                                      </button>
                                      <div className="border-t border-gray-100 my-1" />
                                      <button
                                        onClick={() => {
                                          handleDeleteService(service);
                                          setServiceActionsDropdownOpen(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            )}


          </div>
        );
        
      case 'roles':
        const filteredRoles = getFilteredRoles();

        return (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Roles and Access</h2>
                <p className="text-gray-600 mt-1">Define user roles and control access permissions</p>
              </div>
              <button
                onClick={handleAddRole}
                className="aacore-btn2-primary"
              >
                <Plus className="w-4 h-4" />
                Add New Role
              </button>
            </div>

            {/* Mobile: collapsible filters */}
            <div className="md:hidden">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    const nextOpen = !isRolesFilterSectionOpen;
                    if (!nextOpen) {
                      setRolesStatusDropdownOpen(false);
                    }
                    setIsRolesFilterSectionOpen(nextOpen);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 bg-[#0B4778] text-white hover:bg-[#093a63] transition-colors ${
                    isRolesFilterSectionOpen ? 'rounded-t-lg' : 'rounded-lg'
                  }`}
                  aria-expanded={isRolesFilterSectionOpen}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <SlidersHorizontal className="w-4 h-4" />
                    Filters
                  </span>
                  <span className="flex items-center gap-2 text-sm font-medium">
                    {filteredRoles.length} role{filteredRoles.length !== 1 ? 's' : ''}
                    <ChevronDown className={`w-5 h-5 text-white/90 transition-transform ${isRolesFilterSectionOpen ? 'rotate-180' : ''}`} />
                  </span>
                </button>

                {isRolesFilterSectionOpen && (
                  <div className="p-4 space-y-3 border-t border-gray-200 rounded-b-lg">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search roles..."
                        value={roleFilters.search || ''}
                        onChange={(e) => setRoleFilters(prev => ({ ...prev, search: e.target.value }))}
                        className={`w-full pl-10 ${roleFilters.search ? 'pr-10' : 'pr-3'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]`}
                      />
                      {roleFilters.search && (
                        <button
                          type="button"
                          onClick={() => setRoleFilters(prev => ({ ...prev, search: '' }))}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                          aria-label="Clear search"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="relative roles-status-dropdown-container">
                      <button
                        type="button"
                        onClick={() => setRolesStatusDropdownOpen(!rolesStatusDropdownOpen)}
                        className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-gray-900">
                          {roleFilters.isActive === undefined
                            ? 'All Status'
                            : roleFilters.isActive
                              ? 'Active'
                              : 'Inactive'}
                        </span>
                      </button>

                      {rolesStatusDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setRoleFilters(prev => ({ ...prev, isActive: undefined }));
                              setRolesStatusDropdownOpen(false);
                            }}
                          >
                            All Status
                          </div>
                          <div className="border-t border-gray-200 my-1"></div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setRoleFilters(prev => ({ ...prev, isActive: true }));
                              setRolesStatusDropdownOpen(false);
                            }}
                          >
                            Active
                          </div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setRoleFilters(prev => ({ ...prev, isActive: false }));
                              setRolesStatusDropdownOpen(false);
                            }}
                          >
                            Inactive
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Desktop: always-visible filters */}
            <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col lg:flex-row gap-3">
                {/* Search */}
                <div className="flex-1 lg:max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search roles..."
                      value={roleFilters.search || ''}
                      onChange={(e) => setRoleFilters(prev => ({ ...prev, search: e.target.value }))}
                      className={`w-full pl-10 ${roleFilters.search ? 'pr-10' : 'pr-3'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]`}
                    />
                    {roleFilters.search && (
                      <button
                        type="button"
                        onClick={() => setRoleFilters(prev => ({ ...prev, search: '' }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                        aria-label="Clear search"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Status Filter */}
                <div className="w-full lg:w-auto">
                  <div className="relative roles-status-dropdown-container">
                    <button
                      type="button"
                      onClick={() => setRolesStatusDropdownOpen(!rolesStatusDropdownOpen)}
                      className="w-full lg:w-auto px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em'
                      }}
                    >
                      <span className="text-gray-900">
                        {roleFilters.isActive === undefined 
                          ? 'All Status'
                          : roleFilters.isActive 
                            ? 'Active' 
                            : 'Inactive'
                        }
                      </span>
                    </button>
                    
                    {rolesStatusDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                        <div
                          className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setRoleFilters(prev => ({ ...prev, isActive: undefined }));
                            setRolesStatusDropdownOpen(false);
                          }}
                        >
                          All Status
                        </div>
                        <div className="border-t border-gray-200 my-1"></div>
                        <div
                          className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setRoleFilters(prev => ({ ...prev, isActive: true }));
                            setRolesStatusDropdownOpen(false);
                          }}
                        >
                          Active
                        </div>
                        <div
                          className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setRoleFilters(prev => ({ ...prev, isActive: false }));
                            setRolesStatusDropdownOpen(false);
                          }}
                        >
                          Inactive
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Roles Content */}
            {rolesLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                  <p className="text-lg font-medium">Loading roles...</p>
                  <p className="text-sm">Fetching role data from database</p>
                </div>
              </div>
            ) : filteredRoles.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 text-center text-gray-500">
                  <span className="text-4xl mb-4 block">👥</span>
                  <p className="text-lg font-medium">
                    {roles.length === 0 ? 'No roles created yet' : 'No roles match your filters'}
                  </p>
                  <p className="text-sm">
                    {roles.length === 0
                      ? 'Start by creating your first professional role to define permissions.'
                      : 'Try adjusting your search or filter criteria.'
                    }
                  </p>
                  {roles.length === 0 && (
                    <button
                      onClick={handleAddRole}
                      className="aacore-btn2-primary mt-4"
                    >
                      Create Your First Role
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredRoles.map(role => (
                  <RoleCard
                    key={role.id}
                    role={role}
                    onEdit={handleEditRole}
                    onToggleActive={handleToggleRoleActive}
                    onDelete={handleDeleteRole}
                  />
                ))}
              </div>
            )}
          </div>
        );
        
      case 'professionals':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Personnel</h2>
                <p className="text-gray-600 mt-1">Manage your team members and their service permissions</p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <button 
                  onClick={handleInviteProfessional}
                  className="aacore-btn2-primary w-full sm:w-auto"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>Invite Personnel</span>
                </button>
                {isBusinessOwnerForBusiness(user, currentBusiness?.id || '') && (
                  <button 
                    onClick={() => setShowDisplayPriorityModal(true)}
                    className="hidden md:flex aacore-btn2-outline"
                  >
                    <ListOrdered className="w-4 h-4" />
                    <span>Display Order</span>
                  </button>
                )}
              </div>
            </div>
            
            {/* Invitations List */}
            <InvitationsList
              invitations={invitations}
              roles={roles}
              businessId={currentBusiness?.id || ''}
              onRefresh={loadInvitations}
            />
            
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Search and Status Filter */}
                <div className="flex-1 flex flex-wrap gap-3 items-center">
                  <div className="w-full md:max-w-md relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search personnel..."
                      value={professionalFilters.search || ''}
                      onChange={(e) => setProfessionalFilters(prev => ({ 
                        ...prev, 
                        search: e.target.value 
                      }))}
                      className={`w-full pl-10 ${professionalFilters.search ? 'pr-10' : 'pr-3'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]`}
                    />
                    {professionalFilters.search && (
                      <button
                        type="button"
                        onClick={() => setProfessionalFilters(prev => ({ ...prev, search: '' }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                        aria-label="Clear search"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  <div className="relative w-full md:w-auto personnel-status-dropdown-container">
                    <button
                      type="button"
                      onClick={() => setPersonnelStatusDropdownOpen(!personnelStatusDropdownOpen)}
                      className="w-full md:w-auto px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em'
                      }}
                    >
                      <span className="text-gray-900">
                        {professionalFilters.isActive === undefined 
                          ? 'All Status'
                          : professionalFilters.isActive 
                            ? 'Active' 
                            : 'Inactive'
                        }
                      </span>
                    </button>
                    
                    {personnelStatusDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                        <div
                          className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setProfessionalFilters(prev => ({ ...prev, isActive: undefined }));
                            setPersonnelStatusDropdownOpen(false);
                          }}
                        >
                          All Status
                        </div>
                        <div className="border-t border-gray-200 my-1"></div>
                        <div
                          className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setProfessionalFilters(prev => ({ ...prev, isActive: true }));
                            setPersonnelStatusDropdownOpen(false);
                          }}
                        >
                          Active
                        </div>
                        <div
                          className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setProfessionalFilters(prev => ({ ...prev, isActive: false }));
                            setPersonnelStatusDropdownOpen(false);
                          }}
                        >
                          Inactive
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Professionals Content */}
            {professionalsLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                  <p className="text-lg font-medium">Loading personnel...</p>
                  <p className="text-sm">Fetching personnel data from database</p>
                </div>
              </div>
            ) : getFilteredProfessionals().length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getFilteredProfessionals().map((professional) => (
                  <ProfessionalCard
                    key={professional.id}
                    professional={professional}
                    onToggleActive={handleToggleProfessionalActive}
                    onToggleServicePermission={handleToggleServicePermission}
                    onDelete={handleDeleteProfessional}
                    onEdit={handleEditProfessional}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 text-center text-gray-500">
                  <span className="text-4xl mb-4 block">👨‍💼</span>
                  <p className="text-lg font-medium">
                    {professionals.length === 0 ? 'No personnel added yet' : 'No personnel match your filters'}
                  </p>
                  <p className="text-sm">
                    {professionals.length === 0 
                      ? 'Invite personnel to join your business and start accepting appointments.'
                      : 'Try adjusting your search or filter criteria.'
                    }
                  </p>
                  {professionals.length === 0 && (
                    <button
                      onClick={handleInviteProfessional}
                      className="aacore-btn2-primary mt-4"
                    >
                      Invite Your First Personnel
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
        
      case 'appointments':
        // Appointments are server-filtered by the active date range; don't re-filter client-side
        // or pagination totals will diverge from what we display.
        const displayAppointments = appointments;

        // Check if user is an owner (not a professional)
        const isOwner = !user?.canPerformServices || user?.roles?.some(role => 
          role.isActive && 
          role.permissions?.some(permission => 
            ['manage_roles', 'manage_professionals', 'manage_settings'].includes(permission)
          )
        );

        const appointmentsControlsContent = (
          <>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Appointments</h2>
                <p className="text-gray-600 mt-1">Manage and track all customer appointments</p>
              </div>
              <div className="flex items-center justify-between gap-2 w-full sm:w-auto sm:justify-start">
                <div className="flex items-center gap-2 flex-1 md:flex-none">
                  {isOwner && (
                    <button 
                      onClick={() => {
                        // If the appointments date range UI represents a single day, carry that date forward
                        // to the create-appointment flow so the date picker auto-selects it.
                        //
                        // IMPORTANT: We only prefill when the range is 1 day ("today" mode). If we cannot
                        // confidently determine the business is open (operating hours missing or closed),
                        // fall back to existing navigation to avoid changing business logic.
                        if (appointmentsDateRangeType === 'today') {
                          const { start } = getAppointmentsDateRange(appointmentsDateRangeType, appointmentsDateRangeStart);
                          const dayOfWeek = start.getDay();
                          const dayOperatingHours = operatingHours.find((h: any) => {
                            const dow = (h?.dayOfWeek ?? h?.day_of_week);
                            return Number(dow) === dayOfWeek;
                          });
                          const isClosed = dayOperatingHours
                            ? Boolean((dayOperatingHours as any).isClosed ?? (dayOperatingHours as any).is_closed)
                            : true;

                          if (dayOperatingHours && !isClosed) {
                            const dateStr = formatLocalYmd(start);
                            navigate(buildCreateAppointmentUrl({ date: dateStr, from: 'appointments' }));
                            return;
                          }
                        }

                        // Default behavior (unchanged)
                        navigate(buildCreateAppointmentUrl({ from: 'appointments' }));
                      }}
                      className="aacore-btn2-primary flex-1 md:flex-none"
                    >
                      <Plus className="w-4 h-4" />
                      Appointment
                    </button>
                  )}
                  {isOwner && (
                    <div className="hidden lg:block relative appointment-settings-dropdown-container">
                      <button 
                        onClick={() => setIsAppointmentSettingsDropdownOpen(!isAppointmentSettingsDropdownOpen)}
                        className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200 flex items-center gap-2"
                        title="Settings"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${isAppointmentSettingsDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isAppointmentSettingsDropdownOpen && (
                        <div className="absolute right-0 top-full mt-2 min-w-[220px] bg-white border border-gray-300 rounded-lg shadow-lg z-50 py-2">
                          <button
                            onClick={() => {
                              handleSectionChange('closures');
                              setIsAppointmentSettingsDropdownOpen(false);
                            }}
                            className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-medium text-gray-700 whitespace-nowrap"
                          >
                            <CalendarX className="w-4 h-4" />
                            Closures
                          </button>
                          <button
                            onClick={() => {
                              setIsAppointmentSettingsModalOpen(true);
                              setIsAppointmentSettingsDropdownOpen(false);
                            }}
                            className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-medium text-gray-700 whitespace-nowrap"
                          >
                            <Settings className="w-4 h-4" />
                            Settings
                          </button>
                          <button
                            onClick={() => {
                              handleSectionChange('statistics');
                              setIsAppointmentSettingsDropdownOpen(false);
                            }}
                            className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-medium text-gray-700 whitespace-nowrap"
                          >
                            <BarChart3 className="w-4 h-4" />
                            Statistics
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 mr-2">
                  <button 
                    onClick={() => handleViewChange('grid')}
                    title="Grid View"
                    className={`${appointmentView === 'grid' ? 'aacore-btn2-primary' : 'aacore-btn2-outline bg-gray-100 hover:bg-gray-200'}`}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleViewChange('table')}
                    title="Table View"
                    className={`hidden md:inline-flex ${appointmentView === 'table' ? 'aacore-btn2-primary' : 'aacore-btn2-outline bg-gray-100 hover:bg-gray-200'}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleViewChange('calendar')}
                    title="Calendar View"
                    className={`${appointmentView === 'calendar' ? 'aacore-btn2-primary' : 'aacore-btn2-outline bg-gray-100 hover:bg-gray-200'}`}
                  >
                    <Calendar className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Date Range Navigation and Filters */}
            {(appointmentView === 'grid' || appointmentView === 'table' || appointmentView === 'calendar') && (
              <div className="relative z-30 mt-4 max-md:mt-0 max-md:p-0 max-md:border-0 max-md:shadow-none max-md:bg-transparent md:bg-white md:rounded-lg md:shadow-sm md:border md:border-gray-200 md:p-4">
                {/* Desktop (xl and above): All controls on same line */}
                <div className="hidden xl:grid grid-cols-[auto_1fr_auto] items-center gap-3">
                  {/* Left side: Date Range Type Selector */}
                  <div className="flex items-center bg-gray-50 rounded-lg p-1 w-fit justify-self-start">
                    <button
                      onClick={() => handleAppointmentsDateRangeChange('today')}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                        appointmentsDateRangeType === 'today'
                          ? 'bg-white text-[#0B4778] shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => handleAppointmentsDateRangeChange('3days')}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                        appointmentsDateRangeType === '3days'
                          ? 'bg-white text-[#0B4778] shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      3 Days
                    </button>
                    <button
                      onClick={() => handleAppointmentsDateRangeChange('1week')}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                        appointmentsDateRangeType === '1week'
                          ? 'bg-white text-[#0B4778] shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      1 Week
                    </button>
                  </div>

                  {/* Center: Date Range Navigation */}
                  <div className="flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm justify-self-center min-w-0">
                    <button
                      onClick={() => handleAppointmentsDateRangeNavigate('prev')}
                      className="p-1 hover:bg-gray-50 rounded transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center h-6 w-6"
                      title="Previous period"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="relative appointments-date-picker-container flex items-center z-40 min-w-0">
                      <button
                        type="button"
                        onClick={() => {
                          setIsAppointmentsDatePickerOpen(!isAppointmentsDatePickerOpen);
                          setAppointmentsDatePickerMonth(new Date(appointmentsDateRangeStart));
                        }}
                        className="flex items-center gap-2 px-3 hover:bg-gray-50 rounded transition-colors min-w-0"
                      >
                        <Calendar className="w-4 h-4 text-[#0B4778]" />
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {formatAppointmentsDateRange(appointmentsDateRangeType, appointmentsDateRangeStart)}
                        </span>
                      </button>
                      
                      {isAppointmentsDatePickerOpen && (
                        <>
                          <div className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden" onClick={() => setIsAppointmentsDatePickerOpen(false)}></div>
                          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 sm:absolute sm:inset-x-auto sm:top-full sm:translate-y-0 sm:left-0 sm:right-auto sm:mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-[100] min-w-[280px] w-auto sm:w-auto max-w-[calc(100vw-2rem)] sm:max-w-none">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-sm font-medium text-gray-900">Select Date</h3>
                              <button
                                onClick={() => setIsAppointmentsDatePickerOpen(false)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            
                            {/* Calendar navigation */}
                            <div className="flex items-center justify-between mb-4">
                              <button
                                onClick={() => {
                                  const newMonth = new Date(appointmentsDatePickerMonth);
                                  newMonth.setMonth(newMonth.getMonth() - 1);
                                  setAppointmentsDatePickerMonth(newMonth);
                                }}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                              >
                                <ChevronLeft className="w-4 h-4 text-gray-600" />
                              </button>
                              <span className="text-sm font-medium text-gray-900">
                                {appointmentsDatePickerMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                              </span>
                              <button
                                onClick={() => {
                                  const newMonth = new Date(appointmentsDatePickerMonth);
                                  newMonth.setMonth(newMonth.getMonth() + 1);
                                  setAppointmentsDatePickerMonth(newMonth);
                                }}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                              >
                                <ChevronRight className="w-4 h-4 text-gray-600" />
                              </button>
                            </div>
                            
                            {/* Calendar grid */}
                            <div className="grid grid-cols-7 gap-1 mb-2">
                              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                                  {day}
                                </div>
                              ))}
                            </div>
                            <div className="grid grid-cols-7 gap-1">
                              {generateProfessionalCalendarDays(
                                appointmentsDatePickerMonth.getFullYear(),
                                appointmentsDatePickerMonth.getMonth()
                              ).map((date, index) => {
                                if (!date) {
                                  return <div key={index} className="h-8"></div>;
                                }
                                
                                const { start } = getAppointmentsDateRange(appointmentsDateRangeType, appointmentsDateRangeStart);
                                const isSelected = date.toDateString() === start.toDateString();
                                const isToday = date.toDateString() === new Date().toDateString();
                                
                                return (
                                  <button
                                    key={index}
                                    onClick={() => handleAppointmentsDateSelect(date)}
                                    className={`h-8 rounded-md text-sm font-medium transition-all ${
                                      isSelected
                                        ? 'bg-[#0B4778] text-white shadow-sm'
                                        : isToday
                                        ? 'bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30'
                                        : 'hover:bg-gray-100 text-gray-900'
                                    }`}
                                  >
                                    {date.getDate()}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => handleAppointmentsDateRangeNavigate('next')}
                      className="p-1 hover:bg-gray-50 rounded transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center h-6 w-6"
                      title="Next period"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Right side: Status and Professional Filters */}
                  <div className="flex items-center gap-3 justify-self-end">
                    {/* Status Dropdown */}
                    <div className="relative status-dropdown-container">
                      <button
                        type="button"
                        onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                        className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between min-w-[140px]"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-gray-900 text-sm">
                          {appointmentFilters.status 
                            ? appointmentFilters.status.charAt(0) + appointmentFilters.status.slice(1).toLowerCase()
                            : 'All Status'
                          }
                        </span>
                      </button>
                      
                      {statusDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto right-0">
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleFilterChange('status', '');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            All Status
                          </div>
                          <div className="border-t border-gray-200 my-1"></div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleFilterChange('status', 'PENDING');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            Pending
                          </div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleFilterChange('status', 'CONFIRMED');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            Confirmed
                          </div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleFilterChange('status', 'COMPLETED');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            Completed
                          </div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleFilterChange('status', 'CANCELLED');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            Cancelled
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Professional Dropdown */}
                    <div className="relative professional-dropdown-container">
                      <button
                        type="button"
                        onClick={() => {
                          if (!professionalDropdownOpen && professionals.length === 0) {
                            loadProfessionals();
                          }
                          setProfessionalDropdownOpen(!professionalDropdownOpen);
                        }}
                        className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between min-w-[160px]"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-gray-900 text-sm">
                          {appointmentFilters.professionalId 
                            ? (() => {
                                const professional = professionals.find(p => p.id === appointmentFilters.professionalId || p.userId === appointmentFilters.professionalId);
                                return professional 
                                  ? professional.name || `${professional.firstName || professional.first_name || ''} ${professional.lastName || professional.last_name || ''}`
                                  : 'Professional not found';
                              })()
                            : 'All Professionals'
                          }
                        </span>
                      </button>
                      
                                            {professionalDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto right-0">
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleFilterChange('professionalId', '');
                              setProfessionalDropdownOpen(false);
                            }}
                          >
                            All Professionals
                          </div>
                          <div className="border-t border-gray-200 my-1"></div>
                          {professionals.filter((professional) => professional.canPerformServices).map((professional) => (
                            <div
                              key={professional.id}
                              className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                handleFilterChange('professionalId', professional.id);
                                setProfessionalDropdownOpen(false);
                              }}
                            >
                              {professional.name || `${professional.firstName || professional.first_name || ''} ${professional.lastName || professional.last_name || ''}`}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                      </div>

                {/* Mobile/Tablet (including iPad): Date Range Dropdown and Navigation */}
                <div className="xl:hidden space-y-3">
                  {/* Mobile-only layout:
                      Row 1: Professional (wide) + Status (narrow)
                      Row 2: Date range nav (wide) + Range type (narrow)
                  */}

                  {/* Row 1 (mobile): Status (left, narrow) + Professional (right, wide) */}
                  <div className="flex items-center gap-3 md:hidden">
                    {/* Status Dropdown (narrow, left) */}
                    <div className="relative status-dropdown-container flex-shrink-0 w-[7.5rem]">
                      <button
                        type="button"
                        onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                        className="w-full px-3 py-2.5 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between shadow-sm hover:border-gray-300 transition-colors"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-sm font-medium text-gray-900">
                          {appointmentFilters.status 
                            ? appointmentFilters.status.charAt(0) + appointmentFilters.status.slice(1).toLowerCase()
                            : 'All Status'
                          }
                        </span>
                      </button>
                      
                      {statusDropdownOpen && (
                        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto min-w-[120px] w-full">
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange('status', '');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            All Status
                      </div>
                          <div className="border-t border-gray-100 my-1"></div>
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange('status', 'PENDING');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            Pending
                          </div>
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange('status', 'CONFIRMED');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            Confirmed
                          </div>
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange('status', 'COMPLETED');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            Completed
                          </div>
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange('status', 'CANCELLED');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            Cancelled
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Professional Dropdown (wide, right) */}
                    <div className="relative flex-1 professional-dropdown-container">
                        <button
                          type="button"
                          onClick={() => {
                          if (!professionalDropdownOpen && professionals.length === 0) {
                            loadProfessionals();
                          }
                          setProfessionalDropdownOpen(!professionalDropdownOpen);
                        }}
                        className="w-full px-3 py-2.5 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between shadow-sm hover:border-gray-300 transition-colors"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-sm font-medium text-gray-900">
                          {appointmentFilters.professionalId 
                            ? (() => {
                                const professional = professionals.find(p => p.id === appointmentFilters.professionalId || p.userId === appointmentFilters.professionalId);
                                return professional 
                                  ? professional.name || `${professional.firstName || professional.first_name || ''} ${professional.lastName || professional.last_name || ''}`
                                  : 'Professional not found';
                              })()
                            : 'All Professionals'
                          }
                        </span>
                      </button>
                      
                      {professionalDropdownOpen && (
                        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto min-w-[150px] w-full">
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange('professionalId', '');
                            setProfessionalDropdownOpen(false);
                            }}
                          >
                            All Professionals
                          </div>
                          <div className="border-t border-gray-200 my-1"></div>
                          {professionals.map((professional) => (
                            <div
                              key={professional.id}
                              className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleFilterChange('professionalId', professional.id);
                                setProfessionalDropdownOpen(false);
                              }}
                            >
                              {professional.name || `${professional.firstName || professional.first_name || ''} ${professional.lastName || professional.last_name || ''}`}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tablet/iPad (md..xl): 2-up 50/50 layout */}
                  <div className="hidden md:grid xl:hidden grid-cols-2 gap-3 min-w-0">
                    {/* Status Dropdown */}
                    <div className="relative status-dropdown-container w-full min-w-0">
                      <button
                        type="button"
                        onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                        className="w-full px-3 py-2.5 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between shadow-sm hover:border-gray-300 transition-colors"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-sm font-medium text-gray-900">
                          {appointmentFilters.status 
                            ? appointmentFilters.status.charAt(0) + appointmentFilters.status.slice(1).toLowerCase()
                            : 'All Status'
                          }
                        </span>
                      </button>
                      
                      {statusDropdownOpen && (
                        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto min-w-[150px] w-full">
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange('status', '');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            All Status
                      </div>
                          <div className="border-t border-gray-100 my-1"></div>
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange('status', 'PENDING');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            Pending
                          </div>
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange('status', 'CONFIRMED');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            Confirmed
                          </div>
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange('status', 'COMPLETED');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            Completed
                          </div>
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange('status', 'CANCELLED');
                              setStatusDropdownOpen(false);
                            }}
                          >
                            Cancelled
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Professional Dropdown */}
                    <div className="relative professional-dropdown-container w-full min-w-0">
                        <button
                          type="button"
                          onClick={() => {
                          if (!professionalDropdownOpen && professionals.length === 0) {
                            loadProfessionals();
                          }
                          setProfessionalDropdownOpen(!professionalDropdownOpen);
                        }}
                        className="w-full px-3 py-2.5 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between shadow-sm hover:border-gray-300 transition-colors"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-sm font-medium text-gray-900">
                          {appointmentFilters.professionalId 
                            ? (() => {
                                const professional = professionals.find(p => p.id === appointmentFilters.professionalId || p.userId === appointmentFilters.professionalId);
                                return professional 
                                  ? professional.name || `${professional.firstName || professional.first_name || ''} ${professional.lastName || professional.last_name || ''}`
                                  : 'Professional not found';
                              })()
                            : 'All Professionals'
                          }
                        </span>
                      </button>
                      
                      {professionalDropdownOpen && (
                        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto min-w-[150px] w-full">
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange('professionalId', '');
                            setProfessionalDropdownOpen(false);
                            }}
                          >
                            All Professionals
                          </div>
                          <div className="border-t border-gray-200 my-1"></div>
                          {professionals.map((professional) => (
                            <div
                              key={professional.id}
                              className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleFilterChange('professionalId', professional.id);
                                setProfessionalDropdownOpen(false);
                              }}
                            >
                              {professional.name || `${professional.firstName || professional.first_name || ''} ${professional.lastName || professional.last_name || ''}`}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Row 2 (mobile): Range type (left, narrow) + Date range nav (right, wide) */}
                  <div className="flex items-center gap-3 md:hidden min-w-0">
                    {/* Date Range Dropdown (narrow, left) - min width so "1 Week" is not truncated */}
                    <div className="relative appointments-date-range-dropdown-container flex-shrink-0 w-[7.5rem]">
                      <button
                        type="button"
                        onClick={() => {
                          setAppointmentsDateRangeDropdownOpen(!appointmentsDateRangeDropdownOpen);
                        }}
                        className="w-full min-w-0 px-3 py-2.5 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between shadow-sm hover:border-gray-300 transition-colors"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                          {appointmentsDateRangeType === 'today' ? 'Today' :
                           appointmentsDateRangeType === '3days' ? '3 Days' :
                           appointmentsDateRangeType === '1week' ? '1 Week' : '3 Days'}
                        </span>
                      </button>
                      
                      {appointmentsDateRangeDropdownOpen && (
                        <div className="absolute z-[9999] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto min-w-[120px] w-full">
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAppointmentsDateRangeChange('today');
                              setAppointmentsDateRangeDropdownOpen(false);
                            }}
                          >
                            Today
                          </div>
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAppointmentsDateRangeChange('3days');
                              setAppointmentsDateRangeDropdownOpen(false);
                            }}
                          >
                            3 Days
                          </div>
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAppointmentsDateRangeChange('1week');
                              setAppointmentsDateRangeDropdownOpen(false);
                            }}
                          >
                            1 Week
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Date Range Navigation (wide, right) */}
                    <div className="flex-1 min-w-0 flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                      <button
                        onClick={() => handleAppointmentsDateRangeNavigate('prev')}
                        className="p-1 hover:bg-gray-50 rounded transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center h-6 w-6 flex-shrink-0"
                        title="Previous period"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <div className="relative appointments-date-picker-container flex items-center z-40 min-w-0">
                        <button
                          type="button"
                          onClick={() => {
                            setIsAppointmentsDatePickerOpen(!isAppointmentsDatePickerOpen);
                            setAppointmentsDatePickerMonth(new Date(appointmentsDateRangeStart));
                          }}
                          className="flex items-center px-3 hover:bg-gray-50 rounded transition-colors min-w-0"
                        >
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {formatAppointmentsDateRange(appointmentsDateRangeType, appointmentsDateRangeStart)}
                          </span>
                        </button>
                      
                      {isAppointmentsDatePickerOpen && (
                        <>
                          <div className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden" onClick={() => setIsAppointmentsDatePickerOpen(false)}></div>
                          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 sm:absolute sm:inset-x-auto sm:top-full sm:translate-y-0 sm:left-0 sm:right-auto sm:mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-[100] min-w-[280px] w-auto sm:w-auto max-w-[calc(100vw-2rem)] sm:max-w-none">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-sm font-medium text-gray-900">Select Date</h3>
                              <button
                                onClick={() => setIsAppointmentsDatePickerOpen(false)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            
                            {/* Calendar navigation */}
                            <div className="flex items-center justify-between mb-4">
                              <button
                                onClick={() => {
                                  const newMonth = new Date(appointmentsDatePickerMonth);
                                  newMonth.setMonth(newMonth.getMonth() - 1);
                                  setAppointmentsDatePickerMonth(newMonth);
                                }}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                              >
                                <ChevronLeft className="w-4 h-4 text-gray-600" />
                              </button>
                              <span className="text-sm font-medium text-gray-900">
                                {appointmentsDatePickerMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                              </span>
                              <button
                                onClick={() => {
                                  const newMonth = new Date(appointmentsDatePickerMonth);
                                  newMonth.setMonth(newMonth.getMonth() + 1);
                                  setAppointmentsDatePickerMonth(newMonth);
                                }}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                              >
                                <ChevronRight className="w-4 h-4 text-gray-600" />
                              </button>
                            </div>
                            
                            {/* Calendar grid */}
                            <div className="grid grid-cols-7 gap-1 mb-2">
                              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                                  {day}
                                </div>
                              ))}
                            </div>
                            <div className="grid grid-cols-7 gap-1">
                              {generateProfessionalCalendarDays(
                                appointmentsDatePickerMonth.getFullYear(),
                                appointmentsDatePickerMonth.getMonth()
                              ).map((date, index) => {
                                if (!date) {
                                  return <div key={index} className="h-8"></div>;
                                }
                                
                                const { start } = getAppointmentsDateRange(appointmentsDateRangeType, appointmentsDateRangeStart);
                                const isSelected = date.toDateString() === start.toDateString();
                                const isToday = date.toDateString() === new Date().toDateString();
                                
                                return (
                                  <button
                                    key={index}
                                    onClick={() => handleAppointmentsDateSelect(date)}
                                    className={`h-8 rounded-md text-sm font-medium transition-all ${
                                      isSelected
                                        ? 'bg-[#0B4778] text-white shadow-sm'
                                        : isToday
                                        ? 'bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30'
                                        : 'hover:bg-gray-100 text-gray-900'
                                    }`}
                                  >
                                    {date.getDate()}
                                  </button>
                                );
                              })}
                </div>
              </div>
                        </>
                      )}
                    </div>
                      <button
                        onClick={() => handleAppointmentsDateRangeNavigate('next')}
                        className="p-1 hover:bg-gray-50 rounded transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center h-6 w-6"
                        title="Next period"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Tablet/iPad (md..xl): show range type + date nav on same row (50/50) */}
                  <div className="hidden md:grid xl:hidden grid-cols-2 gap-3 min-w-0">
                    {/* Date Range Dropdown (left) */}
                    <div className="relative appointments-date-range-dropdown-container w-full min-w-0">
                      <button
                        type="button"
                        onClick={() => {
                          setAppointmentsDateRangeDropdownOpen(!appointmentsDateRangeDropdownOpen);
                        }}
                        className="w-full min-w-0 px-3 py-2.5 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between shadow-sm hover:border-gray-300 transition-colors"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                          {appointmentsDateRangeType === 'today' ? 'Today' :
                           appointmentsDateRangeType === '3days' ? '3 Days' :
                           appointmentsDateRangeType === '1week' ? '1 Week' : '3 Days'}
                        </span>
                      </button>
                      
                      {appointmentsDateRangeDropdownOpen && (
                        <div className="absolute z-[9999] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto min-w-[120px] w-full">
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAppointmentsDateRangeChange('today');
                              setAppointmentsDateRangeDropdownOpen(false);
                            }}
                          >
                            Today
                          </div>
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAppointmentsDateRangeChange('3days');
                              setAppointmentsDateRangeDropdownOpen(false);
                            }}
                          >
                            3 Days
                          </div>
                          <div
                            className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAppointmentsDateRangeChange('1week');
                              setAppointmentsDateRangeDropdownOpen(false);
                            }}
                          >
                            1 Week
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Date Range Navigation (right) */}
                    <div className="w-full min-w-0 flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                      <button
                        onClick={() => handleAppointmentsDateRangeNavigate('prev')}
                        className="p-1 hover:bg-gray-50 rounded transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center h-6 w-6 flex-shrink-0"
                        title="Previous period"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <div className="relative appointments-date-picker-container flex items-center z-40 min-w-0">
                        <button
                          type="button"
                          onClick={() => {
                            setIsAppointmentsDatePickerOpen(!isAppointmentsDatePickerOpen);
                            setAppointmentsDatePickerMonth(new Date(appointmentsDateRangeStart));
                          }}
                          className="flex items-center px-3 hover:bg-gray-50 rounded transition-colors min-w-0"
                        >
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {formatAppointmentsDateRange(appointmentsDateRangeType, appointmentsDateRangeStart)}
                          </span>
                        </button>

                        {isAppointmentsDatePickerOpen && (
                          <>
                            <div className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden" onClick={() => setIsAppointmentsDatePickerOpen(false)}></div>
                            <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 sm:absolute sm:inset-x-auto sm:top-full sm:translate-y-0 sm:left-0 sm:right-auto sm:mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-[100] min-w-[280px] w-auto sm:w-auto max-w-[calc(100vw-2rem)] sm:max-w-none">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-medium text-gray-900">Select Date</h3>
                                <button
                                  onClick={() => setIsAppointmentsDatePickerOpen(false)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                              
                              {/* Calendar navigation */}
                              <div className="flex items-center justify-between mb-4">
                                <button
                                  onClick={() => {
                                    const newMonth = new Date(appointmentsDatePickerMonth);
                                    newMonth.setMonth(newMonth.getMonth() - 1);
                                    setAppointmentsDatePickerMonth(newMonth);
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                >
                                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                                </button>
                                <span className="text-sm font-medium text-gray-900">
                                  {appointmentsDatePickerMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                </span>
                                <button
                                  onClick={() => {
                                    const newMonth = new Date(appointmentsDatePickerMonth);
                                    newMonth.setMonth(newMonth.getMonth() + 1);
                                    setAppointmentsDatePickerMonth(newMonth);
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                >
                                  <ChevronRight className="w-4 h-4 text-gray-600" />
                                </button>
                              </div>
                              
                              {/* Calendar grid */}
                              <div className="grid grid-cols-7 gap-1 mb-2">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                  <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                                    {day}
                                  </div>
                                ))}
                              </div>
                              <div className="grid grid-cols-7 gap-1">
                                {generateProfessionalCalendarDays(
                                  appointmentsDatePickerMonth.getFullYear(),
                                  appointmentsDatePickerMonth.getMonth()
                                ).map((date, index) => {
                                  if (!date) {
                                    return <div key={index} className="h-8"></div>;
                                  }
                                  
                                  const { start } = getAppointmentsDateRange(appointmentsDateRangeType, appointmentsDateRangeStart);
                                  const isSelected = date.toDateString() === start.toDateString();
                                  const isToday = date.toDateString() === new Date().toDateString();
                                  
                                  return (
                                    <button
                                      key={index}
                                      onClick={() => handleAppointmentsDateSelect(date)}
                                      className={`h-8 rounded-md text-sm font-medium transition-all ${
                                        isSelected
                                          ? 'bg-[#0B4778] text-white shadow-sm'
                                          : isToday
                                          ? 'bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30'
                                          : 'hover:bg-gray-100 text-gray-900'
                                      }`}
                                    >
                                      {date.getDate()}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <button
                        onClick={() => handleAppointmentsDateRangeNavigate('next')}
                        className="p-1 hover:bg-gray-50 rounded transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center h-6 w-6"
                        title="Next period"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        );

        return (
          <div className="flex flex-col gap-6 pb-20 lg:pb-6">
            {/* Always-visible unassigned warning (above filter/controls, including when mobile controls are collapsed) */}
            {isOwner && currentBusiness?.id && (
              <UnassignedAssignmentsWarning
                businessId={currentBusiness.id}
                professionals={professionals}
                refreshKey={unassignedAssignmentsRefreshKey}
                onAssigned={() => {
                  setUnassignedAssignmentsRefreshKey((k) => k + 1);
                  loadAppointments(appointmentPagination.currentPage);
                }}
              />
            )}

            {/* Mobile-only: collapsible controls header */}
            <div className="md:hidden">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    const nextOpen = !isMobileAppointmentsControlsOpen;
                    if (!nextOpen) {
                      // Close any open dropdowns/modals within the controls when collapsing
                      setStatusDropdownOpen(false);
                      setProfessionalDropdownOpen(false);
                      setAppointmentsDateRangeDropdownOpen(false);
                      setIsAppointmentsDatePickerOpen(false);
                      setIsAppointmentSettingsDropdownOpen(false);
                    }
                    setIsMobileAppointmentsControlsOpen(nextOpen);
                  }}
                  className={`w-full flex items-center justify-between gap-3 bg-[#0B4778] text-white hover:bg-[#093a63] transition-colors px-4 py-3 ${
                    isMobileAppointmentsControlsOpen ? 'rounded-t-lg' : 'rounded-lg'
                  }`}
                  aria-expanded={isMobileAppointmentsControlsOpen}
                  aria-controls="appointments-mobile-controls"
                >
                  <div className="min-w-0 text-left">
                    <div className="text-sm font-semibold truncate flex items-center gap-2">
                      <Calendar className="w-4 h-4 flex-shrink-0 text-white/90" />
                      {formatAppointmentsDateRange(appointmentsDateRangeType, appointmentsDateRangeStart)}
                    </div>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-white/90 transition-transform ${isMobileAppointmentsControlsOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isMobileAppointmentsControlsOpen && (
                  <div id="appointments-mobile-controls" className="p-4 space-y-4 border-t border-gray-200 rounded-b-lg">
                    {appointmentsControlsContent}
                  </div>
                )}
              </div>
            </div>

            {/* Tablet/Desktop: existing controls unchanged */}
            <div className="hidden md:block">
              {appointmentsControlsContent}
            </div>

            {/* Loading State */}
            {appointmentsLoading && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                  <p className="text-lg font-medium text-gray-900 mb-2">Loading appointments...</p>
                  <p className="text-sm text-gray-500">Please wait while we fetch your appointments</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {appointmentsError && !appointmentsLoading && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-12 text-center">
                  <div className="text-red-500 text-4xl mb-4">⚠️</div>
                  <p className="text-lg font-medium text-gray-900 mb-2">Failed to load appointments</p>
                  <p className="text-sm text-gray-500 mb-6">{appointmentsError}</p>
                  <button
                    onClick={() => loadAppointments(1)}
                    className="aacore-btn2-primary mx-auto"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {/* Appointments Content */}
            {!appointmentsLoading && !appointmentsError && appointmentView === 'grid' && (() => {
              // Check if filtered results are empty for grid view
              if (displayAppointments.length === 0) {
                return (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="text-center text-gray-500">
                      <i className="bi bi-calendar2-x text-gray-400 text-4xl mb-4 block"></i>
                      <p className="text-sm font-medium text-gray-600">No appointments for selected period.</p>
                        </div>
                      </div>
                );
              }
              return (
                <div className="md:bg-white md:rounded-lg md:shadow-sm md:border md:border-gray-200">
                  <div className="md:p-6">
                    <div className="grid gap-4">
                      {displayAppointments.map((appointment) => (
                        <div 
                          key={appointment.id} 
                          onClick={() => {
                            if (window.innerWidth < 768) {
                              navigate(`/appointments/${appointment.id}?from=${activeSection}`);
                            }
                          }}
                          className="grid grid-cols-[1fr_auto] md:block items-center md:items-start gap-3 bg-white border border-gray-200 rounded-lg px-4 pt-4 pb-4 hover:shadow-md transition-shadow cursor-pointer md:cursor-default"
                        >
                          <div className="min-w-0">
                            <div className="flex items-start justify-between mb-3">
                            <div className="flex items-start space-x-3">
                              {/* Calendar Icon with Booking Initiator indicator */}
                              <div className="relative flex-shrink-0">
                                {/* Booking Initiator Icon - positioned at top-right corner */}
                                {appointment.bookingInitiator && (
                                  <div className="absolute -top-1 right-0 z-10">
                                    {appointment.bookingInitiator === 'CUSTOMER' ? (
                                      <span 
                                        className="inline-flex items-center justify-center border w-4 h-4 rounded-full bg-green-100 border-green-200"
                                        title="Customer booked"
                                      >
                                        <UserIcon className="w-2.5 h-2.5 text-green-700" />
                                      </span>
                                    ) : (
                                      <span 
                                        className="inline-flex items-center justify-center border w-4 h-4 rounded-md bg-orange-100 border-orange-200"
                                        title="Booked by staff"
                                      >
                                        <Pencil className="w-2.5 h-2.5 text-orange-700" />
                                      </span>
                                    )}
                                  </div>
                                )}
                                <div className="w-10 h-10 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-[#0B4778]">
                                  <i className="bi bi-calendar3 text-lg"></i>
                                </div>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <h3 className="font-medium text-gray-900">
                              {appointment.customer_first_name} {appointment.customer_last_name}
                                  </h3>
                                  <span className={`hidden md:inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                    appointment.status === 'CONFIRMED' 
                                      ? 'bg-green-100 text-green-800'
                                      : appointment.status === 'PENDING'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : appointment.status === 'COMPLETED'
                                      ? 'bg-[#0B4778]/20 text-[#0B4778]'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {String(appointment.status).charAt(0) + String(appointment.status).slice(1).toLowerCase()}
                                  </span>
                          </div>
                                {/* Mobile: Stack date/time with duration, and service names on separate lines */}
                                <div className="md:hidden flex flex-col gap-1.5 text-sm">
                                  <div className="text-gray-600">
                                    {!appointment?.scheduledAt && (appointment as any)?.flexScheduledAt?.earliest && (appointment as any)?.flexScheduledAt?.latest ? (
                                      <div className="flex flex-col gap-0.5">
                                        <span className="whitespace-nowrap">{getAppointmentStartIso(appointment) ? formatDate(getAppointmentStartIso(appointment)!) : 'Date TBD'} - {getTotalDuration(appointment)} min</span>
                                        <span>{getAppointmentTimeDisplay(appointment)}</span>
                                      </div>
                                    ) : (
                                      <span className="whitespace-nowrap">{getAppointmentDateTimeDisplay(appointment)} - {getTotalDuration(appointment)} min</span>
                                    )}
                                  </div>
                                  {appointment.assignments && appointment.assignments.length > 0 ? (
                                    <div className="flex flex-col gap-0.5 mt-0.5">
                                      {appointment.assignments.map((assignment: any, index: number) => {
                                        const icon = getAssignmentIcon(assignment);
                                        const svc = services.find((s: any) => String(s.id) === String(assignment.serviceId)) as any;
                                        const rawLoc = svc?.serviceLocation ?? svc?.service_location;
                                        const loc = typeof rawLoc === 'string' ? rawLoc.toUpperCase().trim() : '';
                                        const isOnSite = loc === 'ON_SITE' || loc === 'ONSITE';
                                        const professionalName = assignment.professionalFirstName && assignment.professionalLastName
                                          ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.`
                                          : null;
                                        const showProfessionalName = professionals.length > 1 && settings.showProfessionalNameOnAssignment && professionalName;
                                        return (
                                          <div key={index} className="flex items-start gap-2 text-gray-900 font-medium">
                                            <span className="text-base flex-shrink-0 mt-0.5">{icon || '📦'}</span>
                                            <div className="min-w-0">
                                              <div className="truncate">
                                                {assignment.serviceName || 'Unknown Service'}
                                              </div>
                                              {(isOnSite || showProfessionalName) && (
                                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                  {isOnSite && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 flex-shrink-0">
                                                      On-Site
                                                    </span>
                                                  )}
                                                  {showProfessionalName && (
                                                    <span className="text-gray-600 text-sm font-normal truncate">
                                                      ({professionalName})
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                        </div>
                                  ) : (
                                    <div className="text-gray-500 italic mt-0.5">No services assigned</div>
                                  )}
                        </div>
                                {/* Desktop: Services on separate lines with professional names */}
                                <div className="hidden md:flex flex-col gap-1 text-sm text-gray-600">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span>{getAppointmentDateTimeDisplay(appointment)}</span>
                                    <span>•</span>
                                    <span>{getTotalDuration(appointment)} min</span>
                                  </div>
                                  {appointment.assignments && appointment.assignments.length > 0 ? (
                                    appointment.assignments.map((assignment: any, index: number) => {
                                      const icon = getAssignmentIcon(assignment);
                                      const svc = services.find((s: any) => String(s.id) === String(assignment.serviceId)) as any;
                                      const rawLoc = svc?.serviceLocation ?? svc?.service_location;
                                      const loc = typeof rawLoc === 'string' ? rawLoc.toUpperCase().trim() : '';
                                      const isOnSite = loc === 'ON_SITE' || loc === 'ONSITE';
                                      const professionalName = assignment.professionalFirstName && assignment.professionalLastName
                                        ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.`
                                        : null;
                                      const showProfessionalName = professionals.length > 1 && settings.showProfessionalNameOnAssignment && professionalName;
                                      return (
                                        <div key={index} className="flex items-start gap-1">
                                          <span className="text-xs mt-0.5">{icon || '📦'}</span>
                                          <div className="min-w-0">
                                            <div className="truncate">
                                              {assignment.serviceName || 'Unknown Service'}
                                              {showProfessionalName && <span className="text-gray-600"> ({professionalName})</span>}
                                            </div>
                                            {isOnSite && (
                                              <div className="mt-0.5">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                                  On-Site
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <span>No services</span>
                                  )}
                        </div>
                      </div>
                        </div>
                            <div className="hidden md:flex items-center space-x-2">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/appointments/${appointment.id}?from=${activeSection}`);
                                }}
                                className="aacore-btn2-primary text-sm"
                              >
                                <Eye className="w-4 h-4" />
                                <span>View Details</span>
                              </button>
                            </div>
                    </div>
                  </div>
                  
                  {/* Mobile right column: status (top-aligned) + chevron (vertically centered) */}
                  <div className="md:hidden self-stretch relative w-12">
                    <div className="absolute top-0 right-0">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        appointment.status === 'CONFIRMED' 
                          ? 'bg-green-100 text-green-800'
                          : appointment.status === 'PENDING'
                          ? 'bg-yellow-100 text-yellow-800'
                          : appointment.status === 'COMPLETED'
                          ? 'bg-[#0B4778]/20 text-[#0B4778]'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {String(appointment.status).charAt(0) + String(appointment.status).slice(1).toLowerCase()}
                      </span>
                    </div>
                    <div className="absolute inset-y-0 right-0 flex items-center text-gray-400">
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
                ))}
              </div>
                  </div>
                </div>
              );
            })()}

            {!appointmentsLoading && !appointmentsError && appointmentView === 'table' && (() => {
              // Check if filtered results are empty for table view
              if (displayAppointments.length === 0) {
                return (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="text-center text-gray-500">
                      <i className="bi bi-calendar2-x text-gray-400 text-4xl mb-4 block"></i>
                      <p className="text-sm font-medium text-gray-600">No appointments for selected period.</p>
                    </div>
                  </div>
                );
              }
              
              return (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-full">
                <div className="overflow-x-auto w-full">
                  <table className="w-full divide-y divide-gray-200 border border-gray-200 border-collapse">
                    <thead className="bg-gray-50">
                      <tr className="divide-x divide-gray-200">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Service
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date & Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Duration
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {displayAppointments.map((appointment) => (
                        <tr key={appointment.id} className="hover:bg-gray-50 divide-x divide-gray-200">
                          <td className="px-6 py-4 [vertical-align:top]">
                            <div className="flex items-center">
                              {/* Calendar Icon with Booking Initiator indicator */}
                              <div className="relative mr-3 flex-shrink-0">
                                {/* Booking Initiator Icon - positioned at top-right corner */}
                                {appointment.bookingInitiator && (
                                  <div className="absolute -top-1 right-0 z-10">
                                    {appointment.bookingInitiator === 'CUSTOMER' ? (
                                      <span 
                                        className="inline-flex items-center justify-center border w-4 h-4 rounded-full bg-green-100 border-green-200"
                                        title="Customer booked"
                                      >
                                        <UserIcon className="w-2.5 h-2.5 text-green-700" />
                                      </span>
                                    ) : (
                                      <span 
                                        className="inline-flex items-center justify-center border w-4 h-4 rounded-md bg-orange-100 border-orange-200"
                                        title="Booked by staff"
                                      >
                                        <Pencil className="w-2.5 h-2.5 text-orange-700" />
                                      </span>
                                    )}
                                  </div>
                                )}
                                <div className="w-8 h-8 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-[#0B4778]">
                                  <i className="bi bi-calendar3"></i>
                                </div>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {appointment.customer_first_name} {appointment.customer_last_name}
                                </div>
                                <div 
                                  className="text-sm text-gray-500 truncate max-w-[200px]"
                                  title={appointment.customer_email && !appointment.customer_email.includes('@appointment.local') ? appointment.customer_email : undefined}
                                >
                                  {appointment.customer_email && appointment.customer_email.includes('@appointment.local')
                                    ? 'Guest Customer (Info not provided)'
                                    : appointment.customer_email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 [vertical-align:top]">
                            <div className="text-sm text-gray-900 flex flex-col gap-1">
                              {appointment.assignments && appointment.assignments.length > 0 ? (
                                appointment.assignments.map((assignment: any, index: number) => {
                                  const icon = getAssignmentIcon(assignment);
                                  const professionalName = assignment.professionalFirstName && assignment.professionalLastName
                                    ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.`
                                    : null;
                                  const showProfessionalName = professionals.length > 1 && settings.showProfessionalNameOnAssignment && professionalName;
                                  return (
                                    <div key={index} className="flex items-center gap-1.5">
                                      <span className="text-sm">{icon || '📦'}</span>
                                      <span>
                                        {assignment.serviceName || 'Unknown Service'}
                                        {showProfessionalName && <span className="text-gray-600"> ({professionalName})</span>}
                                      </span>
                                    </div>
                                  );
                                })
                              ) : (
                                <span>No services assigned</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap [vertical-align:top]">
                            <div className="text-sm text-gray-900">{getAppointmentStartIso(appointment) ? formatDate(getAppointmentStartIso(appointment)!) : 'Date TBD'}</div>
                            <div className="text-sm text-gray-500">{getAppointmentTimeDisplay(appointment)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap [vertical-align:top]">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                appointment.status === 'CONFIRMED' 
                                  ? 'bg-green-100 text-green-800'
                                  : appointment.status === 'PENDING'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : appointment.status === 'COMPLETED'
                                  ? 'bg-[#0B4778]/20 text-[#0B4778]'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                              {String(appointment.status).charAt(0) + String(appointment.status).slice(1).toLowerCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 [vertical-align:top]">
                              {getTotalDuration(appointment)} min
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium [vertical-align:top]">
                              <button 
                                onClick={() => navigate(`/appointments/${appointment.id}?from=${activeSection}`)}
                                className="text-[#0B4778] hover:text-[#0B4778] flex items-center gap-1"
                              >
                                <Eye className="w-4 h-4" />
                                View
                              </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              );
            })()}

            {!appointmentsLoading && !appointmentsError && appointmentView === 'calendar' && (() => {
              // Get date range for calendar view
              const { start, end } = getAppointmentsDateRange(appointmentsDateRangeType, appointmentsDateRangeStart);

              const professionalColumns =
                isAppointmentsTodayAllProfessionals && professionals.length > 0
                  ? professionals
                      .filter((p: any) => {
                        const isActive = (p?.isActive ?? p?.is_active);
                        const canPerformServices = p?.canPerformServices;
                        return (typeof isActive === 'boolean' ? isActive : true) && canPerformServices === true;
                      })
                      .map((p: any) => {
                        const name =
                          p?.name ||
                          `${p?.firstName || p?.first_name || ''} ${p?.lastName || p?.last_name || ''}`.trim() ||
                          'Professional';
                        return { id: String(p?.userId || p?.id), name };
                      })
                      .filter((c: { id: string; name: string }) => Boolean(c.id) && Boolean(c.name))
                      .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name))
                  : [];

              const hasUnassignedBlocks = displayAppointments.some((appt: any) => {
                const assignments = Array.isArray(appt?.assignments) ? appt.assignments : [];
                // Unassigned should show if ANY service/assignment lacks a professional.
                if (assignments.length === 0) return true;
                return assignments.some((a: any) => !a?.professionalId);
              });

              const professionalColumnsWithUnassigned =
                isAppointmentsTodayAllProfessionals && appointmentsCalendarDisplayMode === 'professionalColumns'
                  ? (hasUnassignedBlocks ? [{ id: '__unassigned__', name: 'Unassigned' }, ...professionalColumns] : professionalColumns)
                  : professionalColumns;

              return (
                <div className="relative z-0">
                  {isAppointmentsTodayAllProfessionals && professionalColumns.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setAppointmentsCalendarDisplayMode(prev =>
                          prev === 'professionalColumns' ? 'default' : 'professionalColumns'
                        )
                      }
                      className={`absolute top-3 right-3 z-10 p-2 rounded-lg border shadow-sm transition-colors ${
                        appointmentsCalendarDisplayMode === 'professionalColumns'
                          ? 'bg-[#0B4778] text-white border-[#0B4778]'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}
                      title={
                        appointmentsCalendarDisplayMode === 'professionalColumns'
                          ? 'Switch to standard calendar view'
                          : 'Switch to professional columns view'
                      }
                      aria-label={
                        appointmentsCalendarDisplayMode === 'professionalColumns'
                          ? 'Switch to standard calendar view'
                          : 'Switch to professional columns view'
                      }
                    >
                      <Columns className="w-4 h-4" />
                    </button>
                  )}
                  <AppointmentCalendar
                    appointments={displayAppointments}
                    movingAppointmentId={movingAppointmentId}
                    viewMode={calendarViewMode}
                    onViewModeChange={setCalendarViewMode}
                    allowBookingInTimeOffSlots={true}
                    onRangeNavigate={handleAppointmentsDateRangeNavigate}
                    onSwipeDayNavigate={
                      (appointmentsDateRangeType === 'today' || appointmentsDateRangeType === '3days') &&
                      appointmentsCalendarDisplayMode !== 'professionalColumns'
                        ? handleAppointmentsSwipeNavigate
                        : undefined
                    }
                    onAppointmentMove={async (appointmentId: string, newDate: Date) => {
                      if (movingAppointmentId && String(movingAppointmentId) !== String(appointmentId)) return;
                      setMovingAppointmentId(String(appointmentId));
                      try {
                        // Optimistic UI update (server will recompute per-assignment from/to times).
                        setAppointments((prev) =>
                          prev.map((a) =>
                            String(a.id) === String(appointmentId)
                              ? { ...a, scheduledAt: newDate.toISOString() }
                              : a
                          )
                        );

                        await apiService.updateAppointment(appointmentId, {
                          scheduledAt: newDate.toISOString(),
                        });

                        toast.success('Appointment rescheduled');
                        loadAppointments(appointmentPagination.currentPage);
                      } catch (error) {
                        console.error('Error moving appointment:', error);
                        toast.error('Failed to reschedule appointment');
                        // Re-sync from server in case our optimistic update is now stale.
                        loadAppointments(appointmentPagination.currentPage);
                      } finally {
                        setMovingAppointmentId(null);
                      }
                    }}
                    onAssignmentDurationChange={async ({ appointmentId, assignmentId, durationMinutes }) => {
                      if (movingAppointmentId && String(movingAppointmentId) !== String(appointmentId)) return;
                      setMovingAppointmentId(String(appointmentId));
                      try {
                        await apiService.updateAssignmentDuration({
                          appointmentId,
                          assignmentId,
                          durationMinutes
                        });
                        toast.success('Duration updated');
                        loadAppointments(appointmentPagination.currentPage);
                      } catch (error: any) {
                        console.error('Error updating assignment duration:', error);
                        const msg = error?.response?.data?.message || 'Failed to update duration';
                        toast.error(msg);
                        loadAppointments(appointmentPagination.currentPage);
                      } finally {
                        setMovingAppointmentId(null);
                      }
                    }}
                    onAppointmentClick={(appointment: Appointment) => {
                      setSelectedAppointment(appointment);
                      setIsAppointmentDetailsModalOpen(true);
                    }}
                    onTimeSlotDoubleClick={(date: Date, time: string, resourceId?: string) => {
                      // Format date as YYYY-MM-DD
                      const dateStr = formatLocalYmd(date);
                      // Navigate to create appointment page with pre-filled date, time, and optionally the column's professional for pre-selection
                      navigate(buildCreateAppointmentUrl({
                        date: dateStr,
                        time,
                        from: 'appointments',
                        prefillProfessionalId: resourceId && resourceId !== '__unassigned__' ? resourceId : undefined
                      }));
                    }}
                    dateRange={{ start, end }}
                    showProfessionalNames={appointmentsCalendarDisplayMode !== 'professionalColumns'}
                    showProfessionalNameInAssignmentBlock={
                      professionals.length > 1 && settings.showProfessionalNameOnAssignment
                    }
                    operatingHourRange={calendarOperatingHourRange}
                    timeOffs={appointmentsProfessionalTimeOffs}
                    resourceColumns={
                      isAppointmentsTodayAllProfessionals && appointmentsCalendarDisplayMode === 'professionalColumns'
                        ? professionalColumnsWithUnassigned
                        : undefined
                    }
                  />
                </div>
              );
            })()}

            {/* Pagination Controls - Only show for non-calendar views */}
            {!appointmentsLoading && !appointmentsError && appointmentView !== 'calendar' && appointmentPagination.totalPages > 1 && (
              <div className="flex justify-center items-center space-x-4 mt-8">
                <button
                  onClick={() => handlePageChange(appointmentPagination.currentPage - 1)}
                  disabled={!appointmentPagination.hasPrevPage}
                  className={`px-4 py-2 rounded-lg transition-all duration-300 text-sm font-medium ${
                    appointmentPagination.hasPrevPage
                      ? 'bg-[#0B4778] text-white hover:bg-[#0a3d66]'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Previous
                </button>
                
                <div className="flex items-center space-x-2">
                  {Array.from({ length: appointmentPagination.totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 rounded-lg transition-all duration-300 text-sm font-medium ${
                        page === appointmentPagination.currentPage
                          ? 'bg-[#0B4778] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => handlePageChange(appointmentPagination.currentPage + 1)}
                  disabled={!appointmentPagination.hasNextPage}
                  className={`px-4 py-2 rounded-lg transition-all duration-300 text-sm font-medium ${
                    appointmentPagination.hasNextPage
                      ? 'bg-[#0B4778] text-white hover:bg-[#0a3d66]'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Next
                </button>
              </div>
            )}

            {/* Pagination Info - Only show for non-calendar views */}
            {!appointmentsLoading && !appointmentsError && appointmentView !== 'calendar' && appointmentPagination.total > 0 && (
              <div className="text-center mt-4">
                <p className="text-sm text-gray-600">
                  Showing {((appointmentPagination.currentPage - 1) * appointmentPagination.limit) + 1} to {Math.min(appointmentPagination.currentPage * appointmentPagination.limit, appointmentPagination.total)} of {appointmentPagination.total} appointments
                </p>
              </div>
            )}
          </div>
        );
        
      case 'my-bookings':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">My Bookings</h2>
                <p className="text-gray-600 mt-1">View and manage your appointments as a customer</p>
              </div>
            </div>

            {/* My Bookings Content */}
            <div className="space-y-6">
              {/* Filters */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex flex-wrap gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className="relative my-bookings-status-dropdown-container">
                      <button
                        type="button"
                        onClick={() => setMyBookingsStatusDropdownOpen(!myBookingsStatusDropdownOpen)}
                        className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-gray-900">
                          {appointmentFilters.status === '' ? 'All Status' :
                           appointmentFilters.status === 'PENDING' ? 'Pending' :
                           appointmentFilters.status === 'CONFIRMED' ? 'Confirmed' :
                           appointmentFilters.status === 'COMPLETED' ? 'Completed' :
                           appointmentFilters.status === 'CANCELLED' ? 'Cancelled' : 'All Status'}
                        </span>
                      </button>
                      
                      {myBookingsStatusDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setAppointmentFilters(prev => ({ ...prev, status: '' }));
                              setMyBookingsStatusDropdownOpen(false);
                            }}
                          >
                            All Status
                          </div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setAppointmentFilters(prev => ({ ...prev, status: 'PENDING' }));
                              setMyBookingsStatusDropdownOpen(false);
                            }}
                          >
                            Pending
                          </div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setAppointmentFilters(prev => ({ ...prev, status: 'CONFIRMED' }));
                              setMyBookingsStatusDropdownOpen(false);
                            }}
                          >
                            Confirmed
                          </div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setAppointmentFilters(prev => ({ ...prev, status: 'COMPLETED' }));
                              setMyBookingsStatusDropdownOpen(false);
                            }}
                          >
                            Completed
                          </div>
                          <div
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setAppointmentFilters(prev => ({ ...prev, status: 'CANCELLED' }));
                              setMyBookingsStatusDropdownOpen(false);
                            }}
                          >
                            Cancelled
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                    <input
                      type="date"
                      value={appointmentFilters.dateFrom}
                      onChange={(e) => setAppointmentFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                    <input
                      type="date"
                      value={appointmentFilters.dateTo}
                      onChange={(e) => setAppointmentFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => {
                        setAppointmentFilters({
                          status: '',
                          professionalId: '',
                          dateFrom: '',
                          dateTo: ''
                        });
                        setMyBookingsStatusDropdownOpen(false);
                      }}
                      className="px-3 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>

              {/* My Bookings List */}
              {myBookingsLoading ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading your bookings...</p>
                  </div>
                </div>
              ) : myBookingsError ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="text-center">
                    <p className="text-red-600">{myBookingsError}</p>
                  </div>
                </div>
              ) : myBookings && myBookings.length > 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-6">
                    <div className="grid gap-4">
                      {myBookings
                        .filter(appointment => {
                          if (appointmentFilters.status && appointment.status !== appointmentFilters.status) return false;
                          
                          // Date range filtering
                          if (appointmentFilters.dateFrom) {
                            const appointmentStartIso = getAppointmentStartIso(appointment);
                            if (!appointmentStartIso) return false;
                            const appointmentDate = new Date(appointmentStartIso);
                            const fromDate = localDateFromYmd(appointmentFilters.dateFrom);
                            if (!fromDate) return false;
                            if (appointmentDate < fromDate) return false;
                          }
                          
                          if (appointmentFilters.dateTo) {
                            const appointmentStartIso = getAppointmentStartIso(appointment);
                            if (!appointmentStartIso) return false;
                            const appointmentDate = new Date(appointmentStartIso);
                            const toDate = localDateFromYmd(appointmentFilters.dateTo);
                            if (!toDate) return false;
                            toDate.setHours(23, 59, 59, 999);
                            if (appointmentDate > toDate) return false;
                          }
                          
                          return true;
                        })
                        .map((appointment) => (
                          <div key={appointment.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-start space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center text-white font-bold">
                                  {appointment.business_name?.[0] || 'B'}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <h3 className="font-medium text-gray-900">
                                      {appointment.business_name || 'Unknown Business'}
                                    </h3>
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(appointment.status)}`}>
                                      {String(appointment.status).charAt(0) + String(appointment.status).slice(1).toLowerCase()}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 flex items-center gap-1.5 flex-wrap">
                                    {appointment.assignments && appointment.assignments.length > 0 ? (
                                      appointment.assignments.map((assignment: any, index: number) => {
                                        const icon = getAssignmentIcon(assignment);
                                        return (
                                          <span key={index} className="flex items-center gap-1">
                                            <span className="text-xs">{icon || '📦'}</span>
                                            <span>{assignment.serviceName || 'Unknown Service'}</span>
                                            {index < appointment.assignments.length - 1 && <span>,</span>}
                                          </span>
                                        );
                                      })
                                    ) : (
                                      <span>No services</span>
                                    )}
                                    <span>•</span>
                                    <span>{getAppointmentDateTimeDisplay(appointment)}</span>
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    Duration: {getTotalDuration(appointment)} min • Total: ${getTotalPrice(appointment).toFixed(2)}
                                  </p>
                                  {getProfessionalNames(appointment) && (
                                    <p className="text-sm text-gray-600">
                                      Professional: {getProfessionalNames(appointment)}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => {
                                    setSelectedAppointment(appointment);
                                    setIsAppointmentDetailsModalOpen(true);
                                  }}
                                  className="px-3 py-1 text-sm bg-[#0B4778]/20 text-[#0B4778] rounded hover:bg-[#0B4778]/30 flex items-center gap-1"
                                >
                                  <i className="bi bi-eye"></i>
                                  View Details
                                </button>
                                {appointment.status === 'PENDING' && (
                                  <button
                                    onClick={() => handleCancelAppointment(appointment)}
                                    className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-1"
                                  >
                                    <i className="bi bi-x-circle"></i>
                                    Cancel
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="text-center">
                    <p className="text-gray-600">No bookings found.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
        
      case 'my-gift-cards':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">My Gift Cards</h2>
                <p className="text-gray-600 mt-1">View and manage your gift cards</p>
              </div>
            </div>

            {/* My Gift Cards Content */}
            <div className="space-y-6">
              {/* My Gift Cards List */}
              {myGiftCardsLoading ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading your gift cards...</p>
                  </div>
                </div>
              ) : myGiftCardsError ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="text-center">
                    <p className="text-red-600">{myGiftCardsError}</p>
                  </div>
                </div>
              ) : myGiftCards && myGiftCards.length > 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-6">
                    <div className="grid gap-4">
                      {myGiftCards.map((giftCard) => (
                        <div key={giftCard.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-start space-x-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold">
                                🎁
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <h3 className="font-medium text-gray-900">
                                    {giftCard.business?.name || 'Unknown Business'}
                                  </h3>
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    giftCard.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                                    giftCard.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {giftCard.status}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">
                                  Code: {giftCard.code} • Amount: ${giftCard.amount.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Balance: ${giftCard.balance.toFixed(2)} • Expires: {giftCard.expiresAt ? formatDate(giftCard.expiresAt) : 'No expiry'}
                                </p>
                                {giftCard.message && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    Message: {giftCard.message}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  // TODO: Implement gift card details modal
                                  toast.success('Gift card details feature coming soon');
                                }}
                                className="px-3 py-1 text-sm bg-[#0B4778]/20 text-[#0B4778] rounded hover:bg-[#0B4778]/30 flex items-center gap-1"
                              >
                                <i className="bi bi-eye"></i>
                                View Details
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="text-center">
                    <p className="text-gray-600">No gift cards found.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
        
      case 'my-appointments':
        const myAppointmentsControlsContent = (
          <>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">My Appointments</h2>
                <p className="text-gray-600 mt-1">View and manage your own appointments</p>
              </div>
              <div className="flex items-center justify-between gap-2 w-full sm:w-auto sm:justify-start">
                <button 
                  onClick={() => {
                    // If the My Appointments date range UI represents a single day, carry that date forward
                    // to the create-appointment flow so the date picker auto-selects it.
                    //
                    // IMPORTANT: We only prefill when the range is 1 day ("today" mode). If we cannot
                    // confidently determine the business is open (operating hours missing or closed),
                    // fall back to existing navigation to avoid changing business logic.
                    if (professionalDateRangeType === 'today') {
                      const { start } = getProfessionalDateRange(professionalDateRangeType, professionalDateRangeStart);
                      const dayOfWeek = start.getDay();
                      const dayOperatingHours = operatingHours.find((h: any) => {
                        const dow = (h?.dayOfWeek ?? h?.day_of_week);
                        return Number(dow) === dayOfWeek;
                      });
                      const isClosed = dayOperatingHours
                        ? Boolean((dayOperatingHours as any).isClosed ?? (dayOperatingHours as any).is_closed)
                        : true;

                          if (dayOperatingHours && !isClosed) {
                            const dateStr = formatLocalYmd(start);
                            navigate(buildCreateAppointmentUrl({ date: dateStr, from: 'my-appointments' }));
                            return;
                          }
                        }

                        // Default behavior (unchanged)
                        navigate(buildCreateAppointmentUrl({ from: 'my-appointments' }));
                  }}
                  className="aacore-btn2-primary flex-1 md:flex-none"
                >
                  <Plus className="w-4 h-4" />
                  Appointment
                </button>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button 
                    onClick={() => handleProfessionalViewChange('grid')}
                    title="Grid View"
                    className={`${professionalAppointmentView === 'grid' ? 'aacore-btn2-primary' : 'aacore-btn2-outline bg-gray-100 hover:bg-gray-200'}`}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleProfessionalViewChange('table')}
                    title="Table View"
                    className={`hidden md:inline-flex ${professionalAppointmentView === 'table' ? 'aacore-btn2-primary' : 'aacore-btn2-outline bg-gray-100 hover:bg-gray-200'}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleProfessionalViewChange('calendar')}
                    title="Calendar View"
                    className={`${professionalAppointmentView === 'calendar' ? 'aacore-btn2-primary' : 'aacore-btn2-outline bg-gray-100 hover:bg-gray-200'}`}
                  >
                    <Calendar className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Date Range Selector */}
            <div className="relative z-30 mt-4 max-md:mt-0 max-md:p-0 max-md:border-0 max-md:shadow-none max-md:bg-transparent md:bg-white md:rounded-lg md:shadow-sm md:border md:border-gray-200 md:p-4">
                {/* Tablet/iPad: Date Range and Status Dropdowns on Same Line (like mobile but with calendar) */}
                <div className="hidden md:flex xl:hidden items-center gap-3">
                  {/* Date Range Dropdown */}
                  <div className="relative flex-1 my-appointments-date-range-dropdown-container">
                    <button
                      type="button"
                      onClick={() => {
                        setMyAppointmentsDateRangeDropdownOpen(!myAppointmentsDateRangeDropdownOpen);
                        // Close status dropdown if open
                        if (myAppointmentsStatusDropdownOpen) {
                          setMyAppointmentsStatusDropdownOpen(false);
                        }
                      }}
                      className="w-full px-3 py-2.5 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between shadow-sm hover:border-gray-300 transition-colors"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em'
                      }}
                    >
                      <span className="text-sm font-medium text-gray-900">
                        {professionalDateRangeType === 'today' ? 'Today' :
                         professionalDateRangeType === '3days' ? '3 Days' :
                         professionalDateRangeType === '1week' ? '1 Week' : '3 Days'}
                      </span>
                    </button>
                    
                    {myAppointmentsDateRangeDropdownOpen && (
                      <div className="absolute z-[9999] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto min-w-[120px] w-full">
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleProfessionalDateRangeChange('today');
                            setMyAppointmentsDateRangeDropdownOpen(false);
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            handleProfessionalDateRangeChange('today');
                            setMyAppointmentsDateRangeDropdownOpen(false);
                          }}
                        >
                          Today
                        </div>
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleProfessionalDateRangeChange('3days');
                            setMyAppointmentsDateRangeDropdownOpen(false);
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            handleProfessionalDateRangeChange('3days');
                            setMyAppointmentsDateRangeDropdownOpen(false);
                          }}
                        >
                          3 Days
                        </div>
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleProfessionalDateRangeChange('1week');
                            setMyAppointmentsDateRangeDropdownOpen(false);
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            handleProfessionalDateRangeChange('1week');
                            setMyAppointmentsDateRangeDropdownOpen(false);
                          }}
                        >
                          1 Week
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Status Dropdown */}
                  <div className="relative flex-1 my-appointments-status-dropdown-container">
                    <button
                      type="button"
                      onClick={() => {
                        setMyAppointmentsStatusDropdownOpen(!myAppointmentsStatusDropdownOpen);
                        // Close date range dropdown if open
                        if (myAppointmentsDateRangeDropdownOpen) {
                          setMyAppointmentsDateRangeDropdownOpen(false);
                        }
                      }}
                      className="w-full px-3 py-2.5 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between shadow-sm hover:border-gray-300 transition-colors"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em'
                      }}
                    >
                      <span className="text-sm font-medium text-gray-900">
                        {appointmentFilters.status === '' ? 'All Status' :
                         appointmentFilters.status === 'PENDING' ? 'Pending' :
                         appointmentFilters.status === 'CONFIRMED' ? 'Confirmed' :
                         appointmentFilters.status === 'COMPLETED' ? 'Completed' :
                         appointmentFilters.status === 'CANCELLED' ? 'Cancelled' : 'All Status'}
                      </span>
                    </button>
                    
                    {myAppointmentsStatusDropdownOpen && (
                      <div className="absolute z-50 left-0 right-0 sm:right-auto sm:left-auto sm:right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto min-w-[150px] w-full sm:w-auto">
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAppointmentFilters(prev => ({ ...prev, status: '' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            setAppointmentFilters(prev => ({ ...prev, status: '' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                        >
                          All Status
                        </div>
                        <div className="border-t border-gray-100 my-1"></div>
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAppointmentFilters(prev => ({ ...prev, status: 'PENDING' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            setAppointmentFilters(prev => ({ ...prev, status: 'PENDING' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                        >
                          Pending
                        </div>
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAppointmentFilters(prev => ({ ...prev, status: 'CONFIRMED' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            setAppointmentFilters(prev => ({ ...prev, status: 'CONFIRMED' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                        >
                          Confirmed
                        </div>
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAppointmentFilters(prev => ({ ...prev, status: 'COMPLETED' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            setAppointmentFilters(prev => ({ ...prev, status: 'COMPLETED' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                        >
                          Completed
                        </div>
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAppointmentFilters(prev => ({ ...prev, status: 'CANCELLED' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            setAppointmentFilters(prev => ({ ...prev, status: 'CANCELLED' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                        >
                          Cancelled
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tablet/iPad: Date Range Navigation */}
                <div className="hidden md:flex xl:hidden items-center justify-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm mt-3">
                  <button
                    onClick={() => handleProfessionalDateRangeNavigate('prev')}
                    className="p-1 hover:bg-gray-50 rounded transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center h-6 w-6"
                    title="Previous period"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="relative professional-date-picker-container flex items-center z-40">
                    <button
                      type="button"
                      onClick={() => {
                        setIsProfessionalDatePickerOpen(!isProfessionalDatePickerOpen);
                        setProfessionalDatePickerMonth(new Date(professionalDateRangeStart));
                      }}
                      className="flex items-center gap-2 px-3 hover:bg-gray-50 rounded transition-colors"
                    >
                      <Calendar className="w-4 h-4 text-[#0B4778]" />
                      <span className="text-sm font-medium text-gray-900">
                        {formatProfessionalDateRange(professionalDateRangeType, professionalDateRangeStart)}
                      </span>
                    </button>
                    
                    {isProfessionalDatePickerOpen && (
                      <>
                        {/* Mobile: Fixed overlay */}
                        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden" onClick={() => setIsProfessionalDatePickerOpen(false)}></div>
                        <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 sm:absolute sm:inset-x-auto sm:top-full sm:translate-y-0 sm:left-0 sm:right-auto sm:mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-[100] min-w-[280px] w-auto sm:w-auto max-w-[calc(100vw-2rem)] sm:max-w-none">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-gray-900">Select Date</h3>
                            <button
                              onClick={() => setIsProfessionalDatePickerOpen(false)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          
                          {/* Calendar navigation */}
                          <div className="flex items-center justify-between mb-4">
                            <button
                              onClick={() => {
                                const newMonth = new Date(professionalDatePickerMonth);
                                newMonth.setMonth(newMonth.getMonth() - 1);
                                setProfessionalDatePickerMonth(newMonth);
                              }}
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                            >
                              <ChevronLeft className="w-4 h-4 text-gray-600" />
                            </button>
                            <span className="text-sm font-medium text-gray-900">
                              {professionalDatePickerMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                            </span>
                            <button
                              onClick={() => {
                                const newMonth = new Date(professionalDatePickerMonth);
                                newMonth.setMonth(newMonth.getMonth() + 1);
                                setProfessionalDatePickerMonth(newMonth);
                              }}
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                            >
                              <ChevronRight className="w-4 h-4 text-gray-600" />
                            </button>
                          </div>
                          
                          {/* Calendar grid */}
                          <div className="grid grid-cols-7 gap-1 mb-2">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                              <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                                {day}
                              </div>
                            ))}
                          </div>
                          <div className="grid grid-cols-7 gap-1">
                            {generateProfessionalCalendarDays(
                              professionalDatePickerMonth.getFullYear(),
                              professionalDatePickerMonth.getMonth()
                            ).map((date, index) => {
                              if (!date) {
                                return <div key={index} className="h-8"></div>;
                              }
                              
                              const { start } = getProfessionalDateRange(professionalDateRangeType, professionalDateRangeStart);
                              const isSelected = date.toDateString() === start.toDateString();
                              const isToday = date.toDateString() === new Date().toDateString();
                              
                              return (
                                <button
                                  key={index}
                                  onClick={() => handleProfessionalDateSelect(date)}
                                  className={`h-8 rounded-md text-sm font-medium transition-all ${
                                    isSelected
                                      ? 'bg-[#0B4778] text-white shadow-sm'
                                      : isToday
                                      ? 'bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30'
                                      : 'hover:bg-gray-100 text-gray-900'
                                  }`}
                                >
                                  {date.getDate()}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => handleProfessionalDateRangeNavigate('next')}
                    className="p-1 hover:bg-gray-50 rounded transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center h-6 w-6"
                    title="Next period"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Desktop (xl and above): All filters on same line with toggle buttons */}
                <div className="hidden xl:flex items-center relative">
                  {/* Date Range Type Selector - Desktop: Button Group */}
                  <div className="flex items-center bg-gray-50 rounded-lg p-1 w-fit">
                    <button
                      onClick={() => handleProfessionalDateRangeChange('today')}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                        professionalDateRangeType === 'today'
                          ? 'bg-white text-[#0B4778] shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => handleProfessionalDateRangeChange('3days')}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                        professionalDateRangeType === '3days'
                          ? 'bg-white text-[#0B4778] shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      3 Days
                    </button>
                    <button
                      onClick={() => handleProfessionalDateRangeChange('1week')}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                        professionalDateRangeType === '1week'
                          ? 'bg-white text-[#0B4778] shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      1 Week
                    </button>
                  </div>

                  {/* Date Range Navigation - Centered */}
                  <div className="flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm absolute left-1/2 -translate-x-1/2">
                    <button
                      onClick={() => handleProfessionalDateRangeNavigate('prev')}
                      className="p-1 hover:bg-gray-50 rounded transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center h-6 w-6"
                      title="Previous period"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="relative professional-date-picker-container flex items-center z-40">
                      <button
                        type="button"
                        onClick={() => {
                          setIsProfessionalDatePickerOpen(!isProfessionalDatePickerOpen);
                          setProfessionalDatePickerMonth(new Date(professionalDateRangeStart));
                        }}
                        className="flex items-center gap-2 px-3 hover:bg-gray-50 rounded transition-colors"
                      >
                        <Calendar className="w-4 h-4 text-[#0B4778]" />
                        <span className="text-sm font-medium text-gray-900">
                          {formatProfessionalDateRange(professionalDateRangeType, professionalDateRangeStart)}
                        </span>
                      </button>
                      
                      {isProfessionalDatePickerOpen && (
                        <>
                          {/* Mobile: Fixed overlay */}
                          <div className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden" onClick={() => setIsProfessionalDatePickerOpen(false)}></div>
                          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 sm:absolute sm:inset-x-auto sm:top-full sm:translate-y-0 sm:left-0 sm:right-auto sm:mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-[100] min-w-[280px] w-auto sm:w-auto max-w-[calc(100vw-2rem)] sm:max-w-none">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-sm font-medium text-gray-900">Select Date</h3>
                              <button
                                onClick={() => setIsProfessionalDatePickerOpen(false)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          
                          {/* Calendar navigation */}
                          <div className="flex items-center justify-between mb-4">
                            <button
                              onClick={() => {
                                const newMonth = new Date(professionalDatePickerMonth);
                                newMonth.setMonth(newMonth.getMonth() - 1);
                                setProfessionalDatePickerMonth(newMonth);
                              }}
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                            >
                              <ChevronLeft className="w-4 h-4 text-gray-600" />
                            </button>
                            <span className="text-sm font-medium text-gray-900">
                              {professionalDatePickerMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                            </span>
                            <button
                              onClick={() => {
                                const newMonth = new Date(professionalDatePickerMonth);
                                newMonth.setMonth(newMonth.getMonth() + 1);
                                setProfessionalDatePickerMonth(newMonth);
                              }}
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                            >
                              <ChevronRight className="w-4 h-4 text-gray-600" />
                            </button>
                          </div>
                          
                          {/* Calendar grid */}
                          <div className="grid grid-cols-7 gap-1 mb-2">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                              <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                                {day}
                              </div>
                            ))}
                          </div>
                          <div className="grid grid-cols-7 gap-1">
                            {generateProfessionalCalendarDays(
                              professionalDatePickerMonth.getFullYear(),
                              professionalDatePickerMonth.getMonth()
                            ).map((date, index) => {
                              if (!date) {
                                return <div key={index} className="h-8"></div>;
                              }
                              
                              const { start } = getProfessionalDateRange(professionalDateRangeType, professionalDateRangeStart);
                              const isSelected = date.toDateString() === start.toDateString();
                              const isToday = date.toDateString() === new Date().toDateString();
                              
                              return (
                                <button
                                  key={index}
                                  onClick={() => handleProfessionalDateSelect(date)}
                                  className={`h-8 rounded-md text-sm font-medium transition-all ${
                                    isSelected
                                      ? 'bg-[#0B4778] text-white shadow-sm'
                                      : isToday
                                      ? 'bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30'
                                      : 'hover:bg-gray-100 text-gray-900'
                                  }`}
                                >
                                  {date.getDate()}
                                </button>
                              );
                            })}
                          </div>
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => handleProfessionalDateRangeNavigate('next')}
                      className="p-1 hover:bg-gray-50 rounded transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center h-6 w-6"
                      title="Next period"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Status Dropdown - Desktop - Right aligned */}
                  <div className="relative my-appointments-status-dropdown-container ml-auto">
                    <button
                      type="button"
                      onClick={() => setMyAppointmentsStatusDropdownOpen(!myAppointmentsStatusDropdownOpen)}
                      className="w-full sm:w-auto px-3 py-2.5 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between shadow-sm hover:border-gray-300 transition-colors"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em'
                      }}
                    >
                      <span className="text-sm font-medium text-gray-900">
                        {appointmentFilters.status === '' ? 'All Status' :
                         appointmentFilters.status === 'PENDING' ? 'Pending' :
                         appointmentFilters.status === 'CONFIRMED' ? 'Confirmed' :
                         appointmentFilters.status === 'COMPLETED' ? 'Completed' :
                         appointmentFilters.status === 'CANCELLED' ? 'Cancelled' : 'All Status'}
                      </span>
                    </button>
                    
                    {myAppointmentsStatusDropdownOpen && (
                      <div className="absolute z-50 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto min-w-[150px]">
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setAppointmentFilters(prev => ({ ...prev, status: '' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                        >
                          All Status
                        </div>
                        <div className="border-t border-gray-100 my-1"></div>
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setAppointmentFilters(prev => ({ ...prev, status: 'PENDING' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                        >
                          Pending
                        </div>
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setAppointmentFilters(prev => ({ ...prev, status: 'CONFIRMED' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                        >
                          Confirmed
                        </div>
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setAppointmentFilters(prev => ({ ...prev, status: 'COMPLETED' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                        >
                          Completed
                        </div>
                        <div
                          className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setAppointmentFilters(prev => ({ ...prev, status: 'CANCELLED' }));
                            setMyAppointmentsStatusDropdownOpen(false);
                          }}
                        >
                          Cancelled
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mobile (below md): Date Range and Status + Navigation (no nested collapsible) */}
                <div className="md:hidden space-y-3">
                  {/* Mobile: Date Range and Status Dropdowns on Same Line */}
                  <div className="flex items-center gap-3">
                        {/* Date Range Dropdown */}
                        <div className="relative flex-1 my-appointments-date-range-dropdown-container">
                          <button
                            type="button"
                            onClick={() => {
                              setMyAppointmentsDateRangeDropdownOpen(!myAppointmentsDateRangeDropdownOpen);
                              // Close status dropdown if open
                              if (myAppointmentsStatusDropdownOpen) {
                                setMyAppointmentsStatusDropdownOpen(false);
                              }
                            }}
                            className="w-full px-3 py-2.5 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between shadow-sm hover:border-gray-300 transition-colors"
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: 'right 0.5rem center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '1.5em 1.5em'
                            }}
                          >
                            <span className="text-sm font-medium text-gray-900">
                              {professionalDateRangeType === 'today' ? 'Today' :
                               professionalDateRangeType === '3days' ? '3 Days' :
                               professionalDateRangeType === '1week' ? '1 Week' : '3 Days'}
                            </span>
                          </button>
                          
                          {myAppointmentsDateRangeDropdownOpen && (
                            <div className="absolute z-[9999] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto min-w-[120px] w-full">
                              <div
                                className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleProfessionalDateRangeChange('today');
                                  setMyAppointmentsDateRangeDropdownOpen(false);
                                }}
                                onTouchEnd={(e) => {
                                  e.preventDefault();
                                  handleProfessionalDateRangeChange('today');
                                  setMyAppointmentsDateRangeDropdownOpen(false);
                                }}
                              >
                                Today
                              </div>
                              <div
                                className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleProfessionalDateRangeChange('3days');
                                  setMyAppointmentsDateRangeDropdownOpen(false);
                                }}
                                onTouchEnd={(e) => {
                                  e.preventDefault();
                                  handleProfessionalDateRangeChange('3days');
                                  setMyAppointmentsDateRangeDropdownOpen(false);
                                }}
                              >
                                3 Days
                              </div>
                              <div
                                className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleProfessionalDateRangeChange('1week');
                                  setMyAppointmentsDateRangeDropdownOpen(false);
                                }}
                                onTouchEnd={(e) => {
                                  e.preventDefault();
                                  handleProfessionalDateRangeChange('1week');
                                  setMyAppointmentsDateRangeDropdownOpen(false);
                                }}
                              >
                                1 Week
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Status Dropdown */}
                        <div className="relative flex-1 my-appointments-status-dropdown-container">
                          <button
                            type="button"
                            onClick={() => {
                              setMyAppointmentsStatusDropdownOpen(!myAppointmentsStatusDropdownOpen);
                              // Close date range dropdown if open
                              if (myAppointmentsDateRangeDropdownOpen) {
                                setMyAppointmentsDateRangeDropdownOpen(false);
                              }
                            }}
                            className="w-full px-3 py-2.5 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between shadow-sm hover:border-gray-300 transition-colors"
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: 'right 0.5rem center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '1.5em 1.5em'
                            }}
                          >
                            <span className="text-sm font-medium text-gray-900">
                              {appointmentFilters.status === '' ? 'All Status' :
                               appointmentFilters.status === 'PENDING' ? 'Pending' :
                               appointmentFilters.status === 'CONFIRMED' ? 'Confirmed' :
                               appointmentFilters.status === 'COMPLETED' ? 'Completed' :
                               appointmentFilters.status === 'CANCELLED' ? 'Cancelled' : 'All Status'}
                            </span>
                          </button>
                          
                          {myAppointmentsStatusDropdownOpen && (
                            <div className="absolute z-50 left-0 right-0 sm:right-auto sm:left-auto sm:right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto min-w-[150px] w-full sm:w-auto">
                              <div
                                className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setAppointmentFilters(prev => ({ ...prev, status: '' }));
                                  setMyAppointmentsStatusDropdownOpen(false);
                                }}
                                onTouchEnd={(e) => {
                                  e.preventDefault();
                                  setAppointmentFilters(prev => ({ ...prev, status: '' }));
                                  setMyAppointmentsStatusDropdownOpen(false);
                                }}
                              >
                                All Status
                              </div>
                              <div className="border-t border-gray-100 my-1"></div>
                              <div
                                className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setAppointmentFilters(prev => ({ ...prev, status: 'PENDING' }));
                                  setMyAppointmentsStatusDropdownOpen(false);
                                }}
                                onTouchEnd={(e) => {
                                  e.preventDefault();
                                  setAppointmentFilters(prev => ({ ...prev, status: 'PENDING' }));
                                  setMyAppointmentsStatusDropdownOpen(false);
                                }}
                              >
                                Pending
                              </div>
                              <div
                                className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setAppointmentFilters(prev => ({ ...prev, status: 'CONFIRMED' }));
                                  setMyAppointmentsStatusDropdownOpen(false);
                                }}
                                onTouchEnd={(e) => {
                                  e.preventDefault();
                                  setAppointmentFilters(prev => ({ ...prev, status: 'CONFIRMED' }));
                                  setMyAppointmentsStatusDropdownOpen(false);
                                }}
                              >
                                Confirmed
                              </div>
                              <div
                                className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setAppointmentFilters(prev => ({ ...prev, status: 'COMPLETED' }));
                                  setMyAppointmentsStatusDropdownOpen(false);
                                }}
                                onTouchEnd={(e) => {
                                  e.preventDefault();
                                  setAppointmentFilters(prev => ({ ...prev, status: 'COMPLETED' }));
                                  setMyAppointmentsStatusDropdownOpen(false);
                                }}
                              >
                                Completed
                              </div>
                              <div
                                className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 active:bg-gray-100 cursor-pointer touch-manipulation"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setAppointmentFilters(prev => ({ ...prev, status: 'CANCELLED' }));
                                  setMyAppointmentsStatusDropdownOpen(false);
                                }}
                                onTouchEnd={(e) => {
                                  e.preventDefault();
                                  setAppointmentFilters(prev => ({ ...prev, status: 'CANCELLED' }));
                                  setMyAppointmentsStatusDropdownOpen(false);
                                }}
                              >
                                Cancelled
                              </div>
                            </div>
                          )}
                        </div>
                  </div>

                  {/* Mobile: Date Range Navigation */}
                  <div className="flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                        <button
                          onClick={() => handleProfessionalDateRangeNavigate('prev')}
                          className="p-1 hover:bg-gray-50 rounded transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center h-6 w-6"
                          title="Previous period"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <div className="relative professional-date-picker-container flex items-center">
                          <button
                            type="button"
                            onClick={() => {
                              setIsProfessionalDatePickerOpen(!isProfessionalDatePickerOpen);
                              setProfessionalDatePickerMonth(new Date(professionalDateRangeStart));
                            }}
                            className="flex items-center gap-2 px-3 hover:bg-gray-50 rounded transition-colors"
                          >
                            <Calendar className="w-4 h-4 text-[#0B4778]" />
                            <span className="text-sm font-medium text-gray-900">
                              {formatProfessionalDateRange(professionalDateRangeType, professionalDateRangeStart)}
                            </span>
                          </button>
                          
                          {isProfessionalDatePickerOpen && (
                            <>
                              {/* Mobile: Fixed overlay */}
                              <div className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden" onClick={() => setIsProfessionalDatePickerOpen(false)}></div>
                              <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 sm:absolute sm:inset-x-auto sm:top-full sm:translate-y-0 sm:left-0 sm:right-auto sm:mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-[100] min-w-[280px] w-auto sm:w-auto max-w-[calc(100vw-2rem)] sm:max-w-none">
                                <div className="flex items-center justify-between mb-4">
                                  <h3 className="text-sm font-medium text-gray-900">Select Date</h3>
                                  <button
                                    onClick={() => setIsProfessionalDatePickerOpen(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                
                                {/* Calendar navigation */}
                                <div className="flex items-center justify-between mb-4">
                                  <button
                                    onClick={() => {
                                      const newMonth = new Date(professionalDatePickerMonth);
                                      newMonth.setMonth(newMonth.getMonth() - 1);
                                      setProfessionalDatePickerMonth(newMonth);
                                    }}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                  >
                                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                                  </button>
                                  <span className="text-sm font-medium text-gray-900">
                                    {professionalDatePickerMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                  </span>
                                  <button
                                    onClick={() => {
                                      const newMonth = new Date(professionalDatePickerMonth);
                                      newMonth.setMonth(newMonth.getMonth() + 1);
                                      setProfessionalDatePickerMonth(newMonth);
                                    }}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                  >
                                    <ChevronRight className="w-4 h-4 text-gray-600" />
                                  </button>
                                </div>
                                
                                {/* Calendar grid */}
                                <div className="grid grid-cols-7 gap-1 mb-2">
                                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                    <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                                      {day}
                                    </div>
                                  ))}
                                </div>
                                <div className="grid grid-cols-7 gap-1">
                                  {generateProfessionalCalendarDays(
                                    professionalDatePickerMonth.getFullYear(),
                                    professionalDatePickerMonth.getMonth()
                                  ).map((date, index) => {
                                    if (!date) {
                                      return <div key={index} className="h-8"></div>;
                                    }
                                    
                                    const { start } = getProfessionalDateRange(professionalDateRangeType, professionalDateRangeStart);
                                    const isSelected = date.toDateString() === start.toDateString();
                                    const isToday = date.toDateString() === new Date().toDateString();
                                    
                                    return (
                                      <button
                                        key={index}
                                        onClick={() => handleProfessionalDateSelect(date)}
                                        className={`h-8 rounded-md text-sm font-medium transition-all ${
                                          isSelected
                                            ? 'bg-[#0B4778] text-white shadow-sm'
                                            : isToday
                                            ? 'bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30'
                                            : 'hover:bg-gray-100 text-gray-900'
                                        }`}
                                      >
                                        {date.getDate()}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        <button
                          onClick={() => handleProfessionalDateRangeNavigate('next')}
                          className="p-1 hover:bg-gray-50 rounded transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center h-6 w-6"
                          title="Next period"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                </div>
              </div>
          </>
        );

        return (
          <div className="flex flex-col gap-6 pb-20 lg:pb-6">
            {/* Mobile-only: collapsible controls header */}
            <div className="md:hidden">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    const nextOpen = !isMobileMyAppointmentsControlsOpen;
                    if (!nextOpen) {
                      // Close any open dropdowns/modals within the controls when collapsing
                      setMyAppointmentsStatusDropdownOpen(false);
                      setMyAppointmentsDateRangeDropdownOpen(false);
                      setIsProfessionalDatePickerOpen(false);
                    }
                    setIsMobileMyAppointmentsControlsOpen(nextOpen);
                  }}
                  className="w-full flex items-center justify-between gap-3 bg-[#0B4778] text-white hover:bg-[#093a63] transition-colors px-4 py-3"
                  aria-expanded={isMobileMyAppointmentsControlsOpen}
                  aria-controls="my-appointments-mobile-controls"
                >
                  <div className="min-w-0 text-left">
                    <div className="text-sm font-semibold truncate flex items-center gap-2">
                      <Calendar className="w-4 h-4 flex-shrink-0 text-white/90" />
                      {formatProfessionalDateRange(professionalDateRangeType, professionalDateRangeStart)}
                    </div>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-white/90 transition-transform ${isMobileMyAppointmentsControlsOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isMobileMyAppointmentsControlsOpen && (
                  <div id="my-appointments-mobile-controls" className="p-4 space-y-4 border-t border-gray-200">
                    {myAppointmentsControlsContent}
                  </div>
                )}
              </div>
            </div>

            {/* Tablet/Desktop: existing controls unchanged */}
            <div className="hidden md:block">
              {myAppointmentsControlsContent}
            </div>

            <div className="space-y-6">
              {/* Professional Appointments List */}
              {professionalAppointmentsLoading ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading your appointments...</p>
                  </div>
                </div>
              ) : professionalAppointments && professionalAppointments.length > 0 ? (
                <>
                  {/* Filter appointments based on filters */}
                  {(() => {
                    const filteredAppointments = professionalAppointments.filter(appointment => {
                      if (appointmentFilters.status && appointment.status !== appointmentFilters.status) return false;
                      return true;
                    });

                    // Grid View
                    if (professionalAppointmentView === 'grid') {
                      // Check if filtered results are empty for grid view
                      if (filteredAppointments.length === 0) {
                        return (
                          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="text-center text-gray-500">
                              <i className="bi bi-calendar2-x text-gray-400 text-4xl mb-4 block"></i>
                              <p className="text-sm font-medium text-gray-600">No appointments for selected period.</p>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div className="md:bg-white md:rounded-lg md:shadow-sm md:border md:border-gray-200">
                          <div className="md:p-6">
                            <div className="grid gap-4">
                              {filteredAppointments.map((appointment) => (
                                <div 
                                  key={appointment.id} 
                                  onClick={() => {
                                    if (window.innerWidth < 768) {
                                      navigate(`/appointments/${appointment.id}?from=${activeSection}`);
                                    }
                                  }}
                                  className="grid grid-cols-[1fr_auto] md:block items-center md:items-start gap-3 bg-white border border-gray-200 rounded-lg px-4 pt-4 pb-4 hover:shadow-md transition-shadow cursor-pointer md:cursor-default"
                                >
                                  <div className="min-w-0">
                                    <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-start space-x-3">
                                      {/* Calendar Icon with Booking Initiator indicator */}
                                      <div className="relative flex-shrink-0">
                                        {/* Booking Initiator Icon - positioned at top-right corner */}
                                        {appointment.bookingInitiator && (
                                          <div className="absolute -top-1 right-0 z-10">
                                            {appointment.bookingInitiator === 'CUSTOMER' ? (
                                              <span 
                                                className="inline-flex items-center justify-center border w-4 h-4 rounded-full bg-green-100 border-green-200"
                                                title="Customer booked"
                                              >
                                                <UserIcon className="w-2.5 h-2.5 text-green-700" />
                                              </span>
                                            ) : (
                                              <span 
                                                className="inline-flex items-center justify-center border w-4 h-4 rounded-md bg-orange-100 border-orange-200"
                                                title="Booked by staff"
                                              >
                                                <Pencil className="w-2.5 h-2.5 text-orange-700" />
                                              </span>
                                            )}
                                          </div>
                                        )}
                                        <div className="w-10 h-10 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-[#0B4778]">
                                          <i className="bi bi-calendar3 text-lg"></i>
                                        </div>
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                          <h3 className="font-medium text-gray-900">
                                            {appointment.customer_first_name} {appointment.customer_last_name}
                                          </h3>
                                          <span className={`hidden md:inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                            appointment.status === 'CONFIRMED' 
                                              ? 'bg-green-100 text-green-800'
                                              : appointment.status === 'PENDING'
                                              ? 'bg-yellow-100 text-yellow-800'
                                              : appointment.status === 'COMPLETED'
                                              ? 'bg-[#0B4778]/20 text-[#0B4778]'
                                              : 'bg-red-100 text-red-800'
                                          }`}>
                                            {String(appointment.status).charAt(0) + String(appointment.status).slice(1).toLowerCase()}
                                          </span>
                                        </div>
                                        {/* Mobile: Stack date/time with duration, and service names on separate lines */}
                                        <div className="md:hidden flex flex-col gap-1.5 text-sm">
                                          <div className="text-gray-600">
                                            {!appointment?.scheduledAt && (appointment as any)?.flexScheduledAt?.earliest && (appointment as any)?.flexScheduledAt?.latest ? (
                                              <div className="flex flex-col gap-0.5">
                                                <span className="whitespace-nowrap">{getAppointmentStartIso(appointment) ? formatDate(getAppointmentStartIso(appointment)!) : 'Date TBD'} - {getTotalDuration(appointment)} min</span>
                                                <span>{getAppointmentTimeDisplay(appointment)}</span>
                                              </div>
                                            ) : (
                                              <span className="whitespace-nowrap">{getAppointmentDateTimeDisplay(appointment)} - {getTotalDuration(appointment)} min</span>
                                            )}
                                          </div>
                                          {appointment.assignments && appointment.assignments.length > 0 ? (
                                            <div className="flex flex-col gap-0.5 mt-0.5">
                                              {appointment.assignments.map((assignment: any, index: number) => {
                                                const icon = getAssignmentIcon(assignment);
                                                const professionalName = assignment.professionalFirstName && assignment.professionalLastName
                                                  ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.`
                                                  : null;
                                                const showProfessionalName = professionals.length > 1 && settings.showProfessionalNameOnAssignment && professionalName;
                                                return (
                                                  <div key={index} className="flex items-center gap-2 text-gray-900 font-medium">
                                                    <span className="text-base flex-shrink-0">{icon || '📦'}</span>
                                                    <span>
                                                      {assignment.serviceName || 'Unknown Service'}
                                                      {showProfessionalName && <span className="text-gray-600"> ({professionalName})</span>}
                                                    </span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          ) : (
                                            <div className="text-gray-500 italic mt-0.5">No services assigned</div>
                                          )}
                                        </div>
                                        {/* Desktop: Services on separate lines with professional names */}
                                        <div className="hidden md:flex flex-col gap-1 text-sm text-gray-600">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span>{getAppointmentDateTimeDisplay(appointment)}</span>
                                            <span>•</span>
                                            <span>{getTotalDuration(appointment)} min</span>
                                          </div>
                                          {appointment.assignments && appointment.assignments.length > 0 ? (
                                            appointment.assignments.map((assignment: any, index: number) => {
                                              const icon = getAssignmentIcon(assignment);
                                              const professionalName = assignment.professionalFirstName && assignment.professionalLastName
                                                ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.`
                                                : null;
                                              const showProfessionalName = professionals.length > 1 && settings.showProfessionalNameOnAssignment && professionalName;
                                              return (
                                                <div key={index} className="flex items-center gap-1">
                                                  <span className="text-xs">{icon || '📦'}</span>
                                                  <span>
                                                    {assignment.serviceName || 'Unknown Service'}
                                                    {showProfessionalName && <span className="text-gray-600"> ({professionalName})</span>}
                                                  </span>
                                                </div>
                                              );
                                            })
                                          ) : (
                                            <span>No services</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="hidden md:flex items-center space-x-2">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/appointments/${appointment.id}?from=${activeSection}`);
                                          }}
                                          className="aacore-btn2-primary text-sm"
                                        >
                                          <Eye className="w-4 h-4" />
                                          <span>View Details</span>
                                        </button>
                                    </div>
                                  </div>
                                  </div>
                                  
                                  {/* Mobile right column: status (top-aligned) + chevron (vertically centered) */}
                                  <div className="md:hidden self-stretch relative w-12">
                                    <div className="absolute top-0 right-0">
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        appointment.status === 'CONFIRMED' 
                                          ? 'bg-green-100 text-green-800'
                                          : appointment.status === 'PENDING'
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : appointment.status === 'COMPLETED'
                                          ? 'bg-[#0B4778]/20 text-[#0B4778]'
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                        {String(appointment.status).charAt(0) + String(appointment.status).slice(1).toLowerCase()}
                                      </span>
                                    </div>
                                    <div className="absolute inset-y-0 right-0 flex items-center text-gray-400">
                                      <ChevronRight className="w-5 h-5" />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // Table View
                    if (professionalAppointmentView === 'table') {
                      // Check if filtered results are empty for table view
                      if (filteredAppointments.length === 0) {
                        return (
                          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="text-center text-gray-500">
                              <i className="bi bi-calendar2-x text-gray-400 text-4xl mb-4 block"></i>
                              <p className="text-sm font-medium text-gray-600">No appointments for selected period.</p>
                            </div>
                          </div>
                        );
                      }
                      
                      return (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-full">
                          <div className="overflow-x-auto w-full">
                            <table className="w-full divide-y divide-gray-200 border border-gray-200 border-collapse">
                              <thead className="bg-gray-50">
                                <tr className="divide-x divide-gray-200">
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Customer
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Service
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date & Time
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Duration
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {filteredAppointments.map((appointment) => (
                                  <tr key={appointment.id} className="hover:bg-gray-50 divide-x divide-gray-200">
                                    <td className="px-6 py-4 whitespace-nowrap [vertical-align:top]">
                                      <div className="flex items-center">
                                        {/* Calendar Icon with Booking Initiator indicator */}
                                        <div className="relative mr-3 flex-shrink-0">
                                          {/* Booking Initiator Icon - positioned at top-right corner */}
                                          {appointment.bookingInitiator && (
                                            <div className="absolute -top-1 right-0 z-10">
                                              {appointment.bookingInitiator === 'CUSTOMER' ? (
                                                <span 
                                                  className="inline-flex items-center justify-center border w-4 h-4 rounded-full bg-green-100 border-green-200"
                                                  title="Customer booked"
                                                >
                                                  <UserIcon className="w-2.5 h-2.5 text-green-700" />
                                                </span>
                                              ) : (
                                                <span 
                                                  className="inline-flex items-center justify-center border w-4 h-4 rounded-md bg-orange-100 border-orange-200"
                                                  title="Booked by staff"
                                                >
                                                  <Pencil className="w-2.5 h-2.5 text-orange-700" />
                                                </span>
                                              )}
                                            </div>
                                          )}
                                          <div className="w-8 h-8 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-[#0B4778]">
                                            <i className="bi bi-calendar3"></i>
                                          </div>
                                        </div>
                                        <div>
                                          <div className="text-sm font-medium text-gray-900">
                                            {appointment.customer_first_name} {appointment.customer_last_name}
                                          </div>
                                          <div className="text-sm text-gray-500">
                                            {appointment.customer_email && appointment.customer_email.includes('@appointment.local')
                                              ? 'Guest Customer (Info not provided)'
                                              : appointment.customer_email}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 [vertical-align:top]">
                                      <div className="text-sm text-gray-900 flex flex-col gap-1">
                                        {appointment.assignments && appointment.assignments.length > 0 ? (
                                          appointment.assignments.map((assignment: any, index: number) => {
                                            const icon = getAssignmentIcon(assignment);
                                            const professionalName = assignment.professionalFirstName && assignment.professionalLastName
                                              ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.`
                                              : null;
                                            const showProfessionalName = professionals.length > 1 && settings.showProfessionalNameOnAssignment && professionalName;
                                            return (
                                              <div key={index} className="flex items-center gap-1.5">
                                                <span className="text-sm">{icon || '📦'}</span>
                                                <span>
                                                  {assignment.serviceName || 'Unknown Service'}
                                                  {showProfessionalName && <span className="text-gray-600"> ({professionalName})</span>}
                                                </span>
                                              </div>
                                            );
                                          })
                                        ) : (
                                          <span>No services assigned</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap [vertical-align:top]">
                                      <div className="text-sm text-gray-900">{getAppointmentStartIso(appointment) ? formatDate(getAppointmentStartIso(appointment)!) : 'Date TBD'}</div>
                                      <div className="text-sm text-gray-500">{getAppointmentTimeDisplay(appointment)}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap [vertical-align:top]">
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                        appointment.status === 'CONFIRMED' 
                                          ? 'bg-green-100 text-green-800'
                                          : appointment.status === 'PENDING'
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : appointment.status === 'COMPLETED'
                                          ? 'bg-[#0B4778]/20 text-[#0B4778]'
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                        {String(appointment.status).charAt(0) + String(appointment.status).slice(1).toLowerCase()}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 [vertical-align:top]">
                                      {getTotalDuration(appointment)} min
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium [vertical-align:top]">
                                      <button
                                        onClick={() => navigate(`/appointments/${appointment.id}?from=${activeSection}`)}
                                        className="text-[#0B4778] hover:text-[#0B4778] flex items-center gap-1"
                                      >
                                        <Eye className="w-4 h-4" />
                                        View
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    }

                    // Calendar View
                    if (professionalAppointmentView === 'calendar') {
                      const { start, end } = getProfessionalDateRange(professionalDateRangeType, professionalDateRangeStart);
                      return (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative z-0">
                          <AppointmentCalendar
                            appointments={filteredAppointments}
                            movingAppointmentId={movingAppointmentId}
                            viewMode={calendarViewMode}
                            onViewModeChange={setCalendarViewMode}
                            allowBookingInTimeOffSlots={true}
                            onRangeNavigate={handleProfessionalDateRangeNavigate}
                            onAppointmentMove={async (appointmentId: string, newDate: Date) => {
                              if (movingAppointmentId && String(movingAppointmentId) !== String(appointmentId)) return;
                              setMovingAppointmentId(String(appointmentId));
                              try {
                                // Optimistic update for professional view.
                                setProfessionalAppointments((prev) =>
                                  prev.map((a) =>
                                    String(a.id) === String(appointmentId)
                                      ? { ...a, scheduledAt: newDate.toISOString() }
                                      : a
                                  )
                                );

                                await apiService.updateAppointment(appointmentId, {
                                  scheduledAt: newDate.toISOString(),
                                });

                                toast.success('Appointment rescheduled');
                                loadProfessionalAppointments();
                              } catch (error) {
                                console.error('Error moving appointment:', error);
                                toast.error('Failed to reschedule appointment');
                                loadProfessionalAppointments();
                              } finally {
                                setMovingAppointmentId(null);
                              }
                            }}
                            onAssignmentDurationChange={async ({ appointmentId, assignmentId, durationMinutes }) => {
                              if (movingAppointmentId && String(movingAppointmentId) !== String(appointmentId)) return;
                              setMovingAppointmentId(String(appointmentId));
                              try {
                                await apiService.updateAssignmentDuration({
                                  appointmentId,
                                  assignmentId,
                                  durationMinutes,
                                });
                                toast.success('Duration updated');
                                loadProfessionalAppointments();
                              } catch (error: any) {
                                console.error('Error updating assignment duration:', error);
                                const msg = error?.response?.data?.message || 'Failed to update duration';
                                toast.error(msg);
                                loadProfessionalAppointments();
                              } finally {
                                setMovingAppointmentId(null);
                              }
                            }}
                            onAppointmentClick={(appointment: Appointment) => {
                              setSelectedAppointment(appointment);
                              setIsAppointmentDetailsModalOpen(true);
                            }}
                            onTimeSlotDoubleClick={(date: Date, time: string) => {
                              // Format date as YYYY-MM-DD
                              const dateStr = formatLocalYmd(date);
                              // Navigate to create appointment page with pre-filled date and time
                              // Include from=my-appointments since this is in the my-appointments section
                              navigate(buildCreateAppointmentUrl({ date: dateStr, time, from: 'my-appointments' }));
                            }}
                            dateRange={{ start, end }}
                            showProfessionalNameInAssignmentBlock={
                              professionals.length > 1 && settings.showProfessionalNameOnAssignment
                            }
                            operatingHourRange={calendarOperatingHourRange}
                            timeOffs={timeOffs}
                            currentProfessionalId={user?.id}
                          />
                        </div>
                      );
                    }

                    return null;
                  })()}
                </>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="text-center text-gray-500">
                    <i className="bi bi-calendar2-week text-[#0B4778] text-4xl mb-4 block"></i>
                    <p className="text-lg font-medium">No appointments for selected period.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
        
      case 'my-services':
        // Get unique categories with "My Favorites" option
        const hasProfessionalFavorites = professionalFavoriteServiceIds.size > 0;
        const myServicesCategories = [
          { id: 'my-services', name: 'My Services', icon: '🛠️' },
          ...(hasProfessionalFavorites ? [{ id: 'my-favorites', name: 'My Favorites', icon: '❤️' }] : []),
          ...categories
        ];

        // Filter services by category or "My Services" selection
        let filteredMyServices = myServicesCategoryFilter === 'my-services'
          ? services.filter(service => professionalServices.some(ps => ps.id === service.id))
          : myServicesCategoryFilter === 'my-favorites'
          ? services.filter(service => professionalFavoriteServiceIds.has(service.id))
          : myServicesCategoryFilter
          ? services.filter(service => service.category?.id === myServicesCategoryFilter)
          : services;
        if (myServicesSearch.trim()) {
          filteredMyServices = searchServices(filteredMyServices, myServicesSearch);
        }

        return (
          <div className="space-y-6">
            <div className="flex flex-col gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">My Services</h2>
                <p className="text-gray-600 mt-1">Select the services you can perform as a professional</p>
              </div>

              {/* Search, Category Filter, and View Toggle */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full md:w-auto md:flex-1">
                  {/* Search */}
                  <div className="relative w-full md:max-w-md md:flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search services..."
                      value={myServicesSearch}
                      onChange={(e) => setMyServicesSearch(e.target.value)}
                      className={`w-full h-10 pl-10 ${myServicesSearch ? 'pr-10' : 'pr-3'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]`}
                    />
                    {myServicesSearch && (
                      <button
                        type="button"
                        onClick={() => setMyServicesSearch('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                        aria-label="Clear search"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Category Filter */}
                  <div className="relative my-services-category-dropdown-container w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => setMyServicesCategoryDropdownOpen(!myServicesCategoryDropdownOpen)}
                      className="w-full sm:w-auto h-10 px-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between min-w-[150px] gap-2"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em'
                      }}
                    >
                      <span className="flex items-center gap-2 text-gray-900 text-sm leading-none">
                        {myServicesCategoryFilter === 'my-services' ? (
                          <>
                            <span className="text-base leading-none">🛠️</span>
                            <span>My Services</span>
                          </>
                        ) : myServicesCategoryFilter === 'my-favorites' ? (
                          <>
                            <span className="text-base leading-none">❤️</span>
                            <span>My Favorites</span>
                          </>
                        ) : myServicesCategoryFilter ? (
                          <>
                            {categories.find(c => c.id === myServicesCategoryFilter)?.icon && (
                              <span className="text-base leading-none">{categories.find(c => c.id === myServicesCategoryFilter)?.icon}</span>
                            )}
                            <span>{categories.find(c => c.id === myServicesCategoryFilter)?.name || 'All Services'}</span>
                          </>
                        ) : (
                          <>
                            <List className="w-4 h-4" />
                            <span>All Services</span>
                          </>
                        )}
                      </span>
                    </button>
                    
                    {myServicesCategoryDropdownOpen && (
                      <div className="absolute z-50 w-auto min-w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                        <div
                          className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer flex items-center gap-2 whitespace-nowrap"
                          onClick={() => {
                            setMyServicesCategoryFilter('my-services');
                            setMyServicesCategoryDropdownOpen(false);
                          }}
                        >
                          <span className="text-lg">🛠️</span>
                          <span>My Services</span>
                        </div>
                        {hasProfessionalFavorites && (
                          <>
                            <div
                              className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer flex items-center gap-2 whitespace-nowrap"
                              onClick={() => {
                                setMyServicesCategoryFilter('my-favorites');
                                setMyServicesCategoryDropdownOpen(false);
                              }}
                            >
                              <span className="text-lg">❤️</span>
                              <span>My Favorites</span>
                            </div>
                            <div className="border-t border-gray-200 my-1"></div>
                          </>
                        )}
                        <div
                          className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer flex items-center gap-2 whitespace-nowrap"
                          onClick={() => {
                            setMyServicesCategoryFilter(undefined);
                            setMyServicesCategoryDropdownOpen(false);
                          }}
                        >
                          <List className="w-4 h-4" />
                          <span>All Services</span>
                        </div>
                        <div className="border-t border-gray-200 my-1"></div>
                        {myServicesCategories.filter(cat => cat.id !== 'my-services' && cat.id !== 'my-favorites').map((category: { id: string; name: string; icon?: string }) => (
                          <div
                            key={category.id}
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer flex items-center gap-2 whitespace-nowrap"
                            onClick={() => {
                              setMyServicesCategoryFilter(category.id);
                              setMyServicesCategoryDropdownOpen(false);
                            }}
                          >
                            {category.icon && (
                              <span className="text-lg">{category.icon}</span>
                            )}
                            <span>{category.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* View Toggle Buttons */}
                <div className="hidden md:flex items-center gap-2 ml-auto">
                  <button 
                    onClick={() => setMyServicesViewMode('grid')}
                    title="Grid View"
                    className={`${myServicesViewMode === 'grid' ? 'aacore-btn2-primary' : 'aacore-btn2-outline bg-gray-100 hover:bg-gray-200'}`}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => setMyServicesViewMode('list')}
                    title="List View"
                    className={`${myServicesViewMode === 'list' ? 'aacore-btn2-primary' : 'aacore-btn2-outline bg-gray-100 hover:bg-gray-200'}`}
                  >
                    <Table className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Services Selection Content */}
            <div className="space-y-6">
              {(professionalServicesLoading || servicesLoading) ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading services...</p>
                  </div>
                </div>
              ) : (
                <div className="md:bg-white md:rounded-lg md:shadow-sm md:border md:border-gray-200">
                  <div className="p-0 md:p-6">
                    {/* Service Category and Selected Count */}
                    <div className="mb-4 flex items-center justify-between">
                      <p className="hidden md:flex text-lg font-medium text-gray-900 items-center gap-2">
                        {myServicesCategoryFilter === 'my-services' ? (
                          <>
                            <span className="text-lg">🛠️</span>
                            <span>My Services</span>
                          </>
                        ) : myServicesCategoryFilter ? (
                          <>
                            {categories.find(c => c.id === myServicesCategoryFilter)?.icon && (
                              <span className="text-lg">{categories.find(c => c.id === myServicesCategoryFilter)?.icon}</span>
                            )}
                            <span>{categories.find(c => c.id === myServicesCategoryFilter)?.name || 'All Services'}</span>
                          </>
                        ) : (
                          <>
                            <List className="w-5 h-5" />
                            <span>All Services</span>
                          </>
                        )}
                      </p>
                      <div className="flex items-center gap-3 w-full md:w-auto justify-between md:justify-end">
                        {filteredMyServices.length > 0 && myServicesCategoryFilter !== 'my-services' && myServicesCategoryFilter !== 'my-favorites' && (() => {
                          const selectedCount = professionalServices.filter(ps => filteredMyServices.some(fs => fs.id === ps.id)).length;
                          const allSelected = selectedCount === filteredMyServices.length;
                          const noneSelected = selectedCount === 0;
                          return (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={handleSelectAllServices}
                                disabled={allSelected}
                                className={`shadow-sm hover:shadow-md ${
                                  allSelected 
                                    ? 'aacore-btn2-secondary opacity-50 cursor-not-allowed' 
                                    : 'aacore-btn2-primary'
                                }`}
                              >
                                <CheckSquare className="w-4 h-4" />
                                Select All
                              </button>
                              <button
                                onClick={handleDeselectAllServices}
                                disabled={noneSelected}
                                className={`shadow-sm hover:shadow-md aacore-btn2-secondary ${
                                  noneSelected 
                                    ? 'opacity-50 cursor-not-allowed' 
                                    : ''
                                }`}
                              >
                                <Square className="w-4 h-4" />
                                Deselect All
                              </button>
                            </div>
                          );
                        })()}
                        {filteredMyServices.length > 0 && (
                          <p className="text-sm font-medium text-gray-700">
                            {myServicesCategoryFilter === 'my-services' 
                              ? `${professionalServices.filter(ps => filteredMyServices.some(fs => fs.id === ps.id)).length} services selected`
                              : myServicesCategoryFilter === 'my-favorites'
                              ? `${filteredMyServices.length} favorite service${filteredMyServices.length === 1 ? '' : 's'}`
                              : `Selected ${professionalServices.filter(ps => filteredMyServices.some(fs => fs.id === ps.id)).length}/${filteredMyServices.length}`
                            }
                          </p>
                        )}
                      </div>
                    </div>
                    {myServicesViewMode === 'grid' ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredMyServices.map((service) => {
                          const isSelected = professionalServices.some(ps => ps.id === service.id);
                          const resolvedIcon = (service as any).icon || service.icon || service.category?.icon || '📦';
                          return (
                            <div 
                              key={service.id} 
                              className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-sm relative"
                              onClick={() => handleToggleServiceSelection(service.id)}
                            >
                              {/* Selection indicator in top right */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleServiceSelection(service.id);
                                }}
                                className={`absolute top-3 right-3 flex items-center justify-center transition-colors ${
                                  isSelected
                                    ? 'w-8 h-8 bg-green-600 rounded text-white hover:bg-green-700'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                                aria-label={isSelected ? 'Deselect service' : 'Select service'}
                              >
                                {isSelected ? (
                                  <Check className="w-5 h-5" />
                                ) : (
                                  <Plus className="w-6 h-6" />
                                )}
                              </button>
                              
                              {/* Service Name and Description */}
                              <div className="mb-4">
                                <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
                                  <span className="text-lg flex-shrink-0">{resolvedIcon}</span>
                                  {service.name}
                                  {/* Heart icon after service name for all categories */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleProfessionalFavoriteToggle(e, service.id);
                                    }}
                                    className={`p-1 rounded-full transition-all ${
                                      professionalFavoriteServiceIds.has(service.id)
                                        ? 'text-red-500 hover:text-red-600'
                                        : 'text-gray-400 hover:text-red-500'
                                    }`}
                                    aria-label={professionalFavoriteServiceIds.has(service.id) ? 'Remove from favorites' : 'Add to favorites'}
                                    title={professionalFavoriteServiceIds.has(service.id) ? 'Remove from favorites' : 'Add to favorites'}
                                  >
                                    <Heart 
                                      className={`w-4 h-4 ${professionalFavoriteServiceIds.has(service.id) ? 'fill-current' : ''}`}
                                    />
                                  </button>
                                </h3>
                                {service.description && (
                                  <p className="text-sm text-gray-700">{service.description}</p>
                                )}
                              </div>
                              
                              {/* Service Details with Icons - bottom left */}
                              <div className="flex items-center gap-4 text-sm text-gray-700">
                                <div className="flex items-center gap-1.5">
                                  <Clock className="w-4 h-4" />
                                  <span>{service.durationMinutes} min</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <i className="bi bi-tag text-sm"></i>
                                  <span>${typeof service.price === 'number' ? service.price.toFixed(2) : service.price}</span>
                                </div>
                                {service.category && (
                                  <div className="flex items-center gap-1.5">
                                    <Briefcase className="w-4 h-4" />
                                    <span>{service.category.name}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {filteredMyServices.map((service) => {
                          const isSelected = professionalServices.some(ps => ps.id === service.id);
                          const resolvedIcon = (service as any).icon || service.icon || service.category?.icon || '📦';
                          return (
                            <div 
                              key={service.id} 
                              className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 relative ${
                                isSelected 
                                  ? 'border-[#0B4778] bg-[#0B4778]/10 shadow-md' 
                                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                              }`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleToggleServiceSelection(service.id);
                              }}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex items-center space-x-3 flex-1">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    isSelected 
                                      ? 'bg-[#0B4778] text-white' 
                                      : 'bg-gray-200 text-gray-600'
                                  }`}>
                                    {isSelected ? '✓' : '+'}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h3 className="font-medium text-gray-900 mb-1 flex items-center gap-2">
                                      <span className="text-lg flex-shrink-0">{resolvedIcon}</span>
                                      {service.name}
                                      {/* Heart icon after service name for all categories */}
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleProfessionalFavoriteToggle(e, service.id);
                                        }}
                                        className={`p-1 rounded-full transition-all ${
                                          professionalFavoriteServiceIds.has(service.id)
                                            ? 'text-red-500 hover:text-red-600'
                                            : 'text-gray-400 hover:text-red-500'
                                        }`}
                                        aria-label={professionalFavoriteServiceIds.has(service.id) ? 'Remove from favorites' : 'Add to favorites'}
                                        title={professionalFavoriteServiceIds.has(service.id) ? 'Remove from favorites' : 'Add to favorites'}
                                      >
                                        <Heart 
                                          className={`w-4 h-4 ${professionalFavoriteServiceIds.has(service.id) ? 'fill-current' : ''}`}
                                        />
                                      </button>
                                    </h3>
                                    {service.description && (
                                      <p className="text-sm text-gray-600 line-clamp-1 mb-2">{service.description}</p>
                                    )}
                                    <div className="flex items-center gap-4 text-sm text-gray-600">
                                      <span>{service.durationMinutes} min</span>
                                      <span>${service.price}</span>
                                      {service.category && (
                                        <span className="text-xs text-gray-500">
                                          Category: {service.category.name}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                                  isSelected ? 'bg-[#0B4778]/20 text-[#0B4778]' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {isSelected ? 'Selected' : 'Not Selected'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {filteredMyServices.length === 0 && (
                      <div className="text-center text-gray-500 py-8">
                        <span className="text-4xl mb-4 block">🛠️</span>
                        <p className="text-lg font-medium">
                          {myServicesSearch.trim()
                            ? 'No matching services'
                            : myServicesCategoryFilter === 'my-services' 
                            ? 'Your service list is empty'
                            : 'No services available'
                          }
                        </p>
                        <p className="text-sm mb-6">
                          {myServicesSearch.trim()
                            ? 'Try a different search term or clear the search.'
                            : myServicesCategoryFilter === 'my-services'
                            ? 'Select the services you can perform to make them available for booking.'
                            : myServicesCategoryFilter 
                              ? 'No services found in the selected category.'
                              : 'There are no services configured for this business.'
                          }
                        </p>
                        {myServicesSearch.trim() ? (
                          <button
                            onClick={() => setMyServicesSearch('')}
                            className="aacore-btn2-primary shadow-sm hover:shadow-md"
                          >
                            Clear Search
                          </button>
                        ) : myServicesCategoryFilter ? (
                          <button
                            onClick={() => {
                              setMyServicesCategoryFilter(undefined);
                              setMyServicesCategoryDropdownOpen(false);
                            }}
                            className="aacore-btn2-primary shadow-sm hover:shadow-md"
                          >
                            <LayoutGrid className="w-4 h-4" />
                            Browse All Services
                          </button>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
        
      case 'my-work-schedule':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">My Work Schedule</h2>
                <p className="text-gray-600 mt-1">Manage your weekly work schedule</p>
              </div>
            </div>

            <div className="md:bg-white md:rounded-lg md:shadow-sm md:border md:border-gray-200">
              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6" aria-label="Tabs">
                  <button
                    onClick={() => setWorkScheduleActiveTab('weekly-hours')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      workScheduleActiveTab === 'weekly-hours'
                        ? 'border-[#0B4778] text-[#0B4778]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Weekly Hours
                  </button>
                  <button
                    onClick={() => setWorkScheduleActiveTab('time-off')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      workScheduleActiveTab === 'time-off'
                        ? 'border-[#0B4778] text-[#0B4778]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Time Off
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-4 md:p-6">
                {workScheduleActiveTab === 'weekly-hours' && (
                  <div className="space-y-4 md:space-y-6">
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Weekly Hours</h3>
                      <p className="text-gray-600 text-sm">These hours repeat every week</p>
                    </div>
                    {professionalScheduleLoading ? (
                      <div className="p-6">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                          <p className="text-gray-600">Loading your work schedule...</p>
                        </div>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200 -mx-4 md:mx-0">
                        {[0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
                          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                          const dayName = dayNames[dayOfWeek];
                          const schedule = professionalSchedule.find(s => s.dayOfWeek === dayOfWeek);
                          const hasSchedule = schedule && schedule.isAvailable && schedule.startTime && schedule.endTime;

                          return (
                            <div key={dayOfWeek} className="flex items-center justify-between px-3 md:px-6 py-4 w-full">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-900 truncate">{dayName}</h3>
                                {hasSchedule ? (
                                  <p className="text-gray-700 text-sm md:text-base mt-1">
                                    {schedule.startTime} - {schedule.endTime}
                                  </p>
                                ) : (
                                  <p className="text-gray-400 text-sm md:text-base mt-1">No schedule set</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 md:gap-2 gap-3 flex-shrink-0">
                                <div className="relative work-schedule-actions-dropdown-container" ref={(el) => { workScheduleActionsDropdownRefs.current[`weekly-${dayOfWeek}`] = el; }}>
                                  <button
                                    onClick={() => setWorkScheduleActionsDropdownOpen(
                                      workScheduleActionsDropdownOpen === `weekly-${dayOfWeek}` ? null : `weekly-${dayOfWeek}`
                                    )}
                                    className="flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                  >
                                    <i className={`bi bi-chevron-down transition-transform duration-200 text-lg ${workScheduleActionsDropdownOpen === `weekly-${dayOfWeek}` ? 'rotate-180' : ''}`}></i>
                                  </button>
                                  {workScheduleActionsDropdownOpen === `weekly-${dayOfWeek}` && (
                                    <div className="absolute right-0 top-full mt-2 min-w-[200px] bg-white border border-gray-300 rounded-lg shadow-lg z-30 py-2">
                                      {hasSchedule ? (
                                        <>
                                          <button
                                            onClick={() => {
                                              setWorkScheduleActionsDropdownOpen(null);
                                              handleEditSchedule(schedule);
                                            }}
                                            className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                                          >
                                            <i className="bi bi-pencil text-[#0B4778]"></i>
                                            Edit Schedule
                                          </button>
                                          <button
                                            onClick={() => {
                                              setWorkScheduleActionsDropdownOpen(null);
                                              setScheduleToDelete({ dayOfWeek, dayName });
                                            }}
                                            className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                                          >
                                            <i className="bi bi-trash text-red-600"></i>
                                            Delete Schedule
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          onClick={() => {
                                            setWorkScheduleActionsDropdownOpen(null);
                                            handleEditSchedule({ dayOfWeek });
                                          }}
                                          className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                                        >
                                          <i className="bi bi-plus-circle text-[#0B4778]"></i>
                                          Set Schedule
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {workScheduleActiveTab === 'time-off' && (() => {
                  // Time offs are business-local DATE/TIME concepts. Do NOT use `new Date('YYYY-MM-DD')`
                  // because it is interpreted as UTC and can shift the displayed calendar day depending
                  // on the viewer's timezone (common when backend runs in UTC in production).
                  const toYmd = (v: any): string => {
                    if (!v) return '';
                    const s = String(v);
                    return s.includes('T') ? s.split('T')[0] : s;
                  };
                  const parseLocalDate = (ymd: string): Date => {
                    const parts = ymd.split('-');
                    if (parts.length !== 3) return new Date(ymd);
                    const year = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1; // 0-indexed
                    const day = parseInt(parts[2], 10);
                    return new Date(year, month, day);
                  };
                  const getTimeOffStatus = (timeOff: any) => {
                    const now = new Date();
                    now.setHours(0, 0, 0, 0);
                    const startDate = parseLocalDate(toYmd(timeOff.startDate));
                    startDate.setHours(0, 0, 0, 0);
                    const endDate = parseLocalDate(toYmd(timeOff.endDate));
                    endDate.setHours(23, 59, 59, 999);
                    if (endDate < now) return 'past';
                    if (startDate > now) return 'upcoming';
                    return 'current';
                  };
                  const getTimeOffStartTime = (timeOff: any) => parseLocalDate(toYmd(timeOff.startDate)).getTime();
                  const getTimeOffEndTime = (timeOff: any) => {
                    const endDate = parseLocalDate(toYmd(timeOff.endDate));
                    endDate.setHours(23, 59, 59, 999);
                    return endDate.getTime();
                  };
                  const filteredTimeOffs = timeOffs.filter((timeOff) => {
                    const status = getTimeOffStatus(timeOff);
                    if (timeOffsFilter === 'upcoming') return status === 'upcoming' || status === 'current';
                    if (timeOffsFilter === 'past') return status === 'past';
                    return true;
                  });
                  const sortedTimeOffs = [...filteredTimeOffs].sort((a, b) => {
                    if (timeOffsFilter === 'upcoming') {
                      return getTimeOffStartTime(a) - getTimeOffStartTime(b);
                    }
                    if (timeOffsFilter === 'past') {
                      const endDiff = getTimeOffEndTime(b) - getTimeOffEndTime(a);
                      if (endDiff !== 0) return endDiff;
                      return getTimeOffStartTime(b) - getTimeOffStartTime(a);
                    }
                    return getTimeOffStartTime(b) - getTimeOffStartTime(a);
                  });
                  const timeOffsFilterLabel =
                    timeOffsFilter === 'upcoming' ? 'Upcoming' : timeOffsFilter === 'past' ? 'Past' : 'All';
                  const timeOffsEmptyMessage =
                    timeOffsFilter === 'past'
                      ? 'No past time offs.'
                      : timeOffsFilter === 'upcoming'
                        ? 'No upcoming time offs. Create one to block your calendar.'
                        : null;

                  return (
                  <div className="space-y-4 md:space-y-6">
                    <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <h3 className="text-lg font-medium text-gray-900">{timeOffsFilterLabel} Time Off</h3>
                      <div className="flex items-center gap-3 w-full sm:w-auto">
                        <div className="relative time-offs-filter-dropdown-container flex-1 sm:flex-none">
                          <button
                            type="button"
                            onClick={() => setTimeOffsFilterDropdownOpen(!timeOffsFilterDropdownOpen)}
                            className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between sm:min-w-[140px]"
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: 'right 0.5rem center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '1.5em 1.5em'
                            }}
                          >
                            <span className="text-gray-900 text-sm">{timeOffsFilterLabel}</span>
                          </button>
                          {timeOffsFilterDropdownOpen && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto right-0">
                              <div
                                className={`px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer ${timeOffsFilter === 'all' ? 'bg-gray-50 font-medium' : ''}`}
                                onClick={() => {
                                  setTimeOffsFilter('all');
                                  setTimeOffsFilterDropdownOpen(false);
                                }}
                              >
                                All
                              </div>
                              <div className="border-t border-gray-200 my-1"></div>
                              <div
                                className={`px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer ${timeOffsFilter === 'upcoming' ? 'bg-gray-50 font-medium' : ''}`}
                                onClick={() => {
                                  setTimeOffsFilter('upcoming');
                                  setTimeOffsFilterDropdownOpen(false);
                                }}
                              >
                                Upcoming
                              </div>
                              <div
                                className={`px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer ${timeOffsFilter === 'past' ? 'bg-gray-50 font-medium' : ''}`}
                                onClick={() => {
                                  setTimeOffsFilter('past');
                                  setTimeOffsFilterDropdownOpen(false);
                                }}
                              >
                                Past
                              </div>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleOpenTimeOffModal()}
                          className="aacore-btn2-primary flex-1 sm:flex-none justify-center"
                        >
                          <i className="bi bi-plus"></i>
                          Time Off
                        </button>
                      </div>
                    </div>
                    {timeOffsLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading time offs...</p>
                      </div>
                    ) : timeOffsError ? (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-600">{timeOffsError}</p>
                      </div>
                    ) : timeOffs.length === 0 ? (
                      <div className="text-center py-8">
                        <i className="bi bi-calendar-x text-4xl text-gray-400 mb-4"></i>
                        <p className="text-gray-600 text-base">
                          Create a time off entry to block your calendar
                        </p>
                        <p className="text-gray-600 text-base">
                          and prevent bookings during that time.
                        </p>
                      </div>
                    ) : sortedTimeOffs.length === 0 ? (
                      <div className="text-center py-8">
                        <i className="bi bi-calendar-x text-4xl text-gray-400 mb-4"></i>
                        <p className="text-gray-600 text-base">{timeOffsEmptyMessage}</p>
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {sortedTimeOffs.map((timeOff) => {
                          const startYmd = toYmd(timeOff.startDate);
                          const endYmd = toYmd(timeOff.endDate);
                          const formatDate = (ymd: string) =>
                            parseLocalDate(ymd).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            });
                          const formatTime = (time: string) => {
                            if (!time) return '';
                            const [hours, minutes] = time.split(':');
                            const hour = parseInt(hours);
                            const ampm = hour >= 12 ? 'PM' : 'AM';
                            const hour12 = hour % 12 || 12;
                            return `${hour12}:${minutes} ${ampm}`;
                          };

                          // Ensure isAllDay is a boolean
                          const isAllDay = timeOff.isAllDay === true || timeOff.isAllDay === 'true' || timeOff.isAllDay === 1;

                          return (
                            <div key={timeOff.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900 mb-2">{timeOff.label}</h4>
                                  <div className="text-sm text-gray-600 space-y-1">
                                    <div className="flex items-center gap-2">
                                      <i className="bi bi-calendar3"></i>
                                      <span>
                                        {formatDate(startYmd)}
                                        {startYmd && endYmd && startYmd !== endYmd && ` - ${formatDate(endYmd)}`}
                                      </span>
                                    </div>
                                    {isAllDay ? (
                                      <div className="flex items-center gap-2">
                                        <i className="bi bi-clock"></i>
                                        <span>All Day</span>
                                      </div>
                                    ) : timeOff.startTime && timeOff.endTime ? (
                                      <div className="flex items-center gap-2">
                                        <i className="bi bi-clock"></i>
                                        <span>{formatTime(timeOff.startTime)} - {formatTime(timeOff.endTime)}</span>
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="relative work-schedule-actions-dropdown-container" ref={(el) => { workScheduleActionsDropdownRefs.current[`timeoff-${timeOff.id}`] = el; }}>
                                    <button
                                      onClick={() => setWorkScheduleActionsDropdownOpen(
                                        workScheduleActionsDropdownOpen === `timeoff-${timeOff.id}` ? null : `timeoff-${timeOff.id}`
                                      )}
                                      className="flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                      <i className={`bi bi-chevron-down transition-transform duration-200 text-lg ${workScheduleActionsDropdownOpen === `timeoff-${timeOff.id}` ? 'rotate-180' : ''}`}></i>
                                    </button>
                                    {workScheduleActionsDropdownOpen === `timeoff-${timeOff.id}` && (
                                      <div className="absolute right-0 bottom-full mb-2 min-w-[200px] bg-white border border-gray-300 rounded-lg shadow-lg z-30 py-2">
                                        <button
                                          onClick={() => {
                                            setWorkScheduleActionsDropdownOpen(null);
                                            handleOpenTimeOffModal(timeOff);
                                          }}
                                          className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                                        >
                                          <i className="bi bi-pencil text-[#0B4778]"></i>
                                          Edit Time Off
                                        </button>
                                        <button
                                          onClick={() => {
                                            setWorkScheduleActionsDropdownOpen(null);
                                            setTimeOffToDelete({ id: timeOff.id, label: timeOff.label });
                                          }}
                                          className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                                        >
                                          <i className="bi bi-trash text-red-600"></i>
                                          Delete Time Off
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );
        
      case 'my-customer-reviews':
        // Calculate rating statistics
        const calculateReviewStats = () => {
          if (!professionalCustomerReviews || professionalCustomerReviews.length === 0) {
            return {
              totalReviews: 0,
              averageRating: 0,
              ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
              last30DaysCount: 0,
              last30DaysAverage: 0
            };
          }

          const now = new Date();
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          
          const last30DaysReviews = professionalCustomerReviews.filter(review => {
            const reviewDate = new Date(review.createdAt);
            return reviewDate >= thirtyDaysAgo;
          });

          const totalRating = professionalCustomerReviews.reduce((sum, review) => sum + review.rating, 0);
          const averageRating = totalRating / professionalCustomerReviews.length;

          const last30DaysRating = last30DaysReviews.reduce((sum, review) => sum + review.rating, 0);
          const last30DaysAverage = last30DaysReviews.length > 0 ? last30DaysRating / last30DaysReviews.length : 0;

          const ratingDistribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
          professionalCustomerReviews.forEach(review => {
            if (review.rating >= 1 && review.rating <= 5) {
              ratingDistribution[review.rating as keyof typeof ratingDistribution]++;
            }
          });

          return {
            totalReviews: professionalCustomerReviews.length,
            averageRating,
            ratingDistribution,
            last30DaysCount: last30DaysReviews.length,
            last30DaysAverage
          };
        };

        const reviewStats = calculateReviewStats();

        // Get unique services for filter
        const uniqueServices = Array.from(
          new Set(
            professionalCustomerReviews
              .map(review => review.service?.name)
              .filter(Boolean) as string[]
          )
        ).sort();

        // Filter reviews
        const getFilteredReviews = () => {
          let filtered = [...professionalCustomerReviews];

          // Rating filter
          if (reviewRatingFilter !== 'all') {
            const minRating = parseInt(reviewRatingFilter);
            filtered = filtered.filter(review => review.rating >= minRating);
          }

          // Service filter
          if (reviewServiceFilter !== 'all') {
            filtered = filtered.filter(review => review.service?.name === reviewServiceFilter);
          }

          // Date filter
          if (reviewDateFilter !== 'all-time') {
            const now = new Date();
            let cutoffDate = new Date();
            
            if (reviewDateFilter === 'last-30-days') {
              cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            } else if (reviewDateFilter === 'last-90-days') {
              cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            } else if (reviewDateFilter === 'this-year') {
              cutoffDate = new Date(now.getFullYear(), 0, 1);
            }

            filtered = filtered.filter(review => {
              const reviewDate = new Date(review.createdAt);
              return reviewDate >= cutoffDate;
            });
          }

          return filtered;
        };

        const filteredReviews = getFilteredReviews();

        // Get rating label
        const getRatingLabel = (rating: number): string => {
          if (rating >= 4.5) return 'Excellent';
          if (rating >= 3.5) return 'Great';
          if (rating >= 2.5) return 'Good';
          if (rating >= 1.5) return 'Okay';
          return 'Poor';
        };

        // Get appointment date if available
        const getAppointmentDate = (review: ServiceReview): string | null => {
          // We'll need to fetch appointment data if not available
          // For now, return null
          return null;
        };

        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">My Reviews</h2>
                <p className="text-gray-600 mt-1">Reviews from customers about your services</p>
              </div>
            </div>

            {professionalCustomerReviewsLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading your customer reviews...</p>
                </div>
              </div>
            ) : professionalCustomerReviewsError ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-center">
                  <p className="text-red-600">{professionalCustomerReviewsError}</p>
                </div>
              </div>
            ) : professionalCustomerReviews && professionalCustomerReviews.length > 0 ? (
              <>
                {/* Rating Summary and Filters Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Rating Summary Card */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-base font-semibold text-gray-900 mb-1">Overall rating</div>
                    <div className="text-sm text-gray-600 mb-4">Reviews tied to <strong>you</strong> as a professional, not the entire store.</div>
                    
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="text-3xl font-bold text-gray-900">
                          {reviewStats.averageRating.toFixed(1)} <span className="text-base font-normal text-gray-600">/ 5</span>
                        </div>
                        <div className="flex items-center gap-0.5 mt-1">
                          {[1, 2, 3, 4, 5].map((star) => {
                            const rating = reviewStats.averageRating;
                            if (star <= Math.floor(rating)) {
                              return <i key={star} className="bi bi-star-fill text-yellow-400 text-lg"></i>;
                            } else if (star === Math.ceil(rating) && rating % 1 >= 0.5) {
                              return <i key={star} className="bi bi-star-half text-yellow-400 text-lg"></i>;
                            } else {
                              return <i key={star} className="bi bi-star text-gray-300 text-lg"></i>;
                            }
                          })}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-gray-600 mb-3">
                          Based on <strong>{reviewStats.totalReviews} reviews</strong> overall.<br />
                          <span>Last 30 days: {reviewStats.last30DaysCount} reviews • {reviewStats.last30DaysAverage.toFixed(1)} avg</span>
                        </div>
                        
                        {/* Rating Distribution */}
                        {[5, 4, 3, 2, 1].map((rating) => {
                          const count = reviewStats.ratingDistribution[rating as keyof typeof reviewStats.ratingDistribution];
                          const percentage = reviewStats.totalReviews > 0 ? (count / reviewStats.totalReviews) * 100 : 0;
                          const barColor = rating === 5 ? 'bg-green-500' : rating === 4 ? 'bg-green-400' : rating === 3 ? 'bg-yellow-400' : rating === 2 ? 'bg-orange-400' : 'bg-red-400';
                          
                          return (
                            <div key={rating} className="flex items-center gap-2 text-sm mb-1">
                              <span className="w-8">{rating} ★</span>
                              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div className={`h-full ${barColor}`} style={{ width: `${percentage}%` }}></div>
                              </div>
                              <span className="w-8 text-right">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Filters Card */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-base font-semibold text-gray-900 mb-1">Filters</div>
                    <div className="text-sm text-gray-600 mb-3">Narrow reviews by rating, service, or time period.</div>
                    
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm border border-[#0B4778]/30 bg-[#0B4778]/10 text-[#0B4778] rounded-full">
                        <i className="bi bi-funnel"></i> Showing: {filteredReviews.length} {filteredReviews.length === 1 ? 'review' : 'reviews'}
                      </span>
                      
                      <div className="relative">
                        <select
                          value={reviewRatingFilter}
                          onChange={(e) => setReviewRatingFilter(e.target.value)}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none pr-8"
                        >
                          <option value="all">Rating: All</option>
                          <option value="5">5 stars only</option>
                          <option value="4">4 stars & up</option>
                          <option value="3">3 stars & up</option>
                        </select>
                      </div>
                      
                      <div className="relative">
                        <select
                          value={reviewServiceFilter}
                          onChange={(e) => setReviewServiceFilter(e.target.value)}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none pr-8"
                        >
                          <option value="all">Service: All services</option>
                          {uniqueServices.map(service => (
                            <option key={service} value={service}>{service}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="relative">
                        <select
                          value={reviewDateFilter}
                          onChange={(e) => setReviewDateFilter(e.target.value)}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none pr-8"
                        >
                          <option value="last-30-days">Date: Last 30 days</option>
                          <option value="last-90-days">Last 90 days</option>
                          <option value="this-year">This year</option>
                          <option value="all-time">All time</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reviews List */}
                {filteredReviews.length > 0 ? (
                  <div className="space-y-2.5">
                    {filteredReviews.map((review) => {
                      const appointmentDate = getAppointmentDate(review);
                      const reviewDate = formatDate(review.createdAt);
                      
                      return (
                        <div key={review.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                          <div className="flex justify-between items-start gap-4 mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="flex items-center gap-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <i
                                      key={star}
                                      className={`bi ${star <= review.rating ? 'bi-star-fill text-yellow-400' : 'bi-star text-gray-300'} text-base`}
                                    ></i>
                                  ))}
                                </div>
                                <span className="text-sm font-semibold text-gray-900">
                                  {review.rating.toFixed(1)} • {getRatingLabel(review.rating)}
                                </span>
                              </div>
                              <div className="text-base font-semibold text-gray-900 mb-2">
                                {review.customer?.firstName} {review.customer?.lastName}
                              </div>
                              <div className="flex flex-wrap gap-2 mb-2">
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 text-sm border border-gray-200 bg-gray-50 text-gray-700 rounded-full">
                                  <i className="bi bi-check-circle-fill text-green-600"></i> Verified appointment
                                </span>
                                {review.service?.name && (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-sm border border-gray-200 bg-gray-50 text-gray-700 rounded-full">
                                    <i className="bi bi-scissors"></i> {review.service.name}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 text-right">
                              {appointmentDate && (
                                <>
                                  Appointment: {appointmentDate}<br />
                                </>
                              )}
                              <span className="hidden sm:inline">Review left: </span>
                              <span>{reviewDate}</span>
                            </div>
                          </div>

                          {review.reviewFeedback && (
                            <p className="text-base text-gray-700 mb-3 leading-relaxed">
                              "{review.reviewFeedback}"
                            </p>
                          )}

                          {/* Replies Section */}
                          {review.replies && review.replies.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <div className="text-sm font-semibold text-gray-900 mb-2">
                                My Replies
                              </div>
                              {renderReplies(review.replies, review.id)}
                            </div>
                          )}

                          <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                            <span className="text-sm text-gray-600">
                              {review.isPublic ? (
                                <span><i className="bi bi-emoji-smile text-green-600"></i> Public review visible on {currentBusiness?.name || 'business'} profile.</span>
                              ) : (
                                <span><i className="bi bi-lock text-gray-400"></i> Private review</span>
                              )}
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setReplyingToReview(review.id);
                                  setReplyingToReply(null);
                                  setReplyMessage('');
                                }}
                                className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-full hover:bg-gray-100"
                              >
                                <i className="bi bi-reply"></i> Reply
                              </button>
                              <button
                                onClick={() => {
                                  if (review.appointmentId) {
                                    navigate(`/appointments/${review.appointmentId}?from=${activeSection}`);
                                  }
                                }}
                                className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-full hover:bg-gray-100"
                              >
                                <i className="bi bi-box-arrow-up-right"></i> View appointment
                              </button>
                            </div>
                          </div>

                          {/* Reply Input */}
                          {replyingToReview === review.id && !replyingToReply && (
                            <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                              <textarea
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-sm"
                                rows={3}
                                placeholder="Write a reply..."
                              />
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleAddReply(review.id)}
                                  disabled={isSubmittingReply}
                                  className="aacore-btn2-primary disabled:opacity-50"
                                >
                                  <Send className="w-4 h-4" />
                                  {isSubmittingReply ? 'Posting...' : 'Post Reply'}
                                </button>
                                <button
                                  onClick={() => {
                                    setReplyingToReview(null);
                                    setReplyMessage('');
                                  }}
                                  disabled={isSubmittingReply}
                                  className="aacore-btn2-outline disabled:opacity-50"
                                >
                                  <X className="w-4 h-4" />
                                  <span>Cancel</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">No reviews match the selected filters.</p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="text-center">
                  <i className="bi bi-star-fill text-yellow-400 text-4xl mb-4 block"></i>
                  <p className="text-lg font-semibold text-gray-900 mb-1">No customer reviews yet</p>
                  <p className="text-sm text-gray-600">
                    Reviews will show up here once clients begin leaving feedback. Encourage them to rate their visit to help you monitor your service quality.
                  </p>
                </div>
              </div>
            )}

          </div>
        );
        
      case 'testimonials':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Testimonials</h2>
                <p className="text-gray-600 mt-1">Manage customer reviews and testimonials for your services</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {serviceReviewStats.total} total • {serviceReviewStats.public} public • {serviceReviewStats.averageRating} avg rating
                </span>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                {/* Search */}
                <div className="flex-1 sm:max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search testimonials..."
                      value={serviceReviewSearch}
                      onChange={(e) => setServiceReviewSearch(e.target.value)}
                      className={`w-full pl-10 ${serviceReviewSearch ? 'pr-10' : 'pr-3'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]`}
                    />
                    {serviceReviewSearch && (
                      <button
                        type="button"
                        onClick={() => setServiceReviewSearch('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                        aria-label="Clear search"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Public / Private */}
                <div className="w-full sm:w-auto">
                  <div className="relative service-reviews-visibility-dropdown-container">
                    <button
                      type="button"
                      onClick={() => setServiceReviewsVisibilityDropdownOpen(!serviceReviewsVisibilityDropdownOpen)}
                      className="w-full sm:w-auto px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between sm:min-w-[140px]"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em'
                      }}
                    >
                      <span className="text-gray-900">
                        {serviceReviewFilters.isPublic === undefined ? 'Public & Private' :
                         serviceReviewFilters.isPublic === true ? 'Public Only' :
                         serviceReviewFilters.isPublic === false ? 'Private Only' : 'Public & Private'}
                      </span>
                    </button>
                    
                    {serviceReviewsVisibilityDropdownOpen && (
                      <div className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto overflow-x-hidden w-full sm:w-fit sm:min-w-full">
                        <div
                          className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer whitespace-nowrap"
                          onClick={() => {
                            handleServiceReviewFilterChange('isPublic', undefined);
                            setServiceReviewsVisibilityDropdownOpen(false);
                          }}
                        >
                          Public & Private
                        </div>
                        <div className="border-t border-gray-200 my-1"></div>
                        <div
                          className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer whitespace-nowrap"
                          onClick={() => {
                            handleServiceReviewFilterChange('isPublic', true);
                            setServiceReviewsVisibilityDropdownOpen(false);
                          }}
                        >
                          Public Only
                        </div>
                        <div
                          className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer whitespace-nowrap"
                          onClick={() => {
                            handleServiceReviewFilterChange('isPublic', false);
                            setServiceReviewsVisibilityDropdownOpen(false);
                          }}
                        >
                          Private Only
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {serviceReviewsLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading testimonials...</p>
                </div>
              </div>
            ) : serviceReviews.length > 0 ? (
              <>
                {/* Outer card wrapper - hidden on mobile, shown on desktop */}
                <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-6">
                    <div className="grid gap-4">
                      {serviceReviews.map((review) => (
                        <div key={review.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow relative">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start space-x-3 flex-1 min-w-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">
                              {review.customer?.firstName?.charAt(0)}{review.customer?.lastName?.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <h3 className="font-medium text-gray-900">
                                  {review.customer?.firstName} {review.customer?.lastName?.charAt(0)}.
                                </h3>
                                {review.isPublic ? (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Public
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Private
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <div className="flex items-center">
                                  {Array.from({ length: 5 }, (_, i) => (
                                    <span key={i} className={`text-sm ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`}>
                                      ★
                                    </span>
                                  ))}
                                </div>
                                <span>•</span>
                                <span>{new Date(review.createdAt).toLocaleDateString()}</span>
                                {/* Business ratings are overall experience; no service/professional context */}
                              </div>
                            </div>
                          </div>
                          {/* Gear Icon Dropdown */}
                          <div className="relative flex-shrink-0 ml-2" ref={(el) => { reviewDropdownRefs.current[review.id] = el; }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleReviewDropdown(review.id);
                              }}
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              aria-label="Review options"
                            >
                              <Settings className="w-5 h-5" />
                            </button>
                            {openReviewDropdowns.has(review.id) && (
                              <div 
                                className="absolute right-0 top-full mt-1 w-max min-w-[12rem] max-w-[calc(100vw-1rem)] overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <div className="py-1">
                                  {review.isPublic ? (
                                    <button
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMakeReviewPrivate(review);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 whitespace-nowrap"
                                    >
                                      <i className="bi bi-eye-slash"></i>
                                      <span>Make Private</span>
                                    </button>
                                  ) : (
                                    <button
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMakeReviewPublic(review);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 whitespace-nowrap"
                                    >
                                      <i className="bi bi-globe"></i>
                                      <span>Make Public</span>
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {(review.title || review.content) && (
                          <p className="text-gray-700 mb-3">
                            <span className="text-gray-400">"</span>
                            {review.title ? `${review.title}${review.content ? ' — ' : ''}` : ''}
                            {review.content || ''}
                            <span className="text-gray-400">"</span>
                          </p>
                        )}

                        {/* Replies Section (one-level) */}
                        {review.replies && review.replies.length > 0 && (
                          <div className="mt-4 pt-3 border-t border-gray-100">
                            <div className="text-sm font-semibold text-gray-900 mb-2">
                              My Replies
                            </div>
                            {renderTestimonialReplies(review.replies, review.id)}
                          </div>
                        )}

                        {/* Reply Input */}
                        {testimonialReplyingToRating === review.id && (
                          <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
                            <textarea
                              value={testimonialReplyMessage}
                              onChange={(e) => setTestimonialReplyMessage(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-sm"
                              rows={3}
                              placeholder="Write a reply..."
                            />
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleAddTestimonialReply(review.id)}
                                disabled={isSubmittingTestimonialReply}
                                className="aacore-btn2-primary disabled:opacity-50"
                              >
                                <MessageSquare className="w-4 h-4" />
                                {isSubmittingTestimonialReply ? 'Posting...' : 'Post Reply'}
                              </button>
                              <button
                                onClick={() => {
                                  setTestimonialReplyingToRating(null);
                                  setTestimonialReplyMessage('');
                                }}
                                disabled={isSubmittingTestimonialReply}
                                className="aacore-btn2-outline disabled:opacity-50"
                              >
                                <X className="w-4 h-4" />
                                <span>Cancel</span>
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Reply Button */}
                        {!testimonialReplyingToRating && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <button
                              onClick={() => {
                                setTestimonialReplyingToRating(review.id);
                                setTestimonialEditingReply(null);
                                setTestimonialReplyMessage('');
                              }}
                              className="text-sm text-[#0B4778] hover:text-[#0B4778] hover:underline focus:outline-none"
                            >
                              <i className="bi bi-reply"></i> Reply
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
                
              {/* Mobile version - no outer card wrapper */}
                <div className="md:hidden space-y-4">
                  {serviceReviews.map((review) => (
                    <div key={review.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-visible">
                      {/* Header Section */}
                      <div className="p-4 pb-3 relative">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="hidden w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                            {review.customer?.firstName?.charAt(0)}{review.customer?.lastName?.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                              <h3 className="font-semibold text-gray-900 text-base">
                                {review.customer?.firstName} {review.customer?.lastName?.charAt(0)}.
                              </h3>
                              {review.isPublic ? (
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
                                  Public
                                </span>
                              ) : (
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 whitespace-nowrap">
                                  Private
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                              <div className="flex items-center">
                                {Array.from({ length: 5 }, (_, i) => (
                                  <span key={i} className={`text-base ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`}>
                                    ★
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span>{new Date(review.createdAt).toLocaleDateString()}</span>
                                {/* Business ratings are overall experience; no service/professional context */}
                              </div>
                            </div>
                          </div>
                          {/* Gear Icon Dropdown */}
                          <div className="relative flex-shrink-0" ref={(el) => { reviewDropdownRefs.current[review.id] = el; }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleReviewDropdown(review.id);
                              }}
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              aria-label="Review options"
                            >
                              <Settings className="w-5 h-5" />
                            </button>
                            {openReviewDropdowns.has(review.id) && (
                              <div
                                className="absolute right-0 top-full mt-1 w-max min-w-[12rem] max-w-[calc(100vw-1rem)] overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <div className="py-1">
                                  {review.isPublic ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMakeReviewPrivate(review);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 whitespace-nowrap"
                                    >
                                      <i className="bi bi-eye-slash"></i>
                                      <span>Make Private</span>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMakeReviewPublic(review);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 whitespace-nowrap"
                                    >
                                      <i className="bi bi-globe"></i>
                                      <span>Make Public</span>
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Review Content */}
                      {(review.title || review.content) && (
                        <div className="px-4 pb-4">
                          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap break-words">
                            <span className="text-gray-400">"</span>
                            {review.title ? `${review.title}${review.content ? ' — ' : ''}` : ''}
                            {review.content || ''}
                            <span className="text-gray-400">"</span>
                          </p>
                        </div>
                      )}

                      {/* Replies Section (one-level) */}
                      {review.replies && review.replies.length > 0 && (
                        <div className="px-4 pb-4 border-t border-gray-100 pt-4">
                          <div className="text-sm font-semibold text-gray-900 mb-3">
                            My Replies
                          </div>
                          {renderTestimonialReplies(review.replies, review.id)}
                        </div>
                      )}

                      {/* Reply Input */}
                      {testimonialReplyingToRating === review.id && (
                        <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-3">
                          <textarea
                            value={testimonialReplyMessage}
                            onChange={(e) => setTestimonialReplyMessage(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-sm resize-none"
                            rows={4}
                            placeholder="Write a reply..."
                          />
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleAddTestimonialReply(review.id)}
                              disabled={isSubmittingTestimonialReply}
                              className="aacore-btn2-primary disabled:opacity-50 flex-1"
                            >
                              <MessageSquare className="w-4 h-4" />
                              {isSubmittingTestimonialReply ? 'Posting...' : 'Post Reply'}
                            </button>
                            <button
                              onClick={() => {
                                setTestimonialReplyingToRating(null);
                                setTestimonialReplyMessage('');
                              }}
                              disabled={isSubmittingTestimonialReply}
                              className="aacore-btn2-outline disabled:opacity-50"
                            >
                              <X className="w-4 h-4" />
                              <span>Cancel</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Reply Button */}
                      {!testimonialReplyingToRating && (
                        <div className="px-4 pb-4 border-t border-gray-100 pt-4">
                          <button
                            onClick={() => {
                              setTestimonialReplyingToRating(review.id);
                              setTestimonialEditingReply(null);
                              setTestimonialReplyMessage('');
                            }}
                            className="aacore-btn2-primary w-full"
                          >
                            <i className="bi bi-reply"></i>
                            <span>Reply</span>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {Math.ceil((serviceReviewPagination.total || 0) / (serviceReviewPagination.limit || 20)) > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-6">
                    <button
                      onClick={() => setServiceReviewPage(prev => Math.max(1, prev - 1))}
                      disabled={serviceReviewPage <= 1}
                      className="px-4 py-2 rounded-lg font-medium transition-opacity disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200 text-gray-700 bg-white"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 text-sm text-gray-600">
                      Page {serviceReviewPage} of {Math.max(1, Math.ceil((serviceReviewPagination.total || 0) / (serviceReviewPagination.limit || 20)))}
                    </span>
                    <button
                      onClick={() => setServiceReviewPage(prev => {
                        const totalPages = Math.max(1, Math.ceil((serviceReviewPagination.total || 0) / (serviceReviewPagination.limit || 20)));
                        return Math.min(totalPages, prev + 1);
                      })}
                      disabled={serviceReviewPage >= Math.max(1, Math.ceil((serviceReviewPagination.total || 0) / (serviceReviewPagination.limit || 20)))}
                      className="px-4 py-2 rounded-lg font-medium transition-opacity disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200 text-gray-700 bg-white"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 text-center text-gray-500">
                  <span className="text-4xl mb-4 block">⭐</span>
                  <p className="text-lg font-medium">No testimonials found</p>
                  <p className="text-sm">
                    {serviceReviewFilters.isPublic !== undefined 
                      ? 'Try adjusting your filters to see more results.'
                      : 'Customer testimonials will appear here once clients begin rating their overall experience.'
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        );
        
      case 'giftcards':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Gift Cards</h2>
                <p className="text-gray-600 mt-1">Create and manage gift cards for your customers</p>
              </div>
              <button 
                onClick={handleAddGiftCard}
                className="aacore-btn2-primary"
              >
                <Plus className="w-4 h-4" />
                <span>Issue Gift Card</span>
              </button>
            </div>
            
            {giftCardsLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading gift cards...</p>
                </div>
              </div>
            ) : giftCards.length > 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6">
                  <div className="grid gap-4">
                    {giftCards.map((giftCard) => (
                      <div key={giftCard.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold">
                              {giftCard.recipientName?.split(' ').map(name => name.charAt(0)).join('').slice(0, 2).toUpperCase() || 'GC'}
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{giftCard.recipientName}</h3>
                              <p className="text-sm text-gray-500">Code: {giftCard.code}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">
                              ${giftCard.balance.toFixed(2)}
                            </div>
                            <div className="text-sm text-gray-500">
                              of ${giftCard.amount.toFixed(2)}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                          <div>
                            <span className="font-medium">Status:</span>
                            <span className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${
                              giftCard.status === 'ACTIVE' 
                                ? 'bg-green-100 text-green-800'
                                : giftCard.status === 'REDEEMED'
                                ? 'bg-[#0B4778]/20 text-[#0B4778]'
                                : giftCard.status === 'EXPIRED'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {giftCard.status}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium">Issued:</span>
                            <span className="ml-1">{new Date(giftCard.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                        
                        {giftCard.message && (
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            <p className="text-sm text-gray-700 italic">"{giftCard.message}"</p>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-500">
                            {giftCard.recipientEmail && (
                              <span>Email: {giftCard.recipientEmail}</span>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditGiftCard(giftCard)}
                              className="text-[#0B4778] hover:text-[#0B4778] text-sm font-medium"
                            >
                              Edit
                            </button>
                            {giftCard.status === 'ACTIVE' && (
                              <button
                                onClick={() => handleToggleGiftCardStatus(giftCard.id, 'CANCELLED')}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 text-center text-gray-500">
                  <span className="text-4xl mb-4 block">🎁</span>
                  <p className="text-lg font-medium">No gift cards issued yet</p>
                  <p className="text-sm">Issue gift cards to customers and track their usage.</p>
                </div>
              </div>
            )}
          </div>
        );
        
      case 'site-config':
        return (
          <>
          <div className="space-y-5 pb-28">
            {/* Hero: title, Reset only - Save moved to sticky footer */}
            <div className="grid gap-2.5 mb-4">
              <div className="hidden flex-col gap-4 lg:flex lg:flex-row lg:items-start lg:justify-between">
                <h2 className="text-xl font-bold text-gray-900 md:text-2xl m-0">Site Configuration</h2>
                <div className="flex flex-shrink-0 flex-wrap justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleResetSiteConfig}
                    disabled={businessLoading}
                    className="aacore-btn2-danger disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reset
                  </button>
                </div>
              </div>
              <p className="text-gray-600 text-sm md:text-base mt-0 mb-0">Configure your business information and public-facing website.</p>
            </div>

            {businessLoading ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4" />
                  <p className="text-gray-600">Loading business information...</p>
                </div>
              </div>
            ) : (
              <div className="overflow-visible md:bg-white md:rounded-2xl md:shadow-md md:border md:border-gray-200">
                {/* Tab bar - reference config-nav-wrap: gradient bg, pill tabs */}
                <div className="md:rounded-t-2xl bg-gradient-to-b from-[#fbfdff] to-[#f6f9fc] border-b border-gray-200">
                  {/* Mobile: dropdown with floating < and > (same size and vertical position as appointment calendar floating arrows) */}
                  <div className="relative py-3.5 md:hidden" ref={siteConfigTabDropdownRef}>
                    {/* Floating left - fixed at same vertical position as calendar arrows: top-1/2 + 64px, same size w-8 h-8 */}
                    <button
                      type="button"
                      onClick={() => {
                        const order: (typeof siteConfigActiveTab)[] = ['business-info', 'services-categories', 'operating-hours', 'branding-theme', 'payments', 'cancellation-rescheduling-policy'];
                        const idx = order.indexOf(siteConfigActiveTab);
                        const nextTab = idx <= 0 ? order[order.length - 1] : order[idx - 1];
                        setSiteConfigActiveTab(nextTab);
                        setIsSiteConfigTabDropdownOpen(false);
                        if (nextTab === 'operating-hours' && currentBusiness?.id) loadOperatingHours(currentBusiness.id);
                      }}
                      className="fixed left-2 z-20 rounded-full bg-white/20 hover:bg-white/35 border border-gray-200/50 shadow-sm shadow-black/5 backdrop-blur-sm transition-colors p-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B4778]/60 touch-manipulation"
                      style={{ top: '50%', transform: 'translateY(calc(-50% + 64px))' }}
                      title="Previous section"
                      aria-label="Previous section"
                    >
                      <ChevronLeft className="w-8 h-8 text-gray-700/80" />
                    </button>
                    {/* Floating right - fixed at same vertical position as calendar arrows */}
                    <button
                      type="button"
                      onClick={() => {
                        const order: (typeof siteConfigActiveTab)[] = ['business-info', 'services-categories', 'operating-hours', 'branding-theme', 'payments', 'cancellation-rescheduling-policy'];
                        const idx = order.indexOf(siteConfigActiveTab);
                        const nextTab = idx < 0 || idx >= order.length - 1 ? order[0] : order[idx + 1];
                        setSiteConfigActiveTab(nextTab);
                        setIsSiteConfigTabDropdownOpen(false);
                        if (nextTab === 'operating-hours' && currentBusiness?.id) loadOperatingHours(currentBusiness.id);
                      }}
                      className="fixed right-2 z-20 rounded-full bg-white/20 hover:bg-white/35 border border-gray-200/50 shadow-sm shadow-black/5 backdrop-blur-sm transition-colors p-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B4778]/60 touch-manipulation"
                      style={{ top: '50%', transform: 'translateY(calc(-50% + 64px))' }}
                      title="Next section"
                      aria-label="Next section"
                    >
                      <ChevronRight className="w-8 h-8 text-gray-700/80" />
                    </button>
                    <div className="relative w-full">
                      <button
                        type="button"
                        id="site-config-section-trigger"
                        aria-haspopup="listbox"
                        aria-expanded={isSiteConfigTabDropdownOpen}
                        aria-label="Choose configuration section"
                        onClick={() => setIsSiteConfigTabDropdownOpen((o) => !o)}
                        className="flex h-12 w-full items-center justify-between gap-2 rounded-xl border border-gray-300 bg-white px-3 text-left text-sm shadow-sm transition-colors hover:border-gray-400 focus:border-[#0B4778] focus:outline-none focus:ring-2 focus:ring-[#0B4778]/30"
                      >
                        {(() => {
                          const active = SITE_CONFIG_SECTION_TABS.find((t) => t.id === siteConfigActiveTab);
                          return (
                            <span className="flex min-w-0 flex-1 items-center gap-3">
                              {active && (
                                <i className={`${active.icon} flex-shrink-0 text-base text-[#0B4778]`} aria-hidden />
                              )}
                              <span className="min-w-0 truncate font-medium text-[#0B4778]">
                                {active?.label ?? 'Section'}
                              </span>
                            </span>
                          );
                        })()}
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${isSiteConfigTabDropdownOpen ? 'rotate-180' : ''}`}
                          aria-hidden
                        />
                      </button>
                      {isSiteConfigTabDropdownOpen && (
                        <ul
                          role="listbox"
                          aria-labelledby="site-config-section-trigger"
                          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-[min(70vh,24rem)] overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                        >
                          {SITE_CONFIG_SECTION_TABS.map(({ id, label, icon }) => (
                            <li key={id} role="presentation">
                              <button
                                type="button"
                                role="option"
                                aria-selected={siteConfigActiveTab === id}
                                className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                                  siteConfigActiveTab === id
                                    ? 'bg-[#0B4778]/8 text-[#0B4778]'
                                    : 'text-gray-800 hover:bg-gray-50'
                                }`}
                                onClick={() => {
                                  setSiteConfigActiveTab(id);
                                  if (id === 'operating-hours' && currentBusiness?.id) {
                                    loadOperatingHours(currentBusiness.id);
                                  }
                                  setIsSiteConfigTabDropdownOpen(false);
                                }}
                              >
                                <i className={`${icon} flex-shrink-0 text-base opacity-90`} aria-hidden />
                                <span className="min-w-0 leading-snug">{label}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* Desktop: pill-style tabs in grid - reference config-tabs */}
                  <nav className="hidden md:grid md:grid-cols-3 lg:grid-cols-6 gap-2.5 px-4 py-4" aria-label="Site configuration sections" role="tablist">
                    {SITE_CONFIG_SECTION_TABS.map(({ id, label, icon }) => (
                      <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={siteConfigActiveTab === id}
                        onClick={() => {
                          setSiteConfigActiveTab(id as typeof siteConfigActiveTab);
                          if (id === 'operating-hours' && currentBusiness?.id) loadOperatingHours(currentBusiness.id);
                        }}
                        className={`min-h-[54px] py-3 px-3.5 rounded-2xl border font-semibold text-sm text-center transition-all flex flex-col items-center justify-center gap-1.5 ${
                          siteConfigActiveTab === id
                            ? 'bg-white border-[#0B4778]/40 text-[#0B4778] shadow-sm'
                            : 'border-transparent bg-transparent text-gray-500 hover:bg-white hover:border-gray-300 hover:text-gray-900'
                        }`}
                      >
                        <i className={`${icon} text-base flex-shrink-0`} aria-hidden />
                        <span className="leading-tight">{label}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab content area — mobile: horizontal padding comes from main shell (p-4); avoid double inset vs hero */}
                <div className="px-0 py-5 md:p-6">
                  {siteConfigActiveTab === 'business-info' && (
                <div className="space-y-4 md:space-y-6">
                  {/* Section header - reference: section-header with title row, chip, section-sub */}
                  <div className="mb-5 flex flex-col gap-3 md:grid md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:gap-x-4 md:gap-y-3">
                    <div className="flex w-full min-w-0 flex-row items-start justify-between gap-2 md:contents">
                      <h3 className="m-0 min-w-0 flex-1 text-lg font-bold tracking-tight text-gray-900 md:text-xl">
                        Business Profile
                      </h3>
                      <div className="inline-flex shrink-0 items-center gap-2 rounded-full border border-[#0B4778]/20 bg-[#0B4778]/10 px-3 py-2 text-xs font-semibold whitespace-nowrap text-[#0B4778] md:justify-self-end">
                        <i className="bi bi-eye" aria-hidden />
                        Visible to customers
                      </div>
                    </div>
                    <p className="mb-0 mt-0 w-full text-sm text-gray-600 md:col-span-2">
                      Update the core business details customers will see across your public booking website.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
                      <input
                        type="text"
                        value={businessFormData.name}
                        onChange={(e) => handleBusinessFormChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-gray-900"
                        placeholder="Enter your business name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                      <input
                        type="url"
                        value={businessFormData.website}
                        onChange={(e) => handleBusinessFormChange('website', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-gray-900"
                        placeholder="https://your-website.com"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <label className="block text-sm font-medium text-gray-700">Public URL</label>
                        <InfoTooltip content="Keep this short and customer-friendly. This description may appear near the top of your public booking site." />
                      </div>
                      <div className={`flex overflow-hidden rounded-lg border ${
                        publicUrlHasValidationError ? 'border-red-500' : 'border-gray-300'
                      }`}>
                        <span className="inline-flex items-center px-2 md:px-3 border-r border-gray-300 bg-gray-50 text-gray-500 text-[10px] md:text-sm whitespace-nowrap flex-shrink-0">
                          https://booking.aacore.net/
                        </span>
                        <input
                          type="text"
                          value={businessFormData.shortName}
                          onChange={(e) => handlePublicUrlChange(e.target.value)}
                          maxLength={BUSINESS_SHORT_NAME_MAX_LENGTH}
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          className={`flex-1 min-w-0 px-3 py-2 border-0 focus:ring-2 focus:ring-inset text-gray-900 ${
                            publicUrlHasValidationError ? 'focus:ring-red-500' : 'focus:ring-[#0B4778]'
                          }`}
                          placeholder="your-business-name"
                        />
                      </div>
                      {isPublicUrlChanged && shortNameValidation.formatError && (
                        <p className="mt-1 text-sm text-red-600">{shortNameValidation.formatError}</p>
                      )}
                      {isPublicUrlChanged && shortNameValidation.checking && (
                        <p className="mt-1 text-sm text-blue-900 flex items-center">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-900 mr-2"></div>
                          Checking availability...
                        </p>
                      )}
                      {isPublicUrlChanged && !shortNameValidation.checking && !shortNameValidation.available && (
                        <p className="mt-1 text-sm text-red-600">
                          {shortNameValidation.message || 'This public URL is already taken. Please choose a different one.'}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Lowercase letters, numbers, hyphens, and underscores only.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Business Email</label>
                      <input
                        type="email"
                        value={businessFormData.email}
                        onChange={(e) => handleBusinessFormChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-gray-900"
                        placeholder="Enter your business email"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="block text-sm font-medium text-gray-700">About your Business</label>
                      <InfoTooltip content="Keep this short and customer-friendly. This description may appear near the top of your public booking site." />
                    </div>
                    <textarea
                      value={businessFormData.description}
                      onChange={(e) => handleBusinessFormChange('description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] min-h-[118px] resize-y text-gray-900"
                      rows={3}
                      placeholder="Tell customers about your business..."
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                      <input
                        type="text"
                        value={businessFormData.address}
                        onChange={(e) => handleBusinessFormChange('address', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-gray-900"
                        placeholder="Enter your business address"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={businessFormData.phone}
                        onChange={(e) => handleBusinessFormChange('phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-gray-900"
                        placeholder="Enter your business phone number"
                      />
                    </div>
                  </div>

                  {/* Contact Form */}
                  <div className="border-t border-gray-200 pt-1.5 -mt-4 md:-mt-5" aria-hidden />
                  <div className="p-4 md:p-5 border border-gray-200 rounded-xl bg-gray-50/80 flex flex-col">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-base font-bold text-gray-900 m-0">Contact Form</h4>
                      <InfoTooltip content="Leave this on if you want a Contact Us form visible on the public-facing site." />
                    </div>
                    <p className="text-gray-600 text-sm mt-1 mb-0">Show or hide the Contact Us form on your public website.</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                      <span className="text-sm font-semibold text-gray-900">Show contact form</span>
                      <button
                        type="button"
                        onClick={() => handleBusinessFormChange('showContactForm', !businessFormData.showContactForm)}
                        className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2 ${businessFormData.showContactForm ? 'bg-[#0B4778]' : 'bg-gray-200'}`}
                        role="switch"
                        aria-checked={businessFormData.showContactForm}
                      >
                        <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ${businessFormData.showContactForm ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  </div>

                  {/* Social Links - reference: section-header + grid-3 */}
                  <div className="border-t border-gray-200 pt-6 mt-6">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-bold text-gray-900 m-0">Social Links</h3>
                      <InfoTooltip content="Only add the social links you want to show. Leave any field blank to hide that icon from the public site." />
                    </div>
                    <p className="text-gray-600 text-sm mt-0 mb-4">Add only the accounts you want shown on the public page.</p>
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
                      {(Object.keys(SOCIAL_URL_CONFIG) as SocialUrlField[]).map((field) => {
                        const { displayPrefix, matchPrefixes, placeholder } = SOCIAL_URL_CONFIG[field];
                        const label =
                          field === 'facebookUrl'
                            ? 'Facebook URL'
                            : field === 'instagramUrl'
                              ? 'Instagram URL'
                              : 'YouTube URL';
                        const iconClass =
                          field === 'facebookUrl'
                            ? 'bi bi-facebook'
                            : field === 'instagramUrl'
                              ? 'bi bi-instagram'
                              : 'bi bi-youtube';

                        return (
                          <div key={field}>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                              <i className={`${iconClass} text-gray-600`}></i>
                              {label}
                            </label>
                            <div className="flex">
                              <div className="flex items-center px-3 py-2 bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg text-gray-600 text-xs sm:text-sm whitespace-nowrap">
                                {displayPrefix}
                              </div>
                              <input
                                type="text"
                                value={getSocialUrlSuffix(businessFormData[field], matchPrefixes)}
                                onChange={(e) => handleSocialUrlSuffixChange(field, e.target.value)}
                                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                                placeholder={placeholder}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <p className="text-xs text-gray-500 mt-0">
                      Leave any field blank to hide that social icon on your website.
                    </p>
                  </div>
                    </div>
                  )}

                  {siteConfigActiveTab === 'services-categories' && (
                    <div className="space-y-4 md:space-y-6">
                      {/* Section header - reference */}
                      <div className="grid grid-cols-[1fr_auto] gap-3 md:gap-4 mb-5">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 m-0 tracking-tight">Services & Categories</h3>
                        <p className="col-span-full text-gray-600 text-sm mt-0 mb-0">Control where services happen and choose the categories that best describe your business.</p>
                      </div>
                      {/* Service Location - reference subsection */}
                      <div className="p-4 md:p-5 border border-gray-200 rounded-xl bg-gray-50/50 mb-6">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-base font-bold text-gray-900 m-0">Service Location</h4>
                          <InfoTooltip content="This setting determines whether appointments are shown as in-store, on-site, or both. Updating this can affect existing services for the business." />
                        </div>
                        <p className="text-gray-600 text-sm mt-0 mb-4">Choose where appointments are performed.</p>
                        <div className="flex flex-wrap gap-4">
                          <label className="flex items-start gap-2.5 p-3.5 border border-gray-200 rounded-xl bg-white min-w-[200px] cursor-pointer hover:border-[#0B4778]/30 hover:bg-gray-50/50 transition-colors">
                            <input type="radio" name="serviceLocation" value="IN_STORE" checked={businessFormData.serviceLocation === 'IN_STORE'} onChange={(e) => handleBusinessFormChange('serviceLocation', e.target.value as 'IN_STORE' | 'ON_SITE' | 'BOTH')} className="mt-0.5 w-4 h-4 border-gray-300 focus:ring-[#0B4778] text-[#0B4778]" />
                            <div>
                              <span className="block font-semibold text-sm text-gray-900">In-store</span>
                              <span className="block text-xs text-gray-500 mt-0.5">Services happen at the business location.</span>
                            </div>
                          </label>
                          <label className="flex items-start gap-2.5 p-3.5 border border-gray-200 rounded-xl bg-white min-w-[200px] cursor-pointer hover:border-[#0B4778]/30 hover:bg-gray-50/50 transition-colors">
                            <input type="radio" name="serviceLocation" value="ON_SITE" checked={businessFormData.serviceLocation === 'ON_SITE'} onChange={(e) => handleBusinessFormChange('serviceLocation', e.target.value as 'IN_STORE' | 'ON_SITE' | 'BOTH')} className="mt-0.5 w-4 h-4 border-gray-300 focus:ring-[#0B4778] text-[#0B4778]" />
                            <div>
                              <span className="block font-semibold text-sm text-gray-900">On-site</span>
                              <span className="block text-xs text-gray-500 mt-0.5">Services happen at the customer's location.</span>
                            </div>
                          </label>
                          <label className="flex items-start gap-2.5 p-3.5 border border-gray-200 rounded-xl bg-white min-w-[200px] cursor-pointer hover:border-[#0B4778]/30 hover:bg-gray-50/50 transition-colors">
                            <input type="radio" name="serviceLocation" value="BOTH" checked={businessFormData.serviceLocation === 'BOTH'} onChange={(e) => handleBusinessFormChange('serviceLocation', e.target.value as 'IN_STORE' | 'ON_SITE' | 'BOTH')} className="mt-0.5 w-4 h-4 border-gray-300 focus:ring-[#0B4778] text-[#0B4778]" />
                            <div>
                              <span className="block font-semibold text-sm text-gray-900">Both</span>
                              <span className="block text-xs text-gray-500 mt-0.5">Offer a mix of in-store and on-site services.</span>
                            </div>
                          </label>
                        </div>
                        {(businessFormData.serviceLocation === 'IN_STORE' || businessFormData.serviceLocation === 'ON_SITE') && (
                          <p className="text-amber-700 text-sm mt-3 flex items-start gap-2" role="note">
                            <span className="font-medium shrink-0">Note:</span>
                            <span>If you save with &quot;{businessFormData.serviceLocation === 'IN_STORE' ? 'In-store' : 'On-site'}&quot;, the location for all existing services for this business will be updated to match.</span>
                          </p>
                        )}
                      </div>

                      {/* Business Categories Section */}
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <div className="mb-6">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-medium text-gray-900 m-0">Business Categories</h3>
                            <span className="md:hidden">
                              <InfoTooltip content="Pick one or more categories that best match your business. Selecting a category lets you choose from its subcategories. Subcategories help customers understand exactly what kind of services you provide inside the chosen category." />
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">Select one or more categories that best describe your business. Click on a category to see its subcategories.</p>
                        </div>

                        {/* Desktop: Two-column layout */}
                        <div className="hidden md:grid md:grid-cols-2 md:gap-6">
                          {/* Categories Column */}
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <h4 className="text-sm font-semibold text-gray-700 m-0">Categories</h4>
                              <InfoTooltip content="Pick one or more categories that best match your business. Selecting a category lets you choose from its subcategories on the right." />
                            </div>
                            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                              {businessCategories.length === 0 ? (
                                <p className="text-sm text-gray-500">Loading categories...</p>
                              ) : (
                                businessCategories.map((category, index) => {
                                  const isSelected = businessFormData.categories?.includes(category.id) || false;
                                  const isActive = selectedCategoryId === category.id;
                                  const isOthers = category.name === 'Others';
                                  const hasSubcategories = category.subcategories && category.subcategories.length > 0;
                                  
                                  return (
                                    <React.Fragment key={category.id}>
                                      {isOthers && index > 0 && (
                                        <div className="border-t border-gray-200 my-3"></div>
                                      )}
                                      <div
                                        className={`flex items-start p-3 border rounded-lg transition-colors ${
                                          isActive
                                            ? 'border-[#0B4778] bg-[#0B4778]/10 shadow-sm'
                                            : isSelected
                                            ? 'border-[#0B4778]/40 bg-[#0B4778]/10/50'
                                            : 'border-gray-300 hover:border-gray-400'
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => handleCategoryCheckboxToggle(category.id)}
                                          onClick={(e) => e.stopPropagation()}
                                          className="h-4 w-4 text-[#0B4778] focus:ring-[#0B4778] border-gray-300 rounded mt-0.5"
                                        />
                                        <div 
                                          className="ml-3 flex-1 cursor-pointer"
                                          onClick={() => hasSubcategories && handleCategoryCardClick(category.id)}
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className={`text-sm font-medium ${isSelected ? 'text-[#0B4778]' : 'text-gray-900'}`}>
                                              {category.name}
                                            </span>
                                            {hasSubcategories && (
                                              <span className="text-xs text-gray-500">
                                                ({category.subcategories?.length} subcategories)
                                              </span>
                                            )}
                                          </div>
                                          {category.description && (
                                            <p className={`text-xs mt-1 ${isSelected ? 'text-[#0B4778]' : 'text-gray-500'}`}>
                                              {category.description}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </React.Fragment>
                                  );
                                })
                              )}
                            </div>
                          </div>

                          {/* Subcategories Column */}
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <h4 className="text-sm font-semibold text-gray-700 m-0">
                                {selectedCategoryId 
                                  ? businessCategories.find(c => c.id === selectedCategoryId)?.name + ' Subcategories'
                                  : 'Subcategories'}
                              </h4>
                              <InfoTooltip content="Subcategories help customers understand exactly what kind of services you provide inside the chosen category." />
                            </div>
                            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                              {selectedCategoryId ? (
                                (() => {
                                  const selectedCategory = businessCategories.find(c => c.id === selectedCategoryId);
                                  const subcategories = selectedCategory?.subcategories || [];
                                  const isCategorySelected = businessFormData.categories?.includes(selectedCategoryId) || false;
                                  
                                  if (subcategories.length === 0) {
                                    return (
                                      <p className="text-sm text-gray-500 italic">
                                        {selectedCategory?.name === 'Others' 
                                          ? 'This category has no subcategories.'
                                          : 'No subcategories available for this category.'}
                                      </p>
                                    );
                                  }
                                  
                                  return subcategories.map((subcategory) => {
                                    const isSubcategorySelected = businessFormData.subCategories?.includes(subcategory.id) || false;
                                    
                                    return (
                                      <label
                                        key={subcategory.id}
                                        className={`flex items-center p-2.5 border rounded-lg transition-colors ${
                                          isSubcategorySelected && isCategorySelected
                                            ? 'border-[#0B4778] bg-[#0B4778]/10'
                                            : !isCategorySelected
                                            ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                                            : 'border-gray-300 hover:border-gray-400 cursor-pointer'
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSubcategorySelected}
                                          onChange={() => handleSubcategoryToggle(subcategory.id)}
                                          disabled={!isCategorySelected}
                                          className="h-4 w-4 text-[#0B4778] focus:ring-[#0B4778] border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                        <div className="ml-3 flex-1">
                                          <span className={`text-sm ${isSubcategorySelected && isCategorySelected ? 'text-[#0B4778] font-medium' : 'text-gray-700'}`}>
                                            {subcategory.name}
                                          </span>
                                          {subcategory.description && (
                                            <p className={`text-xs mt-0.5 ${isSubcategorySelected && isCategorySelected ? 'text-[#0B4778]' : 'text-gray-500'}`}>
                                              {subcategory.description}
                                            </p>
                                          )}
                                        </div>
                                      </label>
                                    );
                                  });
                                })()
                              ) : (
                                <p className="text-sm text-gray-500 italic">Select a category to view its subcategories</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Mobile: Collapsible layout */}
                        <div className="md:hidden space-y-3">
                          {businessCategories.length === 0 ? (
                            <p className="text-sm text-gray-500">Loading categories...</p>
                          ) : (
                            businessCategories.slice(0, showAllCategoriesMobile ? businessCategories.length : 5).map((category, index) => {
                              const isSelected = businessFormData.categories?.includes(category.id) || false;
                              const isExpanded = selectedCategoryId === category.id;
                              const isOthers = category.name === 'Others';
                              const hasSubcategories = category.subcategories && category.subcategories.length > 0;
                              
                              return (
                                <React.Fragment key={category.id}>
                                  {isOthers && index > 0 && (
                                    <div className="border-t border-gray-200 my-3"></div>
                                  )}
                                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                                    <div
                                      className={`flex items-start p-3 transition-colors ${
                                        isSelected
                                          ? 'bg-[#0B4778]/10'
                                          : 'bg-white'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => handleCategoryCheckboxToggle(category.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-4 w-4 text-[#0B4778] focus:ring-[#0B4778] border-gray-300 rounded mt-0.5"
                                      />
                                      <div 
                                        className="ml-3 flex-1"
                                        onClick={() => hasSubcategories && handleCategoryCardClick(category.id)}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex-1">
                                            <span className={`text-sm font-medium ${isSelected ? 'text-[#0B4778]' : 'text-gray-900'}`}>
                                              {category.name}
                                            </span>
                                            {category.description && (
                                              <p className={`text-xs mt-1 ${isSelected ? 'text-[#0B4778]' : 'text-gray-500'}`}>
                                                {category.description}
                                              </p>
                                            )}
                                          </div>
                                          {hasSubcategories && (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleCategoryCardClick(category.id);
                                              }}
                                              className="ml-2 p-1 -m-1 text-gray-500 hover:text-gray-700 touch-manipulation"
                                              aria-expanded={isExpanded}
                                              aria-label={isExpanded ? 'Collapse subcategories' : 'Expand subcategories'}
                                            >
                                              <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Subcategories (collapsible) */}
                                    {isExpanded && hasSubcategories && (
                                      <div className="border-t border-gray-200 bg-gray-50 p-3 space-y-2">
                                        {category.subcategories?.map((subcategory) => {
                                          const isSubcategorySelected = businessFormData.subCategories?.includes(subcategory.id) || false;
                                          
                                          return (
                                            <label
                                              key={subcategory.id}
                                              className={`flex items-center p-2.5 border rounded-lg transition-colors ${
                                                isSubcategorySelected && isSelected
                                                  ? 'border-[#0B4778] bg-[#0B4778]/10'
                                                  : !isSelected
                                                  ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                                                  : 'border-gray-300 hover:border-gray-400 cursor-pointer'
                                              }`}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={isSubcategorySelected}
                                                onChange={() => handleSubcategoryToggle(subcategory.id)}
                                                disabled={!isSelected}
                                                className="h-4 w-4 text-[#0B4778] focus:ring-[#0B4778] border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                              />
                                              <div className="ml-3 flex-1">
                                                <span className={`text-sm ${isSubcategorySelected && isSelected ? 'text-[#0B4778] font-medium' : 'text-gray-700'}`}>
                                                  {subcategory.name}
                                                </span>
                                                {subcategory.description && (
                                                  <p className={`text-xs mt-0.5 ${isSubcategorySelected && isSelected ? 'text-[#0B4778]' : 'text-gray-500'}`}>
                                                    {subcategory.description}
                                                  </p>
                                                )}
                                              </div>
                                            </label>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </React.Fragment>
                              );
                            })
                          )}
                          {businessCategories.length > 5 && !showAllCategoriesMobile && (
                            <button
                              onClick={() => setShowAllCategoriesMobile(true)}
                              className="w-full py-2.5 px-4 text-sm font-medium text-[#0B4778] border border-[#0B4778] rounded-lg hover:bg-[#0B4778]/10 transition-colors"
                            >
                              Show more...
                            </button>
                          )}
                        </div>

                        {(businessFormData.categories && businessFormData.categories.length > 0) || (businessFormData.subCategories && businessFormData.subCategories.length > 0) ? (
                          <p className="text-xs text-gray-500 mt-3">
                            {businessFormData.categories?.length || 0} categor{businessFormData.categories?.length === 1 ? 'y' : 'ies'} and {businessFormData.subCategories?.length || 0} subcategor{businessFormData.subCategories?.length === 1 ? 'y' : 'ies'} selected
                          </p>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {siteConfigActiveTab === 'operating-hours' && (
                    <div className="space-y-4">
                    {/* Section header */}
                    <div className="mb-5 space-y-2">
                      <h3 className="m-0 text-lg font-bold tracking-tight text-gray-900 md:text-xl">Business Hours</h3>
                      <p className="mb-0 mt-0 text-sm text-gray-600">
                        Set the timezone and weekly operating hours shown on your website and used in scheduling logic.
                      </p>
                    </div>
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-lg font-medium text-gray-900 m-0">Business Timezone</h4>
                        <InfoTooltip content="This timezone is used to interpret hours, closures, schedules, and time off rules correctly." />
                      </div>
                      <div className="mt-0">
                        <div className="flex-1 md:max-w-xs">
                          <select
                            value={(businessFormData as any).timezone || getBrowserTimeZone() || 'UTC'}
                            onChange={(e) => handleBusinessFormChange('timezone', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white"
                            aria-label="Business Timezone"
                          >
                            <option value="UTC">UTC</option>
                            {US_TIMEZONES.map(tz => (
                              <option key={tz.value} value={tz.value}>
                                {tz.label.split(' — ')[0]}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Operating Hours</h3>
                    </div>
                    
                    {operatingHoursLoading ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B4778] mx-auto mb-2"></div>
                        <p className="text-sm text-gray-600">Loading operating hours...</p>
                      </div>
                    ) : (
                      <div className="space-y-3 md:space-y-4">
                        {[0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
                          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                          const dayName = dayNames[dayOfWeek];
                          const operatingHour = operatingHours.find(h => h.dayOfWeek === dayOfWeek) || {
                            dayOfWeek,
                            isClosed: false,
                            openTime: '',
                            closeTime: ''
                          };

                          return (
                            <div
                              key={dayOfWeek}
                              className="flex min-w-0 flex-col gap-3 border border-gray-200 bg-white py-3 px-4 shadow-sm rounded-lg md:py-4 md:px-5"
                            >
                              {/* Same layout at all breakpoints: day + copy | Closed, then stacked Open / Close */}
                              <div className="flex min-w-0 items-center justify-between gap-2">
                                <div className="flex min-w-0 items-center gap-2">
                                  <span className="text-sm font-medium text-gray-700">{dayName}</span>
                                  {dayOfWeek >= 1 && dayOfWeek <= 6 && (
                                    <div className="relative group shrink-0">
                                      <button
                                        type="button"
                                        className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                                        onClick={() => {
                                          const prevDayOfWeek = dayOfWeek - 1;
                                          const sourceDay =
                                            operatingHours.find(h => h.dayOfWeek === prevDayOfWeek) ?? {
                                              dayOfWeek: prevDayOfWeek,
                                              isClosed: false,
                                              openTime: '',
                                              closeTime: '',
                                            };
                                          setOperatingHours(current => {
                                            const existing = current.find(h => h.dayOfWeek === dayOfWeek);
                                            if (existing) {
                                              return current.map(h =>
                                                h.dayOfWeek === dayOfWeek
                                                  ? {
                                                      ...h,
                                                      openTime: sourceDay.openTime,
                                                      closeTime: sourceDay.closeTime,
                                                      isClosed: sourceDay.isClosed,
                                                    }
                                                  : h
                                              );
                                            }
                                            return [
                                              ...current,
                                              {
                                                id: '',
                                                businessId: currentBusiness?.id || '',
                                                dayOfWeek,
                                                openTime: sourceDay.openTime,
                                                closeTime: sourceDay.closeTime,
                                                isClosed: sourceDay.isClosed,
                                              },
                                            ];
                                          });
                                        }}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                                          <path d="M16 3H5a2 2 0 00-2 2v11h2V5h11V3z"/>
                                          <path d="M19 7H9a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V9a2 2 0 00-2-2zm0 12H9V9h10v10z"/>
                                        </svg>
                                      </button>
                                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none whitespace-nowrap">
                                        <div className="bg-gray-900 text-white text-xs rounded-lg py-1.5 px-2 shadow-lg">
                                          Copy hours from previous day
                                          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={operatingHour.isClosed}
                                    onChange={(e) => handleOperatingHoursChange(dayOfWeek, 'isClosed', e.target.checked)}
                                    className="rounded border-gray-300 text-[#0B4778] focus:ring-[#0B4778]"
                                  />
                                  <span className="text-sm text-gray-600 whitespace-nowrap">Closed</span>
                                </div>
                              </div>

                              <div
                                className={`flex min-w-0 flex-col gap-3 ${operatingHour.isClosed ? 'opacity-60 pointer-events-none' : ''}`}
                              >
                                <div className="flex min-w-0 items-center gap-2">
                                  <span className="shrink-0 text-sm text-gray-600 min-w-[3rem]">Open:</span>
                                  <div className="min-w-0 flex-1">
                                    <TimePicker
                                      value={formatTimeForDisplay(operatingHour.openTime)}
                                      onChange={(time) => handleTimeChange(dayOfWeek, 'openTime', time)}
                                      className="text-sm"
                                    />
                                  </div>
                                </div>

                                <div className="flex min-w-0 items-center gap-2">
                                  <span className="shrink-0 text-sm text-gray-600 min-w-[3rem]">Close:</span>
                                  <div className="min-w-0 flex-1">
                                    <TimePicker
                                      value={formatTimeForDisplay(operatingHour.closeTime)}
                                      onChange={(time) => handleTimeChange(dayOfWeek, 'closeTime', time)}
                                      className="text-sm"
                                      initialPeriod="PM"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
              </div>
            )}
        
                  {siteConfigActiveTab === 'branding-theme' && (
          <div className="space-y-6">
            {brandingLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading branding settings...</p>
              </div>
            ) : (
              <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:gap-6">
                {/* Left column on desktop; flattened on mobile via contents */}
                <div className="contents lg:flex lg:flex-col lg:gap-6">
                  {/* Logo card */}
                  <div className="order-1 rounded-xl border-2 border-dashed border-gray-300 bg-white p-5 md:p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm font-medium text-gray-700">Logo</label>
                      <InfoTooltip content="Upload or replace the logo shown on your public booking website." />
                    </div>
                    <p className="text-gray-600 text-sm mb-4">Upload or replace the logo shown on your public booking website.</p>
                    <div className="flex flex-col items-center text-center">
                      {brandingFormData.logoUrl ? (
                        <>
                          <div className="relative w-20 h-20 rounded-full overflow-hidden flex items-center justify-center border-2 border-gray-200 bg-gray-50 mb-3">
                            {(logoImageLoading || brandingSaving) && (
                              <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50" aria-hidden>
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B4778]" />
                              </div>
                            )}
                            <img
                              key={brandingFormData.logoUrl}
                              src={getLogoUrl(brandingFormData.logoUrl)}
                              alt="Business Logo"
                              className={`w-full h-full object-contain p-1 transition-opacity ${logoImageLoading || brandingSaving ? 'opacity-0' : 'opacity-100'}`}
                              onLoad={() => setLogoImageLoading(false)}
                              onError={(e) => {
                                console.error('Error loading logo:', brandingFormData.logoUrl);
                                setLogoImageLoading(false);
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                          <p className="text-sm font-medium text-gray-900 mb-4">{businessFormData.name || 'Your business'}</p>
                          <div className="flex flex-wrap gap-2 justify-center">
                            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload-branding" />
                            <label htmlFor="logo-upload-branding" className="aacore-btn2-primary cursor-pointer inline-flex items-center gap-2">
                              <i className="bi bi-upload" aria-hidden />
                              Update Logo
                            </label>
                            <button type="button" onClick={() => handleBrandingFormChange('logoUrl', '')} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-sm font-medium">
                              <i className="bi bi-trash" aria-hidden />
                              Remove
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="w-20 h-20 rounded-full flex items-center justify-center border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 mb-3">
                            {brandingSaving ? (
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B4778]" aria-hidden />
                            ) : (
                              <i className="bi bi-building text-2xl" aria-hidden />
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-4">{businessFormData.name || 'Your business'}</p>
                          <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload-branding" />
                          <label htmlFor="logo-upload-branding" className="aacore-btn2-primary cursor-pointer inline-flex items-center gap-2">
                            <i className="bi bi-upload" aria-hidden />
                            Upload Logo
                          </label>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Logo Link Behavior — only when a logo is uploaded */}
                  {brandingFormData.logoUrl && (
                    <div className="order-1 p-4 md:p-5 border border-gray-200 rounded-xl bg-gray-50/80 flex flex-col">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-base font-bold text-gray-900 m-0">Logo Link Behavior</h4>
                        <InfoTooltip content="Logo will link to your AACore-provided website by default. You may customize to direct to your business website." />
                      </div>
                      <p className="text-gray-600 text-sm mt-1 mb-0">Control what happens when customers click your logo.</p>
                      {businessFormData.website && businessFormData.website.trim() !== '' && (
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                          <label htmlFor="websiteRedirectEnabled" className="text-sm font-semibold text-gray-900 cursor-pointer">Clicking logo opens business website</label>
                          <button
                            type="button"
                            onClick={() => handleBusinessFormChange('websiteRedirectEnabled', !businessFormData.websiteRedirectEnabled)}
                            className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2 ${businessFormData.websiteRedirectEnabled ? 'bg-[#0B4778]' : 'bg-gray-200'}`}
                            role="switch"
                            aria-checked={businessFormData.websiteRedirectEnabled}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ${businessFormData.websiteRedirectEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Live Preview card */}
                  <div className="order-4 lg:order-2 rounded-xl border border-gray-200 bg-white shadow-sm p-5 md:p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm font-medium text-gray-700">Live Preview</label>
                      <InfoTooltip content="See how the selected font and colors work together before saving." />
                    </div>
                    <p className="text-gray-600 text-sm mb-4">See how the selected font and colors work together before saving.</p>
                    <div
                      className="rounded-lg border border-gray-200 p-4 overflow-hidden"
                      style={{
                        backgroundColor: brandingFormData.backgroundColor,
                        color: brandingFormData.textColor,
                        fontFamily: brandingFormData.fontFamily
                      }}
                    >
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium mb-3" style={{ backgroundColor: brandingFormData.primaryColor, color: '#fff' }}>
                        {brandingFormData.themePreset ? (THEME_PRESETS[brandingFormData.themePreset]?.name || 'Custom') + ' theme' : 'Custom theme'}
                      </span>
                      <div className="flex justify-center mb-3">
                        {brandingFormData.logoUrl ? (
                          <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center border border-gray-200 bg-white">
                            <img src={getLogoUrl(brandingFormData.logoUrl)} alt="" className="w-full h-full object-contain p-0.5" />
                          </div>
                        ) : (
                          <div className="w-12 h-12 rounded-full flex items-center justify-center border border-gray-200 bg-gray-100 text-gray-500">
                            <i className="bi bi-building" aria-hidden />
                          </div>
                        )}
                      </div>
                      <p className="text-center font-semibold text-sm mb-1" style={{ color: brandingFormData.primaryColor }}>{businessFormData.name || 'Your business'}</p>
                      <p className="text-center text-xs opacity-90 mb-4">Fast, friendly, and reliable home cleaning service designed to feel trustworthy on every screen.</p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        <button type="button" className="px-3 py-1.5 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: brandingFormData.primaryColor }}>Book Now</button>
                        <button type="button" className="px-3 py-1.5 rounded-lg border text-sm font-medium" style={{ borderColor: brandingFormData.primaryColor, color: brandingFormData.primaryColor }}>View Services</button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right column on desktop; flattened on mobile via contents */}
                <div className="contents lg:flex lg:flex-col lg:gap-6">
                  {/* Typography & Theme card */}
                  <div className="order-2 lg:order-1 rounded-xl border border-gray-200 bg-white shadow-sm p-5 md:p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm font-medium text-gray-700">Typography & Theme</label>
                      <InfoTooltip content="Pick a font family and apply a preset color theme." />
                    </div>
                    <p className="text-gray-600 text-sm mb-4">Pick a font family and apply a preset color theme.</p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Font Family</label>
                        <select
                          value={brandingFormData.fontFamily}
                          onChange={(e) => handleBrandingFormChange('fontFamily', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-gray-900 text-sm"
                        >
                          <option value="Inter">Inter (Modern)</option>
                          <option value="Roboto">Roboto (Clean)</option>
                          <option value="Open Sans">Open Sans (Friendly)</option>
                          <option value="Lato">Lato (Professional)</option>
                          <option value="Poppins">Poppins (Contemporary)</option>
                          <option value="Montserrat">Montserrat (Elegant)</option>
                        </select>
                      </div>
                      <div>
                        <span className="block text-xs font-medium text-gray-500 mb-2">Preset Color Themes</span>
                        <div className="space-y-3">
                          {getThemePresetList().map((preset) => {
                            const presetKey = Object.keys(THEME_PRESETS).find(k => THEME_PRESETS[k].name === preset.name) || '';
                            const isSelectedByPreset = brandingFormData.themePreset === presetKey;
                            const isSelectedByColors = !brandingFormData.themePreset &&
                              brandingFormData.primaryColor?.toUpperCase() === preset.primaryColor.toUpperCase() &&
                              brandingFormData.secondaryColor?.toUpperCase() === preset.secondaryColor.toUpperCase() &&
                              brandingFormData.accentColor?.toUpperCase() === preset.accentColor.toUpperCase() &&
                              brandingFormData.textColor?.toUpperCase() === preset.textColor.toUpperCase() &&
                              brandingFormData.backgroundColor?.toUpperCase() === preset.backgroundColor.toUpperCase();
                            const isSelected = isSelectedByPreset || isSelectedByColors;
                            return (
                              <div
                                key={presetKey}
                                className={`flex items-center justify-between gap-3 p-3 rounded-lg border-2 transition-all ${isSelected ? 'border-[#0B4778] bg-[#0B4778]/5' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300'}`}
                              >
                                <div className="min-w-0 flex-1">
                                  <p className={`text-sm font-semibold truncate ${isSelected ? 'text-[#0B4778]' : 'text-gray-900'}`}>
                                    {preset.name}{isSelected && ' (Current)'}
                                  </p>
                                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                    {[
                                      { label: 'P', color: preset.primaryColor },
                                      { label: 'S', color: preset.secondaryColor },
                                      { label: 'A', color: preset.accentColor },
                                      { label: 'T', color: preset.textColor },
                                      { label: 'B', color: preset.backgroundColor }
                                    ].map(({ label, color }) => (
                                      <div key={label} className="flex items-center gap-0.5">
                                        <div className="w-5 h-5 rounded-md border border-gray-300 flex-shrink-0" style={{ backgroundColor: color }} title={color} />
                                        <span className="text-[10px] font-mono text-gray-500 hidden sm:inline">{color}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleThemePresetSelect(presetKey)}
                                  className={`flex-shrink-0 text-sm font-medium px-3 py-1.5 rounded-lg ${isSelected ? 'aacore-btn2-primary cursor-default' : 'aacore-btn2-outline'}`}
                                >
                                  {isSelected ? <span className="inline-flex items-center gap-1"><Check className="w-4 h-4" /> Applied</span> : 'Apply'}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gallery Images card */}
                  <div className="order-3 lg:order-2 rounded-xl border border-gray-200 bg-white shadow-sm p-5 md:p-6">
                    <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">Gallery Images</label>
                        <InfoTooltip content="Add up to 6 images to showcase your business on your public site. Drag to reorder; order is used on your customer-facing site." />
                      </div>
                      <button
                        type="button"
                        onClick={handleOpenStockImagesModal}
                        disabled={galleryRemainingSlots <= 0}
                        className="hidden md:inline-flex aacore-btn2-outline shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Image className="w-4 h-4" />
                        Browse Stock Images
                      </button>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">Maximum 6 images. Drag to reorder.</p>
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
                      {brandingFormData.galleryImages && brandingFormData.galleryImages.length > 0 ? (
                        <div className="space-y-4">
                          <DndContext sensors={gallerySensors} collisionDetection={closestCenter} onDragEnd={handleGalleryDragEnd}>
                            <SortableContext items={brandingFormData.galleryImages} strategy={rectSortingStrategy}>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {brandingFormData.galleryImages.map((image, index) => (
                                  <SortableGalleryImage
                                    key={image}
                                    imageUrl={image}
                                    index={index}
                                    onRemove={removeGalleryImage}
                                  />
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                          {brandingFormData.galleryImages.length >= MAX_GALLERY_IMAGES ? (
                            <p className="text-sm text-gray-500 text-center">Maximum of 6 gallery images reached. Remove an image to add more.</p>
                          ) : (
                            <div className="flex flex-col sm:flex-row gap-2">
                              <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" id="gallery-upload-branding" disabled={brandingFormData.galleryImages.length >= MAX_GALLERY_IMAGES} />
                              <label htmlFor="gallery-upload-branding" className={`flex-1 text-center aacore-btn2-outline cursor-pointer py-2 ${brandingFormData.galleryImages.length >= MAX_GALLERY_IMAGES ? 'opacity-50 cursor-not-allowed' : ''}`}>Add More Images</label>
                              <button
                                type="button"
                                onClick={handleOpenStockImagesModal}
                                disabled={galleryRemainingSlots <= 0}
                                className="md:hidden aacore-btn2-outline disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <Image className="w-4 h-4" />
                                Browse Stock Images
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <span className="text-2xl text-gray-400 block mb-2">🖼️</span>
                          <p className="text-sm text-gray-600 mb-2">Upload gallery images to showcase your business</p>
                          <p className="text-xs text-gray-500 mb-3">PNG, JPG up to 5MB each • Maximum 6 images</p>
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
                            <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" id="gallery-upload-branding" />
                            <label htmlFor="gallery-upload-branding" className="aacore-btn2-primary cursor-pointer inline-flex items-center justify-center gap-2">
                              <ImagePlus className="w-4 h-4" />
                              Upload Gallery Images
                            </label>
                            <button
                              type="button"
                              onClick={handleOpenStockImagesModal}
                              disabled={galleryRemainingSlots <= 0}
                              className="md:hidden aacore-btn2-outline disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <Image className="w-4 h-4" />
                              Browse Stock Images
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
                  )}

                  {siteConfigActiveTab === 'payments' && (
                    <div className="space-y-6">
                      <div className="mb-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Payments</h3>
                        <p className="text-gray-600 text-sm">
                          Set up online invoice payments and customer deposits for this business. Payment setup is completed directly with Stripe through Stripe Connect Express. Stripe verifies your business and securely handles bank account, payout, card, and payment information. AACore Booking does not store your financial information.
                        </p>
                      </div>

                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        {businessStripeAccountLoading ? (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                            Loading payments status…
                          </div>
                        ) : businessStripeAccountError ? (
                          <div className="text-sm text-red-600">{businessStripeAccountError}</div>
                        ) : null}

                        {!businessStripeAccount ? (
                          <div className="space-y-3">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#0B4778]/10 flex items-center justify-center text-[#0B4778] flex-shrink-0">
                                <CreditCard className="w-5 h-5" />
                              </div>
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900">Online payments are not enabled yet</div>
                                <div className="text-sm text-gray-600 mt-1">
                                  Set up your Stripe Express account directly with Stripe to accept card payments, invoice payments, and customer deposits. Stripe securely handles your bank, payout, card, and payment information. AACore Booking does not store your financial information.
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={handleStartStripeOnboarding}
                              disabled={businessStripeAccountLoading}
                              className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <CreditCard className="w-4 h-4" />
                              Set Up Payments with Stripe
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-gray-900">Stripe account connected</div>
                                {businessStripeAccount.chargesEnabled ? (
                                  <div className="text-sm text-gray-600 mt-1">
                                    Online payments are connected through Stripe. You can now accept invoice payments and require deposits for eligible bookings. Stripe securely manages payment processing, payouts, and financial information. AACore Booking only stores the payment status and related booking or invoice records.
                                  </div>
                                ) : null}
                              </div>
                              <div className="flex flex-col gap-2 md:items-end md:shrink-0 md:ml-auto">
                                <span
                                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
                                    businessStripeAccount.chargesEnabled ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-800 border-amber-200'
                                  }`}
                                >
                                  {businessStripeAccount.chargesEnabled ? <Check className="w-3.5 h-3.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
                                  Charges {businessStripeAccount.chargesEnabled ? 'enabled' : 'not enabled'}
                                </span>
                                <span
                                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
                                    businessStripeAccount.payoutsEnabled ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-800 border-amber-200'
                                  }`}
                                >
                                  {businessStripeAccount.payoutsEnabled ? <Check className="w-3.5 h-3.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
                                  Payouts {businessStripeAccount.payoutsEnabled ? 'enabled' : 'not enabled'}
                                </span>
                                <span
                                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
                                    businessStripeAccount.detailsSubmitted ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-800 border-amber-200'
                                  }`}
                                >
                                  {businessStripeAccount.detailsSubmitted ? <Check className="w-3.5 h-3.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
                                  Details {businessStripeAccount.detailsSubmitted ? 'submitted' : 'incomplete'}
                                </span>
                              </div>
                            </div>

                            {businessStripeAccount.disabledReason ? (
                              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                                Stripe status: {String(businessStripeAccount.disabledReason)}
                              </div>
                            ) : null}

                            {businessStripeAccount.chargesEnabled && businessStripeAccount.payoutsEnabled && businessStripeAccount.detailsSubmitted ? (
                              <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                  type="button"
                                  onClick={handleOpenStripeDashboard}
                                  disabled={businessStripeAccountLoading}
                                  className="aacore-btn2-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  Go to my Stripe account
                                </button>
                                <button
                                  type="button"
                                  onClick={handleRefreshStripeAccount}
                                  disabled={businessStripeAccountLoading}
                                  className="aacore-btn2-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                  Refresh status
                                </button>
                              </div>
                            ) : (
                            <div className="flex flex-col sm:flex-row gap-2">
                              <button
                                type="button"
                                onClick={handleContinueStripeOnboarding}
                                disabled={businessStripeAccountLoading}
                                className="aacore-btn2-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <CreditCard className="w-4 h-4" />
                                Continue Stripe onboarding
                              </button>
                              <button
                                type="button"
                                onClick={handleRefreshStripeAccount}
                                disabled={businessStripeAccountLoading}
                                className="aacore-btn2-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <RefreshCw className="w-4 h-4" />
                                Refresh status
                              </button>
                            </div>
                            )}

                            <div className="text-xs text-gray-500">
                              Tip: once charges are enabled, customers can pay invoices online and funds will be routed to this business’ Stripe account.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
        
                  {siteConfigActiveTab === 'cancellation-rescheduling-policy' && (
                    <div className="space-y-6">
                      <div className="mb-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Cancellation &amp; Rescheduling Policy</h3>
                        <p className="text-gray-600 text-sm">
                          Provide policy details for customers and set the cancellation policy text that will be displayed on your booking site.
                        </p>
                      </div>

                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex flex-col gap-4">
                          <div className="flex flex-col gap-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Customers can cancel/reschedule up to
                            </label>
                            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                              <input
                                type="number"
                                min={0}
                                value={cancelRescheduleUpToValue}
                                onChange={(e) => {
                                  const n = Number(e.target.value);
                                  setCancelRescheduleUpToValue(Number.isFinite(n) ? n : 0);
                                }}
                                className="w-full sm:w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                              />
                              <select
                                value={cancelRescheduleUpToUnit}
                                onChange={(e) => setCancelRescheduleUpToUnit(e.target.value as 'hours' | 'days')}
                                className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                              >
                                <option value="hours">Hours</option>
                                <option value="days">Days</option>
                              </select>
                            </div>
                          </div>

                          <div className="border-t border-gray-200 pt-4">
                            {(() => {
                              const onlinePaymentsEnabled = Boolean(businessStripeAccount?.chargesEnabled);
                              return (
                            <>
                            {!onlinePaymentsEnabled && (
                              <p className="text-sm font-medium text-red-600 mb-3">
                                Stripe payments must be set up before these deposit settings can be enabled. Go to the Online Payments tab to complete Stripe setup.
                              </p>
                            )}
                            <label className={`flex items-start gap-3 ${onlinePaymentsEnabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                              <input
                                type="checkbox"
                                checked={depositRequired}
                                disabled={!onlinePaymentsEnabled}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setDepositRequired(checked);
                                  if (!checked) {
                                    setDepositAmountCentsDigits('');
                                    setDepositNonRefundableOnCancel(false);
                                  }
                                }}
                                className="mt-0.5 h-4 w-4 shrink-0 text-[#0B4778] focus:ring-[#0B4778] border-gray-300 rounded disabled:opacity-50"
                              />
                              <div>
                                <span className="block text-sm font-medium text-gray-700 mb-1">Require a deposit</span>
                                <p className="text-xs text-gray-500">
                                  {onlinePaymentsEnabled ? (
                                    <>
                                      Deposits allow you to collect part of the service cost upfront when a customer books an appointment. This helps reduce no-shows, hold the appointment time, and confirm the booking. Any deposit amount greater than zero will require customers to pay a deposit for standard, non-quote bookings. Enter 0.00 or clear the amount to disable deposits. Deposits are processed securely through Stripe. AACore Booking does not store customer card or financial information.
                                    </>
                                  ) : (
                                    <>
                                      Deposits allow you to collect part of the service cost upfront when a customer books an appointment. This helps reduce no-shows and helps confirm that the customer is serious about the booking. Deposit settings are disabled until online payments are set up through Stripe. To require deposits, first complete Stripe setup. AACore Booking does not store your bank, payout, card, or other financial information.
                                    </>
                                  )}
                                </p>
                              </div>
                            </label>
                            </>
                              );
                            })()}

                            <div className="mt-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-3 gap-y-2">
                              <div className="flex w-full sm:w-auto items-center gap-2">
                                <span className="text-sm text-gray-700 shrink-0">$</span>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={policyDepositCentsToMoneyString(depositAmountCentsDigits)}
                                  placeholder="0.00"
                                  disabled={!Boolean(businessStripeAccount?.chargesEnabled)}
                                  onChange={(e) => {
                                    const digits = e.target.value.replace(/\D/g, '');
                                    setDepositAmountCentsDigits(digits);
                                    if (!digits || policyDepositCentsToAmount(digits) <= 0) {
                                      setDepositNonRefundableOnCancel(false);
                                    }
                                  }}
                                  className="flex-1 min-w-0 w-full sm:flex-none sm:w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white placeholder:text-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                />
                              </div>
                              <label
                                className={`flex items-start gap-2 min-w-0 w-full sm:w-auto ${
                                  policyDepositCentsToAmount(depositAmountCentsDigits) > 0 && Boolean(businessStripeAccount?.chargesEnabled)
                                    ? 'cursor-pointer'
                                    : 'cursor-not-allowed opacity-60'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={depositNonRefundableOnCancel}
                                  disabled={policyDepositCentsToAmount(depositAmountCentsDigits) <= 0 || !Boolean(businessStripeAccount?.chargesEnabled)}
                                  onChange={(e) => setDepositNonRefundableOnCancel(e.target.checked)}
                                  className="mt-0.5 h-4 w-4 shrink-0 text-[#0B4778] focus:ring-[#0B4778] border-gray-300 rounded disabled:opacity-50"
                                />
                                <div>
                                  <span className="text-sm text-gray-700">
                                    Deposit will <strong>not</strong> be refunded if cancelled
                                  </span>
                                  <p className="text-xs text-gray-500 mt-1">
                                    When selected, the deposit is marked as non-refundable if the customer cancels the appointment. Use this only if your business policy clearly states that deposits are not refunded after cancellation.
                                  </p>
                                </div>
                              </label>
                            </div>

                            <div
                              className={`mt-4 ${
                                Boolean(businessStripeAccount?.chargesEnabled) ? '' : 'opacity-60'
                              }`}
                            >
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Cancel appointment if deposit is not received within
                              </label>
                              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                                <input
                                  type="number"
                                  min={1}
                                  value={
                                    Boolean(businessStripeAccount?.chargesEnabled)
                                      ? depositDueWithinValue
                                      : DEFAULT_REGULAR_DEPOSIT_DUE_VALUE
                                  }
                                  disabled={!Boolean(businessStripeAccount?.chargesEnabled)}
                                  onChange={(e) => {
                                    const n = Number(e.target.value);
                                    setDepositDueWithinValue(Number.isFinite(n) && n >= 1 ? n : 1);
                                  }}
                                  className="w-full sm:w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] disabled:bg-gray-50 disabled:cursor-not-allowed"
                                />
                                <select
                                  value={
                                    Boolean(businessStripeAccount?.chargesEnabled)
                                      ? depositDueWithinUnit
                                      : DEFAULT_REGULAR_DEPOSIT_DUE_UNIT
                                  }
                                  disabled={!Boolean(businessStripeAccount?.chargesEnabled)}
                                  onChange={(e) =>
                                    setDepositDueWithinUnit(normalizeDepositDueWithinUnit(e.target.value))
                                  }
                                  className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] disabled:bg-gray-50 disabled:cursor-not-allowed"
                                >
                                  <option value="minutes">Minutes</option>
                                  <option value="hours">Hours</option>
                                  <option value="days">Days</option>
                                </select>
                              </div>
                              <p className="text-xs text-gray-500 mt-2">
                                {Boolean(businessStripeAccount?.chargesEnabled) ? (
                                  <>
                                    For regular bookings, the appointment is automatically canceled if the deposit is not paid within{' '}
                                    {formatDepositDueDurationLabel(depositDueWithinValue, depositDueWithinUnit)}. This does not apply to appointments created from quotes.
                                  </>
                                ) : (
                                  <>
                                    Deposit payment window settings are disabled until online payments are set up through Stripe.
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Policy information</h4>
                        <div className="text-sm text-gray-700 space-y-2">
                          <p className="inline">
                            This section will display your{' '}
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={() => setShowStandardPolicyModal(true)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowStandardPolicyModal(true); } }}
                              className="underline font-medium text-[#0B4778] hover:text-[#093a5f] focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-1 rounded cursor-pointer whitespace-normal break-words"
                            >
                              standard cancellation/rescheduling policy
                            </span>
                            {' '}details for customers.
                          </p>
                          <p className="text-gray-500">
                            Optional: Enter your custom cancellation/rescheduling policy (leave blank to use the standard policy).
                          </p>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Custom Cancellation Policy</label>
                        <textarea
                          value={cancellationPolicyText}
                          onChange={(e) => setCancellationPolicyText(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                          rows={8}
                          placeholder="Enter a custom policy (optional)…"
                        />
                      </div>

                      {/* Customer Appointment Message (business_settings) */}
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 w-9 h-9 rounded-lg bg-[#0B4778]/10 flex items-center justify-center flex-shrink-0">
                              <MessageSquare className="w-5 h-5 text-[#0B4778]" />
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900">Customer Appointment Message</h4>
                              <p className="text-xs text-gray-500 mt-1">Optional note shown to customers during booking.</p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              setSettings(prev => ({
                                ...prev,
                                customerAppointmentMessageEnabled: !prev.customerAppointmentMessageEnabled
                              }))
                            }
                            className={`${
                              settings.customerAppointmentMessageEnabled ? 'bg-[#0B4778]' : 'bg-gray-200'
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2`}
                            aria-label="Toggle customer appointment message"
                          >
                            <span
                              className={`${
                                settings.customerAppointmentMessageEnabled ? 'translate-x-6' : 'translate-x-1'
                              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </button>
                        </div>

                        <div className="mt-4 bg-[#0B4778]/5 border border-[#0B4778]/20 rounded-lg p-3 flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0 border border-[#0B4778]/20">
                            <MessageSquare className="w-4 h-4 text-[#0B4778]" />
                          </div>
                          <p className="text-sm text-gray-700">
                            This message will be displayed on the <span className="font-semibold">appointment summary</span> and included in the{' '}
                            <span className="font-semibold">confirmation email</span>.
                          </p>
                        </div>

                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                          <textarea
                            value={settings.customerAppointmentMessage}
                            onChange={(e) =>
                              setSettings(prev => ({
                                ...prev,
                                customerAppointmentMessage: e.target.value
                              }))
                            }
                            disabled={!settings.customerAppointmentMessageEnabled}
                            rows={4}
                            maxLength={255}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder="Enter a message for customers..."
                          />
                          <div className="mt-2 text-xs text-gray-500 flex items-center justify-between">
                            <span>{!settings.customerAppointmentMessageEnabled ? 'Enable to edit.' : 'Max 255 characters.'}</span>
                            <span>{(settings.customerAppointmentMessage || '').length}/255</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sticky footer: Generate QR Code, Preview, Save Changes - align with main content; mobile: same row, equal width, icon above text */}
          {!businessLoading && (
            <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/90 backdrop-blur-md border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
              <div className="px-4 md:px-8 py-3.5 flex flex-nowrap md:flex-wrap items-stretch md:items-center justify-end gap-2 md:gap-3">
                <button
                  type="button"
                  onClick={handleGenerateQRCode}
                  className="aacore-btn2-secondary flex-1 md:flex-initial min-w-0 inline-flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 py-2.5 md:py-0"
                >
                  <QrCode className="w-4 h-4 flex-shrink-0" />
                  <span className="md:hidden text-xs font-medium">QR</span>
                  <span className="hidden md:inline">Generate QR Code</span>
                </button>
                <button
                  type="button"
                  onClick={handlePreviewWebsite}
                  disabled={!currentBusiness?.shortName && !businessInfo?.shortName}
                  className="aacore-btn2-outline disabled:opacity-50 disabled:cursor-not-allowed flex-1 md:flex-initial min-w-0 inline-flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 py-2.5 md:py-0"
                >
                  <Eye className="w-4 h-4 flex-shrink-0" />
                  <span className="md:hidden text-xs font-medium">Preview</span>
                  <span className="hidden md:inline">Preview</span>
                </button>
                <button
                  type="button"
                  onClick={handleSaveAllSiteConfig}
                  disabled={isSiteConfigSaveDisabled}
                  className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed flex-1 md:flex-initial min-w-0 inline-flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 py-2.5 md:py-0"
                >
                  {businessSaving || operatingHoursSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
                      <span className="text-xs md:text-inherit font-medium md:font-normal">Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 flex-shrink-0" />
                      <span className="md:hidden text-xs font-medium">Save</span>
                      <span className="hidden md:inline">Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          </>
        );
        
      case 'branding':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Branding & Theme</h2>
                <p className="text-gray-600 mt-1">Customize your business appearance and visual identity</p>
                <p className="text-gray-500 mt-2 text-sm">Upload your logo, customize colors, and add gallery images to create a unique brand identity that will be displayed on your public website and booking pages.</p>
              </div>
              <button 
                onClick={handleSaveBranding}
                disabled={brandingSaving}
                className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="bi bi-check"></i>
                {brandingSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            
            {brandingLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading branding settings...</p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
                <div className="space-y-6">
                  {/* Logo Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 md:p-6 text-center">
                      {brandingFormData.logoUrl ? (
                        <div className="space-y-2">
                          <img 
                            src={getLogoUrl(brandingFormData.logoUrl)} 
                            alt="Business Logo" 
                            className="mx-auto max-h-20 max-w-full object-contain"
                            onError={(e) => {
                              console.error('Error loading logo:', brandingFormData.logoUrl);
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                          <button
                            onClick={() => handleBrandingFormChange('logoUrl', '')}
                            className="text-red-600 text-sm hover:text-red-800"
                          >
                            Remove Logo
                          </button>
                        </div>
                      ) : (
                        <div>
                          <span className="text-2xl mb-2 block">📁</span>
                          <p className="text-sm text-gray-600">Click to upload your logo</p>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            id="logo-upload"
                          />
                          <label
                            htmlFor="logo-upload"
                            className="aacore-btn2-primary mt-2 w-auto cursor-pointer"
                          >
                            <i className="bi bi-upload"></i>
                            Upload Logo
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Theme Color Presets */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-4">
                      Color Theme
                    </label>
                    <div className="space-y-3">
                      {getThemePresetList().map((preset) => {
                        const presetKey = Object.keys(THEME_PRESETS).find(
                          key => THEME_PRESETS[key].name === preset.name
                        ) || '';
                        // Check if selected by themePreset or by matching colors
                        const isSelectedByPreset = brandingFormData.themePreset === presetKey;
                        const isSelectedByColors = !brandingFormData.themePreset && 
                          brandingFormData.primaryColor?.toUpperCase() === preset.primaryColor.toUpperCase() &&
                          brandingFormData.secondaryColor?.toUpperCase() === preset.secondaryColor.toUpperCase() &&
                          brandingFormData.accentColor?.toUpperCase() === preset.accentColor.toUpperCase() &&
                          brandingFormData.textColor?.toUpperCase() === preset.textColor.toUpperCase() &&
                          brandingFormData.backgroundColor?.toUpperCase() === preset.backgroundColor.toUpperCase();
                        const isSelected = isSelectedByPreset || isSelectedByColors;
                        
                        return (
                          <div
                            key={presetKey}
                            className={`relative flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                              isSelected
                                ? 'border-[#0B4778] bg-[#0B4778]/10 shadow-lg ring-2 ring-[#0B4778]/30'
                                : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                            }`}
                          >
                            {isSelected && (
                              <div className="absolute top-2 right-2 bg-[#0B4778] text-white rounded-full p-1">
                                <Check className="w-4 h-4" />
                              </div>
                            )}
                            <div className="flex-1">
                              <h3 className={`font-semibold mb-2 ${isSelected ? 'text-[#0B4778]' : 'text-gray-900'}`}>
                                {preset.name}
                                {isSelected && <span className="ml-2 text-sm text-[#0B4778]">(Current)</span>}
                              </h3>
                              <div className="flex items-center gap-2 mb-2">
                                <div
                                  className="w-8 h-8 rounded border border-gray-300"
                                  style={{ backgroundColor: preset.primaryColor }}
                                  title={`Primary: ${preset.primaryColor}`}
                                />
                                <div
                                  className="w-8 h-8 rounded border border-gray-300"
                                  style={{ backgroundColor: preset.secondaryColor }}
                                  title={`Secondary: ${preset.secondaryColor}`}
                                />
                                <div
                                  className="w-8 h-8 rounded border border-gray-300"
                                  style={{ backgroundColor: preset.accentColor }}
                                  title={`Accent: ${preset.accentColor}`}
                                />
                                <div
                                  className="w-8 h-8 rounded border border-gray-300"
                                  style={{ backgroundColor: preset.textColor }}
                                  title={`Text: ${preset.textColor}`}
                                />
                                <div
                                  className="w-8 h-8 rounded border border-gray-300"
                                  style={{ backgroundColor: preset.backgroundColor }}
                                  title={`Background: ${preset.backgroundColor}`}
                                />
                              </div>
                              <div className="text-xs text-gray-600 font-mono">
                                P {preset.primaryColor} • S {preset.secondaryColor} • A {preset.accentColor} • T {preset.textColor} • B {preset.backgroundColor}
                              </div>
                            </div>
                            <button
                              onClick={() => handleThemePresetSelect(presetKey)}
                              className={`ml-4 ${
                                isSelected
                                  ? 'aacore-btn2-primary'
                                  : 'aacore-btn2-outline'
                              }`}
                            >
                              {isSelected ? (
                                <span className="flex items-center gap-2">
                                  <Check className="w-4 h-4" />
                                  Applied
                                </span>
                              ) : (
                                'Apply'
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Gallery Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gallery Images
                      <span className="text-xs text-gray-500 font-normal ml-2">
                        ({brandingFormData.galleryImages?.length || 0}/6 max)
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">Drag to reorder; order is used on your customer-facing site.</p>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 md:p-6">
                      {brandingFormData.galleryImages && brandingFormData.galleryImages.length > 0 ? (
                        <div className="space-y-4">
                          <DndContext sensors={gallerySensors} collisionDetection={closestCenter} onDragEnd={handleGalleryDragEnd}>
                            <SortableContext items={brandingFormData.galleryImages} strategy={rectSortingStrategy}>
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {brandingFormData.galleryImages.map((image, index) => (
                                  <SortableGalleryImage
                                    key={image}
                                    imageUrl={image}
                                    index={index}
                                    onRemove={removeGalleryImage}
                                    compact
                                  />
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                          <div className="text-center">
                            {brandingFormData.galleryImages.length >= 6 ? (
                              <p className="text-sm text-gray-500">Maximum of 6 gallery images reached. Remove an image to add more.</p>
                            ) : (
                              <>
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={handleGalleryUpload}
                                  className="hidden"
                                  id="gallery-upload"
                                  disabled={brandingFormData.galleryImages.length >= 6}
                                />
                                <label
                                  htmlFor="gallery-upload"
                                  className={`inline-block aacore-btn2-primary cursor-pointer ${
                                    brandingFormData.galleryImages.length >= 6
                                      ? 'bg-gray-400 cursor-not-allowed'
                                      : ''
                                  }`}
                                >
                                  Add More Images
                                </label>
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center">
                          <span className="text-2xl mb-2 block">🖼️</span>
                          <p className="text-sm text-gray-600">Upload gallery images to showcase your business</p>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB each • Maximum 6 images</p>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleGalleryUpload}
                            className="hidden"
                            id="gallery-upload"
                          />
                          <label
                            htmlFor="gallery-upload"
                            className="aacore-btn2-primary mt-2 cursor-pointer"
                          >
                            <ImagePlus className="w-4 h-4" />
                            Upload Gallery Images
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Font Family */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
                    <select
                      value={brandingFormData.fontFamily}
                      onChange={(e) => handleBrandingFormChange('fontFamily', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                    >
                      <option value="Inter">Inter (Modern)</option>
                      <option value="Roboto">Roboto (Clean)</option>
                      <option value="Open Sans">Open Sans (Friendly)</option>
                      <option value="Lato">Lato (Professional)</option>
                      <option value="Poppins">Poppins (Contemporary)</option>
                      <option value="Montserrat">Montserrat (Elegant)</option>
                    </select>
                  </div>

                  {/* Preview Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                    <div 
                      className="border border-gray-300 rounded-lg p-4"
                      style={{
                        backgroundColor: brandingFormData.backgroundColor,
                        color: brandingFormData.textColor,
                        fontFamily: brandingFormData.fontFamily
                      }}
                    >
                      <div className="text-center">
                        {brandingFormData.logoUrl && (
                          <img 
                            src={getLogoUrl(brandingFormData.logoUrl)} 
                            alt="Logo Preview" 
                            className="mx-auto mb-4 max-h-12"
                          />
                        )}
                        <h3 
                          className="text-lg font-semibold mb-2"
                          style={{ color: brandingFormData.primaryColor }}
                        >
                          Your Business Name
                        </h3>
                        <p className="text-sm mb-4">This is how your website will look with these branding settings.</p>
                        <button
                          className="px-4 py-2 rounded-lg text-white font-medium"
                          style={{ backgroundColor: brandingFormData.primaryColor }}
                        >
                          Sample Button
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
        

        
      case 'notifications':
        // Helper to format notification date
        const formatNotificationDate = (dateString: string) => {
          const date = new Date(dateString);
          const now = new Date();
          const diffMs = now.getTime() - date.getTime();
          const diffMins = Math.floor(diffMs / 60000);
          const diffHours = Math.floor(diffMs / 3600000);
          const diffDays = Math.floor(diffMs / 86400000);

          if (diffMins < 1) return 'Just now';
          if (diffMins < 60) return `${diffMins} min ago`;
          if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
          if (diffDays === 1) return 'Yesterday';
          if (diffDays < 7) return `${diffDays} days ago`;
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        };

        // Helper to get empty state message based on filter
        const getEmptyStateMessage = () => {
          switch (notificationFilter) {
            case 'unread':
              return {
                title: 'No unread notifications',
                description: 'You\'re all caught up! All your notifications have been read.'
              };
            case 'appointments':
              return {
                title: 'No Appointment Notifications',
                description: 'You\'ll see appointment-related notifications here when appointments are booked, cancelled, or updated.'
              };
            case 'services':
              return {
                title: 'No Service Notifications',
                description: 'You\'ll see service-related notifications here when services are added or updated.'
              };
            case 'reviews':
              return {
                title: 'No Review Notifications',
                description: 'You\'ll see review-related notifications here when customers leave reviews.'
              };
            default:
              return {
                title: 'No notifications',
                description: 'You\'ll see notifications here when invitations are accepted, appointments change, or customers leave reviews.'
              };
          }
        };

        const handleNotificationFilterChange = (filter: 'all' | 'unread' | 'appointments' | 'services' | 'reviews') => {
          setNotificationFilter(filter);
          setNotificationPage(1); // Reset to first page when filter changes
        };

        // Count unread notifications for "Mark all read" button
        const hasUnreadNotifications = notifications.some(n => !n.is_read);

        return (
          <div className="flex flex-col gap-2 md:gap-6">
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Notifications</h2>
                  <p className="hidden md:block text-gray-600 mt-1">Updates related to your appointments, schedule, services, and customer activity.</p>
                </div>
                {/* Desktop Refresh Button - Hidden on mobile */}
                <button
                  onClick={loadNotifications}
                  disabled={notificationsLoading}
                  className="hidden md:flex items-center gap-2 px-4 py-2 bg-[#0B4778] text-white rounded-lg hover:bg-[#0a3d66] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Refresh notifications"
                >
                  <RefreshCw className={`w-4 h-4 ${notificationsLoading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>

            {/* Mobile-only collapsible top section (filters + search + description) */}
            <div className="space-y-2">
              <button
                type="button"
                className="md:hidden w-full inline-flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-[#0B4778] bg-[#0B4778] text-white"
                aria-expanded={!isMobileNotificationsTopCollapsed}
                aria-controls="mobile-notifications-top-section"
                onClick={() => {
                  setIsMobileNotificationsTopCollapsed((prev) => {
                    const next = !prev;
                    if (next) setIsNotificationFilterDropdownOpen(false);
                    return next;
                  });
                }}
              >
                <div className="flex items-start gap-2 min-w-0">
                  <i className="bi bi-sliders text-white/90 mt-0.5"></i>
                  <div className="flex flex-col items-start min-w-0">
                    <span className="text-sm font-semibold text-white">Search &amp; Filters</span>
                    <span className="text-xs text-white/80 truncate">
                      {notificationFilter === 'all' && 'All'}
                      {notificationFilter === 'unread' && 'Unread'}
                      {notificationFilter === 'appointments' && 'Appointments'}
                      {notificationFilter === 'services' && 'Services'}
                      {notificationFilter === 'reviews' && 'Reviews'}
                      {notificationSearchQuery.trim() ? ` · “${notificationSearchQuery.trim()}”` : ''}
                    </span>
                  </div>
                </div>
                <i
                  className={`bi bi-chevron-down shrink-0 transition-transform ${
                    isMobileNotificationsTopCollapsed ? '' : 'rotate-180'
                  }`}
                ></i>
              </button>

              <div
                id="mobile-notifications-top-section"
                className={`${isMobileNotificationsTopCollapsed ? 'hidden' : 'block'} md:block space-y-4`}
              >
                <p className="md:hidden text-gray-600">
                  Updates related to your appointments, schedule, services, and customer activity.
                </p>

                {/* Filter Toolbar */}
                <div className="flex flex-wrap gap-2 items-center justify-between">
                  {/* Mobile Dropdown - visible on mobile, hidden on desktop */}
                  <div className="md:hidden w-full flex gap-2 notification-filter-dropdown-container">
                    <div className="flex-1 relative">
                      <button
                        type="button"
                        onClick={() => setIsNotificationFilterDropdownOpen(!isNotificationFilterDropdownOpen)}
                        className={`w-full inline-flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all ${
                          notificationFilter === 'all'
                            ? 'bg-[#0B4778]/10 text-[#0B4778] border-[#0B4778]/30'
                            : notificationFilter === 'unread'
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : notificationFilter === 'appointments'
                            ? 'bg-[#0B4778]/10 text-[#0B4778] border-[#0B4778]/30'
                            : notificationFilter === 'services'
                            ? 'bg-teal-50 text-teal-700 border-teal-200'
                            : notificationFilter === 'reviews'
                            ? 'bg-gray-50 text-gray-700 border-gray-300'
                            : 'bg-white text-gray-700 border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {notificationFilter === 'all' && <i className="bi bi-inbox"></i>}
                          {notificationFilter === 'unread' && <i className="bi bi-envelope-open"></i>}
                          {notificationFilter === 'appointments' && <i className="bi bi-calendar2-week"></i>}
                          {notificationFilter === 'services' && <i className="bi bi-tools"></i>}
                          {notificationFilter === 'reviews' && <i className="bi bi-star"></i>}
                          <span>
                            {notificationFilter === 'all' && 'All'}
                            {notificationFilter === 'unread' && 'Unread'}
                            {notificationFilter === 'appointments' && 'Appointments'}
                            {notificationFilter === 'services' && 'Services'}
                            {notificationFilter === 'reviews' && 'Reviews'}
                          </span>
                        </div>
                        <i className={`bi bi-chevron-down transition-transform ${isNotificationFilterDropdownOpen ? 'rotate-180' : ''}`}></i>
                      </button>
                      
                      {isNotificationFilterDropdownOpen && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
                          <div className="py-1">
                            <button
                              onClick={() => {
                                handleNotificationFilterChange('all');
                                setIsNotificationFilterDropdownOpen(false);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-left transition-colors ${
                                notificationFilter === 'all'
                                  ? 'bg-[#0B4778]/10 text-[#0B4778]'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <i className="bi bi-inbox"></i>
                              <span>All</span>
                            </button>
                            <button
                              onClick={() => {
                                handleNotificationFilterChange('unread');
                                setIsNotificationFilterDropdownOpen(false);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-left transition-colors ${
                                notificationFilter === 'unread'
                                  ? 'bg-amber-50 text-amber-700'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <i className="bi bi-envelope-open"></i>
                              <span>Unread</span>
                            </button>
                            <button
                              onClick={() => {
                                handleNotificationFilterChange('appointments');
                                setIsNotificationFilterDropdownOpen(false);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-left transition-colors ${
                                notificationFilter === 'appointments'
                                  ? 'bg-[#0B4778]/10 text-[#0B4778]'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <i className="bi bi-calendar2-week"></i>
                              <span>Appointments</span>
                            </button>
                            <button
                              onClick={() => {
                                handleNotificationFilterChange('services');
                                setIsNotificationFilterDropdownOpen(false);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-left transition-colors ${
                                notificationFilter === 'services'
                                  ? 'bg-teal-50 text-teal-700'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <i className="bi bi-tools"></i>
                              <span>Services</span>
                            </button>
                            <button
                              onClick={() => {
                                handleNotificationFilterChange('reviews');
                                setIsNotificationFilterDropdownOpen(false);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-left transition-colors ${
                                notificationFilter === 'reviews'
                                  ? 'bg-gray-50 text-gray-700'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <i className="bi bi-star"></i>
                              <span>Reviews</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleMarkAllNotificationsAsRead}
                      disabled={!hasUnreadNotifications}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all ${
                        hasUnreadNotifications
                          ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <i className="bi bi-check-all"></i>
                      <span className="hidden sm:inline">Mark All Read</span>
                      <span className="sm:hidden">Mark Read</span>
                    </button>
                  </div>

                  {/* Desktop Buttons - hidden on mobile, visible on desktop */}
                  <div className="hidden md:flex flex-wrap gap-2">
                    <button
                      onClick={() => handleNotificationFilterChange('all')}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                        notificationFilter === 'all'
                          ? 'bg-[#0B4778]/10 text-[#0B4778] border-[#0B4778]/30'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <i className="bi bi-inbox"></i>
                      All
                    </button>
                    <button
                      onClick={() => handleNotificationFilterChange('unread')}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                        notificationFilter === 'unread'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <i className="bi bi-envelope-open"></i>
                      Unread
                    </button>
                    <button
                      onClick={() => handleNotificationFilterChange('appointments')}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                        notificationFilter === 'appointments'
                          ? 'bg-[#0B4778]/10 text-[#0B4778] border-[#0B4778]/30'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <i className="bi bi-calendar2-week"></i>
                      Appointments
                    </button>
                    <button
                      onClick={() => handleNotificationFilterChange('services')}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                        notificationFilter === 'services'
                          ? 'bg-teal-50 text-teal-700 border-teal-200'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <i className="bi bi-tools"></i>
                      Services
                    </button>
                    <button
                      onClick={() => handleNotificationFilterChange('reviews')}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                        notificationFilter === 'reviews'
                          ? 'bg-gray-50 text-gray-700 border-gray-300'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <i className="bi bi-star"></i>
                      Reviews
                    </button>
                  </div>
                  {/* Desktop Mark All Read Button - hidden on mobile, visible on desktop */}
                  <button
                    onClick={handleMarkAllNotificationsAsRead}
                    disabled={!hasUnreadNotifications}
                    className={`hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                      hasUnreadNotifications
                        ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <i className="bi bi-check-all"></i>
                    Mark All Read
                  </button>
                </div>

                {/* Notification search - fuzzy search by service, customer, phone, email, professional */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={notificationSearchQuery}
                    onChange={(e) => setNotificationSearchQuery(e.target.value)}
                    placeholder="Search by service, customer, phone, email, professional..."
                    className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0B4778]/30 focus:border-[#0B4778]"
                    aria-label="Search notifications"
                  />
                  {notificationSearchQuery && (
                    <button
                      type="button"
                      onClick={() => setNotificationSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                      aria-label="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            {allNotificationsForSearchLoading && notificationSearchQuery.trim() && (
              <p className="text-sm text-gray-500">Searching all notifications…</p>
            )}
            
            {notificationsLoading ? (
              <div className="mt-3 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                  <p className="text-lg font-medium">Loading notifications...</p>
                </div>
              </div>
            ) : displayedNotifications.length > 0 ? (
              <div className="mt-3 space-y-2.5">
                {displayedNotifications.map((notification) => {
                  const iconInfo = getNotificationIcon(notification.category, notification.type);
                  const isUnread = !notification.is_read;

                  const invoicePaymentBody = (() => {
                    if (notification.type !== 'INVOICE_PAYMENT_RECEIVED') return '';
                    const trimmed = String(notification.message || '').trim();
                    if (trimmed) return trimmed;
                    const m = notification.metadata || {};
                    const cust = String(m.customerName ?? m.customer_name ?? '').trim() || 'Customer';
                    const inv = String(m.invoiceNumber ?? m.invoice_number ?? '').trim();
                    const amtRaw = m.amountPaid ?? m.amount_paid;
                    const amt = typeof amtRaw === 'number' && Number.isFinite(amtRaw) ? amtRaw : NaN;
                    if (inv && !Number.isNaN(amt)) return `${cust} paid ${inv} for $${amt.toFixed(2)}.`;
                    if (!Number.isNaN(amt)) return `${cust} paid $${amt.toFixed(2)}.`;
                    if (inv) return `${cust} paid invoice ${inv}.`;
                    return `${cust} paid an invoice.`;
                  })();
                  
                  // Build meta information as JSX elements to allow styling
                  const metaParts: (string | JSX.Element)[] = [];
                  metaParts.push(formatNotificationDate(notification.created_at));
                  
                  if (notification.metadata) {
                    // Add customer name without label (invoice paid uses full body + structured meta instead)
                    if (notification.type !== 'INVOICE_PAYMENT_RECEIVED') {
                      const first = notification.metadata.customer_first_name;
                      const last = notification.metadata.customer_last_name;
                      const rawCustomerName = notification.metadata.customerName;

                      const customerName =
                        (first || last)
                          ? [first, last]
                              .map((v: any) => (v == null ? '' : String(v).trim()))
                              .filter(Boolean)
                              .join(' ')
                          : (rawCustomerName == null
                              ? ''
                              : String(rawCustomerName)
                                  .split(/\s+/)
                                  .filter((p) => p && p.toLowerCase() !== 'null')
                                  .join(' ')
                                  .trim());

                      if (customerName) {
                        metaParts.push(
                          <span key="customer" className="font-semibold">{customerName}</span>
                        );
                      }
                    }
                    
                    // Handle professional display logic
                    if (notification.metadata.assignments && Array.isArray(notification.metadata.assignments)) {
                      const assignments = notification.metadata.assignments;
                      
                      // Filter assignments with professionals
                      const assignmentsWithPro = assignments.filter((a: any) => a.professionalId && a.professionalFirstName && a.professionalLastName);
                      
                      if (assignmentsWithPro.length > 0) {
                        // Get unique professionals
                        type ProfessionalInfo = {
                          firstName: string;
                          lastName: string;
                          insistOnProfessional: boolean;
                        };
                        
                        const uniqueProfessionals = Array.from(
                          new Map(
                            assignmentsWithPro.map((a: any) => [
                              a.professionalId,
                              {
                                firstName: a.professionalFirstName,
                                lastName: a.professionalLastName,
                                insistOnProfessional: a.insistOnProfessional
                              } as ProfessionalInfo
                            ])
                          ).values()
                        ) as ProfessionalInfo[];
                        
                        if (uniqueProfessionals.length === 1) {
                          // Single professional
                          const pro = uniqueProfessionals[0];
                          const proName = `${pro.firstName} ${pro.lastName.charAt(0)}.`;
                          metaParts.push(
                            <span key="professional">
                              Pro: <span className="font-semibold">{proName}</span>
                              {pro.insistOnProfessional && (
                                <span className="text-red-600 font-semibold"> (Requested)</span>
                              )}
                            </span>
                          );
                        } else {
                          // Multiple professionals
                          metaParts.push(
                            <span key="professional">
                              Pro: <span className="font-semibold">Multiple</span>
                            </span>
                          );
                        }
                      }
                      // If assignmentsWithPro.length === 0, don't show pro text
                    }
                    // For REVIEW category notifications, show review feedback instead of service name
                    if (notification.category === 'REVIEW' && notification.metadata.reviewFeedback) {
                      metaParts.push(
                        <span key="review">
                          Review: <span className="italic">"{notification.metadata.reviewFeedback}"</span>
                        </span>
                      );
                    }
                    if (notification.metadata.scheduledAt) {
                      const scheduledDate = new Date(notification.metadata.scheduledAt);
                      metaParts.push(`For: ${scheduledDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
                    }
                  }

                  // Removed "From:..." section from notification subtext

                  // Determine action button based on notification type
                  let actionButton = null;
                  let actionIcon = 'bi bi-eye';
                  let actionText = 'View';
                  
                  if (notification.type === 'APPOINTMENT_BOOKED' || notification.type === 'APPOINTMENT_CANCELLED' || notification.type === 'APPOINTMENT_RESCHEDULED' || notification.type === 'APPOINTMENT_DEPOSIT_RECEIVED' || notification.type === 'APPOINTMENT_DEPOSIT_REQUESTED') {
                    actionIcon = 'bi bi-calendar3';
                    const appointmentId = notification.metadata?.appointmentId;
                    actionButton = (
                      <button
                        onClick={() => {
                          if (isUnread) handleMarkNotificationAsRead(notification.id);
                          if (appointmentId) {
                            navigate(`/appointments/${appointmentId}?from=notifications`);
                          } else {
                            // Fallback to my-appointments if appointmentId is not available
                            handleSectionChange('my-appointments');
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30 hover:bg-[#0B4778]/20 transition-colors"
                      >
                        <i className={actionIcon}></i>
                        {actionText}
                      </button>
                    );
                  } else if (notification.type === 'QUOTE_REQUEST_SUBMITTED' || notification.type === 'QUOTE_OFFER_ACCEPTED') {
                    actionIcon = 'bi bi-file-earmark-text';
                    const quoteId = notification.metadata?.quoteId;
                    actionButton = (
                      <button
                        onClick={() => {
                          if (isUnread) handleMarkNotificationAsRead(notification.id);
                          if (quoteId) {
                            navigate(`/dashboard/business/quote-requests/${quoteId}?from=notifications`);
                          } else {
                            handleSectionChange('quote-requests');
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30 hover:bg-[#0B4778]/20 transition-colors"
                      >
                        <i className={actionIcon}></i>
                        {actionText}
                      </button>
                    );
                  } else if (notification.type === 'INVOICE_PAYMENT_RECEIVED') {
                    actionIcon = 'bi bi-receipt';
                    const paidInvoiceId =
                      notification.metadata?.invoiceId ||
                      notification.metadata?.invoice_id;
                    actionButton = (
                      <button
                        onClick={() => {
                          if (isUnread) handleMarkNotificationAsRead(notification.id);
                          if (paidInvoiceId) {
                            navigate(`/dashboard/business/invoices/${paidInvoiceId}?from=notifications`);
                          } else {
                            handleSectionChange('invoices');
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30 hover:bg-[#0B4778]/20 transition-colors"
                      >
                        <i className={actionIcon}></i>
                        {actionText}
                      </button>
                    );
                  } else if (notification.category === 'SERVICE') {
                    actionIcon = 'bi bi-card-checklist';
                    actionButton = (
                      <button
                        onClick={() => {
                          if (isUnread) handleMarkNotificationAsRead(notification.id);
                          handleSectionChange('my-services');
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30 hover:bg-[#0B4778]/20 transition-colors"
                      >
                        <i className={actionIcon}></i>
                        {actionText}
                      </button>
                    );
                  } else if (notification.category === 'REVIEW') {
                    actionIcon = 'bi bi-eye';
                    actionButton = (
                      <button
                        onClick={() => {
                          if (isUnread) handleMarkNotificationAsRead(notification.id);
                          setSelectedReviewNotification(notification);
                          setIsReviewDetailsModalOpen(true);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30 hover:bg-[#0B4778]/20 transition-colors"
                      >
                        <i className={actionIcon}></i>
                        {actionText}
                      </button>
                    );
                  } else {
                    actionButton = (
                      <button
                        onClick={() => isUnread ? handleMarkNotificationAsRead(notification.id) : null}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30 hover:bg-[#0B4778]/20 transition-colors"
                      >
                        <i className="bi bi-check"></i>
                        {isUnread ? 'Mark read' : actionText}
                      </button>
                    );
                  }

                  // Get the onClick handler from the action button
                  const getCardClickHandler = () => {
                    if (notification.type === 'APPOINTMENT_BOOKED' || notification.type === 'APPOINTMENT_CANCELLED' || notification.type === 'APPOINTMENT_RESCHEDULED' || notification.type === 'APPOINTMENT_DEPOSIT_RECEIVED' || notification.type === 'APPOINTMENT_DEPOSIT_REQUESTED') {
                      const appointmentId = notification.metadata?.appointmentId;
                      return () => {
                        if (isUnread) handleMarkNotificationAsRead(notification.id);
                        if (appointmentId) {
                          navigate(`/appointments/${appointmentId}?from=notifications`);
                        } else {
                          handleSectionChange('my-appointments');
                        }
                      };
                    } else if (notification.type === 'QUOTE_REQUEST_SUBMITTED' || notification.type === 'QUOTE_OFFER_ACCEPTED') {
                      const quoteId = notification.metadata?.quoteId;
                      return () => {
                        if (isUnread) handleMarkNotificationAsRead(notification.id);
                        if (quoteId) {
                          navigate(`/dashboard/business/quote-requests/${quoteId}?from=notifications`);
                        } else {
                          handleSectionChange('quote-requests');
                        }
                      };
                    } else if (notification.type === 'INVOICE_PAYMENT_RECEIVED') {
                      const paidInvoiceId =
                        notification.metadata?.invoiceId ||
                        notification.metadata?.invoice_id;
                      return () => {
                        if (isUnread) handleMarkNotificationAsRead(notification.id);
                        if (paidInvoiceId) {
                          navigate(`/dashboard/business/invoices/${paidInvoiceId}?from=notifications`);
                        } else {
                          handleSectionChange('invoices');
                        }
                      };
                    } else if (notification.category === 'SERVICE') {
                      return () => {
                        if (isUnread) handleMarkNotificationAsRead(notification.id);
                        handleSectionChange('my-services');
                      };
                    } else if (notification.category === 'REVIEW') {
                      return () => {
                        if (isUnread) handleMarkNotificationAsRead(notification.id);
                        setSelectedReviewNotification(notification);
                        setIsReviewDetailsModalOpen(true);
                      };
                    } else {
                      return () => isUnread ? handleMarkNotificationAsRead(notification.id) : null;
                    }
                  };

                  return (
                    <div 
                      key={notification.id} 
                      className={`grid grid-cols-[auto_1fr_auto] gap-3 items-center border border-gray-200 rounded-xl p-3 bg-white shadow-sm ${
                        isUnread ? 'border-l-4 border-l-green-500' : ''
                      } md:cursor-default cursor-pointer md:hover:bg-white hover:bg-gray-50 transition-colors`}
                      onClick={(e) => {
                        // Only handle click on mobile (when action button is hidden)
                        if (window.innerWidth < 768) {
                          getCardClickHandler()();
                        }
                      }}
                    >
                      {/* Badge Icon with Initiator indicator */}
                      <div className="relative">
                        {/* Initiator Icon - positioned above calendar icon */}
                        {((
                          notification.type === 'APPOINTMENT_BOOKED' && notification.metadata?.bookingInitiator
                        ) || (
                          notification.type === 'APPOINTMENT_RESCHEDULED' && notification.metadata?.rescheduleInitiator
                        ) || (
                          notification.type === 'APPOINTMENT_CANCELLED' && notification.metadata?.cancelInitiator
                        )) && (
                          <div className="absolute -top-1 right-0 z-10">
                            {(
                              (notification.type === 'APPOINTMENT_BOOKED' && notification.metadata?.bookingInitiator === 'CUSTOMER') ||
                              (notification.type === 'APPOINTMENT_RESCHEDULED' && notification.metadata?.rescheduleInitiator === 'CUSTOMER') ||
                              (notification.type === 'APPOINTMENT_CANCELLED' && notification.metadata?.cancelInitiator === 'CUSTOMER')
                            ) ? (
                              <span 
                                className="inline-flex items-center justify-center border w-4 h-4 rounded-full bg-green-100 border-green-200"
                                title="Customer initiated"
                              >
                                <UserIcon className="w-2.5 h-2.5 text-green-700" />
                              </span>
                            ) : (
                              <span 
                                className="inline-flex items-center justify-center border w-4 h-4 rounded-md bg-orange-100 border-orange-200"
                                title="Business/staff initiated"
                              >
                                <Pencil className="w-2.5 h-2.5 text-orange-700" />
                              </span>
                            )}
                          </div>
                        )}
                        <div className={`w-10 h-10 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center ${iconInfo.color}`}>
                          <i className={`${iconInfo.icon} text-lg`}></i>
                        </div>
                      </div>
                      
                      {/* Summary */}
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 text-sm leading-tight mb-1 flex items-center gap-2">
                          <span className="flex items-center gap-1.5">
                            {(() => {
                              // For appointment-related notifications, append service name
                              if ((notification.title === 'Appointment Confirmed' || 
                                   notification.title === 'Appointment Pending Confirmation' ||
                                   notification.title === 'Appointment Pending Deposit' ||
                                   notification.title === 'Deposit requested for appointment' ||
                                   notification.title === 'Appointment Canceled' || 
                                   notification.title === 'Appointment Cancelled' ||
                                   notification.title === 'Appointment Booked' ||
                                   notification.title === 'Appointment Rescheduled' ||
                                   notification.title === 'Customer Rescheduled Appointment' ||
                                   notification.title === 'Appointment Rescheduled by Customer') && notification.metadata?.serviceName) {
                                return `${notification.title} - ${notification.metadata.serviceName}`;
                              }
                              // For REVIEW category notifications, append rating with star icons
                              if (notification.category === 'REVIEW' && notification.metadata?.rating) {
                                const rating = notification.metadata.rating;
                                return (
                                  <>
                                    {notification.title} - {Array.from({ length: rating }, (_, i) => (
                                      <i key={i} className="bi bi-star-fill text-yellow-400"></i>
                                    ))}
                                  </>
                                );
                              }
                              return notification.title;
                            })()}
                          </span>
                          {notification.metadata?.status === 'PENDING' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                              PENDING
                            </span>
                          )}
                        </div>
                        {invoicePaymentBody ? (
                          <div className="text-sm text-gray-800 mt-1.5 leading-snug">{invoicePaymentBody}</div>
                        ) : null}
                        <div className="text-xs text-gray-500">
                          {metaParts.map((part, index) => (
                            <span key={index}>
                              {index > 0 && ' • '}
                              {part}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {/* Show action button on desktop only */}
                        <div className="hidden md:flex">
                          {actionButton}
                        </div>
                        {/* Show chevron icon on mobile only */}
                        <div className="flex md:hidden text-gray-400">
                          <ChevronRight className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 text-center text-gray-500">
                  {notificationSearchQuery.trim() && (notifications.length > 0 || (allNotificationsForSearch != null && allNotificationsForSearch.length > 0)) ? (
                    <>
                      <i className="bi bi-search text-gray-400 text-4xl mb-4 block"></i>
                      <p className="text-lg font-medium">No notifications match your search</p>
                      <p className="text-sm">Try a different search or clear the search box.</p>
                      <button
                        type="button"
                        onClick={() => setNotificationSearchQuery('')}
                        className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                      >
                        <X className="w-4 h-4" />
                        Clear search
                      </button>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-bell text-amber-500 text-4xl mb-4 block"></i>
                      <p className="text-lg font-medium">{getEmptyStateMessage().title}</p>
                      <p className="text-sm">{getEmptyStateMessage().description}</p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Pagination */}
            {(() => {
              const inSearchMode = isNotificationSearchMode;
              const totalPages = inSearchMode ? notificationSearchTotalPages : (notificationPagination?.totalPages ?? 1);
              const totalCount = inSearchMode ? filteredNotifications.length : (notificationPagination?.total ?? 0);
              const hasPrevPage = inSearchMode ? notificationPage > 1 : (notificationPagination?.hasPrevPage ?? false);
              const hasNextPage = inSearchMode ? notificationPage < totalPages : (notificationPagination?.hasNextPage ?? false);
              const startItem = inSearchMode ? (notificationPage - 1) * notificationsPerPage + 1 : ((notificationPage - 1) * notificationsPerPage) + 1;
              const endItem = inSearchMode ? Math.min(notificationPage * notificationsPerPage, totalCount) : Math.min(notificationPage * notificationsPerPage, totalCount);
              const showPagination = notificationPagination != null && (totalPages > 1 || (inSearchMode && totalCount > 0));
              if (!showPagination) return null;
              return (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-t border-gray-200 pt-4">
                  <div className="text-sm text-gray-700 text-center md:text-left">
                    {inSearchMode
                      ? <>Showing {startItem} to {endItem} of {totalCount} match{totalCount !== 1 ? 'es' : ''}</>
                      : <>Showing {startItem} to {endItem} of {totalCount} notifications</>
                    }
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => setNotificationPage(prev => Math.max(1, prev - 1))}
                      disabled={!hasPrevPage}
                      className={`px-3 py-1 text-sm rounded-lg border ${
                        hasPrevPage ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (notificationPage <= 3) {
                          pageNum = i + 1;
                        } else if (notificationPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = notificationPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setNotificationPage(pageNum)}
                            className={`px-3 py-1 text-sm rounded-lg ${
                              pageNum === notificationPage ? 'bg-[#0B4778] text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => setNotificationPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={!hasNextPage}
                      className={`px-3 py-1 text-sm rounded-lg border ${
                        hasNextPage ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        );
        
      case 'statistics':
        return currentBusiness?.id ? (
          <BusinessStatisticsSection
            businessId={currentBusiness.id}
            onBack={() => handleSectionChange('appointments')}
            refreshTrigger={statisticsRefreshTrigger}
          />
        ) : null;

      case 'closures':
        // Helper to parse date string in local timezone (YYYY-MM-DD format)
        const parseLocalDate = (dateStr: string): Date => {
          if (!dateStr) return new Date();
          // If it's already a date string with time, extract just the date part
          const datePart = dateStr.split('T')[0];
          const parts = datePart.split('-');
          if (parts.length !== 3) return new Date(dateStr);
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const day = parseInt(parts[2], 10);
          return new Date(year, month, day);
        };
        
        // Helper to format closure date (using local time)
        const formatClosureDate = (dateString: string) => {
          const date = parseLocalDate(dateString);
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        };
        
        // Helper to get closure status (using local time)
        const getClosureStatus = (closure: BusinessClosure) => {
          const now = new Date();
          now.setHours(0, 0, 0, 0); // Set to local midnight for date comparison
          const startDate = parseLocalDate(closure.startDate);
          startDate.setHours(0, 0, 0, 0);
          const endDate = parseLocalDate(closure.endDate);
          endDate.setHours(23, 59, 59, 999); // End of day
          if (endDate < now) return 'past';
          if (startDate > now) return 'upcoming';
          return 'current';
        };

        const getClosureStartTime = (closure: BusinessClosure) => parseLocalDate(closure.startDate).getTime();
        const getClosureEndTime = (closure: BusinessClosure) => {
          const endDate = parseLocalDate(closure.endDate);
          endDate.setHours(23, 59, 59, 999);
          return endDate.getTime();
        };

        // Filter by All / Upcoming (includes current) / Past
        const filteredClosures = closures.filter((closure) => {
          const status = getClosureStatus(closure);
          if (closuresFilter === 'upcoming') return status === 'upcoming' || status === 'current';
          if (closuresFilter === 'past') return status === 'past';
          return true;
        });

        // Sort per filter: upcoming earliest first; past most recent end first; all latest start first
        const sortedClosures = [...filteredClosures].sort((a, b) => {
          if (closuresFilter === 'upcoming') {
            return getClosureStartTime(a) - getClosureStartTime(b);
          }
          if (closuresFilter === 'past') {
            const endDiff = getClosureEndTime(b) - getClosureEndTime(a);
            if (endDiff !== 0) return endDiff;
            return getClosureStartTime(b) - getClosureStartTime(a);
          }
          return getClosureStartTime(b) - getClosureStartTime(a);
        });

        const closuresFilterLabel =
          closuresFilter === 'upcoming' ? 'Upcoming' : closuresFilter === 'past' ? 'Past' : 'All';
        const closuresEmptyMessage =
          closuresFilter === 'past'
            ? 'No past closures.'
            : closuresFilter === 'upcoming'
              ? 'No upcoming closures. Create one to block off specific dates.'
              : 'No closures scheduled yet. Create one to block off specific dates.';
        
        return (
          <div className="pb-24 lg:pb-0">
            {/* Back to Appointments Link - Desktop Only */}
            <div className="hidden lg:block mb-6">
              <button
                onClick={() => handleSectionChange('appointments')}
                className="flex items-center gap-2 text-gray-600 hover:text-[#0B4778] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Back to Appointments</span>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Closures</h2>
                <p className="text-gray-600 mt-1">Closures override store hours and professional availability.</p>
              </div>
              <button
                onClick={() => {
                  setEditingClosure(null);
                  setClosureFormData({
                    startDate: '',
                    endDate: '',
                    closureType: 'all_day',
                    startTime: '',
                    endTime: '',
                    reason: '',
                    repeatYearly: false
                  });
                  setShowClosureForm(true);
                }}
                className="aacore-btn2-primary"
              >
                <Plus className="w-4 h-4" />
                New Closure
              </button>
            </div>

            {closuresLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                  <p className="text-lg font-medium">Loading closures...</p>
                </div>
              </div>
            ) : closuresError ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 text-center text-red-500">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-4" />
                  <p className="text-lg font-medium">Error loading closures</p>
                  <p className="text-sm">{closuresError}</p>
                </div>
              </div>
            ) : (
              <div className={`grid grid-cols-1 ${showClosureForm ? 'lg:grid-cols-2' : ''} gap-6`}>
                {/* Closures List */}
                <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${showClosureForm ? 'hidden lg:block' : ''}`}>
                  <div className="flex items-center justify-between mb-4 gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">{closuresFilterLabel} Closures</h3>
                    <div className="relative closures-filter-dropdown-container">
                      <button
                        type="button"
                        onClick={() => setClosuresFilterDropdownOpen(!closuresFilterDropdownOpen)}
                        className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 flex items-center justify-between min-w-[140px]"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <span className="text-gray-900 text-sm">{closuresFilterLabel}</span>
                      </button>
                      {closuresFilterDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto right-0">
                          <div
                            className={`px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer ${closuresFilter === 'all' ? 'bg-gray-50 font-medium' : ''}`}
                            onClick={() => {
                              setClosuresFilter('all');
                              setClosuresFilterDropdownOpen(false);
                            }}
                          >
                            All
                          </div>
                          <div className="border-t border-gray-200 my-1"></div>
                          <div
                            className={`px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer ${closuresFilter === 'upcoming' ? 'bg-gray-50 font-medium' : ''}`}
                            onClick={() => {
                              setClosuresFilter('upcoming');
                              setClosuresFilterDropdownOpen(false);
                            }}
                          >
                            Upcoming
                          </div>
                          <div
                            className={`px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer ${closuresFilter === 'past' ? 'bg-gray-50 font-medium' : ''}`}
                            onClick={() => {
                              setClosuresFilter('past');
                              setClosuresFilterDropdownOpen(false);
                            }}
                          >
                            Past
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {sortedClosures.length > 0 ? (
                    <div className="space-y-3">
                      {sortedClosures.map((closure) => {
                        const status = getClosureStatus(closure);
                        const isSelected = editingClosure?.id === closure.id;
                        const isSameDay = closure.startDate === closure.endDate;
                        const dateDisplay = isSameDay 
                          ? formatClosureDate(closure.startDate)
                          : `${formatClosureDate(closure.startDate)} - ${formatClosureDate(closure.endDate)}`;
                        
                        let timeDisplay = '';
                        if (closure.closureType === 'all_day') {
                          timeDisplay = 'All day';
                        } else if (closure.startTime && closure.endTime) {
                          const formatTime = (time: string) => {
                            const [hours, minutes] = time.split(':');
                            const hour = parseInt(hours);
                            const ampm = hour >= 12 ? 'PM' : 'AM';
                            const displayHour = hour % 12 || 12;
                            return `${displayHour}:${minutes} ${ampm}`;
                          };
                          timeDisplay = `${formatTime(closure.startTime)}–${formatTime(closure.endTime)}`;
                        }
                        
                        return (
                          <div
                            key={closure.id}
                            onClick={() => {
                              setEditingClosure(closure);
                              // Extract date part (YYYY-MM-DD) ensuring local time handling
                              const getDatePart = (dateStr: string) => {
                                const datePart = dateStr.split('T')[0];
                                // Ensure it's in YYYY-MM-DD format
                                if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
                                  return datePart;
                                }
                                // If not, parse and reformat in local time
                                const date = parseLocalDate(dateStr);
                                const year = date.getFullYear();
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                return `${year}-${month}-${day}`;
                              };
                              // Convert 24-hour format to 12-hour format for TimePicker
                              const convert24To12 = (time24: string): string => {
                                if (!time24) return '';
                                const [hours, minutes] = time24.split(':');
                                const hour = parseInt(hours, 10);
                                const min = minutes || '00';
                                const period = hour >= 12 ? 'PM' : 'AM';
                                const displayHour = hour % 12 || 12;
                                return `${displayHour.toString().padStart(2, '0')}:${min} ${period}`;
                              };
                              
                              setClosureFormData({
                                startDate: getDatePart(closure.startDate),
                                endDate: getDatePart(closure.endDate),
                                closureType: closure.closureType,
                                startTime: convert24To12(closure.startTime || ''),
                                endTime: convert24To12(closure.endTime || ''),
                                reason: closure.reason || '',
                                repeatYearly: closure.repeatYearly
                              });
                              setShowClosureForm(true);
                            }}
                            className={`group p-4 rounded-lg border-2 cursor-pointer transition-all ${
                              isSelected
                                ? 'border-[#0B4778] bg-[#0B4778]/5'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="font-medium text-gray-900">{dateDisplay}</div>
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    status === 'upcoming'
                                      ? 'bg-gray-100 text-gray-800'
                                      : status === 'current'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {status === 'upcoming' ? 'Upcoming' : status === 'current' ? 'Current' : 'Past'}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {timeDisplay}
                                  {closure.reason && ` · ${closure.reason}`}
                                </div>
                              </div>
                              <div className="flex items-center pl-2">
                                <ChevronRight className={`w-4 h-4 transition-colors ${isSelected ? 'text-[#0B4778]' : 'text-gray-400 group-hover:text-gray-600'}`} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="text-sm">{closuresEmptyMessage}</p>
                    </div>
                  )}
                </div>

                {/* Closure Details Form - Only show when a closure is selected or creating new */}
                {showClosureForm && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Header (mirrors Add Time Off layout) */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {showClosureForm && !editingClosure ? 'New Closure Details' : 'Closure Details'}
                    </h3>
                  </div>
                  
                  {showClosureForm ? (
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (!currentBusiness?.id) return;
                        
                        try {
                          // Convert 12-hour format (from TimePicker) to 24-hour format (for backend)
                          const convert12To24 = (time12: string): string => {
                            if (!time12) return '';
                            const parts = time12.trim().split(' ');
                            if (parts.length < 2) return '';
                            const [time, period] = parts;
                            const [hours, minutes] = time.split(':');
                            let hour24 = parseInt(hours, 10);
                            const min = minutes || '00';
                            
                            if (period.toUpperCase() === 'PM' && hour24 !== 12) {
                              hour24 += 12;
                            } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
                              hour24 = 0;
                            }
                            
                            return `${hour24.toString().padStart(2, '0')}:${min}`;
                          };

                          if (closureFormData.closureType === 'time_range') {
                            if (!closureFormData.startTime) {
                              toast.error('Please select a start time for this closure');
                              return;
                            }
                            if (!closureFormData.endTime) {
                              toast.error('Please select an end time for this closure');
                              return;
                            }
                          }
                          
                          if (editingClosure) {
                            await apiService.updateBusinessClosure(currentBusiness.id, editingClosure.id, {
                              startDate: closureFormData.startDate!,
                              endDate: closureFormData.endDate!,
                              closureType: closureFormData.closureType!,
                              startTime: closureFormData.closureType === 'time_range' ? convert12To24(closureFormData.startTime || '') : undefined,
                              endTime: closureFormData.closureType === 'time_range' ? convert12To24(closureFormData.endTime || '') : undefined,
                              reason: closureFormData.reason,
                              repeatYearly: closureFormData.repeatYearly || false
                            });
                            toast.success('Closure updated successfully');
                          } else {
                            await apiService.createBusinessClosure(currentBusiness.id, {
                              startDate: closureFormData.startDate!,
                              endDate: closureFormData.endDate!,
                              closureType: closureFormData.closureType!,
                              startTime: closureFormData.closureType === 'time_range' ? convert12To24(closureFormData.startTime || '') : undefined,
                              endTime: closureFormData.closureType === 'time_range' ? convert12To24(closureFormData.endTime || '') : undefined,
                              reason: closureFormData.reason,
                              repeatYearly: closureFormData.repeatYearly || false
                            });
                            toast.success('Closure created successfully');
                          }
                          setShowClosureForm(false);
                          setEditingClosure(null);
                          loadClosures();
                        } catch (error: any) {
                          console.error('Error saving closure:', error);
                          toast.error(error.response?.data?.message || 'Failed to save closure');
                        }
                      }}
                      className="flex flex-col"
                    >
                      {/* Body (mirrors Add Time Off layout) */}
                      <div className="p-6 space-y-4">
                        {/* Reason */}
                        <div>
                          <label htmlFor="closure-reason" className="block text-sm font-medium text-gray-700 mb-2">
                            Reason (optional)
                          </label>
                          <input
                            id="closure-reason"
                            type="text"
                            value={closureFormData.reason || ''}
                            onChange={(e) => setClosureFormData({ ...closureFormData, reason: e.target.value })}
                            placeholder="e.g., Holiday, Renovation, Private event"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                          />
                        </div>

                        {/* Start Date and End Date */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="closure-start-date" className="block text-sm font-medium text-gray-700 mb-2">
                              Start Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              id="closure-start-date"
                              type="date"
                              required
                              value={closureFormData.startDate || ''}
                              onChange={(e) => {
                                const newStartDate = e.target.value;
                                // If end date is empty or same as old start date, set it to new start date
                                const shouldUpdateEndDate = !closureFormData.endDate || closureFormData.endDate === closureFormData.startDate;
                                setClosureFormData({
                                  ...closureFormData,
                                  startDate: newStartDate,
                                  endDate: shouldUpdateEndDate ? newStartDate : closureFormData.endDate
                                });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                            />
                          </div>
                          <div>
                            <label htmlFor="closure-end-date" className="block text-sm font-medium text-gray-700 mb-2">
                              End Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              id="closure-end-date"
                              type="date"
                              required
                              value={closureFormData.endDate || ''}
                              onChange={(e) => setClosureFormData({ ...closureFormData, endDate: e.target.value })}
                              min={closureFormData.startDate || undefined}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                            />
                          </div>
                        </div>

                        {/* All Day Checkbox (maps to closureType) */}
                        <div>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(closureFormData.closureType || 'all_day') === 'all_day'}
                              onChange={(e) => {
                                const isAllDay = e.target.checked;
                                setClosureFormData({
                                  ...closureFormData,
                                  closureType: isAllDay ? 'all_day' : 'time_range',
                                  startTime: isAllDay ? '' : closureFormData.startTime,
                                  endTime: isAllDay ? '' : closureFormData.endTime
                                });
                              }}
                              className="w-4 h-4 text-[#0B4778] bg-gray-100 border-gray-300 rounded focus:ring-[#0B4778] focus:ring-2"
                            />
                            <span className="text-sm font-medium text-gray-700">All Day</span>
                          </label>
                        </div>

                      {/* Time Range (if not all day) */}
                      {closureFormData.closureType === 'time_range' && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="closure-start-time" className="block text-sm font-medium text-gray-700 mb-2">
                              Start Time <span className="text-red-500">*</span>
                            </label>
                            <TimePicker
                              value={closureFormData.startTime || ''}
                              onChange={(time) => {
                                setClosureFormData({ ...closureFormData, startTime: time });
                              }}
                              placeholder="--:-- --"
                              className="w-full"
                              minuteInterval={30}
                            />
                          </div>
                          <div>
                            <label htmlFor="closure-end-time" className="block text-sm font-medium text-gray-700 mb-2">
                              End Time <span className="text-red-500">*</span>
                            </label>
                            <TimePicker
                              value={closureFormData.endTime || ''}
                              onChange={(time) => {
                                setClosureFormData({ ...closureFormData, endTime: time });
                              }}
                              placeholder="--:-- --"
                              className="w-full"
                              minuteInterval={30}
                              alignRight={true}
                            />
                          </div>
                        </div>
                      )}

                      {/* Repeat Yearly */}
                        <div>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              id="repeatYearly"
                              checked={closureFormData.repeatYearly || false}
                              onChange={(e) => setClosureFormData({ ...closureFormData, repeatYearly: e.target.checked })}
                              className="w-4 h-4 text-[#0B4778] bg-gray-100 border-gray-300 rounded focus:ring-[#0B4778] focus:ring-2"
                            />
                            <span className="text-sm font-medium text-gray-700">Repeat yearly</span>
                          </label>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className={`flex items-center space-x-3 p-6 border-t border-gray-200 ${editingClosure ? '' : ''}`}>
                        <button
                          type="button"
                          onClick={() => {
                            setShowClosureForm(false);
                            setEditingClosure(null);
                            setClosureFormData({
                              startDate: '',
                              endDate: '',
                              closureType: 'all_day',
                              startTime: '',
                              endTime: '',
                              reason: '',
                              repeatYearly: false
                            });
                          }}
                          className="aacore-btn2-outline flex-1 bg-gray-100 hover:bg-gray-200"
                        >
                          <i className="bi bi-x"></i>
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="aacore-btn2-primary flex-1"
                        >
                          <i className="bi bi-check"></i>
                          Save
                        </button>
                        {editingClosure && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowDeleteClosureModal(true);
                            }}
                            className="aacore-btn2-danger flex-1"
                          >
                            <i className="bi bi-trash"></i>
                            <span>Remove</span>
                          </button>
                        )}
                      </div>
                    </form>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">Click on a closure from the list or click "New" to add one.</p>
                    </div>
                  )}
                </div>
                )}
              </div>
            )}
            </div>
          </div>
        );
        
      case 'customers':
        if (customersAccessDeniedShown || !canAccessCustomersPage(user, currentBusiness?.id)) {
          return (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center max-w-lg mx-auto">
              <AlertTriangle className="w-14 h-14 text-red-500 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-6">
                You do not have permission to view the Customers page for this business. Contact your business owner
                if you need access.
              </p>
              <button
                type="button"
                onClick={() => {
                  const fallback =
                    user?.canPerformServices && currentBusiness?.id && !isBusinessOwnerForBusiness(user, currentBusiness.id)
                      ? 'my-appointments'
                      : 'appointments';
                  const next = new URLSearchParams(searchParams);
                  next.set('section', fallback);
                  setSearchParams(next, { replace: true });
                  setActiveSection(fallback);
                  setCustomersAccessDeniedShown(false);
                }}
                className="aacore-btn2-primary"
              >
                Go to Dashboard
              </button>
            </div>
          );
        }

        return (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Customers</h2>
                <p className="text-gray-600 mt-1">View and manage your customer database.</p>
              </div>
              {/* Customer Count - Desktop only */}
              <div className="hidden sm:flex text-gray-600 items-center gap-1.5">
                <i className="bi bi-people"></i>
                <span>
                  {customerPagination?.total ?? (customers?.length ?? 0)} {(customerPagination?.total ?? (customers?.length ?? 0)) === 1 ? 'Customer' : 'Customers'}
                </span>
              </div>
            </div>

            {/* Search, Sort, and View Toggles Toolbar */}
              <div className="md:bg-white md:rounded-lg md:shadow-sm md:border md:border-gray-200 md:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
                {/* Search Bar */}
                <div className="flex-1 sm:flex-initial w-full md:max-w-md relative">
                  <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="Search by name, email, or phone"
                    value={customerSearchQuery}
                    onChange={(e) => {
                      setCustomerSearchQuery(e.target.value);
                      setCustomerPage(1);
                    }}
                    className={`w-full py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:border-transparent ${customerSearchQuery ? 'pl-10 pr-10' : 'pl-10 pr-4'}`}
                  />
                  {customerSearchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setCustomerSearchQuery('');
                        setCustomerPage(1);
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label="Clear search"
                    >
                      <i className="bi bi-x text-xl"></i>
                    </button>
                  )}
                </div>

                {/* Customer Count and Sort/View Controls Row */}
                <div className="flex items-center justify-between gap-3">
                  {/* Customer Count - Mobile only, left-aligned */}
                  <div className="sm:hidden text-gray-600">
                    {customerPagination?.total ?? (customers?.length ?? 0)} {(customerPagination?.total ?? (customers?.length ?? 0)) === 1 ? 'Customer' : 'Customers'}
                  </div>

                  {/* Sort Dropdown and View Toggles */}
                  <div className="flex items-center gap-3">
                  {/* Sort Dropdown */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700 whitespace-nowrap">Sort:</label>
                    <div className="relative">
                      <button
                        onClick={() => setCustomerSortDropdownOpen(!customerSortDropdownOpen)}
                        className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-semibold text-gray-900 flex items-center gap-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#0B4778]"
                      >
                        <span>
                          {customerSortOption === 'most-recent-next' && 'Recent'}
                          {customerSortOption === 'name-az' && 'A-Z'}
                          {customerSortOption === 'name-za' && 'Z-A'}
                          {customerSortOption === 'most-appointments' && 'Most Visits'}
                          {customerSortOption === 'least-appointments' && 'Least Visits'}
                          {customerSortOption === 'newest-customer' && 'Newest'}
                          {customerSortOption === 'oldest-customer' && 'Oldest'}
                        </span>
                        <i className={`bi bi-chevron-down text-xs transition-transform ${customerSortDropdownOpen ? 'rotate-180' : ''}`}></i>
                      </button>
                      {customerSortDropdownOpen && (
                        <>
                          <div 
                            className="fixed inset-0 z-10" 
                            onClick={() => setCustomerSortDropdownOpen(false)}
                          ></div>
                          <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                            <button
                            onClick={() => {
                              setCustomerSortOption('most-recent-next');
                              setCustomerSortDropdownOpen(false);
                              setCustomerPage(1);
                            }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg ${
                                customerSortOption === 'most-recent-next' ? 'bg-[#0B4778]/10 text-[#0B4778] font-semibold' : 'text-gray-900'
                              }`}
                            >
                              Recent
                            </button>
                            <button
                              onClick={() => {
                                setCustomerSortOption('name-az');
                                setCustomerSortDropdownOpen(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg ${
                                customerSortOption === 'name-az' ? 'bg-[#0B4778]/10 text-[#0B4778] font-semibold' : 'text-gray-900'
                              }`}
                            >
                              A-Z
                            </button>
                            <button
                              onClick={() => {
                                setCustomerSortOption('name-za');
                                setCustomerSortDropdownOpen(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg ${
                                customerSortOption === 'name-za' ? 'bg-[#0B4778]/10 text-[#0B4778] font-semibold' : 'text-gray-900'
                              }`}
                            >
                              Z-A
                            </button>
                            <button
                              onClick={() => {
                                setCustomerSortOption('most-appointments');
                                setCustomerSortDropdownOpen(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg ${
                                customerSortOption === 'most-appointments' ? 'bg-[#0B4778]/10 text-[#0B4778] font-semibold' : 'text-gray-900'
                              }`}
                            >
                              Most Visits
                            </button>
                            <button
                              onClick={() => {
                                setCustomerSortOption('least-appointments');
                                setCustomerSortDropdownOpen(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg ${
                                customerSortOption === 'least-appointments' ? 'bg-[#0B4778]/10 text-[#0B4778] font-semibold' : 'text-gray-900'
                              }`}
                            >
                              Least Visits
                            </button>
                            <button
                              onClick={() => {
                                setCustomerSortOption('newest-customer');
                                setCustomerSortDropdownOpen(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg ${
                                customerSortOption === 'newest-customer' ? 'bg-[#0B4778]/10 text-[#0B4778] font-semibold' : 'text-gray-900'
                              }`}
                            >
                              Newest
                            </button>
                            <button
                              onClick={() => {
                                setCustomerSortOption('oldest-customer');
                                setCustomerSortDropdownOpen(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg ${
                                customerSortOption === 'oldest-customer' ? 'bg-[#0B4778]/10 text-[#0B4778] font-semibold' : 'text-gray-900'
                              }`}
                            >
                              Oldest
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* View Toggle Buttons - Hidden on mobile */}
                  <div className="hidden sm:flex gap-2">
                    <button 
                      onClick={() => setCustomerView('grid')}
                      title="Grid View"
                      className={`px-3 py-2 rounded-lg text-sm flex items-center justify-center ${
                        customerView === 'grid' 
                          ? 'bg-[#0B4778] text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => setCustomerView('table')}
                      title="Table View"
                      className={`px-3 py-2 rounded-lg text-sm flex items-center justify-center ${
                        customerView === 'table' 
                          ? 'bg-[#0B4778] text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                  </div>
                </div>
              </div>
              </div>

            {/* Loading State */}
            {customersLoading && customers.length === 0 && !customersError && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-12 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                  <p className="text-lg font-medium text-gray-900 mb-2">Loading customers...</p>
                  <p className="text-sm text-gray-500">Please wait while we fetch your customers</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {customersError && !customersLoading && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-12 text-center">
                  <div className="text-red-500 text-4xl mb-4">⚠️</div>
                  <p className="text-lg font-medium text-gray-900 mb-2">Failed to load customers</p>
                  <p className="text-sm text-gray-500 mb-6">{customersError}</p>
                  <button
                    onClick={() => loadCustomers()}
                    className="aacore-btn2-primary"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {/* Customers Content - Grid View */}
            {!customersError && (customers.length > 0 || !customersLoading) && customerView === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(!customers || customers.length === 0) ? (
                        <div className="col-span-full bg-white rounded-lg shadow-sm border border-gray-200">
                          <div className="p-12 text-center text-gray-500">
                            <span className="text-4xl mb-4 block">👤</span>
                            <p className="text-lg font-medium text-gray-900 mb-2">No customers found</p>
                            <p className="text-sm">Customers will appear here once they book appointments.</p>
                          </div>
                        </div>
                      ) : (
                  (customers || []).map((customer) => {
                    // Helper function to format date
                    const formatDate = (dateString: string) => {
                      const date = new Date(dateString);
                      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
                    };

                    // Helper function to calculate days difference
                    const getDaysDifference = (dateString: string, isFuture: boolean = false) => {
                      const date = new Date(dateString);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      date.setHours(0, 0, 0, 0);
                      const diffTime = date.getTime() - today.getTime();
                      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                      
                      if (isFuture) {
                        if (diffDays === 0) return 'Today';
                        if (diffDays === 1) return 'Tomorrow';
                        return `${diffDays} days`;
                      } else {
                        const daysAgo = Math.abs(diffDays);
                        if (daysAgo === 0) return 'Today';
                        if (daysAgo === 1) return '1 day ago';
                        return `${daysAgo} days ago`;
                      }
                    };

                    // Determine which appointment info to show (next takes priority)
                    const appointmentInfo = customer.nextAppointment 
                      ? {
                          kind: 'Next',
                          date: customer.nextAppointment.scheduledAt,
                          status: customer.nextAppointment.status,
                          isFuture: true
                        }
                      : customer.lastAppointment
                      ? {
                          kind: 'Last',
                          date: customer.lastAppointment.scheduledAt,
                          status: customer.lastAppointment.status,
                          isFuture: false
                        }
                      : null;

                    // Get status icon and color
                    const getStatusInfo = () => {
                      if (!appointmentInfo) {
                        return { icon: 'bi-dash-circle', color: 'text-red-500', text: 'No visits yet' };
                      }
                      if (appointmentInfo.status === 'CONFIRMED' && appointmentInfo.isFuture) {
                        return { icon: 'bi-calendar-check-fill', color: 'text-amber-500', text: 'Confirmed' };
                      } else if (appointmentInfo.status === 'COMPLETED') {
                        return { icon: 'bi-check-circle-fill', color: 'text-green-500', text: 'Completed' };
                      } else if (appointmentInfo.status === 'CANCELLED') {
                        return { icon: 'bi-x-octagon-fill', color: 'text-red-500', text: 'Canceled' };
                      }
                      return { icon: 'bi-calendar-check-fill', color: 'text-amber-500', text: 'Confirmed' };
                    };

                    const statusInfo = getStatusInfo();
                    const whenText = appointmentInfo ? getDaysDifference(appointmentInfo.date, appointmentInfo.isFuture) : 'No visits yet';

                    return (
                      <div 
                        key={customer.id} 
                        className="grid grid-cols-[1fr_auto] md:block items-center md:items-start gap-3 bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow p-4 cursor-pointer md:cursor-default"
                        onClick={() => {
                          if (window.innerWidth < 768) {
                            navigate(`/dashboard/business/customers/${customer.id}`);
                          }
                        }}
                      >
                        <div className="min-w-0">
                          {/* Top Row: Avatar + Name/Email + View Button */}
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-10 h-10 bg-[#0B4778]/20 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-[#0B4778] font-semibold text-sm">
                                  {customer.firstName?.[0]}{customer.lastName?.[0]}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-900 truncate">
                                  {customer.firstName} {customer.lastName}
                                </h3>
                                <p
                                  className="text-sm text-gray-600 flex items-center gap-2 mt-1 min-w-0"
                                  title={customer.email && customer.email.includes('@appointment.local') ? 'Guest Customer (Info not provided)' : customer.email}
                                >
                                  <i className="bi bi-envelope flex-shrink-0"></i>
                                  <span className="truncate">
                                    {customer.email && customer.email.includes('@appointment.local')
                                      ? 'Guest Customer (Info not provided)'
                                      : customer.email}
                                  </span>
                                </p>
                                {!!(customer as any).phone && (
                                  <p className="text-sm text-gray-600 flex items-center gap-2 mt-1 min-w-0">
                                    <i className="bi bi-telephone flex-shrink-0"></i>
                                    <span className="truncate">{displayCustomerPhone((customer as any).phone, canViewFullCustomerPhone(user, currentBusiness?.id))}</span>
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/business/customers/${customer.id}`);
                              }}
                              className="hidden md:flex flex-shrink-0 w-9 h-9 items-center justify-center rounded-lg border border-[#0B4778]/30 bg-[#0B4778]/10 hover:bg-[#0B4778]/20 transition-colors"
                              title="View"
                            >
                              <i className="bi bi-eye text-[#0B4778]"></i>
                            </button>
                          </div>

                          {/* Customer Since */}
                          <div className="text-xs text-gray-600 mb-3 flex items-center gap-1.5">
                            <i className="bi bi-calendar3 text-purple-600"></i>
                            <span>Customer since:</span>
                            <time className="border-b border-dotted border-gray-400 cursor-help" title={new Date(customer.createdAt).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}>
                              {formatDate(customer.createdAt)}
                            </time>
                          </div>

                          {/* Key-Value Pairs */}
                          <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                            {/* Appointment Count */}
                            <span className="flex items-center gap-1.5" title="Appointments">
                              <i className="bi bi-calendar2-check text-[#0B4778]"></i>
                              <span>{customer.appointmentCount} {customer.appointmentCount === 1 ? 'appt' : 'appts'}</span>
                            </span>

                            {/* Last/Next Appointment Date */}
                            {appointmentInfo && (
                              <span className="flex items-center gap-1.5" title={`${appointmentInfo.kind} ${new Date(appointmentInfo.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`}>
                                <i className={`bi ${appointmentInfo.kind === 'Next' ? 'bi-calendar-day text-amber-500' : 'bi-clock text-amber-500'}`}></i>
                                <span>{appointmentInfo.kind}: {formatDate(appointmentInfo.date)}</span>
                              </span>
                            )}

                            {/* Status Icon with When */}
                            <span className="flex items-center gap-1.5" title={`${statusInfo.text}${appointmentInfo ? ` • ${new Date(appointmentInfo.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}` : ''}${whenText ? ` • ${whenText}` : ''}`}>
                              <i className={`bi ${statusInfo.icon} ${statusInfo.color}`}></i>
                              {whenText && <span>{whenText}</span>}
                            </span>
                          </div>
                        </div>
                        
                        {/* Chevron for mobile */}
                        <div className="flex md:hidden text-gray-400">
                          <ChevronRight className="w-5 h-5" />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Customers Content - Table View */}
            {!customersError && (customers.length > 0 || !customersLoading) && customerView === 'table' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {(!customers || customers.length === 0) ? (
                        <div className="p-12 text-center text-gray-500">
                          <span className="text-4xl mb-4 block">👤</span>
                          <p className="text-lg font-medium text-gray-900 mb-2">No customers found</p>
                          <p className="text-sm">Customers will appear here once they book appointments.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto w-full">
                          <table className="w-full divide-y divide-gray-200 border border-gray-200 border-collapse">
                            <thead className="bg-gray-50">
                              <tr className="divide-x divide-gray-200">
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last / Next</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appointments</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {(customers || []).map((customer) => {
                          // Helper function to format date
                          const formatDate = (dateString: string) => {
                            const date = new Date(dateString);
                            return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
                          };

                          // Helper function to calculate days difference
                          const getDaysDifference = (dateString: string, isFuture: boolean = false) => {
                            const date = new Date(dateString);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            date.setHours(0, 0, 0, 0);
                            const diffTime = date.getTime() - today.getTime();
                            const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                            
                            if (isFuture) {
                              if (diffDays === 0) return 'Today';
                              if (diffDays === 1) return 'Tomorrow';
                              return `${diffDays} days`;
                            } else {
                              const daysAgo = Math.abs(diffDays);
                              if (daysAgo === 0) return 'Today';
                              if (daysAgo === 1) return '1 day ago';
                              return `${daysAgo} days ago`;
                            }
                          };

                          // Determine which appointment info to show (next takes priority)
                          const appointmentInfo = customer.nextAppointment 
                            ? {
                                kind: 'Next',
                                date: customer.nextAppointment.scheduledAt,
                                status: customer.nextAppointment.status,
                                isFuture: true
                              }
                            : customer.lastAppointment
                            ? {
                                kind: 'Last',
                                date: customer.lastAppointment.scheduledAt,
                                status: customer.lastAppointment.status,
                                isFuture: false
                              }
                            : null;

                          // Get status chip info
                          const getStatusChip = () => {
                            if (!appointmentInfo) {
                              return null;
                            }
                            if (appointmentInfo.status === 'CONFIRMED' && appointmentInfo.isFuture) {
                              return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Confirmed' };
                            } else if (appointmentInfo.status === 'COMPLETED') {
                              return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500', label: 'Completed' };
                            } else if (appointmentInfo.status === 'CANCELLED') {
                              return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500', label: 'Canceled' };
                            }
                            return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Confirmed' };
                          };

                          const statusChip = getStatusChip();
                          const whenText = appointmentInfo ? getDaysDifference(appointmentInfo.date, appointmentInfo.isFuture) : 'No visits yet';

                          return (
                            <tr key={customer.id} className="hover:bg-gray-50 divide-x divide-gray-200">
                              {/* Customer Column */}
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-[#0B4778]/20 rounded-full flex items-center justify-center flex-shrink-0">
                                    <span className="text-[#0B4778] font-semibold text-sm">
                                      {customer.firstName?.[0]}{customer.lastName?.[0]}
                                    </span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-gray-900 truncate">
                                      {customer.firstName} {customer.lastName}
                                    </div>
                                    <div
                                      className="text-sm text-gray-600 flex items-center gap-2 mt-1 min-w-0"
                                      title={customer.email && customer.email.includes('@appointment.local') ? 'Guest Customer (Info not provided)' : customer.email}
                                    >
                                      <i className="bi bi-envelope flex-shrink-0"></i>
                                      <span className="truncate">
                                        {customer.email && customer.email.includes('@appointment.local')
                                          ? 'Guest Customer (Info not provided)'
                                          : customer.email}
                                      </span>
                                    </div>
                                    {!!(customer as any).phone && (
                                      <div className="text-sm text-gray-600 flex items-center gap-2 mt-1 min-w-0">
                                        <i className="bi bi-telephone flex-shrink-0"></i>
                                        <span className="truncate">{displayCustomerPhone((customer as any).phone, canViewFullCustomerPhone(user, currentBusiness?.id))}</span>
                                      </div>
                                    )}
                                    <div className="text-xs text-gray-600 flex items-center gap-1.5 mt-1">
                                      <i className="bi bi-calendar3 text-purple-600"></i>
                                      <span>Customer since: {formatDate(customer.createdAt)}</span>
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* Last / Next Column */}
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-600">
                                  {appointmentInfo ? (
                                    <div className="flex flex-col gap-2">
                                      <span>
                                        {appointmentInfo.kind}: <strong>{formatDate(appointmentInfo.date)}</strong>
                                      </span>
                                      {statusChip && (
                                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border w-fit ${statusChip.bg} ${statusChip.border} ${statusChip.text}`}>
                                          <i className={`w-2 h-2 rounded-full ${statusChip.dot}`}></i>
                                          {statusChip.label}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-500 flex items-center gap-1.5">
                                      <i className="bi bi-dash-circle text-red-500"></i>
                                      No visits yet
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* When Column */}
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-600 flex items-center gap-1.5">
                                  <i className="bi bi-stopwatch text-green-600"></i>
                                  <span>{whenText}</span>
                                </div>
                              </td>

                              {/* Appointments Column */}
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-1.5 text-sm text-gray-900">
                                  <i className="bi bi-calendar2-check text-[#0B4778]"></i>
                                  <span>{customer.appointmentCount}</span>
                                </div>
                              </td>

                              {/* Actions Column */}
                              <td className="px-6 py-4">
                                <div className="flex justify-start">
                                  <button
                                    onClick={() => navigate(`/dashboard/business/customers/${customer.id}`)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#0B4778]/30 bg-[#0B4778]/10 hover:bg-[#0B4778]/20 transition-colors text-sm font-medium text-[#0B4778]"
                                    title="View"
                                  >
                                    <i className="bi bi-eye"></i>
                                    <span>View</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {!customersError && customerPagination && customerPagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-sm text-gray-700">
                  Showing {((customerPagination.page - 1) * customerPagination.limit) + 1} to {Math.min(customerPagination.page * customerPagination.limit, customerPagination.total)} of {customerPagination.total} customers
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCustomerPage(customerPagination.page - 1)}
                    disabled={!customerPagination.hasPreviousPage}
                    className={`px-3 py-1 text-sm rounded-lg border ${
                      customerPagination.hasPreviousPage
                        ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        : 'border-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, customerPagination.totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (customerPagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (customerPagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (customerPagination.page >= customerPagination.totalPages - 2) {
                        pageNum = customerPagination.totalPages - 4 + i;
                      } else {
                        pageNum = customerPagination.page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCustomerPage(pageNum)}
                          className={`px-3 py-1 text-sm rounded-lg border ${
                            customerPagination.page === pageNum
                              ? 'border-[#0B4778] bg-[#0B4778] text-white'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setCustomerPage(customerPagination.page + 1)}
                    disabled={!customerPagination.hasNextPage}
                    className={`px-3 py-1 text-sm rounded-lg border ${
                      customerPagination.hasNextPage
                        ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        : 'border-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        );
        
      case 'settings':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Business Settings</h2>
                <p className="text-gray-600 mt-1">Configure your business preferences and automation settings</p>
              </div>
            </div>
            
            <PermissionGate permission="manage_settings">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <p className="text-gray-500">Settings have been moved to the Appointments page.</p>
              </div>
            </PermissionGate>
          </div>
        );
        
      default:
        return null;
    }
  };

  const handleMarkNotificationAsRead = async (notificationId: string) => {
    try {
      await apiService.markNotificationAsRead(notificationId);
      // Reload notifications to refresh pagination
      loadNotifications();
      // Refresh unread count for navbar badge
      loadUnreadCount();
      toast.success('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllNotificationsAsRead = async () => {
    if (!currentBusiness?.id) return;
    
    try {
      const result = await apiService.markAllNotificationsAsRead(currentBusiness.id);
      // Reset unread page to 1 and reload notifications
      setNotificationPage(1);
      loadNotifications();
      // Refresh unread count for navbar badge
      loadUnreadCount();
      toast.success(`${result.updatedCount} notifications marked as read`);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };

  type BooleanSettingsKey = {
    [K in keyof typeof settings]: typeof settings[K] extends boolean ? K : never;
  }[keyof typeof settings];

  const handleSettingChange = (key: BooleanSettingsKey, value: boolean) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Sync Appointment Settings modal draft from current settings when modal opens; load booking block size from code_values
  useEffect(() => {
    if (!isAppointmentSettingsModalOpen || !currentBusiness?.id) return;

    setAppointmentSettingsDraft({
      autoConfirmAppointments: settings.autoConfirmAppointments,
      emailAppointmentActivitiesToOwner: settings.emailAppointmentActivitiesToOwner,
      showProfessionalNameOnAssignment: settings.showProfessionalNameOnAssignment,
      bookingSlotMinutes: DEFAULT_BOOKING_SLOT_MINUTES
    });

    let cancelled = false;
    apiService
      .getAppointmentBookingSettings(currentBusiness.id)
      .then((data) => {
        if (!cancelled) {
          setAppointmentSettingsDraft((prev) => ({ ...prev, bookingSlotMinutes: resolveBookingSlotMinutes(data) }));
        }
      })
      .catch(() => {
        /* keep default 30-minute blocks */
      });

    return () => {
      cancelled = true;
    };
  }, [isAppointmentSettingsModalOpen, currentBusiness?.id]); // intentionally not depending on settings so we only init on open

  const handleAppointmentSettingDraftChange = (
    key: 'autoConfirmAppointments' | 'emailAppointmentActivitiesToOwner' | 'showProfessionalNameOnAssignment',
    value: boolean
  ) => {
    setAppointmentSettingsDraft(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveAppointmentSettings = async () => {
    if (!currentBusiness?.id) return;
    setSavingSettings(true);
    try {
      const { bookingSlotMinutes, ...businessDraft } = appointmentSettingsDraft;
      await apiService.updateBusinessSettings(currentBusiness.id, { ...settings, ...businessDraft });
      await apiService.updateAppointmentBookingSettings(currentBusiness.id, { bookingSlotMinutes });
      setSettings(prev => ({ ...prev, ...businessDraft }));
      toast.success('Settings saved successfully');
      setIsAppointmentSettingsModalOpen(false);
    } catch (error) {
      console.error('Error saving appointment settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!currentBusiness?.id) return;
    
    setSavingSettings(true);
    try {
      await apiService.updateBusinessSettings(currentBusiness.id, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Use the total unread count from the separate API call
  const unreadNotificationsCount = totalUnreadCount;

  // Handle notification bell click
  const handleNotificationsClick = () => {
    setActiveSection('notifications');
    setSearchParams({ section: 'notifications' });
    setIsSidebarOpen(false);
  };

  // Don't show the Help & Support banner until Guided Setup is completed (Done).
  // In this dashboard, "completed" is represented by the Guided Setup launcher being disabled for the session.
  const isGuidedSetupBlockingHelpBanner = !!(
    currentBusiness?.id && guidedSetupLauncherEnabled[currentBusiness.id] === true
  );
  const shouldShowHelpSupportBanner =
    SHOW_HELP_SUPPORT_POST_SETUP_BANNER && showHelpSupportBanner && !isGuidedSetupBlockingHelpBanner;

  return (
    <div className="min-h-screen bg-gray-50 no-animations flex flex-col lg:h-screen lg:overflow-hidden">
      <NavigationBar 
        unreadNotificationsCount={unreadNotificationsCount}
        onNotificationsClick={handleNotificationsClick}
      />

      {shouldShowHelpSupportBanner && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] w-[min(100%-2rem,960px)] sm:bottom-6">
          <div className="rounded-2xl shadow-lg border border-white/20 bg-gradient-to-r from-[#0B4778] to-[#0B4778]/90 px-4 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                  <HelpCircle className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="text-white font-semibold leading-tight">Need help? Visit Help &amp; Support</div>
                  <div className="text-white/80 text-sm leading-tight">Guides, FAQs, and contact options — quick and easy.</div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 w-full sm:w-auto sm:ml-auto sm:justify-end shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    try {
                      if (currentBusiness?.id) {
                        localStorage.setItem(getHelpSupportBannerKey(currentBusiness.id), 'closed');
                        localStorage.removeItem(getHelpSupportBannerLegacyKey(currentBusiness.id));
                      }
                    } catch {}
                    setShowHelpSupportBanner(false);
                    navigate('/help-support');
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-[#0B4778] font-semibold hover:bg-white/90"
                >
                  <MessageSquare className="h-4 w-4" />
                  View Page
                </button>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      if (currentBusiness?.id) {
                        localStorage.setItem(getHelpSupportBannerKey(currentBusiness.id), 'closed');
                        localStorage.removeItem(getHelpSupportBannerLegacyKey(currentBusiness.id));
                      }
                    } catch {}
                    setShowHelpSupportBanner(false);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 text-white border border-white/20 hover:bg-white/15"
                >
                  <X className="h-4 w-4" />
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showGuidedSetup && (
        <GuidedSetupModal
          businessName={currentBusiness?.name}
          onClose={() => setShowGuidedSetup(false)}
          onOpenSiteConfigTab={openSiteConfigTab}
          onOpenServicesManagement={() => handleSectionChange('services')}
          onDone={() => {
            const businessId = currentBusiness?.id;
            if (!businessId) return;
            setGuidedSetupLauncherEnabled(prev => ({ ...prev, [businessId]: false }));
          }}
          stepIndex={
            currentBusiness?.id
              ? (guidedSetupStepIndexByBusiness[currentBusiness.id] ?? 0)
              : 0
          }
          onStepIndexChange={(next) => {
            const businessId = currentBusiness?.id;
            if (!businessId) return;
            setGuidedSetupStepIndexByBusiness(prev => ({ ...prev, [businessId]: next }));
          }}
          onSaveSiteConfig={handleSaveAllSiteConfig}
        />
      )}

      {!showGuidedSetup &&
        currentBusiness?.id &&
        isBusinessOwnerForBusiness(user, currentBusiness.id) &&
        guidedSetupLauncherEnabled[currentBusiness.id] === true && (
          <GuidedSetupLauncher onClick={() => setShowGuidedSetup(true)} />
        )}
      
      <div className="flex-1 min-h-0 lg:grid lg:grid-cols-[auto_1fr] lg:overflow-hidden">
        <BusinessDashboardSidebar
          isSidebarOpen={isSidebarOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          activeSection={activeSection}
          onClose={() => setIsSidebarOpen(false)}
          onToggleCollapse={toggleSidebar}
          onSectionChange={(section) => handleSectionChange(section as DashboardSection)}
          unreadNotificationCount={notifications.filter(n => !n.is_read).length}
        />
        
        {/* Main Content */}
        <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden lg:overflow-y-auto">
          {/* Upcoming maintenance banner */}
          {upcomingMaintenance && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5 text-amber-600">
                <AlertTriangle className="w-5 h-5" strokeWidth={2} aria-hidden />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-900">Scheduled maintenance coming up</p>
                <p className="text-sm text-amber-800 mt-0.5">{upcomingMaintenance.message}</p>
                <p className="text-xs text-amber-700 mt-1">
                  Maintenance window:{' '}
                  {new Date(upcomingMaintenance.from).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                  {' – '}
                  {new Date(upcomingMaintenance.to).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  try {
                    localStorage.setItem(DISMISSED_MAINTENANCE_PREFIX + upcomingMaintenance.id, '1');
                  } catch {}
                  setUpcomingMaintenance(null);
                }}
                className="flex-shrink-0 p-1.5 rounded-md text-amber-700 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" strokeWidth={2} />
              </button>
            </div>
          )}
          {/* Mobile Header */}
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="flex-1 text-center text-lg font-semibold text-gray-900">
                {activeSection === 'my-appointments' ? (
                  <span className="md:hidden">My Appointments</span>
                ) : activeSection === 'notifications' ? (
                  <span className="md:hidden">Notifications</span>
                ) : activeSection === 'my-services' ? (
                  <span className="md:hidden">My Services</span>
                ) : activeSection === 'my-work-schedule' ? (
                  <span className="md:hidden">My Work Schedule</span>
                ) : activeSection === 'my-customer-reviews' ? (
                  <span className="md:hidden">My Reviews</span>
                ) : activeSection === 'customers' ? (
                  <span className="md:hidden">Customers</span>
                ) : activeSection === 'services' ? (
                  <span className="md:hidden">Services</span>
                ) : activeSection === 'roles' ? (
                  <span className="md:hidden">Roles and Access</span>
                ) : activeSection === 'professionals' ? (
                  <span className="md:hidden">Personnel</span>
                ) : activeSection === 'appointments' ? (
                  <span className="md:hidden">Appointments</span>
                ) : activeSection === 'closures' ? (
                  <span className="md:hidden">Closures</span>
                ) : activeSection === 'statistics' ? (
                  <span className="md:hidden">Statistics</span>
                ) : activeSection === 'testimonials' ? (
                  <span className="md:hidden">Testimonials</span>
                ) : activeSection === 'giftcards' ? (
                  <span className="md:hidden">Gift Cards</span>
                ) : activeSection === 'site-config' ? (
                  <span className="md:hidden">Site Configuration</span>
                ) : activeSection === 'settings' ? (
                  <span className="md:hidden">Business Settings</span>
                ) : activeSection === 'quote-requests' ? (
                  <span className="md:hidden">Quote Requests</span>
                ) : activeSection === 'invoices' ? (
                  <span className="md:hidden">Invoices</span>
                ) : null}
                <span className={activeSection === 'my-appointments' || activeSection === 'notifications' || activeSection === 'my-services' || activeSection === 'my-work-schedule' || activeSection === 'my-customer-reviews' || activeSection === 'customers' || activeSection === 'services' || activeSection === 'roles' || activeSection === 'professionals' || activeSection === 'appointments' || activeSection === 'quote-requests' || activeSection === 'closures' || activeSection === 'statistics' || activeSection === 'testimonials' || activeSection === 'giftcards' || activeSection === 'site-config' || activeSection === 'settings' || activeSection === 'invoices' ? 'hidden md:inline' : ''}>Business Dashboard</span>
              </h1>
              {/* Refresh Button - Only show for notifications page */}
              {activeSection === 'notifications' ? (
                <button
                  onClick={loadNotifications}
                  disabled={notificationsLoading}
                  className="p-2 rounded-md hover:bg-gray-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Refresh notifications"
                >
                  <RefreshCw className={`w-8 h-8 text-gray-700 -m-1 ${notificationsLoading ? 'animate-spin' : ''}`} />
                </button>
              ) : activeSection === 'professionals' && isBusinessOwnerForBusiness(user, currentBusiness?.id || '') ? (
                /* Display Priority Icon - Only show for professionals page and OWNER */
                <button
                  onClick={() => setShowDisplayPriorityModal(true)}
                  className="p-2 rounded-md hover:bg-gray-100 flex items-center justify-center"
                  title="Display Order"
                >
                  <ListOrdered className="w-8 h-8 text-gray-700 -m-1" />
                </button>
              ) : (activeSection === 'appointments' || activeSection === 'closures' || activeSection === 'statistics') && (!user?.canPerformServices || user?.roles?.some(role => 
                role.isActive && 
                role.permissions?.some(permission => 
                  ['manage_roles', 'manage_professionals', 'manage_settings'].includes(permission)
                )
              )) ? (
                /* Settings Dropdown - Only show for appointments and closures pages */
                <div className="relative" ref={mobileSettingsDropdownRef}>
                  <button
                    onClick={() => setIsMobileSettingsDropdownOpen(!isMobileSettingsDropdownOpen)}
                    className="p-2 rounded-md hover:bg-gray-100 flex items-center justify-center"
                  >
                    <Settings className="w-8 h-8 text-gray-700 -m-1" />
                  </button>
                  {isMobileSettingsDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      {activeSection === 'appointments' && (
                        <button
                          onClick={() => {
                            handleSectionChange('closures');
                            setIsMobileSettingsDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                        >
                          <CalendarX className="w-5 h-5 text-gray-600" />
                          <span>Closures</span>
                        </button>
                      )}
                      {activeSection === 'closures' && (
                        <button
                          onClick={() => {
                            handleSectionChange('appointments');
                            setIsMobileSettingsDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                        >
                          <Calendar className="w-5 h-5 text-gray-600" />
                          <span>Appointments</span>
                        </button>
                      )}
                      {activeSection === 'statistics' && (
                        <button
                          onClick={() => {
                            handleSectionChange('appointments');
                            setIsMobileSettingsDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                        >
                          <Calendar className="w-5 h-5 text-gray-600" />
                          <span>Appointments</span>
                        </button>
                      )}
                      {activeSection === 'appointments' && (
                        <button
                          onClick={() => {
                            setIsAppointmentSettingsModalOpen(true);
                            setIsMobileSettingsDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                        >
                          <Settings className="w-5 h-5 text-gray-600" />
                          <span>Settings</span>
                        </button>
                      )}
                      {activeSection === 'appointments' && (
                        <button
                          onClick={() => {
                            handleSectionChange('statistics');
                            setIsMobileSettingsDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                        >
                          <BarChart3 className="w-5 h-5 text-gray-600" />
                          <span>Statistics</span>
                        </button>
                      )}
                      {(activeSection === 'appointments' || activeSection === 'closures' || activeSection === 'statistics') && (
                        <>
                          <div className="border-t border-gray-200 my-1"></div>
                          <button
                            onClick={() => {
                              if (activeSection === 'appointments') {
                                loadAppointments(appointmentPagination.currentPage);
                              } else if (activeSection === 'closures') {
                                loadClosures();
                              } else if (activeSection === 'statistics') {
                                setStatisticsRefreshTrigger((v) => v + 1);
                              }
                              setIsMobileSettingsDropdownOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                          >
                            <RefreshCw className="w-5 h-5 text-gray-600" />
                            <span>Refresh</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : activeSection === 'quote-requests' ? (
                <button
                  type="button"
                  onClick={() => setIsQuoteOfferSettingsModalOpen(true)}
                  className="p-2 rounded-md hover:bg-gray-100 flex items-center justify-center"
                  title="Offer Settings"
                  aria-label="Offer Settings"
                >
                  <Settings className="w-8 h-8 text-gray-700 -m-1" />
                </button>
              ) : activeSection === 'site-config' ? (
                <button
                  type="button"
                  onClick={handleResetSiteConfig}
                  disabled={businessLoading}
                  className="aacore-btn2-danger inline-flex shrink-0 items-center gap-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Reset site configuration"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Reset
                </button>
              ) : (
                /* Invisible spacer to balance hamburger button and center the title */
                <div className="w-10 h-10"></div>
              )}
            </div>
          </div>
          
          <div className="flex-1 p-4 md:p-8 overflow-x-hidden">
            {renderSectionContent()}
          </div>
        </div>
      </div>

      {/* Standard cancellation/rescheduling policy preview modal (Site Config) */}
      {showStandardPolicyModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="standard-policy-modal-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <h3 id="standard-policy-modal-title" className="text-lg font-semibold text-gray-900">Cancellation policy</h3>
              <button
                type="button"
                onClick={() => setShowStandardPolicyModal(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="p-5 max-h-[70vh] overflow-auto">
              {standardPolicySummary && (
                <p className="text-sm text-gray-700 font-semibold mb-3">{standardPolicySummary}</p>
              )}
              <div className="text-sm text-gray-800 whitespace-pre-line">{standardPolicyText}</div>
            </div>
            <div className="p-5 border-t border-gray-200 flex flex-row justify-end">
              <button
                type="button"
                onClick={() => setShowStandardPolicyModal(false)}
                className="px-4 py-2 rounded-lg font-medium border inline-flex items-center justify-center gap-2 border-[#0B4778] text-[#0B4778] hover:bg-[#0B4778]/5"
              >
                <X className="w-4 h-4" />
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      
      {/* Business Expired Modal (owner-only notice) */}
      {showBusinessExpiredModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900">Business account expired</h3>
              <p className="text-sm text-gray-600 mt-1">
                Your business subscription has expired. To continue using the business dashboard, you’ll need to set up or update payment options.
              </p>
            </div>
            <div className="p-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                className="aacore-btn2-outline gap-1 bg-gray-100 hover:bg-gray-200"
                onClick={() => setShowBusinessExpiredModal(false)}
              >
                <X className="w-4 h-4" />
                Dismiss
              </button>
              <button
                type="button"
                className="aacore-btn2-primary gap-1"
                onClick={() => {
                  setShowBusinessExpiredModal(false);
                  navigate('/dashboard/business/profile#plan-promotion');
                }}
              >
                <Settings className="w-4 h-4" />
                Go to Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate/Deactivate Role Confirmation Modal */}
      {showDeactivateRoleModal && roleToDeactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    roleToDeactivate.role.isActive ? 'bg-orange-100' : 'bg-green-100'
                  }`}>
                    <Power className={`w-6 h-6 ${
                      roleToDeactivate.role.isActive ? 'text-orange-600' : 'text-green-600'
                    }`} />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {roleToDeactivate.role.isActive ? 'Deactivate' : 'Activate'} Role
                  </h3>
                  <p className="text-sm text-gray-500">This action can be reversed.</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to <strong>{roleToDeactivate.role.isActive ? 'deactivate' : 'activate'}</strong> the role <strong>"{roleToDeactivate.role.role}"</strong>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {roleToDeactivate.role.isActive 
                    ? 'This role will no longer be available for assignment to new users. Existing users with this role will retain their permissions.'
                    : 'This role will become available for assignment to new users.'
                  }
                </p>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleDeactivateRoleCancel}
                  className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={() => handleToggleRoleActiveConfirm(roleToDeactivate.role.id, roleToDeactivate.isActive)}
                  className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2 ${
                    roleToDeactivate.role.isActive 
                      ? 'bg-orange-600 hover:bg-orange-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  <Power className="w-4 h-4" />
                  <span>{roleToDeactivate.role.isActive ? 'Deactivate' : 'Activate'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Role Confirmation Modal */}
      {showDeleteRoleModal && roleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Delete Role</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to delete the role <strong>"{roleToDelete.role}"</strong>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  This will permanently remove the role and all associated permissions. Users with this role will lose access.
                </p>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleDeleteRoleCancel}
                  className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleDeleteRoleConfirm}
                  className="aacore-btn2-danger"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Role</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Service Permission Confirmation Modal */}
      {showToggleServicePermissionModal && professionalToToggleService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    professionalToToggleService.canPerformServices ? 'bg-green-100' : 'bg-orange-100'
                  }`}>
                    {professionalToToggleService.canPerformServices ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-orange-600" />
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {professionalToToggleService.canPerformServices ? 'Enable Services' : 'Disable Services'}
                  </h3>
                  <p className="text-sm text-gray-500">This action can be reversed.</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to <strong>{professionalToToggleService.canPerformServices ? 'enable' : 'disable'}</strong> service permissions for <strong>"{professionalToToggleService.professional.name}"</strong>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {professionalToToggleService.canPerformServices 
                    ? 'This professional will be able to perform services and appear in service selection.'
                    : 'This professional will no longer be able to perform services and will be removed from service selection.'
                  }
                </p>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleToggleServicePermissionCancel}
                  className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleToggleServicePermissionConfirm}
                  className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2 ${
                    professionalToToggleService.canPerformServices 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {professionalToToggleService.canPerformServices ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  <span>{professionalToToggleService.canPerformServices ? 'Enable Services' : 'Disable Services'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Professional Active Confirmation Modal */}
      {showToggleProfessionalActiveModal && professionalToToggleActive && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    professionalToToggleActive.professional.isActive ? 'bg-orange-100' : 'bg-green-100'
                  }`}>
                    <Power className={`w-6 h-6 ${
                      professionalToToggleActive.professional.isActive ? 'text-orange-600' : 'text-green-600'
                    }`} />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {professionalToToggleActive.professional.isActive ? 'Disable' : 'Enable'} Professional
                  </h3>
                  <p className="text-sm text-gray-500">This action can be reversed.</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to <strong>{professionalToToggleActive.professional.isActive ? 'disable' : 'enable'}</strong> <strong>"{professionalToToggleActive.professional.name}"</strong>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {professionalToToggleActive.professional.isActive 
                    ? 'This professional will no longer be able to access the business dashboard and perform actions.'
                    : 'This professional will regain access to the business dashboard and be able to perform actions.'
                  }
                </p>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleToggleProfessionalActiveCancel}
                  className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleToggleProfessionalActiveConfirm}
                  className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2 ${
                    professionalToToggleActive.professional.isActive 
                      ? 'bg-orange-600 hover:bg-orange-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  <Power className="w-4 h-4" />
                  <span>{professionalToToggleActive.professional.isActive ? 'Disable' : 'Enable'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Professional Confirmation Modal */}
      {showDeleteProfessionalModal && professionalToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Remove Professional</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to remove <strong>"{professionalToDelete.name}"</strong> from this business?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  This will remove all their schedules, service assignments, and business access. This action cannot be undone.
                </p>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleDeleteProfessionalCancel}
                  className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleDeleteProfessionalConfirm}
                  className="aacore-btn2-danger"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Remove</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Modal */}
      <RoleModal
        isOpen={isRoleModalOpen}
        onClose={() => setIsRoleModalOpen(false)}
        onSave={handleSaveRole}
        role={editingRole}
      />

      {/* Invitation Modal */}
      <ProfessionalDisplayPriorityModal
        isOpen={showDisplayPriorityModal}
        onClose={() => setShowDisplayPriorityModal(false)}
        businessId={currentBusiness?.id || ''}
        onUpdate={loadProfessionals}
      />

      <InvitationModal
        isOpen={isInvitationModalOpen}
        onClose={() => setIsInvitationModalOpen(false)}
        onInviteSent={handleInvitationSent}
        businessId={businessInfo?.id || ''}
      />

      {/* Gift Card Modal */}
      <GiftCardModal
        isOpen={isGiftCardModalOpen}
        onClose={() => {
          setIsGiftCardModalOpen(false);
          setEditingGiftCard(undefined);
        }}
        onSave={editingGiftCard ? handleUpdateGiftCard : undefined}
        onIssued={() => {
          loadGiftCards();
        }}
        giftCard={editingGiftCard}
        businessId={currentBusiness?.id || ''}
      />

      <StockImagesModal
        isOpen={showStockImagesModal}
        onClose={() => setShowStockImagesModal(false)}
        onUseImages={handleUseStockImages}
        maxSelectable={galleryRemainingSlots}
      />

      {/* Service Review Modal - TODO: Create dedicated modal for service reviews */}

      {/* Appointment Details Modal */}
      <AppointmentDetailsModal
        appointment={selectedAppointment}
        isOpen={isAppointmentDetailsModalOpen}
        hideProfessionalAndBusiness={activeSection === 'my-appointments'}
        showProfessionalNameInAssignment={
          activeSection === 'appointments' ? (professionals.length > 1 && settings.showProfessionalNameOnAssignment) : true
        }
        defaultSendUpdateEmail={false}
        onClose={() => {
          setIsAppointmentDetailsModalOpen(false);
          setSelectedAppointment(null);
          // Refresh calendar/list on close to reflect any background updates
          loadAppointments(appointmentPagination.currentPage);
        }}
        onEdit={(appointment) => {
          // Match AppointmentDetailsPage behavior
          setIsAppointmentDetailsModalOpen(false);
          setSelectedAppointment(null);
          navigate(`/dashboard/business/appointments/${appointment.id}/edit${window.location.search || ''}`);
        }}
        onCancel={async (appointmentId, cancellationData) => {
          // The modal already handles the API call and shows toast
          // This callback is just for state management
          loadAppointments(appointmentPagination.currentPage); // Refresh the appointments list
          setIsAppointmentDetailsModalOpen(false);
          setSelectedAppointment(null);
        }}
        isCancelling={isCancellingAppointment}
        onConfirm={async (appointmentId) => {
          // The modal already handles the API call and shows toast
          // This callback is just for state management
          loadAppointments(appointmentPagination.currentPage); // Refresh the appointments list
          setIsAppointmentDetailsModalOpen(false);
          setSelectedAppointment(null);
        }}
        isConfirming={isConfirmingAppointment}
        onReschedule={(appointment) => {
          navigate(`/dashboard/business/reschedule/${appointment.id}`);
        }}
        onStatusChange={(updatedAppointment) => {
          // Update the selected appointment and refresh the list
          setSelectedAppointment(updatedAppointment);
          if (activeSection === 'my-appointments') {
            loadProfessionalAppointments();
          } else {
            loadAppointments(appointmentPagination.currentPage);
          }
        }}
        onNavigateToAppointment={(appt) => {
          setSelectedAppointment(appt);
        }}
        // We want Edit available in calendar modal as well
        showEditButton={true}
      />

      {/* Schedule Modal */}
      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => {
          setIsScheduleModalOpen(false);
          setEditingSchedule(null);
          // Reload schedule after modal closes to ensure UI is up to date
          loadProfessionalSchedule();
        }}
        onSave={handleSaveSchedule}
        schedule={editingSchedule}
        schedules={professionalSchedule}
      />

      {/* Time Off Modal */}
      {isTimeOffModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingTimeOff ? 'Edit Time Off' : 'Add Time Off'}
              </h2>
              <button
                onClick={handleCloseTimeOffModal}
                className="text-gray-400 hover:text-gray-500"
                disabled={timeOffSaving}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Label */}
              <div>
                <label htmlFor="timeoff-label" className="block text-sm font-medium text-gray-700 mb-2">
                  Label <span className="text-red-500">*</span>
                </label>
                <input
                  id="timeoff-label"
                  type="text"
                  value={timeOffFormData.label}
                  onChange={(e) => setTimeOffFormData({ ...timeOffFormData, label: e.target.value })}
                  placeholder="e.g., Vacation, Sick Leave, Personal Day"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                />
              </div>

              {/* Start Date and End Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="timeoff-start-date" className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="timeoff-start-date"
                    type="date"
                    value={timeOffFormData.startDate}
                    onChange={(e) => {
                      const newStartDate = e.target.value;
                      // Auto-populate end date if it's empty or if it's before the new start date
                      const newEndDate = !timeOffFormData.endDate || timeOffFormData.endDate < newStartDate 
                        ? newStartDate 
                        : timeOffFormData.endDate;
                      setTimeOffFormData({ 
                        ...timeOffFormData, 
                        startDate: newStartDate,
                        endDate: newEndDate
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                  />
                </div>
                <div>
                  <label htmlFor="timeoff-end-date" className="block text-sm font-medium text-gray-700 mb-2">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="timeoff-end-date"
                    type="date"
                    value={timeOffFormData.endDate}
                    onChange={(e) => setTimeOffFormData({ ...timeOffFormData, endDate: e.target.value })}
                    min={timeOffFormData.startDate}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                  />
                </div>
              </div>

              {/* All Day Checkbox */}
              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={timeOffFormData.isAllDay}
                    onChange={(e) => setTimeOffFormData({ ...timeOffFormData, isAllDay: e.target.checked, startTime: '', endTime: '' })}
                    className="w-4 h-4 text-[#0B4778] bg-gray-100 border-gray-300 rounded focus:ring-[#0B4778] focus:ring-2"
                  />
                  <span className="text-sm font-medium text-gray-700">All Day</span>
                </label>
              </div>

              {/* Start Time and End Time (only if not all day) */}
              {!timeOffFormData.isAllDay && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="timeoff-start-time" className="block text-sm font-medium text-gray-700 mb-2">
                      Start Time <span className="text-red-500">*</span>
                    </label>
                    <TimePicker
                      value={timeOffFormData.startTime}
                      onChange={(time) => setTimeOffFormData({ ...timeOffFormData, startTime: time })}
                      placeholder="--:-- --"
                      className="w-full"
                      minuteInterval="timeoff"
                      dropdownDirection={isMobile ? 'up' : 'down'}
                    />
                  </div>
                  <div>
                    <label htmlFor="timeoff-end-time" className="block text-sm font-medium text-gray-700 mb-2">
                      End Time <span className="text-red-500">*</span>
                    </label>
                    <TimePicker
                      value={timeOffFormData.endTime}
                      onChange={(time) => setTimeOffFormData({ ...timeOffFormData, endTime: time })}
                      placeholder="--:-- --"
                      className="w-full"
                      minuteInterval="timeoff"
                      alignRight={true}
                      dropdownDirection={isMobile ? 'up' : 'down'}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseTimeOffModal}
                className="aacore-btn2-outline flex-1 bg-gray-100 hover:bg-gray-200"
                disabled={timeOffSaving}
              >
                <i className="bi bi-x"></i>
                Cancel
              </button>
              <button
                onClick={handleSaveTimeOff}
                disabled={timeOffSaving}
                className="aacore-btn2-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="bi bi-check"></i>
                {timeOffSaving ? (editingTimeOff ? 'Updating...' : 'Saving...') : (editingTimeOff ? 'Update' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Time Off Confirmation Dialog */}
      {timeOffToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Delete Time Off</h2>
              <button
                onClick={() => setTimeOffToDelete(null)}
                className="text-gray-400 hover:text-gray-500"
                disabled={isDeletingTimeOff}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete the time off record <span className="font-semibold">"{timeOffToDelete.label}"</span>? This action cannot be undone.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setTimeOffToDelete(null)}
                className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                disabled={isDeletingTimeOff}
              >
                <i className="bi bi-x"></i>
                Cancel
              </button>
              <button
                onClick={handleDeleteTimeOff}
                disabled={isDeletingTimeOff}
                className="aacore-btn2-danger disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeletingTimeOff ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="bi bi-trash"></i>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Schedule Confirmation Dialog */}
      {scheduleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Delete Schedule</h2>
              <button
                onClick={() => setScheduleToDelete(null)}
                className="text-gray-400 hover:text-gray-500"
                disabled={isDeletingSchedule}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete the schedule for <span className="font-semibold">{scheduleToDelete.dayName}</span>? This action cannot be undone.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setScheduleToDelete(null)}
                className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                disabled={isDeletingSchedule}
              >
                <i className="bi bi-x"></i>
                Cancel
              </button>
              <button
                onClick={handleDeleteSchedule}
                disabled={isDeletingSchedule}
                className="aacore-btn2-danger disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeletingSchedule ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="bi bi-trash"></i>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Dialog */}
      <CancellationDialog
        isOpen={isCancellationDialogOpen}
        onClose={() => {
          setIsCancellationDialogOpen(false);
          setCancellationAppointment(null);
        }}
        onConfirm={handleConfirmCancellation}
        appointmentDetails={
          cancellationAppointment
            ? (() => {
                const cancellationStartIso = getAppointmentStartIso(cancellationAppointment);
                const d = cancellationStartIso ? new Date(cancellationStartIso) : new Date(NaN);
                const when = Number.isNaN(d.getTime()) ? (cancellationStartIso ? String(cancellationStartIso) : 'Date TBD') : d.toLocaleString();
                return `Booking at ${cancellationAppointment.business_name} on ${when}`;
              })()
            : ''
        }
        appointment={cancellationAppointment as any}
      />

      {/* Confirm Confirmation Dialog */}
      {confirmConfirmationDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Confirm Appointment</span>
              </h2>
              <button
                onClick={() => setConfirmConfirmationDialog(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-700">
                Are you sure you want to confirm this appointment?
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setConfirmConfirmationDialog(null)}
                className="aacore-btn2-outline"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Go Back</span>
              </button>
              <button
                onClick={handleConfirmConfirm}
                className="aacore-btn2-success"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Confirm Appointment</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Confirmation Dialog */}
      {completeConfirmationDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Mark as Completed
              </h2>
              <button
                onClick={() => setCompleteConfirmationDialog(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-700">
                Are you sure you want to mark this appointment as completed?
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setCompleteConfirmationDialog(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={handleConfirmComplete}
                className="aacore-btn2-primary"
              >
                Mark as Completed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Service Confirmation Modal */}
      {showDeleteServiceModal && serviceToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Delete Service</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to delete the service{' '}
                  <span className="font-medium text-gray-900">"{serviceToDelete.name}"</span>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  This will soft-delete the service (hide it from lists and booking). Existing appointments are not affected.
                </p>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleDeleteServiceCancel}
                  className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleDeleteServiceConfirm}
                  className="aacore-btn2-danger"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Service</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Service Confirmation Modal */}
      {showDeactivateServiceModal && serviceToDeactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    serviceToDeactivate.isActive ? 'bg-orange-100' : 'bg-green-100'
                  }`}>
                    <div className={`w-6 h-6 ${
                      serviceToDeactivate.isActive ? 'text-orange-600' : 'text-green-600'
                    }`}>
                      {serviceToDeactivate.isActive ? '⏸️' : '▶️'}
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {serviceToDeactivate.isActive ? 'Deactivate' : 'Activate'} Service
                  </h3>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to <strong>{serviceToDeactivate.isActive ? 'deactivate' : 'activate'}</strong> <strong>"{serviceToDeactivate.name}"</strong>?
                  {serviceToDeactivate.isActive 
                    ? ' This service will no longer be available for booking.' 
                    : ' This service will become available for booking.'
                  }
                </p>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleDeactivateServiceCancel}
                  className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleDeactivateServiceConfirm}
                  className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2 ${
                    serviceToDeactivate.isActive 
                      ? 'bg-orange-600 hover:bg-orange-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  <Power className="w-4 h-4" />
                  <span>{serviceToDeactivate.isActive ? 'Deactivate' : 'Activate'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRCodeModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={() => setShowQRCodeModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">QR Code</h3>
              <button
                onClick={() => setShowQRCodeModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex flex-col items-center space-y-4 mb-6">
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                <QRCodeSVG
                  value={`https://booking.aacore.net/${businessInfo?.shortName || currentBusiness?.shortName || 'beautyhaven'}`}
                  size={256}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Website URL</p>
                <p className="text-sm text-gray-600 font-mono break-all">
                  https://booking.aacore.net/{businessInfo?.shortName || currentBusiness?.shortName || 'beautyhaven'}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowQRCodeModal(false)}
                className="aacore-btn2-outline flex-1"
              >
                Close
              </button>
              <button
                onClick={handlePrintQRCode}
                className="aacore-btn2-primary flex-1"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Assignment Modal */}
      {isAssignmentModalOpen && assignmentAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold text-gray-900">
                Assign Professionals
              </h2>
              <button
                onClick={() => {
                  setIsAssignmentModalOpen(false);
                  setAssignmentAppointment(null);
                  setAssignmentSelections({});
                  setAssignmentCapabilities({});
                  setAssignmentSchedules({});
                  setAssignmentExistingAppointments({});
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isSavingAssignment}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Customer:</strong> {assignmentAppointment.customer_first_name} {assignmentAppointment.customer_last_name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Date & Time:</strong> {getAppointmentStartIso(assignmentAppointment) ? new Date(getAppointmentStartIso(assignmentAppointment)!).toLocaleString() : 'Date TBD'}
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Service Assignments</h3>
                {assignmentAppointment.assignments?.map((assignment: any) => (
                  <div key={assignment.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="mb-3">
                      <h4 className="font-medium text-gray-900">{assignment.serviceName}</h4>
                      <p className="text-sm text-gray-500">
                        Duration: {assignment.durationMinutes} min • Price: ${typeof assignment.price === 'number' ? assignment.price.toFixed(2) : parseFloat(assignment.price || '0').toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign Professional
                      </label>
                      {loadingAssignmentData ? (
                        <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                          <span className="ml-2 text-sm text-gray-500">Loading professionals...</span>
                        </div>
                      ) : assignmentProfessionals.length === 0 ? (
                        <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-500">
                          No professionals available for this business
                        </div>
                      ) : (
                        <select
                          value={assignmentSelections[assignment.id] || ''}
                          onChange={(e) => {
                            setAssignmentSelections(prev => ({
                              ...prev,
                              [assignment.id]: e.target.value
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                          disabled={isSavingAssignment}
                        >
                          <option value="">No assignment</option>
                          {assignmentProfessionals.length === 0 ? (
                            <option value="" disabled>No professionals available</option>
                          ) : (
                            assignmentProfessionals.map((professional: User) => {
                              const hasCapability = hasServiceCapability(professional.id, assignment.serviceId);
                              const assignmentStartIso = getAppointmentStartIso(assignmentAppointment);
                              const hasConflict = assignmentStartIso ? hasScheduleConflict(professional.id, assignmentStartIso, assignment.durationMinutes) : false;
                              const warnings = [];
                              if (!hasCapability) warnings.push('No capability');
                              if (hasConflict) warnings.push('Schedule conflict');
                              
                              return (
                                <option key={professional.id} value={professional.id}>
                                  {professional.firstName} {professional.lastName}
                                  {warnings.length > 0 && ` (${warnings.join(', ')})`}
                                </option>
                              );
                            })
                          )}
                        </select>
                      )}
                      <div className="mt-2 space-y-1">
                        {assignmentProfessionals.map((professional: User) => {
                          if (assignmentSelections[assignment.id] !== professional.id) return null;
                          const hasCapability = hasServiceCapability(professional.id, assignment.serviceId);
                          const assignmentStartIso = getAppointmentStartIso(assignmentAppointment);
                          const hasConflict = assignmentStartIso ? hasScheduleConflict(professional.id, assignmentStartIso, assignment.durationMinutes) : false;
                          return (
                            <div key={professional.id} className="flex flex-wrap gap-2 text-xs">
                              {!hasCapability && (
                                <div className="flex items-center gap-1 text-yellow-600">
                                  <AlertTriangle className="w-3 h-3" />
                                  <span>No capability for this service</span>
                                </div>
                              )}
                              {hasConflict && (
                                <div className="flex items-center gap-1 text-red-600">
                                  <Clock className="w-3 h-3" />
                                  <span>Schedule conflict</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {assignment.professionalFirstName && assignment.professionalLastName && (
                        <p className="text-xs text-gray-500 mt-1">
                          Currently assigned: {assignment.professionalFirstName} {assignment.professionalLastName}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                onClick={() => {
                  setIsAssignmentModalOpen(false);
                  setAssignmentAppointment(null);
                  setAssignmentSelections({});
                  setAssignmentCapabilities({});
                  setAssignmentSchedules({});
                  setAssignmentExistingAppointments({});
                }}
                className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                disabled={isSavingAssignment}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAssignment}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSavingAssignment}
              >
                {isSavingAssignment ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  'Save Assignments'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Details Modal */}
      {selectedReviewNotification && selectedReviewNotification.metadata && (
        <ReviewDetailsModal
          isOpen={isReviewDetailsModalOpen}
          onClose={() => {
            setIsReviewDetailsModalOpen(false);
            setSelectedReviewNotification(null);
          }}
          reviewData={{
            serviceName: selectedReviewNotification.metadata.serviceName || 'Unknown Service',
            customerName: selectedReviewNotification.metadata.customerName || 
              (selectedReviewNotification.metadata.customer_first_name && selectedReviewNotification.metadata.customer_last_name
                ? `${selectedReviewNotification.metadata.customer_first_name} ${selectedReviewNotification.metadata.customer_last_name}`
                : 'Customer'),
            customer_first_name: selectedReviewNotification.metadata.customer_first_name,
            customer_last_name: selectedReviewNotification.metadata.customer_last_name,
            rating: selectedReviewNotification.metadata.rating || 0,
            reviewFeedback: selectedReviewNotification.metadata.reviewFeedback,
            createdAt: selectedReviewNotification.metadata.createdAt || selectedReviewNotification.created_at,
            professionalName: selectedReviewNotification.metadata.professionalName || 
              (selectedReviewNotification.metadata.professional_first_name && selectedReviewNotification.metadata.professional_last_name
                ? `${selectedReviewNotification.metadata.professional_first_name} ${selectedReviewNotification.metadata.professional_last_name}`
                : undefined),
            appointmentId: selectedReviewNotification.metadata.appointmentId
          }}
        />
      )}

      {/* Appointment Settings Modal */}
      {isAppointmentSettingsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full md:max-h-[calc(100vh-2rem)] md:flex md:flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 md:flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#0B4778]" />
                Appointment Settings
              </h2>
              <button
                onClick={() => setIsAppointmentSettingsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 md:flex-1 md:min-h-0 md:overflow-y-auto">
              <PermissionGate permission="manage_settings">
                <div className="space-y-6">
                  {/* Auto Confirm Appointments Toggle */}
                  <div className="flex items-center md:items-start justify-between py-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-gray-900 m-0">
                          Auto-confirm appointments
                        </h4>
                        <span className="md:hidden">
                          <InfoTooltip content="When on, bookings are confirmed instantly and customers get a confirmation message. When off, bookings stay Pending until you confirm them." />
                        </span>
                      </div>
                      <p className="hidden md:block text-sm text-gray-500 mt-1">
                        When on, bookings are confirmed instantly and customers get a confirmation message. When off, bookings stay Pending until you confirm them.
                      </p>
                    </div>
                    <button
                      onClick={() => handleAppointmentSettingDraftChange('autoConfirmAppointments', !appointmentSettingsDraft.autoConfirmAppointments)}
                      className={`${
                        appointmentSettingsDraft.autoConfirmAppointments ? 'bg-[#0B4778]' : 'bg-gray-200'
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2 ml-4`}
                    >
                      <span
                        className={`${
                          appointmentSettingsDraft.autoConfirmAppointments ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </button>
                  </div>

                  {/* Email Appointment Activities to Owner Toggle */}
                  <div className="flex items-center md:items-start justify-between py-4 border-t border-gray-200">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-gray-900 m-0">
                          Email appointment activity to owner
                        </h4>
                        <span className="md:hidden">
                          <InfoTooltip content="When on, you'll get email updates when appointments are booked, canceled, or rescheduled." />
                        </span>
                      </div>
                      <p className="hidden md:block text-sm text-gray-500 mt-1">
                        When on, you&apos;ll get email updates when appointments are booked, canceled, or rescheduled.
                      </p>
                    </div>
                    <button
                      onClick={() => handleAppointmentSettingDraftChange('emailAppointmentActivitiesToOwner', !appointmentSettingsDraft.emailAppointmentActivitiesToOwner)}
                      className={`${
                        appointmentSettingsDraft.emailAppointmentActivitiesToOwner ? 'bg-[#0B4778]' : 'bg-gray-200'
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2 ml-4`}
                    >
                      <span
                        className={`${
                          appointmentSettingsDraft.emailAppointmentActivitiesToOwner ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </button>
                  </div>

                  {/* Show assigned professional next to service Toggle */}
                  <div className="flex items-center md:items-start justify-between py-4 border-t border-gray-200">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-gray-900 m-0">
                          Show assigned professional next to service
                        </h4>
                        <span className="md:hidden">
                          <InfoTooltip content="When on, the assigned professional's name will appear next to each service in the appointment details." />
                        </span>
                      </div>
                      <p className="hidden md:block text-sm text-gray-500 mt-1">
                        When on, the assigned professional&apos;s name will appear next to each service in the appointment details.
                      </p>
                    </div>
                    <button
                      onClick={() => handleAppointmentSettingDraftChange('showProfessionalNameOnAssignment', !appointmentSettingsDraft.showProfessionalNameOnAssignment)}
                      className={`${
                        appointmentSettingsDraft.showProfessionalNameOnAssignment ? 'bg-[#0B4778]' : 'bg-gray-200'
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2 ml-4`}
                    >
                      <span
                        className={`${
                          appointmentSettingsDraft.showProfessionalNameOnAssignment ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </button>
                  </div>

                  {/* Booking time block size — persisted in code_values */}
                  <div className="flex items-center md:items-start justify-between py-4 border-t border-gray-200 gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-gray-900 m-0">
                          Booking slot size
                        </h4>
                        <span className="md:hidden">
                          <InfoTooltip content="Choose the time blocks shown on the booking calendar. Default is 30 minutes." />
                        </span>
                      </div>
                      <p className="hidden md:block text-sm text-gray-500 mt-1">
                        Choose the time blocks shown on the booking calendar. Default is 30 minutes.
                      </p>
                    </div>
                    <div className="relative flex-shrink-0">
                      <select
                        value={appointmentSettingsDraft.bookingSlotMinutes}
                        onChange={(e) =>
                          setAppointmentSettingsDraft((prev) => ({
                            ...prev,
                            bookingSlotMinutes: Number(e.target.value) as 15 | 30 | 60
                          }))
                        }
                        className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none bg-white text-gray-900 text-sm min-w-[140px]"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em'
                        }}
                      >
                        <option value={60}>1 hour</option>
                        <option value={30}>30 minutes</option>
                        <option value={15}>15 minutes</option>
                      </select>
                    </div>
                  </div>
                </div>
              </PermissionGate>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 md:flex-shrink-0">
              <button
                onClick={() => setIsAppointmentSettingsModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors inline-flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                onClick={() => handleSaveAppointmentSettings()}
                disabled={savingSettings}
                className="aacore-btn2-primary shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0B4778] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingSettings ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Closure Confirmation Modal */}
      {showDeleteClosureModal && editingClosure && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Delete Closure</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to delete this closure?
                </p>
                {editingClosure.reason && (
                  <p className="text-sm text-gray-500 mt-2">
                    Closure: <strong>"{editingClosure.reason}"</strong>
                  </p>
                )}
                <p className="text-sm text-gray-500 mt-2">
                  This will permanently remove the closure and it will no longer block bookings.
                </p>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteClosureModal(false)}
                  className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={async () => {
                    if (!currentBusiness?.id || !editingClosure) return;
                    
                    try {
                      await apiService.deleteBusinessClosure(currentBusiness.id, editingClosure.id);
                      toast.success('Closure deleted successfully');
                      setShowClosureForm(false);
                      setEditingClosure(null);
                      setShowDeleteClosureModal(false);
                      loadClosures();
                    } catch (error: any) {
                      console.error('Error deleting closure:', error);
                      toast.error(error.response?.data?.message || 'Failed to delete closure');
                    }
                  }}
                  className="aacore-btn2-danger"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Closure</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDashboard;
